/**
 * KPI Dashboard with Fixed Grid System
 * 
 * Architecture:
 * - Desktop: 100-column fixed grid (positions never change on resize)
 * - Mobile: 20-column fixed grid (positions never change on resize) 
 * - CSS scaling: Grid scales down/up on smaller screens but maintains exact positions
 * - Breakpoint switching: Auto-switches between desktop/mobile views at 768px
 * - View independence: Desktop and mobile layouts are completely separate
 * 
 * This prevents the layout repositioning issues that occurred with responsive grids.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Activity, FileBarChart, Edit3, Trash2, Calendar, ChevronDown, ShoppingCart, Package2, Monitor, Smartphone, Star, Check, Info, Sparkles, Settings } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

// Draws the total count above each stacked bar (used by the new-leads graph).
const stackedBarTotalsPlugin = {
  id: 'stackedBarTotals',
  afterDatasetsDraw(chart: any) {
    const { ctx, data, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;
    if (!xScale || !yScale) return;
    ctx.save();
    ctx.font = '600 10px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = 'rgb(55, 65, 81)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    (data.labels || []).forEach((_: any, i: number) => {
      const total = data.datasets.reduce((sum: number, ds: any) => {
        const meta = chart.getDatasetMeta(data.datasets.indexOf(ds));
        return meta.hidden ? sum : sum + (Number(ds.data[i]) || 0);
      }, 0);
      if (total <= 0) return;
      ctx.fillText(String(total), xScale.getPixelForValue(i), yScale.getPixelForValue(total) - 3);
    });
    ctx.restore();
  }
};

import PermissionGuard from '../components/PermissionGuard';
import { useBusiness } from '../contexts/BusinessContext';
import {
  fetchStandaloneInvoiceSales,
  sumStandaloneInvoiceSales,
} from '../lib/invoices/standaloneInvoiceSales';
import KPIDashboardSkeleton from '../components/KPIDashboardSkeleton';
import AIAnalyzerModal from '../components/AIAnalyzerModal';
import { 
  getKPIDashboards, 
  createKPIDashboard, 
  updateKPIDashboard,
  deleteKPIDashboard, 
  getDashboardReports,
  createDashboardReport,
  updateDashboardReportLayout,
  deleteDashboardReport,
  updateDashboardDefaultDateRange,
  configureOppositeView,
  detectDeviceType
} from '../lib/kpiDashboards';
import { supabase } from '../lib/supabase';
import { isExcludedAnalyticsEvent } from '../lib/analyticsBotFilter';
import toast from 'react-hot-toast';
import RGL, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { getSuppliers, Customer } from '../lib/customers';
import { listCustomerSegments } from '../lib/customers/segments';
import { createOrder, OrderItem as DBOrderItem, getPendingOrdersForItems } from '../lib/purchaseOrders';
import { StockItem } from '../lib/stockItems';

const GridLayout = WidthProvider(RGL);

// Counter sales carry a linked standalone invoice. If that invoice was voided
// (or is still a draft, i.e. never issued), the sale isn't revenue — product
// sales reports must skip those rows or voided sales inflate the totals.
const isDeadInvoiceProductSale = (sale: {
  invoice?: { status?: string } | { status?: string }[] | null;
}): boolean => {
  const inv = Array.isArray(sale.invoice) ? sale.invoice[0] : sale.invoice;
  return !!inv && (inv.status === 'void' || inv.status === 'draft');
};

// product_sale_items has no line_type column — deposit / delivery / rental
// lines from customer orders are identified by their canonical name prefixes
// (they also carry no stock_item_id, so they can't be categorized by catalog).
const classifyProductSaleLine = (name: string): 'deposit' | 'delivery' | 'rental' | 'product' => {
  // Old lines: "Bottle deposit — …". New lines: "{Label} deposit — {product} (refundable)".
  if (name.startsWith('Deposit refund — ')) return 'deposit';
  if (/ deposit — /i.test(name) && /\(refundable\)\s*$/i.test(name)) return 'deposit';
  if (name.startsWith('Delivery — ') || name.startsWith('Delivery surcharge — ')) return 'delivery';
  if (/^Rental( R-?\d+)? — /.test(name)) return 'rental';
  return 'product';
};

// "Rental R-0016 — Bottle Rack (hold 4) (first month)" → "Bottle Rack (hold 4)"
const rentalLineItemName = (name: string): string =>
  name.replace(/^Rental( R-?\d+)? — /, '').replace(/ \(first (week|month)\)$/, '');

interface Dashboard {
  id: string;
  name: string;
  createdAt: Date;
  reports?: Report[];
  dateRange?: DateRangeOption;
  defaultDateRange?: DateRangeOption;
  customStartDate?: Date;
  customEndDate?: Date;
  createdDeviceType: 'desktop' | 'mobile';
  desktopConfigured: boolean;
  mobileConfigured: boolean;
}

type ReportSize = 'small' | 'medium' | 'large' | 'xl';

interface Report {
  id: string;
  type: string;
  title: string;
  data?: any;
  size?: ReportSize; // New size field
  layout: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

type DateRangeOption = 'this_month' | 'last_month' | 'next_month' | 'this_week' | 'last_week' | 'next_week' | 'this_year' | 'last_year' | 'custom';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

// Size dimension definitions for fixed-size reports
// Desktop: 100-column grid, 1200px width, 8px row height
// - Each column = 12px (1200px / 100)
// - Each row = 8px
const SIZE_DIMENSIONS = {
  desktop: {
    small: { w: 13, h: 9 },   // 156px × 72px (13 cols × 12px = 156px, 9 rows × 8px = 72px) - closest to 160×70
    medium: { w: 20, h: 18 },  // 240px × 144px (20 cols × 12px = 240px, 18 rows × 8px = 144px) - same height as 2 small reports
    large: { w: 40, h: 40 },   // 480px × 320px (40 cols × 12px = 480px, 40 rows × 8px = 320px)
    xl: { w: 60, h: 40 }       // 720px × 320px (60 cols × 12px = 720px, 40 rows × 8px = 320px)
  },
  mobile: {
    small: { w: 9, h: 8 },   // 9 cols × 8 rows = 9 cols × 64px height (closest to 60px with 8px row height)
    medium: { w: 10, h: 15 },  // 10 cols (half width) × 15 rows = 10 cols × 120px height
    large: { w: 20, h: 30 },   // 20 cols (full width) × 30 rows = 20 cols × 240px height (4 medium squares: 2×2 grid)
    xl: { w: 20, h: 41 }       // 20 cols (full width) × 41 rows = 20 cols × 328px height (240px + 90px = 330px, closest with 8px row height)
  }
};

// Helper function to get dimensions for a size
const getSizeDimensions = (size: ReportSize, viewType: 'desktop' | 'mobile'): { w: number; h: number } => {
  return SIZE_DIMENSIONS[viewType][size];
};

// Helper function to get default size (medium)
const getDefaultSize = (): ReportSize => 'medium';

// Helper function to get the smallest available size for a report type (for previews)
const getSmallestAvailableSize = (reportType: string): ReportSize => {
  if (shouldUseSizeSystem(reportType)) {
    return 'small'; // S/M system - smallest is S
  } else if (shouldUseChartSizeSystem(reportType)) {
    return 'large'; // L/XL system - smallest is L
  } else if (shouldUseMLSizeSystem(reportType)) {
    return 'medium'; // M/L system - smallest is M
  } else if (shouldUseMLXLSizeSystem(reportType)) {
    return 'medium'; // M/L/XL system - smallest is M
  }
  return 'medium'; // Default fallback
};

// Helper function to get all available sizes for a report type
const getAvailableSizes = (reportType: string): ReportSize[] => {
  if (shouldUseSizeSystem(reportType)) {
    return ['small', 'medium']; // S/M system
  } else if (shouldUseChartSizeSystem(reportType)) {
    return ['large', 'xl']; // L/XL system
  } else if (shouldUseMLSizeSystem(reportType)) {
    return ['medium', 'large']; // M/L system
  } else if (shouldUseMLXLSizeSystem(reportType)) {
    return ['medium', 'large', 'xl']; // M/L/XL system
  }
  return ['small', 'medium', 'large', 'xl']; // Default fallback
};

// Helper function to format size labels
const formatSizeLabel = (size: ReportSize): string => {
  return size.toUpperCase();
};

// Helper function to get detailed summary for a report type
const getReportSummary = (reportType: string): string => {
  const summaries: { [key: string]: string } = {
    'sales': 'Total revenue from all completed sales after discounts and adjustments. Includes all payment methods and booking types.',
    'bookings_sales': 'Total revenue from bookings only (mobile and regular bookings) after discounts and adjustments. Excludes other sales sources.',
    'product_sales': 'Total revenue from product sales to customers. Includes all product sales from the inventory system. Excludes internal withdrawals and stock adjustments.',
    'gift_card_sales': 'Total revenue from gift card purchases. Shows the initial value of all gift cards sold within the selected date range.',
    'upsells': 'Additional revenue generated from any additional services or products added to bookings while they are in progress. Tracks incremental sales opportunities during active work.',
    'closed_sales': 'Total value of all sales that have been completed and finalized. Represents confirmed revenue from finished bookings.',
    'net_profit': 'Total profit after deducting all expenses including labor, materials, overhead, and other operational costs from revenue.',
    'average_sale': 'Average transaction value calculated by dividing total sales by the number of bookings. Helps track pricing effectiveness.',
    'proposals_sent': 'Total number of proposals or quotes sent to potential customers. Tracks your sales outreach and engagement efforts.',
    'proposals_booked': 'Number of proposals that were converted into confirmed bookings. Measures proposal conversion rate and sales effectiveness.',
    'proposals_approved': 'Proposals that have been approved by customers but may not yet be booked. Tracks pending conversions and customer interest.',
    'sales_graph': 'Visual trend chart showing sales performance over time. Displays daily, weekly, monthly, or yearly sales patterns with comparison to previous periods.',
    'booking_source': 'Breakdown of bookings by their origin source (e.g., website, phone, walk-in, referral). Helps identify which marketing channels drive the most business.',
    'total_jobs': 'Total number of all bookings or jobs in the system, regardless of status. Includes completed, pending, and scheduled work.',
    'completed_jobs': 'Number of jobs that have been finished and marked as complete. Tracks productivity and completion rates.',
    'labour': 'Total labor costs incurred for all jobs. Includes wages, contractor fees, and other labor-related expenses.',
    'job_materials': 'Total cost of materials added to jobs. Does not track from inventory consumption - only tracks materials manually added to job records.',
    'online_booking_volume': 'Number of bookings made through your online booking system. Tracks digital channel performance and customer self-service adoption.',
    'team_rating': 'Average customer rating or feedback score for your team\'s performance. Measures service quality and customer satisfaction.',
    'feedback_percentage': 'Percentage of customers who provided feedback or reviews. Indicates engagement and helps track review collection efforts.',
    'website_visits': 'Total number of visits to your website. Tracks web traffic and online presence.',
    'website_visits_graph': 'Visual chart showing website traffic trends over time. Displays visitor patterns and helps identify traffic sources and trends.',
    'google_reviews': 'Number of clicks or interactions with Google review links. Tracks review engagement and reputation management efforts.',
    'phone_calls': 'Total number of incoming phone calls received. Tracks communication volume and customer contact channels.',
    'missed_calls': 'Number of phone calls that were not answered. Helps identify staffing needs and customer service opportunities.',
    'web_traffic_patterns': 'Comprehensive analytics showing landing pages, popular pages, top clicks, form submissions, and paid vs organic traffic breakdown. Uses UTM tags to identify ad campaign traffic.',
    'crm_leads_overview': 'Summary of CRM lead performance showing total leads, won opportunities, and lost deals. Provides quick overview of sales pipeline health.',
    'crm_new_leads_graph': 'Stacked bar chart of new leads by creation date. Each bar shows how many leads were created that day, broken into open, won, and lost by their current status — segments shift from open to won/lost as leads close.',
    'crm_won_lost_by_staff': 'Won and lost leads grouped by the team member each lead is assigned to. Shows open, won, and lost counts per person with win rate, for leads created in the selected period.',
    'crm_leads_created_by_resource': 'Number of leads each team member created during the selected period, with their share of the total. Grouped by the creating resource.',
    'sales_pins': 'Door-to-door sales pins dropped on the sales map during the selected period, broken down by their current status (Interested, Come Back Later, Estimate Sent, Closed, Not Home, Not Interested). Statuses update as pins are edited, so the mix shifts as knocks progress.',
    'wallet_overview': 'Customer wallet balances at a glance: total liability currently sitting in all wallets (point-in-time, not affected by the date range), plus how much was added to wallets and how much was spent from wallets during the selected period. Wallet top-ups are payments, not sales, so this is where that money is visible.',
    'crm_lead_lifecycle': 'How long leads take to close. For leads created in the selected period: average days from creation to close overall, plus separate averages for created→won and created→lost — useful for spotting lost leads you sat on too long. Close times come from the CRM status-change log.',
    'active_customers': 'Active customers from the Active customers segment on the Customers page. Shows how many customers are active at the end of the period, plus how many became active and how many went inactive compared to the start of the period.',
    'crm_pipeline_stages': 'Visual breakdown of leads distributed across different pipeline stages. Shows where leads are in the sales process and identifies bottlenecks.',
    'crm_leads_by_source': 'Analysis of lead performance by source (e.g., website, referral, social media). Shows which channels generate the most valuable leads with win rates and conversion metrics.',
    'crm_lost_reasons': 'Pie chart breakdown of lost leads by reason. Shows why leads are being lost to help identify patterns and improve conversion rates.',
    'supplies_purchased': 'Total value of supplies and inventory items purchased. Tracks procurement spending and inventory investment.',
    'expenses': 'Total expenses from the expenses tracking system. Includes all business expenses tracked with receipts and manual entries.',
    'stock_alert': 'Number of inventory items that have fallen below reorder thresholds. Helps manage stock levels and prevent stockouts.',
    'payments': 'Total payments collected from all sources including bookings, projects, proposals, product sales, and gift card purchases. Includes deposits, partial payments, and full payments. Shows actual cash flow received.',
    'payments_by_type': 'Breakdown of payments by type (bookings, projects, proposals, products, gift cards) and by status (deposits, full payments, partial payments). Shows total amount and detailed breakdowns.',
    'product_sales_by_category': 'Advanced product sales analysis showing sales grouped by category. Click a category to expand and view detailed sales breakdown by individual items within that category.',
    'service_sales_by_category': 'Advanced service sales analysis showing total booking sales and sales grouped by service category. Click a category to expand and view detailed sales breakdown by individual services within that category.',
    'project_pipeline_stages': 'Visual breakdown of projects distributed across different workflow stages. Shows where projects are in the process and identifies bottlenecks.',
    'project_source': 'Breakdown of projects by their customer source (e.g., website, referral, walk-in). Helps identify which marketing channels drive the most project work.',
    'project_revenue': 'Total potential revenue from projects that have not been converted to bookings. Includes revenue from project proposals and services. Projects converted to bookings are excluded to avoid double-counting.',
    'title': 'Custom text element for adding titles, labels, or notes to your dashboard. Useful for organizing and categorizing dashboard sections.'
  };
  
  return summaries[reportType] || 'Detailed information about this report type.';
};

// Helper function to check if a report type should use S/M size system
const shouldUseSizeSystem = (reportType: string): boolean => {
  const sizeSystemReports = [
    'total_revenue',
    'total_bookings',
    'average_sale',
    'sales',
    'bookings_sales',
    'product_sales',
    'gift_card_sales',
    'upsells',
    'closed_sales',
    // 'net_profit' removed - now uses shouldUseMLXLSizeSystem
    'proposals_sent',
    'proposals_booked',
    'proposals_approved',
    'total_jobs',
    'completed_jobs',
    'labour',
    'job_materials',
    'team_rating',
    'feedback_percentage',
    'google_reviews',
    'phone_calls',
    'missed_calls',
    'website_visits',
    'supplies_purchased',
    'expenses',
    'payments',
    'project_revenue',
    'mrr',
    'arr'
  ];
  return sizeSystemReports.includes(reportType);
};

// Helper function to check if a report type should use L/XL size system (chart reports)
const shouldUseChartSizeSystem = (reportType: string): boolean => {
  const chartSizeReports = [
    'sales_graph',
    'crm_new_leads_graph',
    'crm_won_lost_by_staff',
    'crm_leads_created_by_resource',
    'sales_pins',
    'website_visits_graph',
    'booking_source',
    'project_source',
    'online_booking_volume',
    'crm_leads_by_source',
    'crm_pipeline_stages',
    'crm_lost_reasons',
    'web_traffic_patterns',
    'product_sales_by_category',
    'service_sales_by_category',
    'project_pipeline_stages'
  ];
  return chartSizeReports.includes(reportType);
};

// Helper function to check if a report type should use M/L size system (not S/M only, not L/XL only)
const shouldUseMLSizeSystem = (reportType: string): boolean => {
  const mlSizeReports = [
    'crm_leads_overview',
    'active_customers',
    'wallet_overview',
    'crm_lead_lifecycle',
    'page_route_analytics',
    'click_analytics',
    'payments_by_type',
    'booking_source',
    'project_source'
  ];
  return mlSizeReports.includes(reportType);
};

// Helper function to check if a report type should use M/L/XL size system
const shouldUseMLXLSizeSystem = (reportType: string): boolean => {
  const mlxlSizeReports = [
    'stock_alert',
    'net_profit'
  ];
  return mlxlSizeReports.includes(reportType);
};

// Interfaces for Stock Alert functionality
interface OrderItem {
  item: StockItem;
  quantityToOrder: number;
  totalCost: number;
}

interface SupplierOrder {
  supplier: Customer;
  items: OrderItem[];
  totalCost: number;
  itemCount: number;
}

// StarRating component for Team Rating display
interface StarRatingProps {
  rating: number;
  size?: 'small' | 'medium' | 'large';
  showValue?: boolean;
  dynamicSize?: number; // Custom size in pixels for dynamic scaling
}

const StarRating: React.FC<StarRatingProps> = ({ rating, size = 'medium', showValue = false, dynamicSize }) => {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-5 w-5',
    large: 'h-6 w-6'
  };

  // Calculate dynamic star size based on font size if provided
  const starSize = dynamicSize ? `${Math.max(dynamicSize * 0.6, 12)}px` : undefined;

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;

  return (
    <div className="flex items-center space-x-1">
      <div className="flex space-x-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${dynamicSize ? '' : sizeClasses[size]} ${
              star <= fullStars
                ? 'text-yellow-400 fill-current'
                : star === fullStars + 1 && hasHalfStar
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
            style={dynamicSize ? { width: starSize, height: starSize } : undefined}
          />
        ))}
      </div>
      {showValue && (
        <span className="text-sm font-medium text-gray-700 ml-2">{rating.toFixed(1)}</span>
      )}
    </div>
  );
};

// Utility functions for date range calculations
const getDateRange = (option: DateRangeOption, customStartDate?: Date, customEndDate?: Date): DateRange => {
  const now = new Date();
  const startOfWeek = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day;
    return new Date(start.setDate(diff));
  };
  
  const endOfWeek = (date: Date) => {
    const end = new Date(startOfWeek(date));
    end.setDate(end.getDate() + 6);
    return end;
  };

  const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
  const endOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

  switch (option) {
    case 'this_month':
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now)
      };
    case 'last_month':
      return {
        startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1)),
        endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() - 1))
      };
    case 'next_month':
      return {
        startDate: startOfMonth(new Date(now.getFullYear(), now.getMonth() + 1)),
        endDate: endOfMonth(new Date(now.getFullYear(), now.getMonth() + 1))
      };
    case 'this_week':
      return {
        startDate: startOfWeek(now),
        endDate: endOfWeek(now)
      };
    case 'last_week':
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      return {
        startDate: startOfWeek(lastWeek),
        endDate: endOfWeek(lastWeek)
      };
    case 'next_week':
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);
      return {
        startDate: startOfWeek(nextWeek),
        endDate: endOfWeek(nextWeek)
      };
    case 'this_year':
      return {
        startDate: new Date(now.getFullYear(), 0, 1), // January 1st of current year
        endDate: new Date(now.getFullYear(), 11, 31) // December 31st of current year
      };
    case 'last_year':
      return {
        startDate: new Date(now.getFullYear() - 1, 0, 1), // January 1st of last year
        endDate: new Date(now.getFullYear() - 1, 11, 31) // December 31st of last year
      };
    case 'custom':
      return {
        startDate: customStartDate || startOfMonth(now),
        endDate: customEndDate || endOfMonth(now)
      };
    default:
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now)
      };
  }
};

// Returns true when the range covers exactly one full calendar month
const isFullMonthRange = (startDate: Date, endDate: Date): boolean => {
  const lastDayOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
  return (
    startDate.getDate() === 1 &&
    endDate.getFullYear() === startDate.getFullYear() &&
    endDate.getMonth() === startDate.getMonth() &&
    endDate.getDate() === lastDayOfMonth
  );
};

const getDateRangeLabel = (option: DateRangeOption, customStartDate?: Date, customEndDate?: Date): string => {
  if (option === 'custom' && customStartDate && customEndDate) {
    if (isFullMonthRange(customStartDate, customEndDate)) {
      const sameYear = customStartDate.getFullYear() === new Date().getFullYear();
      return customStartDate.toLocaleDateString('en-US', sameYear ? { month: 'long' } : { month: 'long', year: 'numeric' });
    }
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    return `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`;
  }
  const labels: Record<DateRangeOption, string> = {
    'this_month': 'This Month',
    'last_month': 'Last Month', 
    'next_month': 'Next Month',
    'this_week': 'This Week',
    'last_week': 'Last Week',
    'next_week': 'Next Week',
    'this_year': 'This Year',
    'last_year': 'Last Year',
    'custom': 'Custom Range'
  };
  return labels[option];
};

// Date Range Selector Component
interface DateRangeSelectorProps {
  value: DateRangeOption;
  onChange: (value: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => void;
  defaultValue?: DateRangeOption;
  onSetDefault?: (value: DateRangeOption) => void;
  customStartDate?: Date;
  customEndDate?: Date;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ value, onChange, defaultValue, onSetDefault, customStartDate, customEndDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomPickerOpen, setIsCustomPickerOpen] = useState(false);
  const [tempCustomStartDate, setTempCustomStartDate] = useState<string>('');
  const [tempCustomEndDate, setTempCustomEndDate] = useState<string>('');
  
  const options: DateRangeOption[] = [
    'this_month',
    'last_month', 
    'next_month',
    'this_week',
    'last_week',
    'next_week',
    'this_year',
    'last_year',
    'custom'
  ];

  // Months of the current year so far, most recent first
  const monthOptions = (() => {
    const now = new Date();
    return Array.from({ length: now.getMonth() + 1 }, (_, i) => {
      const monthIndex = now.getMonth() - i;
      const start = new Date(now.getFullYear(), monthIndex, 1);
      const end = new Date(now.getFullYear(), monthIndex + 1, 0, 23, 59, 59);
      return {
        label: start.toLocaleDateString('en-US', { month: 'long' }),
        start,
        end
      };
    });
  })();

  const isMonthSelected = (start: Date, end: Date): boolean => {
    if (value !== 'custom' || !customStartDate || !customEndDate) return false;
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    return sameDay(customStartDate, start) && sameDay(customEndDate, end);
  };

  const handleMonthSelect = (start: Date, end: Date) => {
    onChange('custom', start, end);
    setIsOpen(false);
  };

  const handleSelect = (option: DateRangeOption) => {
    if (option === 'custom') {
      setIsCustomPickerOpen(true);
      // Initialize temp dates with existing custom dates or current month
      if (customStartDate && customEndDate) {
        setTempCustomStartDate(customStartDate.toISOString().split('T')[0]);
        setTempCustomEndDate(customEndDate.toISOString().split('T')[0]);
      } else {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        setTempCustomStartDate(startOfMonth.toISOString().split('T')[0]);
        setTempCustomEndDate(endOfMonth.toISOString().split('T')[0]);
      }
    } else {
    onChange(option);
    setIsOpen(false);
    }
  };

  const handleCustomDateApply = () => {
    if (tempCustomStartDate && tempCustomEndDate) {
      const startDate = new Date(tempCustomStartDate + 'T00:00:00');
      const endDate = new Date(tempCustomEndDate + 'T23:59:59');
      onChange('custom', startDate, endDate);
      setIsCustomPickerOpen(false);
      setIsOpen(false);
    }
  };

  const handleSetDefault = (option: DateRangeOption, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onSetDefault) {
      onSetDefault(option);
    }
  };

  return (
    <div className="relative date-range-selector">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between px-3 py-2 bg-white rounded-lg active:bg-gray-50 transition-colors touch-manipulation border border-gray-300/50 shadow-sm"
        style={{ 
          minHeight: '36px',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        <span className="flex items-center gap-2 text-[15px] font-medium text-gray-900">
          <Calendar className="h-4 w-4 text-gray-600" />
          {getDateRangeLabel(value, customStartDate, customEndDate)}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setIsOpen(false)}
          style={{ 
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-4 overflow-y-auto"
            style={{
              minWidth: '280px',
              maxWidth: '400px',
              width: '100%',
              maxHeight: '80vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 pt-3 pb-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
              {new Date().getFullYear()} Months
            </div>
            {monthOptions.map((month) => {
              const isSelected = isMonthSelected(month.start, month.end);
              return (
                <button
                  key={month.label}
                  onClick={() => handleMonthSelect(month.start, month.end)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-[17px] text-left transition-colors touch-manipulation border-b border-gray-100 ${
                    isSelected ? 'bg-blue-50' : 'active:bg-gray-50'
                  }`}
                  style={{ minHeight: '44px' }}
                >
                  <Calendar className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                  <span className={`font-medium ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
                    {month.label}
                  </span>
                </button>
              );
            })}
            <div className="px-4 pt-3 pb-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide border-t border-gray-200">
              Other Ranges
            </div>
            {options.map((option, index) => {
              // A full-month custom range is shown as selected in the months list above, not here
              const isSelected = value === option &&
                !(option === 'custom' && customStartDate && customEndDate && isFullMonthRange(customStartDate, customEndDate));
              const isDefault = defaultValue === option;
              
              return (
            <div
              key={option}
                  className={`w-full flex items-center justify-between px-4 py-3.5 text-[17px] transition-colors touch-manipulation ${
                    index !== options.length - 1 ? 'border-b border-gray-100' : ''
                  } ${isSelected ? 'bg-blue-50' : 'active:bg-gray-50'}`}
                  style={{ minHeight: '44px' }}
            >
              <button
                onClick={() => handleSelect(option)}
                    className="flex items-center gap-3 flex-1 text-left"
              >
                    <Calendar className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span className={`font-medium ${isSelected ? 'text-blue-600' : 'text-gray-900'}`}>
                {getDateRangeLabel(option)}
                    </span>
              </button>
              {onSetDefault && (
                    <div className="flex items-center gap-2 ml-2" onClick={(e) => handleSetDefault(option, e)}>
                  <input
                    type="checkbox"
                        checked={isDefault}
                    onChange={(e) => e.stopPropagation()}
                    onClick={(e) => handleSetDefault(option, e)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-500">Default</span>
                </div>
              )}
            </div>
              );
            })}
          {onSetDefault && (
              <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
              Check a box to set as default for this dashboard
            </div>
          )}
        </div>
        </div>,
        document.body
      )}

      {/* Custom Date Range Picker Modal */}
      {isCustomPickerOpen && createPortal(
        <div 
          className="fixed inset-0 z-[10000] bg-black/40 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setIsCustomPickerOpen(false)}
          style={{ 
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-4 p-6"
            style={{
              minWidth: '320px',
              maxWidth: '400px',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ letterSpacing: '-0.01em' }}>
              Select Custom Range
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempCustomStartDate}
                  onChange={(e) => setTempCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  style={{ letterSpacing: '-0.01em' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={tempCustomEndDate}
                  onChange={(e) => setTempCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                  style={{ letterSpacing: '-0.01em' }}
                />
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setIsCustomPickerOpen(false)}
                className="px-5 py-2.5 text-[15px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-[10px] active:bg-gray-50 transition-all duration-150 active:scale-[0.97]"
                style={{ 
                  minHeight: '36px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!tempCustomStartDate || !tempCustomEndDate}
                className="px-5 py-2.5 text-[15px] font-semibold text-white bg-blue-600 active:bg-blue-700 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.97] shadow-sm"
                style={{ 
                  minHeight: '36px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em'
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Helper function to format currency without unnecessary decimals
const formatCurrency = (amount: number, showSign: boolean = false): string => {
  const sign = showSign && amount >= 0 ? '+' : '';
  const absAmount = Math.abs(amount);
  
  // If the amount has no decimal part, don't show decimals
  if (absAmount % 1 === 0) {
    return `${sign}$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  } else {
    return `${sign}$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

// Component to render report preview in modal
const ReportPreview: React.FC<{
  reportType: string;
  reportTitle: string;
  size: ReportSize;
  viewType: 'desktop' | 'mobile';
  data?: any;
}> = ({ reportType, reportTitle, size, viewType, data }) => {
  const [websiteVisitsMode, setWebsiteVisitsMode] = useState<'visits' | 'unique'>('visits');

  // Always use the smallest available size for previews
  const smallestSize = getSmallestAvailableSize(reportType);
  const dimensions = getSizeDimensions(smallestSize, 'desktop');
  
  // Calculate actual pixel dimensions based on desktop grid system
  // Desktop: 100-column grid, 1200px width, 8px row height
  // Each column = 12px (1200px / 100)
  // Each row = 8px
  const widthPx = dimensions.w * 12; // columns * 12px
  const heightPx = dimensions.h * 8; // rows * 8px
  
  // Calculate aspect ratio from actual dimensions
  const aspectRatio = widthPx / heightPx;
  
  // For modal previews, limit max width to keep them compact
  // Small reports: max 180px, Medium: max 240px, Large/XL: max 320px
  let maxWidth = 240;
  if (smallestSize === 'small') {
    maxWidth = 180;
  } else if (smallestSize === 'medium') {
    maxWidth = 240;
  } else if (smallestSize === 'large' || smallestSize === 'xl') {
    maxWidth = 320;
  }

  // Render based on report type and smallest size - using scaled dimensions for modal
  const baseCardClasses = "bg-white border border-gray-200/60 rounded-[12px] overflow-hidden w-full";
  const cardStyle = { 
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
    aspectRatio: aspectRatio.toString(),
    maxWidth: `${maxWidth}px`
  };

  if (reportType === 'title') {
    return (
      <div className={baseCardClasses} style={cardStyle}>
        <div className="flex flex-col h-full p-4 items-center justify-center">
          <div className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
            {data?.text || 'Dashboard Title'}
          </div>
        </div>
      </div>
    );
  }

  // S/M size system reports - always use 'small' (smallest)
  if (shouldUseSizeSystem(reportType)) {
    return (
      <div className={baseCardClasses} style={cardStyle}>
        <div className="flex flex-col h-full p-3">
          <div className="mb-2 flex-shrink-0">
            <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
              {reportTitle}
            </span>
          </div>
          <div className="flex-1 flex flex-col items-start justify-center">
            <div className="flex items-center gap-2">
              <div className="text-xl font-bold text-gray-900">
                {data?.value || '$0.00'}
              </div>
              {data?.comparison && (
                <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                  (() => {
                    // For expenses, invert colors: up (increase) is bad (red), down (decrease) is good (green)
                    if (reportType === 'expenses') {
                      return data.comparison.direction === 'up' 
                        ? 'bg-red-100 text-red-700' 
                        : data.comparison.direction === 'down' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700';
                    }
                    // For other reports, normal logic: up is good (green), down is bad (red)
                    return data.comparison.direction === 'up' 
                      ? 'bg-green-100 text-green-700' 
                      : data.comparison.direction === 'down' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-gray-100 text-gray-700';
                  })()
                }`}>
                  {data.comparison.direction === 'up' && '↑'}
                  {data.comparison.direction === 'down' && '↓'}
                  {data.comparison.direction === 'same' && '='}
                  <span>{Math.round(data.comparison.percentage)}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chart reports (L/XL) - always use 'large' (smallest), span 2 columns
  if (shouldUseChartSizeSystem(reportType)) {
    // Special handling for website_visits_graph - match dashboard styling
    if (reportType === 'website_visits_graph') {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            <div className="mb-1.5 flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            <div className="flex-1 flex flex-col min-h-0">
              <div className="text-left mb-1.5 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setWebsiteVisitsMode('visits')}
                    className={`flex items-center gap-1.5 transition-opacity ${websiteVisitsMode === 'visits' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                  >
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgb(59, 130, 246)' }} />
                    <div className="text-lg font-bold text-gray-900">{data?.value || '0'}</div>
                    <div className="text-[10px] text-gray-500 self-end mb-0.5">visits</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setWebsiteVisitsMode('unique')}
                    className={`flex items-center gap-1.5 transition-opacity ${websiteVisitsMode === 'unique' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                  >
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgb(147, 51, 234)' }} />
                    <div className="text-lg font-bold text-gray-900">{data?.uniqueValue || '0'}</div>
                    <div className="text-[10px] text-gray-500 self-end mb-0.5">unique</div>
                  </button>
                  {data?.conversionRate && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgb(234, 88, 12)' }} />
                      <div className="text-lg font-bold text-gray-900">{data.conversionRate}%</div>
                      <div className="text-[10px] text-gray-500 self-end mb-0.5">conv.</div>
                    </div>
                  )}
                </div>
              </div>
              {(websiteVisitsMode === 'visits' ? data?.chartData : data?.uniqueChartData) && (
                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                  <div className="h-full w-full">
                    <Line
                      data={websiteVisitsMode === 'visits' ? data.chartData : data.uniqueChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                              boxWidth: 12,
                              font: {
                                size: 10
                              },
                              padding: 8,
                              usePointStyle: true
                            }
                          },
                          tooltip: {
                            enabled: false
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(59, 130, 246, 0.1)'
                            },
                            ticks: {
                              callback: (value: any) => `${value}`,
                              font: {
                                size: 10
                              }
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              font: {
                                size: 10
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // Special handling for web_traffic_patterns - show preview content
    if (reportType === 'web_traffic_patterns') {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            <div className="mb-1.5 flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            <div className="flex-1 flex flex-col min-h-0">
              <div className="text-left mb-1.5 flex-shrink-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="text-lg font-bold text-gray-900">
                    {data?.value || 'Comprehensive'}
                  </div>
                  {data?.comparison && (
                    <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      data.comparison.direction === 'up' 
                        ? 'bg-green-100 text-green-700' 
                        : data.comparison.direction === 'down' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                    {data.comparison.direction === 'up' && '↑'}
                    {data.comparison.direction === 'down' && '↓'}
                    {data.comparison.direction === 'same' && '='}
                    <span>{Math.round(data.comparison.percentage)}%</span>
                  </div>
                )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {data?.label || 'Traffic Analytics'}
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                <div className="h-full w-full flex items-center justify-center">
                  <div className="text-xs text-gray-400">Traffic Patterns Preview</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Special handling for online_booking_volume - show funnel steps
    if (reportType === 'online_booking_volume') {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            <div className="mb-1.5 flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            <div className="flex-1 flex flex-col min-h-0">
              <div className="text-left mb-1.5 flex-shrink-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="text-lg font-bold text-gray-900">
                    {data?.value || '0.0%'}
                  </div>
                  {data?.comparison && (
                    <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      data.comparison.direction === 'up' 
                        ? 'bg-green-100 text-green-700' 
                        : data.comparison.direction === 'down' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                    {data.comparison.direction === 'up' && '↑'}
                    {data.comparison.direction === 'down' && '↓'}
                    {data.comparison.direction === 'same' && '='}
                    <span>{Math.round(data.comparison.percentage)}%</span>
                  </div>
                )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {data?.label || 'Conversion Rate'}
                </div>
              </div>
              
              {/* Funnel Steps */}
              {data?.funnelData && (
                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                  <div className="space-y-1.5">
                    {(data.funnelData || []).map((step: any, index: number) => {
                      const maxCount = Math.max(...(data.funnelData || []).map((s: any) => s.count));
                      const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                      
                      return (
                        <div key={step.step} className="flex items-center space-x-2">
                          <div className="flex-1 bg-gray-100/50 backdrop-blur-sm rounded-lg h-3 relative overflow-hidden border border-white/30"
                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                            <div 
                              className={`h-full rounded-lg transition-all duration-300 ${
                                index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                index === 1 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                index === 2 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                                index === 3 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                                index === 4 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                'bg-gradient-to-r from-blue-500 to-blue-600'
                              }`}
                              style={{ width: `${percentage}%` }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-[10px] font-medium text-gray-700">
                                {step.count}
                              </span>
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-600 min-w-0 flex-shrink-0 w-12 truncate">
                            {step.step}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // Special handling for crm_pipeline_stages - show stages with progress bars
    if (reportType === 'crm_pipeline_stages') {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            <div className="mb-3 flex-shrink-0">
              <span className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
              {data?.stages && data.stages.length > 0 ? (
                (() => {
                  // Calculate the maximum count for scaling the bars
                  const maxCount = Math.max(...data.stages.map((s: any) => s.count), 1);
                  
                  return (
                    <div className="space-y-3">
                      {data.stages.map((stage: any) => {
                        // Calculate the bar width as a percentage (minimum 5% for visibility)
                        const percentage = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 5) : 5;
                        
                        return (
                          <div key={stage.id} className="space-y-1">
                            {/* Stage name and count */}
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700 truncate">
                                {stage.name}
                              </span>
                              <span className="text-xs font-bold text-blue-600 ml-2">
                                {stage.count}
                              </span>
                            </div>
                            {/* Progress bar */}
                            <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                                style={{ width: `${percentage}%` }}
                              >
                                {stage.count > 0 && (
                                  <span className="text-xs font-semibold text-white">
                                    {stage.count}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-xs text-gray-400">No stages data</div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // Special handling for booking_source - show left sidebar legend and right pie chart
    if (reportType === 'booking_source' && data?.isPieChart) {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            {/* Title - Fixed size based on L/XL */}
            <div className="mb-2 flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            
            {/* Horizontal Layout: Left sidebar + Right chart */}
            <div className="flex-1 flex flex-row min-h-0" style={{ minHeight: 0 }}>
              {/* Left Sidebar: Legend Only */}
              <div className="flex flex-col w-1/3 pr-2 flex-shrink-0">
                {/* Custom Legend on Left */}
                <div className="flex-1 overflow-y-auto">
                  {data?.sourceDetails?.map((detail: any, index: number) => {
                    const backgroundColor = data.chartData?.datasets?.[0]?.backgroundColor?.[index] || 'rgba(59, 130, 246, 0.8)';
                    return (
                      <div key={index} className="flex items-center mb-2 text-xs">
                        <div 
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor }}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-gray-700 font-medium">
                            {detail.source}
                          </div>
                          <div className="text-gray-500">
                            {detail.count} booking{detail.count !== 1 ? 's' : ''}
                          </div>
                          <div className="text-gray-900 font-semibold">
                            ${(detail.sales || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Right Side: Pie Chart */}
              <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                {data?.chartData && (
                  <div className="h-full w-full">
                    <Pie 
                      data={data.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false // Hide default legend since we have custom one
                          },
                          tooltip: {
                            enabled: false
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Special handling for crm_lost_reasons - pie chart of lost leads by reason
    if (reportType === 'crm_lost_reasons' && data?.isPieChart) {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            <div className="mb-2 flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            
            {data?.sourceDetails && data.sourceDetails.length > 0 ? (
              <div className="flex-1 flex flex-row min-h-0" style={{ minHeight: 0 }}>
                <div className="flex flex-col w-1/3 pr-2 flex-shrink-0">
                  <div className="flex-1 overflow-y-auto">
                    {data.sourceDetails.map((detail: any, index: number) => {
                      const backgroundColor = data.chartData?.datasets?.[0]?.backgroundColor?.[index] || '#6B7280';
                      return (
                        <div key={index} className="flex items-center mb-2 text-xs">
                          <div className="w-3 h-3 rounded-full mr-2 flex-shrink-0" style={{ backgroundColor }}></div>
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-gray-700 font-medium">{detail.source}</div>
                            <div className="text-gray-500">{detail.count} lead{detail.count !== 1 ? 's' : ''}</div>
                            <div className="text-gray-900 font-semibold">{formatCurrency(detail.sales)}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                  {data?.chartData && (
                    <div className="h-full w-full">
                      <Pie 
                        data={data.chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { display: false },
                            tooltip: { enabled: false }
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-xs text-gray-400">No lost leads in this period</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Special handling for crm_leads_by_source - show table with sources
    if (reportType === 'crm_leads_by_source') {
      return (
        <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            {/* Title - Fixed size based on L/XL */}
            <div className="mb-3 flex-shrink-0">
              <span className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
              {data?.sources && data.sources.length > 0 ? (
                <div className="w-full">
                  {/* Table Header */}
                  <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1.5fr_1fr] gap-3 pb-3 border-b-2 border-gray-300 text-xs font-bold text-gray-700 mb-2 sticky top-0 bg-gray-50 z-10">
                    <div>Source</div>
                    <div className="text-center">Open</div>
                    <div className="text-center">Won</div>
                    <div className="text-center">Lost</div>
                    <div className="text-right">Total Value</div>
                    <div className="text-center">Win%</div>
                  </div>
                  
                  {/* Table Rows */}
                  <div className="space-y-1">
                    {data.sources.map((sourceData: any, idx: number) => (
                      <div 
                        key={sourceData.source || idx} 
                        className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1.5fr_1fr] gap-3 py-2.5 border-b border-gray-200 text-xs hover:bg-blue-50 rounded transition-colors"
                      >
                        <div className="font-semibold text-gray-900 truncate flex items-center" title={sourceData.source || 'Unknown'}>
                          {sourceData.source || 'Unknown'}
                        </div>
                        <div className="text-center">
                          <div className="text-blue-600 font-bold text-sm">
                            {sourceData.open || 0}
                          </div>
                          <div className="text-blue-500 font-semibold text-xs mt-0.5">
                            ${(sourceData.openValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-green-600 font-bold text-sm">
                            {sourceData.won || 0}
                          </div>
                          <div className="text-green-500 font-semibold text-xs mt-0.5">
                            ${(sourceData.wonValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-red-600 font-bold text-sm">
                            {sourceData.lost || 0}
                          </div>
                          <div className="text-red-500 font-semibold text-xs mt-0.5">
                            ${(sourceData.lostValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                        <div className="text-right flex flex-col justify-center">
                          <div className="font-bold text-gray-900 text-sm">
                            ${(sourceData.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                        <div className="text-center font-bold text-gray-900 text-sm flex items-center justify-center">
                          {(sourceData.winPercentage || 0).toFixed(1)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-2">No leads data available</p>
                    <p className="text-xs text-gray-400">Try adjusting your date range</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
        <div className="flex flex-col h-full" style={{ padding: '12px' }}>
          <div className="mb-1.5 flex-shrink-0">
            <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
              {reportTitle}
            </span>
          </div>
          <div className="flex-1 flex flex-col min-h-0">
            <div className="text-left mb-1.5 flex-shrink-0">
              <div className="flex items-center gap-2 mb-0.5">
                <div className="text-lg font-bold text-gray-900">
                  {data?.value || '$0.00'}
                </div>
                {data?.comparison && (
                  <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                    data.comparison.direction === 'up' 
                      ? 'bg-green-100 text-green-700' 
                      : data.comparison.direction === 'down' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                  {data.comparison.direction === 'up' && '↑'}
                  {data.comparison.direction === 'down' && '↓'}
                  {data.comparison.direction === 'same' && '='}
                  <span>{Math.round(data.comparison.percentage)}%</span>
                </div>
              )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {data?.label || 'Chart Data'}
              </div>
            </div>
            {data?.chartData && (
              <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                {data.isPieChart ? (
                  <div className="h-full w-full">
                    <Pie
                      data={data.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: { enabled: false }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-full w-full">
                    <Line
                      data={data.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            enabled: false
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(59, 130, 246, 0.1)'
                            },
                            ticks: {
                              callback: (value: any) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                              font: {
                                size: 10
                              }
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              font: {
                                size: 10
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Special handling for page_route_analytics - show preview content (before generic M/L check)
  if (reportType === 'page_route_analytics') {
    return (
      <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
        <div className="flex flex-col h-full" style={{ padding: '12px' }}>
          <div className="mb-1.5 flex-shrink-0">
            <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
              {reportTitle}
            </span>
          </div>
          <div className="flex-1 flex flex-col min-h-0">
            <div className="text-left mb-1.5 flex-shrink-0">
              <div className="flex items-center gap-2 mb-0.5">
                <div className="text-lg font-bold text-gray-900">
                  {data?.value || '0'}
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {data?.label || 'Select a route'}
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-xs text-gray-500 text-center">
                Route-specific analytics. Select a page route to view visitor count and traffic sources.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Special handling for click_analytics - show preview content (before generic M/L check)
  if (reportType === 'click_analytics') {
    return (
      <div className={`${baseCardClasses} md:col-span-2`} style={cardStyle}>
        <div className="flex flex-col h-full" style={{ padding: '12px' }}>
          <div className="mb-1.5 flex-shrink-0">
            <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
              {reportTitle}
            </span>
          </div>
          <div className="flex-1 flex flex-col min-h-0">
            <div className="text-left mb-1.5 flex-shrink-0">
              <div className="flex items-center gap-2 mb-0.5">
                <div className="text-lg font-bold text-gray-900">
                  {data?.value || '0'}
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {data?.label || 'Select a click'}
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-xs text-gray-500 text-center">
                Click-specific analytics. Select a click/element to view visitor count and traffic sources.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // M/L size system (CRM Leads Overview, Payments by Type) - always use 'medium' (smallest)
  if (shouldUseMLSizeSystem(reportType)) {
    // Special handling for payments_by_type
    if (reportType === 'payments_by_type') {
      return (
        <div className={baseCardClasses} style={cardStyle}>
          <div className="flex flex-col h-full" style={{ padding: '12px' }}>
            {/* Title */}
            <div className="mb-2 flex-shrink-0">
              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                {reportTitle}
              </span>
            </div>
            <div className="flex-1 flex flex-row gap-3 min-h-0" style={{ minHeight: 0 }}>
              {/* Left Column: Total, Status, and Payment Methods */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Total Payments */}
                <div className="mb-2 flex-shrink-0">
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(data?.total || 0, false)}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Total Payments</div>
                </div>
                
                {/* Payment Status - Deposits and Full Payments Side by Side */}
                {data?.byStatus && data.byStatus.length > 0 && (
                  <div className="mb-2 flex-shrink-0">
                    <div className="grid grid-cols-2 gap-3">
                      {data.byStatus.map((item: { status: string; amount: number; label: string }) => {
                        const isFullPayment = item.status === 'final';
                        const isDeposit = item.status === 'deposit';
                        const amountColor = isFullPayment ? 'text-green-600' : isDeposit ? 'text-yellow-600' : 'text-gray-900';
                        // Change "Full Payments" to "Full"
                        const displayLabel = item.label === 'Full Payments' ? 'Full' : item.label;
                        
                        return (
                          <div key={item.status} className="flex flex-col">
                            <span className="text-[10px] text-gray-600 mb-0.5">{displayLabel}</span>
                            <span className={`text-xs font-semibold ${amountColor}`}>
                              {formatCurrency(item.amount, false)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Payment Methods Breakdown */}
                {data?.byMethod && data.byMethod.length > 0 && (
                  <div className="flex-shrink-0">
                    <div className="text-[10px] text-gray-500 mb-1">By Method</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {data.byMethod.map((item: { method: string; amount: number; label: string }) => (
                        <div key={item.method} className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-600">{item.label}</span>
                          <span className="text-[10px] font-medium text-gray-900">{formatCurrency(item.amount, false)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Right Column: Type Breakdown */}
              {data?.byType && data.byType.length > 0 && (
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="space-y-1">
                    {data.byType.map((item: { type: string; amount: number; label: string }) => (
                      <div key={item.type} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">{item.label}</span>
                        <span className="font-medium text-gray-900">{formatCurrency(item.amount, false)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // Default M/L rendering for other reports (CRM Leads Overview, etc.)
    return (
      <div className={baseCardClasses} style={cardStyle}>
        <div className="flex flex-col h-full" style={{ padding: '12px' }}>
          {/* Title - Fixed size based on M/L */}
          <div className="mb-3 flex-shrink-0">
            <span className="text-base font-semibold text-gray-600">
              {reportTitle}
            </span>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center min-h-0" style={{ minHeight: 0 }}>
            <div className="grid grid-cols-3 gap-4 w-full">
              {/* Total Leads */}
              <div className="text-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-2">Total</div>
                <div className="text-3xl font-bold text-gray-900">
                  {data?.total || 0}
                </div>
              </div>
              {/* Won */}
              <div className="text-center bg-green-50 rounded-lg p-3 border border-green-200">
                <div className="text-xs font-medium text-green-700 mb-2">Won</div>
                <div className="text-3xl font-bold text-green-600">
                  {data?.won || 0}
                </div>
              </div>
              {/* Lost */}
              <div className="text-center bg-red-50 rounded-lg p-3 border border-red-200">
                <div className="text-xs font-medium text-red-700 mb-2">Lost</div>
                <div className="text-3xl font-bold text-red-600">
                  {data?.lost || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // M/L/XL size system (Stock Alert) - always use 'medium' (smallest)
  if (shouldUseMLXLSizeSystem(reportType)) {
    return (
      <div className={baseCardClasses} style={cardStyle}>
        <div className="flex flex-col h-full p-4">
          <div className="mb-3 flex-shrink-0">
            <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-1">
              {reportTitle}
            </span>
          </div>
          <div className="flex-1 flex flex-col items-start justify-center">
            <div className="text-3xl font-bold text-gray-900">
              {data?.value || '0'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {data?.label || 'Items Need Reorder'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className={baseCardClasses} style={cardStyle}>
      <div className="flex flex-col h-full p-4 items-center justify-center">
        <div className="text-sm font-semibold text-gray-900">{reportTitle}</div>
        <div className="text-xs text-gray-500 mt-1">{data?.value || 'Preview'}</div>
      </div>
    </div>
  );
};

// Component for Net Profit report with M/L/XL sizes
const NetProfitReportContent: React.FC<{
  report: Report;
  businessId: string | null;
  dashboards: Dashboard[];
  fetchNetProfitBreakdownData: (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => Promise<{ sales: number; labor: number; supplies: number; fixedExpenses: number; expenses: number; totalCosts: number }>;
}> = ({ report, businessId, dashboards, fetchNetProfitBreakdownData }) => {
  const reportSize = report.size || 'medium';
  const isLarge = reportSize === 'large';
  const [breakdownData, setBreakdownData] = useState<{ sales: number; labor: number; supplies: number; fixedExpenses: number; expenses: number; totalCosts: number } | null>(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [lastDateRange, setLastDateRange] = useState<string>('');
  
  // Find the dashboard this report belongs to
  const dashboard = dashboards.find(d => d.reports?.some(r => r.id === report.id));
  const currentDateRange = (dashboard?.dateRange || dashboard?.default_date_range || 'this_month') as DateRangeOption;
  const currentCustomStartDate = dashboard?.customStartDate;
  const currentCustomEndDate = dashboard?.customEndDate;
  
  // Create a unique key for the current date range to detect changes
  const dateRangeKey = `${currentDateRange}-${currentCustomStartDate?.toISOString() || ''}-${currentCustomEndDate?.toISOString() || ''}`;
  
  // Fetch breakdown data for large size
  useEffect(() => {
    if (isLarge && businessId) {
      // Refetch if date range has changed or if we don't have data yet
      if (dateRangeKey !== lastDateRange || !breakdownData) {
        setLoadingBreakdown(true);
        fetchNetProfitBreakdownData(businessId, currentDateRange, currentCustomStartDate, currentCustomEndDate)
          .then(data => {
            setBreakdownData(data);
            setLastDateRange(dateRangeKey);
            setLoadingBreakdown(false);
          })
          .catch(err => {
            console.error('Error fetching net profit breakdown:', err);
            setLoadingBreakdown(false);
          });
      }
    }
  }, [isLarge, businessId, report.id, dateRangeKey, lastDateRange, breakdownData, currentDateRange, currentCustomStartDate, currentCustomEndDate, fetchNetProfitBreakdownData]);
  
  const maxValue = breakdownData ? Math.max(breakdownData.sales, breakdownData.totalCosts) : 0;
  
  return (
    <div className="flex flex-col h-full" style={{ padding: reportSize === 'xl' ? '16px' : reportSize === 'large' ? '14px' : '12px' }}>
      <div className={`flex items-center justify-between ${reportSize === 'xl' ? 'mb-4' : reportSize === 'large' ? 'mb-3' : 'mb-2'} flex-shrink-0`}>
        <div className="flex items-center">
          <span 
            className={`${reportSize === 'xl' ? 'text-xl' : reportSize === 'large' ? 'text-lg' : 'text-base'} font-medium text-gray-600 truncate`}
          >
            {report.title}
          </span>
        </div>
      </div>
      <div className={`flex-1 flex flex-col items-center justify-center text-center ${reportSize === 'xl' ? 'mb-4' : reportSize === 'large' ? 'mb-3' : 'mb-2'}`}>
        <div className="flex items-center gap-2">
          <div className={`${reportSize === 'xl' ? 'text-4xl' : reportSize === 'large' ? 'text-3xl' : 'text-2xl'} font-bold text-gray-900`}>
            {report.data?.value}
          </div>
          {report.data?.comparison && (
            <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
              report.data.comparison.direction === 'up' 
                ? 'bg-green-100 text-green-700' 
                : report.data.comparison.direction === 'down' 
                ? 'bg-red-100 text-red-700' 
                : 'bg-gray-100 text-gray-700'
            }`}>
              {report.data.comparison.direction === 'up' && '↑'}
              {report.data.comparison.direction === 'down' && '↓'}
              {report.data.comparison.direction === 'same' && '='}
              <span>{Math.round(report.data.comparison.percentage)}%</span>
            </div>
          )}
        </div>
        <div className={`${reportSize === 'xl' ? 'text-base' : reportSize === 'large' ? 'text-sm' : 'text-xs'} text-gray-500 text-center leading-tight`}>
          {report.data?.label}
        </div>
      </div>
      {/* Bar graphs for large size */}
      {isLarge && (
        <div className="flex-shrink-0 space-y-3 mt-2">
          {loadingBreakdown ? (
            <div className="text-center py-2">
              <div className="text-xs text-gray-400">Loading...</div>
            </div>
          ) : breakdownData ? (
            <>
              {/* Sales Bar */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-600">Sales</span>
                  <span className="text-xs font-semibold text-gray-900">{formatCurrency(breakdownData.sales, false)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div 
                    className="bg-green-500 h-full rounded-full transition-all duration-300"
                    style={{ width: `${maxValue > 0 ? (breakdownData.sales / maxValue) * 100 : 0}%` }}
                  />
                </div>
              </div>
              {/* Total Costs Bar - Segmented */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-600">Total Costs</span>
                  <span className="text-xs font-semibold text-gray-900">{formatCurrency(breakdownData.totalCosts, false)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden flex">
                  {breakdownData.totalCosts > 0 && (
                    <>
                      {/* Expenses segment */}
                      {breakdownData.expenses > 0 && (
                        <div 
                          className="bg-purple-500 transition-all duration-300"
                          style={{ width: `${(breakdownData.expenses / breakdownData.totalCosts) * 100}%` }}
                          title={`Expenses: ${formatCurrency(breakdownData.expenses, false)}`}
                        />
                      )}
                      {/* Labor segment */}
                      {breakdownData.labor > 0 && (
                        <div 
                          className="bg-blue-500 transition-all duration-300"
                          style={{ width: `${(breakdownData.labor / breakdownData.totalCosts) * 100}%` }}
                          title={`Labor: ${formatCurrency(breakdownData.labor, false)}`}
                        />
                      )}
                      {/* Supplies segment */}
                      {breakdownData.supplies > 0 && (
                        <div 
                          className="bg-orange-500 transition-all duration-300"
                          style={{ width: `${(breakdownData.supplies / breakdownData.totalCosts) * 100}%` }}
                          title={`Supplies: ${formatCurrency(breakdownData.supplies, false)}`}
                        />
                      )}
                      {/* Fixed Expenses segment */}
                      {breakdownData.fixedExpenses > 0 && (
                        <div 
                          className="bg-red-500 transition-all duration-300"
                          style={{ width: `${(breakdownData.fixedExpenses / breakdownData.totalCosts) * 100}%` }}
                          title={`Fixed Expenses: ${formatCurrency(breakdownData.fixedExpenses, false)}`}
                        />
                      )}
                    </>
                  )}
                </div>
                {/* Cost breakdown labels */}
                <div className="mt-1.5 space-y-1">
                  {breakdownData.expenses > 0 && (
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded bg-purple-500"></div>
                        <span className="text-gray-600">Expenses</span>
                      </div>
                      <span className="text-gray-900 font-medium">{formatCurrency(breakdownData.expenses, false)}</span>
                    </div>
                  )}
                  {breakdownData.labor > 0 && (
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded bg-blue-500"></div>
                        <span className="text-gray-600">Labor</span>
                      </div>
                      <span className="text-gray-900 font-medium">{formatCurrency(breakdownData.labor, false)}</span>
                    </div>
                  )}
                  {breakdownData.supplies > 0 && (
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded bg-orange-500"></div>
                        <span className="text-gray-600">Supplies</span>
                      </div>
                      <span className="text-gray-900 font-medium">{formatCurrency(breakdownData.supplies, false)}</span>
                    </div>
                  )}
                  {breakdownData.fixedExpenses > 0 && (
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded bg-red-500"></div>
                        <span className="text-gray-600">Fixed Expenses</span>
                      </div>
                      <span className="text-gray-900 font-medium">{formatCurrency(breakdownData.fixedExpenses, false)}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="text-xs text-gray-400">No data available</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function KPIDashboard() {
  const { businessId } = useBusiness();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dashboardName, setDashboardName] = useState('');
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddReportsModalOpen, setIsAddReportsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('sales');
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);
  const [reportPreviews, setReportPreviews] = useState<{[key: string]: any}>({});
  const [dashboardToDelete, setDashboardToDelete] = useState<string | null>(null);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);
  const [selectedReports, setSelectedReports] = useState<Array<{type: string; name: string}>>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [reportTitleTexts, setReportTitleTexts] = useState<{[key: string]: string}>({});
  const [isAIAnalyzerOpen, setIsAIAnalyzerOpen] = useState(false);
  const [aiAnalyzerDashboardId, setAIAnalyzerDashboardId] = useState<string | null>(null);
  
  // Pending changes state for edit mode
  const [pendingDashboardTitles, setPendingDashboardTitles] = useState<{[key: string]: string}>({});
  const [pendingReportTitles, setPendingReportTitles] = useState<{[key: string]: string}>({});
  const [pendingLayouts, setPendingLayouts] = useState<{[key: string]: any[]}>({});
  const [originalState, setOriginalState] = useState<{
    dashboardTitles: {[key: string]: string};
    reportTitles: {[key: string]: string};
    layouts: {[key: string]: any[]};
    reportIds: {[key: string]: string[]}; // Track report IDs for each dashboard
  }>({ dashboardTitles: {}, reportTitles: {}, layouts: {}, reportIds: {} });
  
  // Collision detection state
  const [overlappingReports, setOverlappingReports] = useState<Set<string>>(new Set());
  
  // Stock Alert specific state
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrder[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [pendingOrderItems, setPendingOrderItems] = useState<Map<string, number>>(new Map());
  
  // Net Profit breakdown data state - stores breakdown for each report
  const [netProfitBreakdown, setNetProfitBreakdown] = useState<{[reportId: string]: { sales: number; totalCosts: number }}>({});
  
  // CRM Pipeline Stages report state - stores selected pipeline ID for each report
  const [, setCrmPipelineSelections] = useState<{[reportId: string]: number}>({});
  
  // Page Route Analytics state - stores route selections for each report and available routes
  const [pageRouteSelections, setPageRouteSelections] = useState<{[reportId: string]: string}>({});
  const [availableRoutes, setAvailableRoutes] = useState<Array<{ route: string; visitors: number; views: number }>>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  
  // Click Analytics state - stores click selections for each report and available clicks
  const [clickSelections, setClickSelections] = useState<{[reportId: string]: string}>({});
  const [availableClicks, setAvailableClicks] = useState<Array<{ identifier: string; label: string; visitors: number; clicks: number }>>([]);
  const [loadingClicks, setLoadingClicks] = useState(false);
  
  // Product Sales by Category state - stores expanded category for each report and available categories
  const [expandedProductCategories, setExpandedProductCategories] = useState<{[reportId: string]: string | null}>({});
  const [availableProductCategories, setAvailableProductCategories] = useState<Array<{ id: string; name: string; total_sales: number }>>([]);
  const [loadingProductCategories, setLoadingProductCategories] = useState(false);
  const [categoryItemsCache, setCategoryItemsCache] = useState<{[reportId: string]: {[categoryId: string]: any[]}}>({});
  
  // Service Sales by Category state - stores expanded categories and cached service data
  const [expandedServiceCategories, setExpandedServiceCategories] = useState<{[reportId: string]: string | null}>({});
  const [serviceItemsCache, setServiceItemsCache] = useState<{[reportId: string]: {[categoryId: string]: any[]}}>({});
  const [loadingServiceCategories, setLoadingServiceCategories] = useState(false);

  // Website visits graph toggle: 'visits' (default) or 'unique'
  const [websiteVisitsMode, setWebsiteVisitsMode] = useState<'visits' | 'unique'>('visits');
  
  // Conversion rate settings state
  const [conversionSettingsReportId, setConversionSettingsReportId] = useState<string | null>(null);
  const [conversionSettingsEnabled, setConversionSettingsEnabled] = useState(false);
  const [conversionCriteria, setConversionCriteria] = useState<Array<{ type: 'route' | 'click' | 'url_contains'; value: string }>>([]);
  const [conversionSettingsRoutes, setConversionSettingsRoutes] = useState<Array<{ route: string; sessions: number }>>([]);
  const [conversionSettingsClicks, setConversionSettingsClicks] = useState<Array<{ identifier: string; label: string; sessions: number }>>([]);
  const [conversionSettingsLoading, setConversionSettingsLoading] = useState(false);

  const saveConversionSettings = useCallback(async () => {
    const reportId = conversionSettingsReportId;
    if (!reportId || !supabase || !businessId) {
      setConversionSettingsReportId(null);
      return;
    }

    const validCriteria = conversionCriteria.filter(c => c.value);
    const conversionConfig = {
      enabled: conversionSettingsEnabled,
      criteria: validCriteria
    };

    // Step 1: Save config to DB immediately
    const targetDashboard = dashboards.find(d => d.reports?.some(r => r.id === reportId));
    const targetReport = targetDashboard?.reports?.find(r => r.id === reportId);
    if (!targetReport) {
      setConversionSettingsReportId(null);
      return;
    }

    const savedConfig = {
      ...(targetReport.data?.config || {}),
      conversionConfig
    };
    const savedData = { ...targetReport.data, config: savedConfig };
    await supabase.from('kpi_dashboard_reports').update({ report_data: savedData }).eq('id', reportId);

    // Step 2: Close modal immediately so user sees progress
    setConversionSettingsReportId(null);

    // Step 3: Refetch graph data with the new conversion config
    try {
      const dateRange = (targetDashboard?.dateRange || 'this_month') as DateRangeOption;
      const newData = await fetchWebsiteVisitsGraphData(businessId, dateRange, undefined, undefined, conversionConfig);

      // Merge: new chart data + preserved config
      setDashboards(prev => prev.map(d => ({
        ...d,
        reports: d.reports?.map(r => {
          if (r.id !== reportId) return r;
          return { ...r, data: { ...newData, config: savedConfig } };
        })
      })));
    } catch (err) {
      console.error('Error refetching conversion data:', err);
      // Still update config even if refetch fails
      setDashboards(prev => prev.map(d => ({
        ...d,
        reports: d.reports?.map(r => r.id === reportId ? { ...r, data: savedData } : r)
      })));
    }
  }, [conversionSettingsReportId, conversionSettingsEnabled, conversionCriteria, businessId, supabase, dashboards]);

  // Device-specific view state
  const [currentViewType, setCurrentViewType] = useState<'desktop' | 'mobile'>(() => detectDeviceType());
  const [, setConfiguringView] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // Add state for tracking screen size for automatic view switching
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Calculate minimum grid height based on reports and default canvas size
  const getGridHeight = useCallback((dashboard: Dashboard) => {
    const defaultHeight = currentViewType === 'desktop' ? 30 : 40; // Default canvas: Desktop 100x30, Mobile 20x40
    const rowHeight = 8; // Consistent 8px row height for both mobile and desktop
    
    if (!dashboard.reports || dashboard.reports.length === 0) {
      // In edit mode, provide more space for adding reports
      if (isEditMode) {
        return defaultHeight * 1.5 * rowHeight;
      }
      return defaultHeight * rowHeight; // Return default canvas size when empty
    }
    
    // Find the maximum Y + height position from all reports
    const maxBottomPosition = Math.max(
      defaultHeight, // Never go smaller than default canvas
      ...dashboard.reports.map(report => report.layout.y + report.layout.h)
    );
    
    // Use generous height calculation for both edit and display mode
    // to ensure all reports are visible without cutoff
    const calculatedHeight = maxBottomPosition * rowHeight;
    
    if (isEditMode) {
      // In edit mode: add 50% extra space for easier editing
      return Math.ceil(maxBottomPosition * 1.5) * rowHeight + 8;
    } else {
      // In display mode: add 20% buffer to ensure all content is visible
      return Math.ceil(calculatedHeight * 1.2) + 16;
    }
  }, [currentViewType, isEditMode]);

  // Add breakpoint detection for automatic view switching
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      setScreenWidth(newWidth);
      
      // Smart auto-switching: Only switch when both views exist and screen size changes
      const shouldBeMobile = newWidth < 768; // Switch to mobile view at 768px and below
      
      if (shouldBeMobile && currentViewType === 'desktop') {
        // Only switch if there are dashboards configured for both views
        const hasIndependentViews = dashboards.some(d => d.desktopConfigured && d.mobileConfigured);
        const hasMobileOnlyViews = dashboards.some(d => d.mobileConfigured && !d.desktopConfigured);
        
        if (hasIndependentViews || hasMobileOnlyViews) {
          setCurrentViewType('mobile');
        }
      } else if (!shouldBeMobile && currentViewType === 'mobile') {
        // Only switch if there are dashboards configured for both views
        const hasIndependentViews = dashboards.some(d => d.desktopConfigured && d.mobileConfigured);
        const hasDesktopOnlyViews = dashboards.some(d => d.desktopConfigured && !d.mobileConfigured);
        
        if (hasIndependentViews || hasDesktopOnlyViews) {
          setCurrentViewType('desktop');
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentViewType, dashboards]);
  
  // Refs for debouncing auto-save
  const dashboardTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reportTitleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layoutSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingLayoutSaves = useRef<Map<string, { layout: any[]; dashboardId: string }>>(new Map());

  // CRITICAL: Store the absolute latest layout for each dashboard (from onLayoutChange)
  // This ensures we always have the most current layout when saving
  const latestLayoutsRef = useRef<Map<string, any[]>>(new Map());

  // Load dashboards from database
  useEffect(() => {
    if (businessId) {
      loadDashboards();
    }
  }, [businessId]);

  // Reload dashboards when view type changes
  useEffect(() => {
    if (businessId) {
      setIsInitialLoadComplete(false); // Reset initial load state
      loadDashboards();
    }
  }, [currentViewType]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (dashboardTitleTimeoutRef.current) {
        clearTimeout(dashboardTitleTimeoutRef.current);
      }
      if (reportTitleTimeoutRef.current) {
        clearTimeout(reportTitleTimeoutRef.current);
      }
      if (layoutSaveTimeoutRef.current) {
        clearTimeout(layoutSaveTimeoutRef.current);
      }
    };
  }, []);

  const loadDashboards = async (viewType?: 'desktop' | 'mobile') => {
    if (!businessId) return;
    
    try {
      setLoading(true);
      
      // Pre-load all data in parallel for faster loading
      const dashboardsData = await getKPIDashboards(businessId);
      
      // Load reports for each dashboard in parallel
      const dashboardsWithReports: Dashboard[] = await Promise.all(
        dashboardsData.map(async (d) => {
          const reportsData = await getDashboardReports(d.id, viewType || currentViewType);
          const defaultDateRange = (d.default_date_range as DateRangeOption) || 'this_month';
        
          // Update report data with real data based on the default date range
          const reports: Report[] = await Promise.all(
            reportsData.map(async (r) => {
              // CRITICAL: With preset sizes, w/h MUST match preset dimensions
              // Normalize the layout to ensure w/h always match the preset size
              const reportSize = r.size || getDefaultSize();
              const presetDimensions = getSizeDimensions(reportSize, viewType || currentViewType);
              
              // For page_route_analytics, check if we have a saved route selection
              let reportData;
              if (r.report_type === 'page_route_analytics' && r.report_data?.config?.selectedRoute) {
                // We have a saved route, fetch the analytics data for it
                console.log('🔍 Loading page_route_analytics with saved route:', r.report_data.config.selectedRoute);
                const routeData = await fetchPageRouteAnalyticsData(
                  businessId,
                  r.report_data.config.selectedRoute,
                  defaultDateRange
                );
                // Merge the fetched data with the saved config
                reportData = {
                  ...routeData,
                  config: {
                    selectedRoute: r.report_data.config.selectedRoute
                  }
                };
                console.log('✅ Loaded page_route_analytics data:', reportData);
              } else if (r.report_type === 'click_analytics' && r.report_data?.config?.selectedClick) {
                // We have a saved click, fetch the analytics data for it
                console.log('🔍 Loading click_analytics with saved click:', r.report_data.config.selectedClick);
                const clickData = await fetchClickAnalyticsData(
                  businessId,
                  r.report_data.config.selectedClick,
                  defaultDateRange
                );
                // Merge the fetched data with the saved config
                reportData = {
                  ...clickData,
                  config: {
                    selectedClick: r.report_data.config.selectedClick
                  }
                };
                console.log('✅ Loaded click_analytics data:', reportData);
              } else if (r.report_type === 'product_sales_by_category') {
                // We have a product sales by category report, fetch categories data
                const categoryData = await fetchProductSalesByCategoryData(
                  businessId,
                  null, // Always fetch categories view on load
                  defaultDateRange
                );
                // Save the category data (no config needed for expandable rows)
                reportData = categoryData;
                console.log('✅ Loaded product_sales_by_category data:', reportData);
              } else if (r.report_type === 'service_sales_by_category') {
                // We have a service sales by category report, fetch categories data
                const categoryData = await fetchServiceSalesByCategoryData(
                  businessId,
                  null, // Always fetch categories view on load
                  defaultDateRange
                );
                // Save the category data (no config needed for expandable rows)
                reportData = categoryData;
                console.log('✅ Loaded service_sales_by_category data:', reportData);
              } else if (r.report_type === 'project_pipeline_stages') {
                // We have a project pipeline stages report, fetch stages data
                const stagesData = await fetchProjectPipelineStagesData(businessId);
                reportData = stagesData;
                console.log('✅ Loaded project_pipeline_stages data:', reportData);
              } else if (r.report_type === 'title') {
                reportData = r.report_data; // Preserve saved title data
              } else {
                // For other reports, use the standard data fetch
                const reportForDataFetch = (r.report_type === 'page_route_analytics' || r.report_type === 'click_analytics' || r.report_type === 'product_sales_by_category' || r.report_type === 'service_sales_by_category' || r.report_type === 'website_visits_graph')
                  ? { id: r.id, type: r.report_type, data: r.report_data }
                  : undefined;
                reportData = await getFilteredReportDataWithComparison(r.report_type, defaultDateRange, businessId, undefined, undefined, reportForDataFetch);
              }
              
              return {
              id: r.id,
              type: r.report_type,
              title: r.report_title,
                size: reportSize,
              data: reportData,
              layout: {
                  x: r.layout_x, // Position from database
                  y: r.layout_y, // Position from database
                  w: presetDimensions.w, // ALWAYS use preset size width (normalize)
                  h: presetDimensions.h  // ALWAYS use preset size height (normalize)
              }
              };
            })
          );
          
          return {
            id: d.id,
            name: d.title,
            createdAt: new Date(d.created_at),
            reports,
            dateRange: defaultDateRange, // Use the saved default date range
            defaultDateRange: defaultDateRange,
            createdDeviceType: d.created_device_type,
            desktopConfigured: d.desktop_configured,
            mobileConfigured: d.mobile_configured
          };
        })
      );
      
      // Set all data at once to prevent partial renders
      setDashboards(dashboardsWithReports);
      
      // Initialize reportTitleTexts state for all title elements
      const titleTexts: {[key: string]: string} = {};
      dashboardsWithReports.forEach(dashboard => {
        dashboard.reports?.forEach(report => {
          if (report.type === 'title' && report.data?.text) {
            titleTexts[report.id] = report.data.text;
          }
        });
      });
      setReportTitleTexts(titleTexts);
      
      // Small delay to ensure smooth rendering
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Mark initial load as complete
      setIsInitialLoadComplete(true);
      
    } catch (error) {
      console.error('Error loading dashboards:', error);
      toast.error('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDashboard = async () => {
    if (!businessId) {
      toast.error('Business not selected');
      return;
    }

    if (dashboardName.trim()) {
      try {
        // Pass current view type to ensure dashboard is only created for current view
        const newDashboard = await createKPIDashboard({
          business_id: businessId,
          title: dashboardName.trim()
        }, currentViewType);
        
        const formattedDashboard: Dashboard = {
          id: newDashboard.id,
          name: newDashboard.title,
          createdAt: new Date(newDashboard.created_at),
          dateRange: 'this_month', // Default to this month
          defaultDateRange: 'this_month', // Default to this month
          createdDeviceType: newDashboard.created_device_type,
          desktopConfigured: newDashboard.desktop_configured,
          mobileConfigured: newDashboard.mobile_configured
        };
        
        setDashboards(prev => [...prev, formattedDashboard]);
      setDashboardName('');
      setIsModalOpen(false);
        toast.success(`Dashboard created for ${currentViewType} view only`);
      } catch (error) {
        console.error('Error creating dashboard:', error);
        toast.error('Failed to create dashboard');
      }
    }
  };

  const handleDateRangeChange = async (dashboardId: string, newDateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    if (!businessId) return;
    
    setDashboards(prev => prev.map(dashboard => {
      if (dashboard.id === dashboardId) {
        return {
          ...dashboard,
          dateRange: newDateRange,
          customStartDate: customStartDate,
          customEndDate: customEndDate
        };
      }
      return dashboard;
    }));

    // Update reports with new filtered data
    const dashboard = dashboards.find(d => d.id === dashboardId);
    if (dashboard?.reports) {
      const updatedReports = await Promise.all(
        dashboard.reports.map(async (report) => ({
          ...report,
          data: report.type === 'title' 
            ? report.data // Preserve title data when changing date range
            : report.type === 'stock_alert'
            ? report.data // Preserve stock alert data - it's live and date-independent
            : report.type === 'web_traffic_patterns'
            ? await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate) // Reload traffic patterns with new date range
            : report.type === 'page_route_analytics'
            ? await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate, report) // Pass report to get config and fetch route data
            : report.type === 'click_analytics'
            ? await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate, report) // Pass report to get config and fetch click data
            : report.type === 'website_visits_graph'
            ? await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate, report) // Pass report to get conversion config
            : report.type === 'product_sales_by_category'
            ? await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate, report) // Pass report to get config and fetch category data
            : report.type === 'service_sales_by_category'
            ? await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate, report) // Pass report to get config and fetch category data
            : await getFilteredReportDataWithComparison(report.type, newDateRange, businessId, customStartDate, customEndDate)
        }))
      );

      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: updatedReports
          };
        }
        return dashboard;
      }));
    }
    
    toast.success(`Date range updated to ${getDateRangeLabel(newDateRange, customStartDate, customEndDate)}`);
  };

  const handleSetDefaultDateRange = async (dashboardId: string, newDefaultDateRange: DateRangeOption) => {
    try {
      await updateDashboardDefaultDateRange(dashboardId, newDefaultDateRange);
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            defaultDateRange: newDefaultDateRange
          };
        }
        return dashboard;
      }));
      
      toast.success(`Default date range set to ${getDateRangeLabel(newDefaultDateRange)}`);
    } catch (error) {
      console.error('Error setting default date range:', error);
      toast.error('Failed to save default date range');
    }
  };

  // Function to fetch real sales data from the database (includes booking sales + product sales)
  const fetchSalesData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Fetch booking sales
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (bookingsError) {
        console.error('Error fetching booking sales data:', bookingsError);
        throw bookingsError;
      }

      // Calculate total booking sales after discounts
      const totalBookingSales = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // Fetch product sales (actual sales, not internal withdrawals).
      // Counter sales create BOTH a product_sales row AND a linked standalone
      // invoice — the invoice component below already counts those (and
      // correctly drops voided ones), so only legacy invoice-less rows count
      // here to avoid double-counting every counter sale.
      const { data: productSales, error: productSalesError } = await supabase
        .from('product_sales')
        .select('total_amount')
        .eq('business_id', businessId)
        .is('invoice_id', null)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (productSalesError) {
        console.error('Error fetching product sales data:', productSalesError);
        throw productSalesError;
      }

      // Calculate total product sales
      const totalProductSales = (productSales || []).reduce((sum, sale) => {
        return sum + (sale.total_amount || 0);
      }, 0);

      // Standalone (booking-less) invoices issued in range count as sales.
      const standaloneInvoices = await fetchStandaloneInvoiceSales(businessId, {
        start: startDate,
        end: endDate,
      });
      const totalStandaloneSales = sumStandaloneInvoiceSales(standaloneInvoices);

      // Combine booking sales, product sales, and standalone invoices
      const totalSales = totalBookingSales + totalProductSales + totalStandaloneSales;

      return {
        value: formatCurrency(totalSales, false),
        label: `Total Sales After Discounts`
      };
    } catch (error) {
      console.error('Error fetching sales data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Sales After Discounts`
      };
    }
  };

  // Function to fetch real product sales data from product_sales table (actual sales, not internal withdrawals)
  const fetchProductSalesData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query product_sales table for actual product sales (not internal withdrawals)
      // Internal withdrawals don't create product_sales records, only actual sales do
      const { data: productSales, error } = await supabase
        .from('product_sales')
        .select('total_amount, created_at, invoice:invoices!product_sales_invoice_id_fkey(status)')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching product sales data:', error);
        throw error;
      }

      // Calculate total product sales (total_amount already includes tax and discounts)
      const totalProductSales = (productSales || [])
        .filter((sale) => !isDeadInvoiceProductSale(sale))
        .reduce((sum, sale) => {
          return sum + (sale.total_amount || 0);
        }, 0);

      return {
        value: formatCurrency(totalProductSales, false),
        label: `Total Product Sales`
      };
    } catch (error) {
      console.error('Error fetching product sales data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Product Sales`
      };
    }
  };

  // Function to fetch product sales by category data
  const fetchProductSalesByCategoryData = async (
    businessId: string,
    selectedCategoryId: string | null,
    dateRange: DateRangeOption,
    customStartDate?: Date,
    customEndDate?: Date
  ): Promise<{
    value: string;
    label: string;
    categoryData?: any;
  }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      if (!selectedCategoryId) {
        // No category selected - return sales grouped by category.
        // Grouping is LINE-level (not sale-level): each line's total lands in
        // its own group, so deposits/delivery fees no longer inflate the
        // categories of the products they were sold with.
        const { data: categorySales, error } = await supabase
          .from('product_sales')
          .select(`
            id,
            total_amount,
            created_at,
            invoice:invoices!product_sales_invoice_id_fkey(status),
            items:product_sale_items(
              product_name,
              total_price,
              stock_item_id,
              stock_items:stock_item_id(
                category_id,
                categories:category_id(
                  id,
                  name
                )
              )
            )
          `)
          .eq('business_id', businessId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());

        if (error) {
          console.error('Error fetching product sales by category:', error);
          throw error;
        }

        const liveSales = (categorySales || []).filter((sale) => !isDeadInvoiceProductSale(sale));

        // Rental lines carry no stock_item_id — match their item name back to
        // the catalog so rental revenue lands in the item's real category
        // (e.g. Dispensers). Unmatched rentals fall into a "Rentals" group.
        const rentalNames = new Set<string>();
        liveSales.forEach(sale => (sale.items || []).forEach((item: any) => {
          if (classifyProductSaleLine(item.product_name || '') === 'rental') {
            rentalNames.add(rentalLineItemName(item.product_name));
          }
        }));
        const rentalCategoryByName = new Map<string, { id: string; name: string } | null>();
        if (rentalNames.size > 0) {
          const { data: rentalItems } = await supabase
            .from('stock_items')
            .select('name, categories:category_id(id, name)')
            .eq('business_id', businessId)
            .in('name', Array.from(rentalNames));
          (rentalItems || []).forEach((si: any) => {
            const cat = Array.isArray(si.categories) ? si.categories[0] : si.categories;
            rentalCategoryByName.set(si.name, cat || null);
          });
        }

        // Group line totals by category (plus synthetic Deposits / Delivery groups)
        const categoryMap = new Map<string, { name: string; total_sales: number; saleIds: Set<string> }>();
        const addLine = (groupId: string, groupName: string, amount: number, saleId: string) => {
          const existing = categoryMap.get(groupId) || { name: groupName, total_sales: 0, saleIds: new Set<string>() };
          existing.total_sales += amount;
          existing.saleIds.add(saleId);
          categoryMap.set(groupId, existing);
        };

        liveSales.forEach(sale => {
          (sale.items || []).forEach((item: any) => {
            const amount = Number(item.total_price) || 0;
            const kind = classifyProductSaleLine(item.product_name || '');
            if (kind === 'deposit') {
              addLine('deposits', 'Deposits', amount, sale.id);
            } else if (kind === 'delivery') {
              addLine('delivery', 'Delivery', amount, sale.id);
            } else if (kind === 'rental') {
              const cat = rentalCategoryByName.get(rentalLineItemName(item.product_name)) || null;
              if (cat) addLine(cat.id, cat.name, amount, sale.id);
              else addLine('rentals', 'Rentals', amount, sale.id);
            } else {
              const category = item.stock_items?.categories;
              if (category) addLine(category.id, category.name, amount, sale.id);
              else addLine('uncategorized', 'Uncategorized', amount, sale.id);
            }
          });
        });

        // Convert to array and sort by total sales
        const categories = Array.from(categoryMap.entries()).map(([id, data]) => ({
          id,
          name: data.name,
          total_sales: data.total_sales,
          sale_count: data.saleIds.size
        })).sort((a, b) => b.total_sales - a.total_sales);

        const totalSales = categories.reduce((sum, cat) => sum + cat.total_sales, 0);

        return {
          value: formatCurrency(totalSales, false),
          label: 'Total Product Sales by Category',
          categoryData: {
            categories,
            total_sales: totalSales,
            view: 'categories'
          }
        };
      } else {
        // Category selected - return sales by items in that category
        const { data: itemSales, error } = await supabase
          .from('product_sales')
          .select(`
            id,
            total_amount,
            created_at,
            invoice:invoices!product_sales_invoice_id_fkey(status),
            items:product_sale_items(
              id,
              product_name,
              quantity,
              unit_price,
              total_price,
              stock_item_id,
              stock_items:stock_item_id(
                id,
                name,
                category_id
              )
            )
          `)
          .eq('business_id', businessId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());

        if (error) {
          console.error('Error fetching product sales by items:', error);
          throw error;
        }

        const liveItemSales = (itemSales || []).filter((sale) => !isDeadInvoiceProductSale(sale));

        // Rental lines have no stock_item_id — resolve their catalog category
        // by item name so they expand under the right category (see above).
        const rentalNames = new Set<string>();
        liveItemSales.forEach(sale => (sale.items || []).forEach((item: any) => {
          if (classifyProductSaleLine(item.product_name || '') === 'rental') {
            rentalNames.add(rentalLineItemName(item.product_name));
          }
        }));
        const rentalCategoryByName = new Map<string, { id: string; name: string } | null>();
        if (rentalNames.size > 0) {
          const { data: rentalItems } = await supabase
            .from('stock_items')
            .select('name, categories:category_id(id, name)')
            .eq('business_id', businessId)
            .in('name', Array.from(rentalNames));
          (rentalItems || []).forEach((si: any) => {
            const cat = Array.isArray(si.categories) ? si.categories[0] : si.categories;
            rentalCategoryByName.set(si.name, cat || null);
          });
        }

        // Filter lines into the selected group and aggregate by item
        const itemMap = new Map<string, { name: string; total_sales: number; quantity_sold: number; unit_price: number }>();

        liveItemSales.forEach(sale => {
          (sale.items || []).forEach((item: any) => {
            const kind = classifyProductSaleLine(item.product_name || '');
            let belongs = false;
            let itemId: string = item.stock_items?.id || item.product_name;
            let itemName: string = item.stock_items?.name || item.product_name;

            if (selectedCategoryId === 'deposits') {
              belongs = kind === 'deposit';
              itemName = item.product_name
                .replace(/ \(refundable\)$/i, '')
                .replace(/^.*? deposit — /i, '');
              itemId = `deposit:${itemName}`;
            } else if (selectedCategoryId === 'delivery') {
              belongs = kind === 'delivery';
              itemName = item.product_name.replace(/^Delivery — /, '');
              itemId = `delivery:${itemName}`;
            } else if (kind === 'rental') {
              // Rentals belong to their catalog item's category (or the
              // synthetic Rentals group when the name no longer matches).
              const baseName = rentalLineItemName(item.product_name);
              const cat = rentalCategoryByName.get(baseName) || null;
              belongs = selectedCategoryId === 'rentals' ? !cat : cat?.id === selectedCategoryId;
              itemName = `${baseName} (rental)`;
              itemId = `rental:${baseName}`;
            } else if (kind === 'product') {
              const itemCategoryId = item.stock_items?.category_id;
              belongs = (selectedCategoryId === 'uncategorized' && !itemCategoryId) ||
                (!!itemCategoryId && itemCategoryId === selectedCategoryId);
            }

            if (!belongs) return;

            const existing = itemMap.get(itemId) || {
              name: itemName,
              total_sales: 0,
              quantity_sold: 0,
              unit_price: item.unit_price || 0
            };
            itemMap.set(itemId, {
              name: itemName,
              total_sales: existing.total_sales + (item.total_price || 0),
              quantity_sold: existing.quantity_sold + (item.quantity || 0),
              unit_price: item.unit_price || existing.unit_price
            });
          });
        });

        // Convert to array and sort by total sales
        const items = Array.from(itemMap.entries()).map(([id, data]) => ({
          id,
          name: data.name,
          total_sales: data.total_sales,
          quantity_sold: data.quantity_sold,
          unit_price: data.unit_price
        })).sort((a, b) => b.total_sales - a.total_sales);

        // Get category name (synthetic groups first, then real catalog categories)
        const syntheticNames: Record<string, string> = {
          deposits: 'Deposits',
          delivery: 'Delivery',
          rentals: 'Rentals',
          uncategorized: 'Uncategorized'
        };
        const selectedCategory = availableProductCategories.find(cat => cat.id === selectedCategoryId);
        const categoryName = syntheticNames[selectedCategoryId] || selectedCategory?.name || 'Unknown';

        const totalSales = items.reduce((sum, item) => sum + item.total_sales, 0);

        return {
          value: formatCurrency(totalSales, false),
          label: `Product Sales - ${categoryName}`,
          categoryData: {
            items,
            total_sales: totalSales,
            category_id: selectedCategoryId,
            category_name: categoryName,
            view: 'items'
          }
        };
      }
    } catch (error) {
      console.error('Error fetching product sales by category data:', error);
      return {
        value: '$0',
        label: 'Product Sales by Category',
        categoryData: {
          categories: [],
          total_sales: 0,
          view: selectedCategoryId ? 'items' : 'categories'
        }
      };
    }
  };

  // Function to fetch service sales by category data
  const fetchServiceSalesByCategoryData = async (
    businessId: string,
    selectedCategoryId: string | null,
    dateRange: DateRangeOption,
    customStartDate?: Date,
    customEndDate?: Date
  ): Promise<{
    value: string;
    label: string;
    categoryData?: any;
  }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      if (!selectedCategoryId) {
        // No category selected - return sales grouped by category
        // Query bookings first, then get their services
        const { data: bookings, error: bookingsError } = await supabase
          .from('bookings')
          .select(`
            id,
            end_date,
            status,
            is_deleted,
            subtotal,
            discount_amount,
            services:booking_services(
              id,
              total_amount,
              service:service_id(
                category_id,
                categories:category_id(
                  id,
                  name
                )
              )
            )
          `)
          .eq('business_id', businessId)
          .eq('is_deleted', false)
          .neq('status', 'cancelled')
          .gte('end_date', startDate.toISOString().split('T')[0])
          .lte('end_date', endDate.toISOString().split('T')[0]);

        if (bookingsError) {
          console.error('Error fetching bookings for service sales:', bookingsError);
          throw bookingsError;
        }

        // Group sales by category from all booking services
        const categoryMap = new Map<string, { name: string; total_sales: number; sale_count: number }>();
        
        (bookings || []).forEach(booking => {
          (booking.services || []).forEach((service: any) => {
            const serviceAmount = service.total_amount || 0;
            const category = service.service?.categories;
            
            if (category) {
              const catId = category.id;
              const catName = category.name;
              const existing = categoryMap.get(catId) || { name: catName, total_sales: 0, sale_count: 0 };
              categoryMap.set(catId, {
                name: catName,
                total_sales: existing.total_sales + serviceAmount,
                sale_count: existing.sale_count + 1
              });
            } else {
              // Uncategorized
              const uncatId = 'uncategorized';
              const existing = categoryMap.get(uncatId) || { name: 'Uncategorized', total_sales: 0, sale_count: 0 };
              categoryMap.set(uncatId, {
                name: 'Uncategorized',
                total_sales: existing.total_sales + serviceAmount,
                sale_count: existing.sale_count + 1
              });
            }
          });
        });

        // Convert to array and sort by total sales
        const categories = Array.from(categoryMap.entries()).map(([id, data]) => ({
          id,
          name: data.name,
          total_sales: data.total_sales,
          sale_count: data.sale_count
        })).sort((a, b) => b.total_sales - a.total_sales);

        // Calculate total booking sales using the same method as Bookings Sales report
        // This ensures the total matches: booking.subtotal - booking.discount_amount
        const totalSales = (bookings || []).reduce((sum, booking) => {
          const subtotal = booking.subtotal || 0;
          const discountAmount = booking.discount_amount || 0;
          return sum + (subtotal - discountAmount);
        }, 0);

        return {
          value: formatCurrency(totalSales, false),
          label: 'Total Booking Sales by Service Category',
          categoryData: {
            categories,
            total_sales: totalSales,
            view: 'categories'
          }
        };
      } else {
        // Category selected - return sales by individual services in that category
        // Query bookings first, then get their services
        const { data: bookings, error: bookingsError } = await supabase
          .from('bookings')
          .select(`
            id,
            end_date,
            status,
            is_deleted,
            services:booking_services(
              id,
              name,
              total_amount,
              service:service_id(
                id,
                name,
                category_id
              )
            )
          `)
          .eq('business_id', businessId)
          .eq('is_deleted', false)
          .neq('status', 'cancelled')
          .gte('end_date', startDate.toISOString().split('T')[0])
          .lte('end_date', endDate.toISOString().split('T')[0]);

        if (bookingsError) {
          console.error('Error fetching bookings for service sales:', bookingsError);
          throw bookingsError;
        }

        // Filter services by selected category and group by service
        const serviceMap = new Map<string, { name: string; total_sales: number; sale_count: number }>();
        
        (bookings || []).forEach(booking => {
          (booking.services || []).forEach((service: any) => {
            const serviceCategoryId = service.service?.category_id;
            
            // Include if category matches, or if category is "uncategorized" and service has no category
            if ((selectedCategoryId === 'uncategorized' && !serviceCategoryId) || 
                (serviceCategoryId && serviceCategoryId === selectedCategoryId)) {
              const serviceId = service.service?.id || service.name;
              const serviceName = service.service?.name || service.name;
              const existing = serviceMap.get(serviceId) || { 
                name: serviceName, 
                total_sales: 0, 
                sale_count: 0
              };
              
              serviceMap.set(serviceId, {
                name: serviceName,
                total_sales: existing.total_sales + (service.total_amount || 0),
                sale_count: existing.sale_count + 1
              });
            }
          });
        });

        // Convert to array and sort by total sales
        const services = Array.from(serviceMap.entries()).map(([id, data]) => ({
          id,
          name: data.name,
          total_sales: data.total_sales,
          sale_count: data.sale_count
        })).sort((a, b) => b.total_sales - a.total_sales);

        // Get category name - fetch it from service_categories if not uncategorized
        let categoryName = 'Uncategorized';
        if (selectedCategoryId !== 'uncategorized') {
          const { data: categoryData } = await supabase
            .from('service_categories')
            .select('name')
            .eq('id', selectedCategoryId)
            .single();
          
          if (categoryData) {
            categoryName = categoryData.name;
          }
        }

        const totalSales = services.reduce((sum, service) => sum + service.total_sales, 0);

        return {
          value: formatCurrency(totalSales, false),
          label: `Service Sales - ${categoryName}`,
          categoryData: {
            services,
            total_sales: totalSales,
            category_id: selectedCategoryId,
            category_name: categoryName,
            view: 'services'
          }
        };
      }
    } catch (error) {
      console.error('Error fetching service sales by category data:', error);
      return {
        value: '$0',
        label: 'Service Sales by Category',
        categoryData: {
          categories: [],
          total_sales: 0,
          view: selectedCategoryId ? 'services' : 'categories'
        }
      };
    }
  };

  // Function to fetch available product categories
  const fetchAvailableProductCategories = async (
    businessId: string,
    dateRange: DateRangeOption,
    customStartDate?: Date,
    customEndDate?: Date
  ): Promise<Array<{ id: string; name: string; total_sales: number }>> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      // Get all categories that have product sales in the date range
      const { data: categorySales, error } = await supabase
        .from('product_sales')
        .select(`
          id,
          total_amount,
          invoice:invoices!product_sales_invoice_id_fkey(status),
          items:product_sale_items(
            stock_item_id,
            stock_items:stock_item_id(
              category_id,
              categories:category_id(
                id,
                name
              )
            )
          )
        `)
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching available categories:', error);
        throw error;
      }

      // Group by category
      const categoryMap = new Map<string, { name: string; total_sales: number }>();
      let uncategorizedTotal = 0;

      (categorySales || []).filter((sale) => !isDeadInvoiceProductSale(sale)).forEach(sale => {
        const saleAmount = sale.total_amount || 0;
        const categories = new Set<string>();
        
        (sale.items || []).forEach((item: any) => {
          const category = item.stock_items?.categories;
          if (category) {
            categories.add(category.id);
          }
        });

        if (categories.size === 0) {
          uncategorizedTotal += saleAmount;
        } else {
          const amountPerCategory = saleAmount / categories.size;
          categories.forEach(catId => {
            const category = (sale.items || []).find((item: any) => item.stock_items?.categories?.id === catId)?.stock_items?.categories;
            const catName = category?.name || 'Uncategorized';
            const existing = categoryMap.get(catId) || { name: catName, total_sales: 0 };
            categoryMap.set(catId, {
              name: catName,
              total_sales: existing.total_sales + amountPerCategory
            });
          });
        }
      });

      // Convert to array
      const categories = Array.from(categoryMap.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        total_sales: data.total_sales
      }));

      // Add uncategorized if it has sales
      if (uncategorizedTotal > 0) {
        categories.push({
          id: 'uncategorized',
          name: 'Uncategorized',
          total_sales: uncategorizedTotal
        });
      }

      // Sort by total sales
      return categories.sort((a, b) => b.total_sales - a.total_sales);
    } catch (error) {
      console.error('Error fetching available categories:', error);
      return [];
    }
  };

  // Function to fetch real gift card sales data from gift_cards table
  const fetchGiftCardSalesData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query gift_cards table for gift cards purchased within the date range
      // initial_amount represents the value of the gift card when it was sold
      const { data: giftCards, error } = await supabase
        .from('gift_cards')
        .select('initial_amount, created_at')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching gift card sales data:', error);
        throw error;
      }

      // Calculate total gift card sales (sum of initial_amount)
      const totalGiftCardSales = (giftCards || []).reduce((sum, card) => {
        return sum + (card.initial_amount || 0);
      }, 0);

      return {
        value: formatCurrency(totalGiftCardSales, false),
        label: `Total Gift Card Sales`
      };
    } catch (error) {
      console.error('Error fetching gift card sales data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Gift Card Sales`
      };
    }
  };

  // Function to fetch real phone calls data from call_history table
  const fetchPhoneCallsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query call_history for inbound calls within the date range
      const { data: calls, error } = await supabase
        .from('call_history')
        .select('*')
        .eq('business_id', businessId)
        .eq('direction', 'inbound')
        .gte('call_time', startDate.toISOString())
        .lte('call_time', endDate.toISOString());

      if (error) {
        console.error('Error fetching phone calls data:', error);
        return { value: '0', label: 'Incoming Phone Calls' };
      }

      const phoneCallsCount = calls?.length || 0;
      
      return {
        value: phoneCallsCount.toString(),
        label: 'Incoming Phone Calls'
      };
    } catch (error) {
      console.error('Error in fetchPhoneCallsData:', error);
      return { value: '0', label: 'Incoming Phone Calls' };
    }
  };

  // Function to fetch payments data from payments table
  const fetchPaymentsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query completed payments for all payment types within the date range
      // Include payments with booking_id, project_id, proposal_id,
      // product_sale_id, gift_card_id, or invoice_id (standalone invoices)
      const { data: payments, error } = await supabase
        .from('payments')
        .select('amount, booking_id, project_id, proposal_id, product_sale_id, gift_card_id, invoice_id')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .or('booking_id.not.is.null,project_id.not.is.null,proposal_id.not.is.null,product_sale_id.not.is.null,gift_card_id.not.is.null,invoice_id.not.is.null')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());
      
      // Filter to ensure at least one payment type identifier is not null
      // (Additional client-side filter as backup, though the .or() should handle this)
      const validPayments = (payments || []).filter(payment => 
        payment.booking_id || payment.project_id || payment.proposal_id || payment.product_sale_id || payment.gift_card_id || payment.invoice_id
      );

      if (error) {
        console.error('Error fetching payments data:', error);
        throw error;
      }

      // Calculate total payments (including base amount + tip if applicable)
      const totalPayments = validPayments.reduce((sum, payment) => {
        // Include tip_amount if present, otherwise just amount
        const paymentAmount = payment.amount || 0;
        return sum + paymentAmount;
      }, 0);

      return {
        value: formatCurrency(totalPayments, false),
        label: 'Total Payments Collected'
      };
    } catch (error) {
      console.error('Error fetching payments data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: 'Total Payments Collected'
      };
    }
  };

  // Function to fetch payments by type data from payments table
  const fetchPaymentsByTypeData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ 
    total: number;
    byType: { type: string; amount: number; label: string }[];
    byStatus: { status: string; amount: number; label: string }[];
    byMethod: { method: string; amount: number; label: string }[];
  }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query completed payments for all payment types within the date range
      // Include 'method' field to group by payment method
      const { data: payments, error } = await supabase
        .from('payments')
        .select('amount, booking_id, project_id, proposal_id, product_sale_id, gift_card_id, invoice_id, payment_type, method')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .or('booking_id.not.is.null,project_id.not.is.null,proposal_id.not.is.null,product_sale_id.not.is.null,gift_card_id.not.is.null,invoice_id.not.is.null')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching payments by type data:', error);
        throw error;
      }

      // Filter to ensure at least one payment type identifier is not null
      const validPayments = (payments || []).filter(payment => 
        payment.booking_id || payment.project_id || payment.proposal_id || payment.product_sale_id || payment.gift_card_id || payment.invoice_id
      );

      // Calculate total
      const total = validPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);

      // Group by type
      const byTypeMap: { [key: string]: number } = {
        'bookings': 0,
        'projects': 0,
        'proposals': 0,
        'products': 0,
        'gift_cards': 0,
        'invoices': 0
      };

      validPayments.forEach(payment => {
        const amount = payment.amount || 0;
        if (payment.booking_id) byTypeMap['bookings'] += amount;
        else if (payment.project_id) byTypeMap['projects'] += amount;
        else if (payment.proposal_id) byTypeMap['proposals'] += amount;
        else if (payment.product_sale_id) byTypeMap['products'] += amount;
        else if (payment.gift_card_id) byTypeMap['gift_cards'] += amount;
        else if (payment.invoice_id) byTypeMap['invoices'] += amount;
      });

      const byType = [
        { type: 'bookings', amount: byTypeMap['bookings'], label: 'Bookings' },
        { type: 'projects', amount: byTypeMap['projects'], label: 'Projects' },
        { type: 'proposals', amount: byTypeMap['proposals'], label: 'Proposals' },
        { type: 'products', amount: byTypeMap['products'], label: 'Products' },
        { type: 'gift_cards', amount: byTypeMap['gift_cards'], label: 'Gift Cards' },
        { type: 'invoices', amount: byTypeMap['invoices'], label: 'Invoices' }
      ].filter(item => item.amount > 0); // Only show types with payments

      // Group by payment status (deposit, final, partial)
      const byStatusMap: { [key: string]: number } = {
        'deposit': 0,
        'final': 0,
        'partial': 0
      };

      validPayments.forEach(payment => {
        const amount = payment.amount || 0;
        const paymentType = payment.payment_type || 'final'; // Default to 'final' if not specified
        if (paymentType === 'deposit') byStatusMap['deposit'] += amount;
        else if (paymentType === 'final') byStatusMap['final'] += amount;
        else if (paymentType === 'partial') byStatusMap['partial'] += amount;
      });

      const byStatus = [
        { status: 'deposit', amount: byStatusMap['deposit'], label: 'Deposits' },
        { status: 'final', amount: byStatusMap['final'], label: 'Full Payments' },
        { status: 'partial', amount: byStatusMap['partial'], label: 'Partial Payments' }
      ].filter(item => item.amount > 0); // Only show statuses with payments

      // Group by payment method (credit_card, bank_transfer, cash, etc.)
      const byMethodMap: { [key: string]: number } = {};
      
      // Method label mapping for display
      const methodLabels: { [key: string]: string } = {
        'credit_card': 'Credit/Debit',
        'debit_card': 'Debit Card',
        'cash': 'Cash',
        'bank_transfer': 'E-Transfer',
        'check': 'Check',
        'other': 'Other'
      };

      validPayments.forEach(payment => {
        const amount = payment.amount || 0;
        const method = payment.method || 'other';
        if (!byMethodMap[method]) byMethodMap[method] = 0;
        byMethodMap[method] += amount;
      });

      const byMethod = Object.entries(byMethodMap)
        .map(([method, amount]) => ({
          method,
          amount,
          label: methodLabels[method] || method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        }))
        .filter(item => item.amount > 0)
        .sort((a, b) => b.amount - a.amount); // Sort by amount descending

      return {
        total,
        byType,
        byStatus,
        byMethod
      };
    } catch (error) {
      console.error('Error fetching payments by type data:', error);
      // Return fallback data on error
      return {
        total: 0,
        byType: [],
        byStatus: [],
        byMethod: []
      };
    }
  };

  // Function to fetch real missed calls data from call_history table
  const fetchMissedCallsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query call_history for inbound missed calls within the date range
      const { data: calls, error } = await supabase
        .from('call_history')
        .select('*')
        .eq('business_id', businessId)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .gte('call_time', startDate.toISOString())
        .lte('call_time', endDate.toISOString());

      if (error) {
        console.error('Error fetching missed calls data:', error);
        return { value: '0', label: 'Missed Calls' };
      }

      const missedCallsCount = calls?.length || 0;
      
      return {
        value: missedCallsCount.toString(),
        label: 'Missed Calls'
      };
    } catch (error) {
      console.error('Error in fetchMissedCallsData:', error);
      return { value: '0', label: 'Missed Calls' };
    }
  };

  // Function to fetch web traffic patterns data
  const fetchWebTrafficPatternsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ 
    value: string; 
    label: string; 
    trafficData?: any;
  }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Convert to UTC for consistent date handling
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
      
      console.log('🔍 [Web Traffic Patterns Debug]');
      console.log('Date Range:', dateRange);
      console.log('Start Date UTC:', startDateUTC.toISOString());
      console.log('End Date UTC:', endDateUTC.toISOString());
      
      // Use Postgres function to get aggregated traffic patterns (bypasses row limits)
      const { data, error } = await supabase.rpc('get_web_traffic_patterns', {
        p_business_id: businessId,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString()
      });

      if (error) {
        console.error('❌ Error fetching web traffic patterns:', error);
        throw error;
      }

      console.log('✅ Web Traffic Patterns Data:', data);

      // Extract summary stats
      const summary = data?.summary || {};
      const totalEvents = summary.total_events || 0;
      const totalSessions = summary.total_sessions || 0;

      // Structure the data for visualization
      const utmSummary = data?.utm_summary || {};
      const trafficData = {
        landingPages: data?.landing_pages || [],
        popularPages: data?.popular_pages || [],
        topClicks: data?.top_clicks || [],
        formSubmissions: data?.form_submissions || [],
        eventDistribution: data?.event_distribution || [],
        trafficSources: data?.traffic_sources || [],
        utmCampaigns: data?.utm_campaigns || [],
        utmSummary: {
          totalSessions: utmSummary.total_sessions || 0,
          paidSessions: utmSummary.paid_sessions || 0,
          organicSessions: utmSummary.organic_sessions || 0,
          paidPercentage: utmSummary.paid_percentage || 0,
          organicPercentage: utmSummary.organic_percentage || 0
        },
        summary: {
          totalEvents: totalEvents,
          totalSessions: totalSessions,
          totalVisitors: summary.total_visitors || 0,
          totalPageViews: summary.total_page_views || 0,
          totalClicks: summary.total_clicks || 0,
          totalFormSubmissions: summary.total_form_submissions || 0
        }
      };

      return {
        value: totalEvents.toLocaleString(),
        label: `Total Events (${totalSessions.toLocaleString()} sessions)`,
        trafficData
      };
    } catch (error) {
      console.error('❌ Error in fetchWebTrafficPatternsData:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Total Events`,
        trafficData: {
          landingPages: [],
          popularPages: [],
          topClicks: [],
          formSubmissions: [],
          eventDistribution: [],
          trafficSources: [],
          utmCampaigns: [],
          utmSummary: {
            totalSessions: 0,
            paidSessions: 0,
            organicSessions: 0,
            paidPercentage: 0,
            organicPercentage: 0
          },
          summary: {
            totalEvents: 0,
            totalSessions: 0,
            totalVisitors: 0,
            totalPageViews: 0,
            totalClicks: 0,
            totalFormSubmissions: 0
          }
        }
      };
    }
  };

  // Function to fetch page route analytics data
  const fetchPageRouteAnalyticsData = async (
    businessId: string, 
    selectedRoute: string,
    dateRange: DateRangeOption, 
    customStartDate?: Date, 
    customEndDate?: Date
  ): Promise<{ 
    value: string; 
    label: string; 
    routeData?: any;
  }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedRoute) {
        // Return empty data if no route selected
        return {
          value: '0',
          label: 'Select a route',
          routeData: {
            summary: { total_visitors: 0, total_page_views: 0, total_sessions: 0 },
            traffic_sources: [],
            route: ''
          }
        };
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Convert to UTC for consistent date handling
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
      
      console.log('🔍 [Page Route Analytics Debug]', {
        route: selectedRoute,
        dateRange,
        startDate: startDateUTC.toISOString(),
        endDate: endDateUTC.toISOString()
      });
      
      // Use Postgres function to get route-specific analytics
      const { data, error } = await supabase.rpc('get_page_route_analytics', {
        p_business_id: businessId,
        p_route: selectedRoute,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString()
      });

      if (error) {
        console.error('❌ Error fetching page route analytics:', error);
        throw error;
      }

      console.log('✅ Page Route Analytics Data:', data);

      // Extract summary stats
      const summary = data?.summary || {};
      const totalVisitors = summary.total_visitors || 0;
      const trafficSources = data?.traffic_sources || [];

      // Structure the data for visualization
      const routeData = {
        summary: {
          total_visitors: totalVisitors,
          total_page_views: summary.total_page_views || 0,
          total_sessions: summary.total_sessions || 0
        },
        traffic_sources: trafficSources,
        form_submissions: data?.form_submissions || [],
        route: data?.route || selectedRoute
      };

      return {
        value: totalVisitors.toLocaleString(),
        label: `Visitors to ${selectedRoute}`,
        routeData
      };
    } catch (error) {
      console.error('❌ Error in fetchPageRouteAnalyticsData:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Visitors to ${selectedRoute || 'route'}`,
        routeData: {
          summary: { total_visitors: 0, total_page_views: 0, total_sessions: 0 },
          traffic_sources: [],
          route: selectedRoute || ''
        }
      };
    }
  };

  // Function to fetch click analytics data
  const fetchClickAnalyticsData = async (
    businessId: string, 
    selectedClick: string,
    dateRange: DateRangeOption, 
    customStartDate?: Date, 
    customEndDate?: Date
  ): Promise<{ 
    value: string; 
    label: string; 
    clickData?: any;
  }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      if (!selectedClick) {
        // Return empty data if no click selected
        return {
          value: '0',
          label: 'Select a click',
          clickData: {
            summary: { total_visitors: 0, total_clicks: 0, total_sessions: 0 },
            traffic_sources: [],
            click_identifier: ''
          }
        };
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Convert to UTC for consistent date handling
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
      
      console.log('🔍 [Click Analytics Debug]', {
        click: selectedClick,
        dateRange,
        startDate: startDateUTC.toISOString(),
        endDate: endDateUTC.toISOString()
      });
      
      // Use Postgres function to get click-specific analytics
      const { data, error } = await supabase.rpc('get_click_analytics', {
        p_business_id: businessId,
        p_click_identifier: selectedClick,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString()
      });

      if (error) {
        console.error('❌ Error fetching click analytics:', error);
        throw error;
      }

      console.log('✅ Click Analytics Data:', data);

      // Extract summary stats
      const summary = data?.summary || {};
      const totalVisitors = summary.total_visitors || 0;
      const trafficSources = data?.traffic_sources || [];

      // Structure the data for visualization
      const clickData = {
        summary: {
          total_visitors: totalVisitors,
          total_clicks: summary.total_clicks || 0,
          total_sessions: summary.total_sessions || 0
        },
        traffic_sources: trafficSources,
        form_submissions: data?.form_submissions || [],
        click_identifier: data?.click_identifier || selectedClick
      };

      return {
        value: totalVisitors.toLocaleString(),
        label: `Visitors who clicked ${selectedClick}`,
        clickData
      };
    } catch (error) {
      console.error('❌ Error in fetchClickAnalyticsData:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Visitors who clicked ${selectedClick || 'click'}`,
        clickData: {
          summary: { total_visitors: 0, total_clicks: 0, total_sessions: 0 },
          traffic_sources: [],
          click_identifier: selectedClick || ''
        }
      };
    }
  };

  // Function to fetch available routes for selection
  const fetchAvailableRoutes = async (
    businessId: string,
    dateRange: DateRangeOption,
    customStartDate?: Date,
    customEndDate?: Date
  ): Promise<Array<{ route: string; visitors: number; views: number }>> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Convert to UTC for consistent date handling
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
      
      console.log('🔍 [fetchAvailableRoutes]', {
        businessId,
        dateRange,
        startDate: startDateUTC.toISOString(),
        endDate: endDateUTC.toISOString()
      });
      
      // Use Postgres function to get available routes
      const { data, error } = await supabase.rpc('get_available_routes', {
        p_business_id: businessId,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString()
      });

      if (error) {
        console.error('❌ Error fetching available routes:', error);
        throw error;
      }

      console.log('✅ [fetchAvailableRoutes] Loaded routes:', data);
      return data || [];
    } catch (error) {
      console.error('❌ Error in fetchAvailableRoutes:', error);
      return [];
    }
  };

  // Function to fetch available clicks for selection
  const fetchAvailableClicks = async (
    businessId: string,
    dateRange: DateRangeOption,
    customStartDate?: Date,
    customEndDate?: Date
  ): Promise<Array<{ identifier: string; label: string; visitors: number; clicks: number }>> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));

      const { data, error } = await supabase.rpc('get_available_clicks', {
        p_business_id: businessId,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString()
      });

      if (error) {
        console.error('Error fetching available clicks:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching available clicks:', error);
      return [];
    }
  };

  // Function to fetch CRM leads overview data
  const fetchCRMLeadsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ total: number; won: number; lost: number }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query all opportunities within the date range
      const { data: opportunities, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, status')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching CRM leads data:', error);
        throw error;
      }

      const total = opportunities?.length || 0;
      const won = opportunities?.filter(opp => opp.status?.toLowerCase() === 'won').length || 0;
      const lost = opportunities?.filter(opp => opp.status?.toLowerCase() === 'lost').length || 0;

      return { total, won, lost };
    } catch (error) {
      console.error('Error in fetchCRMLeadsData:', error);
      return { total: 0, won: 0, lost: 0 };
    }
  };

  // Resolve resource ids → display names ("Unassigned" for null buckets).
  const fetchResourceNames = async (businessId: string, ids: string[]): Promise<Map<string, string>> => {
    const names = new Map<string, string>();
    if (ids.length === 0) return names;
    const { data } = await supabase
      .from('resources')
      .select('id, first_name, last_name')
      .eq('business_id', businessId)
      .in('id', ids);
    (data || []).forEach((r: any) => {
      names.set(r.id, `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Unnamed');
    });
    return names;
  };

  // Won / Lost by team member: leads CREATED in the range, grouped by their
  // assigned resource, split by current status (open/won/lost) with win rate.
  const fetchCRMWonLostByStaffData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const { data: opportunities, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, status, assigned_resource_id')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching won/lost by staff data:', error);
        throw error;
      }

      const groups = new Map<string, { open: number; won: number; lost: number }>();
      (opportunities || []).forEach(opp => {
        const key = opp.assigned_resource_id || 'unassigned';
        const g = groups.get(key) || { open: 0, won: 0, lost: 0 };
        const status = (opp.status || '').toLowerCase();
        if (status === 'won') g.won++;
        else if (status === 'lost') g.lost++;
        else g.open++;
        groups.set(key, g);
      });

      const names = await fetchResourceNames(
        businessId,
        Array.from(groups.keys()).filter(id => id !== 'unassigned')
      );

      const staff = Array.from(groups.entries()).map(([id, g]) => {
        const closed = g.won + g.lost;
        return {
          id,
          name: id === 'unassigned' ? 'Unassigned' : (names.get(id) || 'Unknown'),
          open: g.open,
          won: g.won,
          lost: g.lost,
          winPercentage: closed > 0 ? (g.won / closed) * 100 : 0
        };
      }).sort((a, b) => (b.won - a.won) || (b.open + b.won + b.lost) - (a.open + a.won + a.lost));

      const totalWon = staff.reduce((sum, s) => sum + s.won, 0);
      const totalLost = staff.reduce((sum, s) => sum + s.lost, 0);

      return {
        value: `${totalWon} • ${totalLost}`,
        label: 'Won • Lost',
        staff
      };
    } catch (error) {
      console.error('Error in fetchCRMWonLostByStaffData:', error);
      return { value: '0 • 0', label: 'Won • Lost', staff: [] };
    }
  };

  // Leads created by team member: leads CREATED in the range, grouped by the
  // creator's resource (created_by_resource_id), with share of total.
  const fetchCRMLeadsCreatedByResourceData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const { data: opportunities, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, created_by_resource_id')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching leads created by resource data:', error);
        throw error;
      }

      const groups = new Map<string, number>();
      (opportunities || []).forEach(opp => {
        const key = opp.created_by_resource_id || 'unassigned';
        groups.set(key, (groups.get(key) || 0) + 1);
      });

      const names = await fetchResourceNames(
        businessId,
        Array.from(groups.keys()).filter(id => id !== 'unassigned')
      );

      const total = (opportunities || []).length;
      const staff = Array.from(groups.entries()).map(([id, created]) => ({
        id,
        name: id === 'unassigned' ? 'No resource' : (names.get(id) || 'Unknown'),
        created,
        percentage: total > 0 ? (created / total) * 100 : 0
      })).sort((a, b) => b.created - a.created);

      return {
        value: String(total),
        label: 'Leads Created',
        staff
      };
    } catch (error) {
      console.error('Error in fetchCRMLeadsCreatedByResourceData:', error);
      return { value: '0', label: 'Leads Created', staff: [] };
    }
  };

  // Lead lifecycle: for leads CREATED in the range, how long from creation to
  // close (won/lost)? Close times come from the CRM's status_changed activity
  // log (activity_data.to = 'Won'/'Lost'); if a lead re-opened and re-closed,
  // the LAST transition to its current status counts. Closed leads with no
  // logged transition (predating the activity log) are counted but excluded
  // from the averages.
  const fetchLeadLifecycleData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const { data: leads, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, status, created_at')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching lead lifecycle data:', error);
        throw error;
      }

      const all = leads || [];
      const closed = all.filter(l => ['won', 'lost'].includes((l.status || '').toLowerCase()));

      // Close timestamps from the activity log, batched over the closed leads.
      const closeTimeByLead = new Map<number, number>();
      if (closed.length > 0) {
        const { data: activities } = await supabase
          .from('opportunity_activities')
          .select('pipeline_opportunities_id, activity_data, created_at')
          .eq('business_id', businessId)
          .eq('activity_type', 'status_changed')
          .in('pipeline_opportunities_id', closed.map(l => l.id));

        const statusByLead = new Map(closed.map(l => [l.id, (l.status || '').toLowerCase()]));
        (activities || []).forEach((a: any) => {
          const leadId = a.pipeline_opportunities_id;
          const to = String(a.activity_data?.to || '').toLowerCase();
          if (to !== statusByLead.get(leadId)) return;
          const t = new Date(a.created_at).getTime();
          const existing = closeTimeByLead.get(leadId);
          if (!existing || t > existing) closeTimeByLead.set(leadId, t); // last transition wins
        });
      }

      const dayMs = 24 * 60 * 60 * 1000;
      const durations = { won: [] as number[], lost: [] as number[] };
      closed.forEach(l => {
        const closeT = closeTimeByLead.get(l.id);
        if (!closeT) return;
        const days = Math.max(0, (closeT - new Date(l.created_at).getTime()) / dayMs);
        durations[(l.status || '').toLowerCase() as 'won' | 'lost'].push(days);
      });

      const avg = (arr: number[]) => (arr.length > 0 ? arr.reduce((s, d) => s + d, 0) / arr.length : null);
      const avgWonDays = avg(durations.won);
      const avgLostDays = avg(durations.lost);
      const allTimed = [...durations.won, ...durations.lost];
      const avgCloseDays = avg(allTimed);

      const fmtDays = (d: number | null) => {
        if (d === null) return '—';
        if (d < 1) return '<1 day';
        return `${d.toFixed(1)} days`;
      };

      const wonCount = closed.filter(l => (l.status || '').toLowerCase() === 'won').length;
      const lostCount = closed.filter(l => (l.status || '').toLowerCase() === 'lost').length;

      return {
        value: fmtDays(avgCloseDays),
        label: 'Avg Time to Close',
        newLeads: all.length,
        openCount: all.length - closed.length,
        wonCount,
        lostCount,
        avgCloseDays,
        avgWonDays,
        avgLostDays,
        avgCloseLabel: fmtDays(avgCloseDays),
        avgWonLabel: fmtDays(avgWonDays),
        avgLostLabel: fmtDays(avgLostDays),
        untimedClosed: closed.length - allTimed.length
      };
    } catch (error) {
      console.error('Error in fetchLeadLifecycleData:', error);
      return {
        value: '—',
        label: 'Avg Time to Close',
        newLeads: 0,
        openCount: 0,
        wonCount: 0,
        lostCount: 0,
        avgCloseDays: null,
        avgWonDays: null,
        avgLostDays: null,
        avgCloseLabel: '—',
        avgWonLabel: '—',
        avgLostLabel: '—',
        untimedClosed: 0
      };
    }
  };

  // Wallet overview: total liability = sum of materialized customer wallet
  // balances (point-in-time, not range-bound). Added/used come from the signed
  // wallet_transactions ledger (+ = credit, − = debit, amounts in dollars).
  // Wallet top-ups deliberately never count as sales, so this report is where
  // that money shows up.
  const fetchWalletOverviewData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const [balancesRes, ledgerRes] = await Promise.all([
        supabase
          .from('customers')
          .select('wallet')
          .eq('business_id', businessId)
          .neq('wallet', 0),
        supabase
          .from('wallet_transactions')
          .select('amount, type')
          .eq('business_id', businessId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
      ]);

      if (balancesRes.error) throw balancesRes.error;
      if (ledgerRes.error) throw ledgerRes.error;

      let liability = 0;
      let customersWithBalance = 0;
      (balancesRes.data || []).forEach((c: any) => {
        const bal = Number(c.wallet) || 0;
        liability += bal;
        if (bal > 0) customersWithBalance++;
      });

      let added = 0;
      let used = 0;
      let topups = 0;
      (ledgerRes.data || []).forEach((t: any) => {
        const amount = Number(t.amount) || 0;
        if (amount > 0) added += amount;
        else used += -amount;
        if (t.type === 'topup') topups += amount;
      });

      return {
        value: formatCurrency(liability, false),
        label: 'Wallet Liability',
        liability,
        added,
        used,
        topups,
        customersWithBalance
      };
    } catch (error) {
      console.error('Error in fetchWalletOverviewData:', error);
      return {
        value: '$0',
        label: 'Wallet Liability',
        liability: 0,
        added: 0,
        used: 0,
        topups: 0,
        customersWithBalance: 0
      };
    }
  };

  // Sales pins: door-to-door pins (address_markers) DROPPED in the range,
  // broken down by their CURRENT knock status. 'customer' markers are excluded
  // — those are auto-synced from the customer list, not dropped pins.
  const fetchSalesPinsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    // Colors/labels mirror the sales map (addressMarkerService).
    const PIN_META: Record<string, { label: string; color: string }> = {
      'interested': { label: 'Interested', color: '#3B82F6' },
      'comeback-later': { label: 'Come Back Later', color: '#F59E0B' },
      'estimate-sent': { label: 'Estimate Sent', color: '#8B5CF6' },
      'booked': { label: 'Closed', color: '#10B981' },
      'not-home': { label: 'Not Home', color: '#9CA3AF' },
      'not-interested': { label: 'Not Interested', color: '#DC2626' },
      'prospect': { label: 'Prospect', color: '#F97316' },
      'bad-customer': { label: 'Bad Customer', color: '#EF4444' }
    };
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const { data: markers, error } = await supabase
        .from('address_markers')
        .select('id, marker_type')
        .eq('business_id', businessId)
        .neq('marker_type', 'customer')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching sales pins data:', error);
        throw error;
      }

      const countByType = new Map<string, number>();
      (markers || []).forEach(m => {
        countByType.set(m.marker_type, (countByType.get(m.marker_type) || 0) + 1);
      });

      const total = (markers || []).length;
      const pins = Array.from(countByType.entries()).map(([type, count]) => ({
        type,
        label: PIN_META[type]?.label || type,
        color: PIN_META[type]?.color || '#9CA3AF',
        count,
        percentage: total > 0 ? (count / total) * 100 : 0
      })).sort((a, b) => b.count - a.count);

      return {
        value: String(total),
        label: 'Sales Pins Dropped',
        pins
      };
    } catch (error) {
      console.error('Error in fetchSalesPinsData:', error);
      return { value: '0', label: 'Sales Pins Dropped', pins: [] };
    }
  };

  // Active Customers: the Active customers segment (a non-void invoice in the
  // last N days). Hidden until that segment is shown on the Customers page.
  // Active status has no stored history, but it's reconstructible from invoice
  // dates, so we snapshot the active set at the START and END of the range:
  //   • active        = customers active at range end (clamped to now)
  //   • becameActive  = active at end but not at start
  //   • wentInactive  = active at start but not at end
  const fetchActiveCustomersData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const segments = await listCustomerSegments(businessId);
      const activeSegment = segments.find((segment) => segment.system_key === 'active_customers');
      if (!activeSegment?.visible) {
        return {
          value: '—',
          label: 'Active Customers',
          enabled: false,
          active: 0,
          previousActive: 0,
          becameActive: 0,
          wentInactive: 0,
          windowDays: 0
        };
      }

      const dayRule = activeSegment.rules.find((rule) => rule.kind === 'invoice_within_days');
      const windowDays = Math.max(1, Number(dayRule?.value?.days) || 45);
      const windowMs = windowDays * 24 * 60 * 60 * 1000;
      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      // Snapshots can't see the future — clamp to now (e.g. mid "this month").
      const now = new Date();
      const endSnap = endDate.getTime() > now.getTime() ? now : endDate;
      const startSnap = startDate.getTime() > now.getTime() ? now : startDate;

      // One fetch covers both snapshots: any qualifying invoice between
      // (rangeStart − window) and rangeEnd.
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select('customer_id, created_at')
        .eq('business_id', businessId)
        .neq('status', 'void')
        .not('customer_id', 'is', null)
        .gte('created_at', new Date(startSnap.getTime() - windowMs).toISOString())
        .lte('created_at', endSnap.toISOString());

      if (error) {
        console.error('Error fetching active customers data:', error);
        throw error;
      }

      const activeAt = (snap: Date): Set<string> => {
        const from = snap.getTime() - windowMs;
        const to = snap.getTime();
        const ids = new Set<string>();
        (invoices || []).forEach(inv => {
          const t = new Date(inv.created_at).getTime();
          if (t >= from && t <= to && inv.customer_id) ids.add(inv.customer_id as string);
        });
        return ids;
      };

      const startActive = activeAt(startSnap);
      const endActive = activeAt(endSnap);

      let becameActive = 0;
      endActive.forEach(id => { if (!startActive.has(id)) becameActive++; });
      let wentInactive = 0;
      startActive.forEach(id => { if (!endActive.has(id)) wentInactive++; });

      const net = endActive.size - startActive.size;
      return {
        value: String(endActive.size),
        label: 'Active Customers',
        enabled: true,
        active: endActive.size,
        previousActive: startActive.size,
        becameActive,
        wentInactive,
        net,
        windowDays
      };
    } catch (error) {
      console.error('Error in fetchActiveCustomersData:', error);
      return {
        value: '0',
        label: 'Active Customers',
        enabled: true,
        active: 0,
        previousActive: 0,
        becameActive: 0,
        wentInactive: 0,
        net: 0,
        windowDays: 45
      };
    }
  };

  // New Leads graph: stacked bar of leads per day (bucketed by CREATION date),
  // split by the lead's CURRENT status. A lead created Sep 1 always counts in
  // Sep 1's bar — when it later closes, that bar's segments shift open→won/lost.
  const fetchNewLeadsGraphData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string; chartData?: any }> => {
    const emptyChart = {
      labels: [] as string[],
      datasets: [] as any[]
    };
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const { data: opportunities, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, status, created_at')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching new leads graph data:', error);
        throw error;
      }

      // Long ranges get monthly buckets so the chart stays readable.
      const rangeDays = Math.ceil((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
      const monthly = dateRange === 'this_year' || dateRange === 'last_year' || rangeDays > 92;

      // Zero-fill every bucket in the range so quiet days still show on the axis.
      const bucketKeys: string[] = [];
      if (monthly) {
        const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        while (cursor <= endDate) {
          bucketKeys.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
          cursor.setMonth(cursor.getMonth() + 1);
        }
      } else {
        const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        while (cursor <= endDate) {
          bucketKeys.push(cursor.toLocaleDateString('en-CA')); // yyyy-mm-dd local
          cursor.setDate(cursor.getDate() + 1);
        }
      }

      const makeCounts = () => Object.fromEntries(bucketKeys.map(k => [k, 0])) as Record<string, number>;
      const counts = { open: makeCounts(), won: makeCounts(), lost: makeCounts() };

      (opportunities || []).forEach(opp => {
        if (!opp.created_at) return;
        const created = new Date(opp.created_at); // bucket by local creation day
        const key = monthly
          ? `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`
          : created.toLocaleDateString('en-CA');
        if (!(key in counts.open)) return;
        const status = (opp.status || '').toLowerCase();
        if (status === 'won') counts.won[key]++;
        else if (status === 'lost') counts.lost[key]++;
        else counts.open[key]++;
      });

      const labels = bucketKeys.map(key => {
        if (monthly) {
          const [year, month] = key.split('-').map(Number);
          return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short' });
        }
        const [year, month, day] = key.split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });

      const chartData = {
        labels,
        datasets: [
          {
            label: 'Won',
            data: bucketKeys.map(k => counts.won[k]),
            backgroundColor: 'rgba(16, 185, 129, 0.85)',
            stack: 'leads',
            borderRadius: 2
          },
          {
            label: 'Lost',
            data: bucketKeys.map(k => counts.lost[k]),
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            stack: 'leads',
            borderRadius: 2
          },
          {
            label: 'Open',
            data: bucketKeys.map(k => counts.open[k]),
            backgroundColor: 'rgba(59, 130, 246, 0.8)',
            stack: 'leads',
            borderRadius: 2
          }
        ]
      };

      const total = (opportunities || []).length;
      return {
        value: String(total),
        label: 'New Leads',
        chartData
      };
    } catch (error) {
      console.error('Error in fetchNewLeadsGraphData:', error);
      return { value: '0', label: 'New Leads', chartData: emptyChart };
    }
  };

  // Function to fetch CRM leads by source data (groups leads by source with Open/Won/Lost counts, total value, and win%)
  const fetchCRMLeadsBySourceData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      console.log('🔍 Fetching CRM Leads by Source Data for business:', businessId, 'dateRange:', dateRange);

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      // Query all opportunities within the date range with source, status, and value
      const { data: opportunities, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, source, status, value')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching CRM leads by source:', error);
        throw error;
      }

      // Group opportunities by source
      const sourceMap: Record<string, {
        source: string;
        open: number;
        openValue: number;
        won: number;
        wonValue: number;
        lost: number;
        lostValue: number;
        totalValue: number;
      }> = {};

      opportunities?.forEach(opp => {
        // Normalize source: trim whitespace, lowercase for consistent grouping
        // But keep original case for display
        const rawSource = opp.source || 'Unknown';
        const normalizedSource = rawSource.trim().toLowerCase();
        const value = parseFloat(opp.value) || 0;

        // Use normalized key for grouping, but store original for display
        if (!sourceMap[normalizedSource]) {
          sourceMap[normalizedSource] = {
            source: rawSource.trim(), // Store original with trimmed whitespace
            open: 0,
            openValue: 0,
            won: 0,
            wonValue: 0,
            lost: 0,
            lostValue: 0,
            totalValue: 0
          };
        }

        // Count and sum value by status
        const normalizedStatus = opp.status?.toLowerCase();
        if (normalizedStatus === 'open') {
          sourceMap[normalizedSource].open++;
          sourceMap[normalizedSource].openValue += value;
        } else if (normalizedStatus === 'won') {
          sourceMap[normalizedSource].won++;
          sourceMap[normalizedSource].wonValue += value;
        } else if (normalizedStatus === 'lost') {
          sourceMap[normalizedSource].lost++;
          sourceMap[normalizedSource].lostValue += value;
        }

        // Sum total value
        sourceMap[normalizedSource].totalValue += value;
      });

      // Convert to array and calculate win%
      const sources = Object.values(sourceMap).map(sourceData => {
        const totalClosed = sourceData.won + sourceData.lost;
        const winPercentage = totalClosed > 0 ? (sourceData.won / totalClosed) * 100 : 0;

        return {
          source: sourceData.source,
          open: sourceData.open,
          openValue: sourceData.openValue,
          won: sourceData.won,
          wonValue: sourceData.wonValue,
          lost: sourceData.lost,
          lostValue: sourceData.lostValue,
          totalValue: sourceData.totalValue,
          winPercentage: Math.round(winPercentage * 100) / 100 // Round to 2 decimal places
        };
      }).sort((a, b) => b.totalValue - a.totalValue); // Sort by total value descending

      const result = {
        sources
      };

      console.log('✨ CRM Leads by Source data:', result);

      return result;
    } catch (error) {
      console.error('Error in fetchCRMLeadsBySourceData:', error);
      return { sources: [] };
    }
  };

  // Function to fetch CRM lost reasons data for pie chart
  const fetchCRMLostReasonsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date) => {
    try {
      if (!supabase) throw new Error('Supabase client is not initialized');

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);

      const { data: opportunities, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, lost_reason, value, status')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) throw error;

      const lost = (opportunities || []).filter(opp => opp.status?.toLowerCase() === 'lost');

      if (lost.length === 0) {
        return {
          value: '0',
          label: 'Lost Leads',
          chartData: null,
          isPieChart: true,
          sourceDetails: []
        };
      }

      const reasonCounts: Record<string, number> = {};
      const reasonValues: Record<string, number> = {};
      lost.forEach(opp => {
        const reason = opp.lost_reason || 'No Reason Recorded';
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
        reasonValues[reason] = (reasonValues[reason] || 0) + (parseFloat(opp.value) || 0);
      });

      const sortedReasons = Object.entries(reasonCounts).sort(([, a], [, b]) => b - a);
      const total = lost.length;

      const colors = [
        '#EF4444', '#F97316', '#F59E0B', '#8B5CF6', '#3B82F6', '#06B6D4', '#10B981', '#EC4899', '#84CC16', '#6B7280'
      ];

      const sourceDetails = sortedReasons.map(([reason, count]) => ({
        source: reason,
        count,
        sales: reasonValues[reason] || 0,
        percentage: ((count / total) * 100).toFixed(1)
      }));

      const chartData = {
        labels: sortedReasons.map(([reason, count]) => {
          const pct = ((count / total) * 100).toFixed(1);
          return `${reason} (${pct}%)`;
        }),
        datasets: [{
          data: sortedReasons.map(([, count]) => count),
          backgroundColor: colors.slice(0, sortedReasons.length),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverBorderWidth: 3
        }]
      };

      return {
        value: total.toString(),
        label: 'Lost Leads',
        chartData,
        isPieChart: true,
        sourceDetails
      };
    } catch (error) {
      console.error('Error fetching CRM lost reasons data:', error);
      return {
        value: '0',
        label: 'Lost Leads',
        chartData: null,
        isPieChart: true,
        sourceDetails: []
      };
    }
  };

  // Function to fetch CRM pipeline stages data (ignores date range)
  const fetchCRMPipelineStagesData = async (businessId: string, pipelineId?: number) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      console.log('🔍 Fetching CRM Pipeline Stages Data for business:', businessId, 'pipeline:', pipelineId);

      // Get all pipelines for this business (stages are stored as JSON in the stages column)
      const { data: pipelines, error: pipelinesError } = await supabase
        .from('pipelines')
        .select('id, pipeline_name, stages')
        .eq('business_id', businessId)
        .order('id', { ascending: true });

      console.log('📊 Fetched pipelines:', pipelines);

      if (pipelinesError) {
        console.error('Error fetching pipelines:', pipelinesError);
        throw pipelinesError;
      }

      if (!pipelines || pipelines.length === 0) {
        console.log('⚠️ No pipelines found for business');
        return { pipelines: [], selectedPipelineId: null, stages: [] };
      }

      // Parse stages from JSON and transform pipelines
      const pipelinesWithStages = pipelines.map(pipeline => ({
        ...pipeline,
        stages: Array.isArray(pipeline.stages) ? pipeline.stages : []
      }));

      // Use provided pipeline or default to first one
      const targetPipelineId = pipelineId || pipelinesWithStages[0].id;
      const selectedPipeline = pipelinesWithStages.find(p => p.id === targetPipelineId) || pipelinesWithStages[0];

      console.log('✅ Selected pipeline:', selectedPipeline);
      console.log('📋 Stages in pipeline:', selectedPipeline.stages);

      // Get opportunity counts for each stage in the selected pipeline (only Open status)
      const { data: opportunities, error: oppsError } = await supabase
        .from('pipeline_opportunities')
        .select('id, stage_id')
        .eq('business_id', businessId)
        .eq('pipeline_id', selectedPipeline.id)
        .eq('status', 'Open');

      console.log('💼 Opportunities in pipeline (Open only):', opportunities);

      if (oppsError) {
        console.error('Error fetching opportunities:', oppsError);
        throw oppsError;
      }

      // Count opportunities per stage
      const stages = (selectedPipeline.stages || []).map((stage: any) => ({
        id: stage.id,
        name: stage.name,
        position: stage.position,
        count: opportunities?.filter(opp => opp.stage_id === stage.id).length || 0
      })).sort((a: any, b: any) => (a.position || 0) - (b.position || 0));

      console.log('📈 Final stages with counts:', stages);

      const result = {
        pipelines: pipelinesWithStages.map((p: any) => ({ id: p.id, name: p.pipeline_name })),
        selectedPipelineId: selectedPipeline.id,
        stages
      };

      console.log('✨ Returning data:', result);

      return result;
    } catch (error) {
      console.error('Error in fetchCRMPipelineStagesData:', error);
      return { pipelines: [], selectedPipelineId: null, stages: [] };
    }
  };

  // Function to fetch project pipeline stages data
  const fetchProjectPipelineStagesData = async (businessId: string) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      console.log('🔍 Fetching Project Pipeline Stages Data for business:', businessId);

      // Get all projects for this business with their status
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, status, project_status')
        .eq('business_id', businessId)
        .eq('project_status', 'open'); // Only count open projects

      console.log('📊 Fetched projects:', projects);

      if (projectsError) {
        console.error('Error fetching projects:', projectsError);
        throw projectsError;
      }

      if (!projects || projects.length === 0) {
        console.log('⚠️ No projects found for business');
        return { stages: [] };
      }

      // Define the order of project statuses (workflow order)
      const statusOrder = [
        'draft',
        'Field Quote Scheduled',
        'Field Quote Completed',
        'Quote - Pending Approval',
        'Quote - Approved',
        'Measurements scheduled',
        'Measurements Complete',
        'Pending',
        'Scheduled',
        'in progress',
        'Completed',
        'Converted to booking'
      ];

      // Count projects per status
      const statusCounts = new Map<string, number>();
      (projects || []).forEach(project => {
        const status = project.status || 'draft';
        statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
      });

      // Create stages array with counts, ordered by workflow
      const stages = statusOrder
        .filter(status => statusCounts.has(status))
        .map((status, index) => ({
          id: index + 1,
          name: status,
          position: index + 1,
          count: statusCounts.get(status) || 0
        }));

      // Add any statuses that aren't in the predefined order (shouldn't happen, but just in case)
      statusCounts.forEach((count, status) => {
        if (!statusOrder.includes(status)) {
          stages.push({
            id: stages.length + 1,
            name: status,
            position: stages.length + 1,
            count
          });
        }
      });

      console.log('📈 Final stages with counts:', stages);

      const result = {
        stages
      };

      console.log('✨ Returning data:', result);

      return result;
    } catch (error) {
      console.error('Error in fetchProjectPipelineStagesData:', error);
      return { stages: [] };
    }
  };

  // Function to fetch real upsells data from the database
  const fetchUpsellsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query non-deleted bookings within the date range, excluding cancelled bookings, only those with upsells data
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('upsells, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled')
        .not('upsells', 'is', null);

      if (error) {
        console.error('Error fetching upsells data:', error);
        throw error;
      }

      // Calculate total upsells (same logic as Sales.tsx)
      const totalUpsells = (bookings || []).reduce((sum, booking) => {
        return sum + (booking.upsells || 0);
      }, 0);

      return {
        value: formatCurrency(totalUpsells, false),
        label: `Total Upsells`
      };
    } catch (error) {
      console.error('Error fetching upsells data:', error);
      // Return fallback data on error
      return {
        value: '+$0',
        label: `Total Upsells`
      };
    }
  };

  // Function to fetch real website visits data from analytics_events table
  const fetchWebsiteVisitsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Convert to UTC midnight for consistent date boundary handling
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
      
      // Use Postgres function to count unique sessions (bypasses row limits)
      const { data, error } = await supabase.rpc('count_website_visits', {
        p_business_id: businessId,
        p_start_date: startDateUTC.toISOString(),
        p_end_date: endDateUTC.toISOString()
      });

      if (error) {
        console.error('❌ Error counting website visits:', error);
        throw error;
      }

      const totalVisits = data || 0;

      return {
        value: totalVisits.toLocaleString(),
        label: `Website Visits`
      };
    } catch (error) {
      console.error('❌ Error in fetchWebsiteVisitsData:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Website Visits`
      };
    }
  };

  // Function to fetch website visits graph data (day-by-day or month-by-month unique sessions)
  const fetchWebsiteVisitsGraphData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date, conversionConfig?: { enabled: boolean; criteria: Array<{ type: 'route' | 'click' | 'url_contains'; value: string }> }): Promise<{ value: string; label: string; chartData?: any; uniqueValue?: string; uniqueChartData?: any; conversionRate?: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      const startDateUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
      const endDateUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59));
      
      const previousPeriod = getPreviousPeriod(dateRange);
      const { startDate: prevStartDate, endDate: prevEndDate } = getDateRange(previousPeriod);
      const prevStartDateUTC = new Date(Date.UTC(prevStartDate.getFullYear(), prevStartDate.getMonth(), prevStartDate.getDate(), 0, 0, 0));
      const prevEndDateUTC = new Date(Date.UTC(prevEndDate.getFullYear(), prevEndDate.getMonth(), prevEndDate.getDate(), 23, 59, 59, 999));
      
      let chartLabels: string[];
      let currentVisitsData: number[], previousVisitsData: number[];
      let currentUniqueData: number[], previousUniqueData: number[];
      let datasetLabel: string, allDates: string[], previousPeriodDates: string[];
      
      if (dateRange === 'this_year') {
        const [{ data: currentData, error: currentError }, { data: previousData, error: previousError }, { data: currentUniqueRaw, error: currentUniqueError }, { data: previousUniqueRaw, error: previousUniqueError }] = await Promise.all([
          supabase.rpc('get_website_visits_by_month', { p_business_id: businessId, p_start_date: startDateUTC.toISOString(), p_end_date: endDateUTC.toISOString() }),
          supabase.rpc('get_website_visits_by_month', { p_business_id: businessId, p_start_date: prevStartDateUTC.toISOString(), p_end_date: prevEndDateUTC.toISOString() }),
          supabase.rpc('get_unique_visitors_by_month', { p_business_id: businessId, p_start_date: startDateUTC.toISOString(), p_end_date: endDateUTC.toISOString() }),
          supabase.rpc('get_unique_visitors_by_month', { p_business_id: businessId, p_start_date: prevStartDateUTC.toISOString(), p_end_date: prevEndDateUTC.toISOString() }),
        ]);

        if (currentError) throw currentError;
        if (previousError) throw previousError;

        const currentYear = new Date().getFullYear();
        const allMonths = Array.from({ length: 12 }, (_, i) => {
          const month = String(i + 1).padStart(2, '0');
          return `${currentYear}-${month}`;
        });

        const currentVisitsByMonth: Record<string, number> = {};
        (currentData || []).forEach((row: any) => { currentVisitsByMonth[row.month] = Number(row.visit_count); });
        const previousVisitsByMonth: Record<string, number> = {};
        (previousData || []).forEach((row: any) => { previousVisitsByMonth[row.month] = Number(row.visit_count); });
        const currentUniqueByMonth: Record<string, number> = {};
        (currentUniqueRaw || []).forEach((row: any) => { currentUniqueByMonth[row.month] = Number(row.visit_count); });
        const previousUniqueByMonth: Record<string, number> = {};
        (previousUniqueRaw || []).forEach((row: any) => { previousUniqueByMonth[row.month] = Number(row.visit_count); });

        allMonths.forEach(month => {
          if (!currentVisitsByMonth[month]) currentVisitsByMonth[month] = 0;
        });

        currentVisitsData = allMonths.map(month => currentVisitsByMonth[month]);
        previousVisitsData = allMonths.map(month => previousVisitsByMonth[month] || 0);
        currentUniqueData = allMonths.map(month => currentUniqueByMonth[month] || 0);
        previousUniqueData = allMonths.map(month => previousUniqueByMonth[month] || 0);
        
        chartLabels = allMonths.map(month => {
          const [year, monthNum] = month.split('-');
          const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
          return date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
        });
        
        datasetLabel = 'Monthly Visits';
        allDates = allMonths;
        previousPeriodDates = allMonths;
      } else {
        const pageSize = 1000;

        const fetchAllEvents = async (start: string, end: string) => {
          let events: any[] = [];
          let page = 0;
          let hasMore = true;
          while (hasMore) {
            const { data, error } = await supabase
              .from('analytics_events')
              .select('timestamp, session_id, visitor_id, user_agent, referrer, page_url')
              .eq('business_id', businessId)
              .eq('event_type', 'page_view')
              .gte('timestamp', start)
              .lte('timestamp', end)
              .range(page * pageSize, (page + 1) * pageSize - 1)
              .order('timestamp', { ascending: true });
            if (error) throw error;
            if (data && data.length > 0) {
              events = events.concat(data);
              hasMore = data.length === pageSize;
              page++;
            } else {
              hasMore = false;
            }
          }
          // Same bot/dev-traffic exclusions as the SQL counting functions
          return events.filter(e => !isExcludedAnalyticsEvent(e));
        };

        const [currentEvents, previousEvents] = await Promise.all([
          fetchAllEvents(startDateUTC.toISOString(), endDateUTC.toISOString()),
          fetchAllEvents(prevStartDateUTC.toISOString(), prevEndDateUTC.toISOString()),
        ]);

        const getLocalDateStr = (timestamp: string): string => {
          const date = new Date(timestamp);
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        // Group by date: sessions and visitors
        const groupByDate = (events: any[]) => {
          const sessions: Record<string, Set<string>> = {};
          const visitors: Record<string, Set<string>> = {};
          events.forEach((event: any) => {
            const d = getLocalDateStr(event.timestamp);
            if (!sessions[d]) sessions[d] = new Set();
            if (!visitors[d]) visitors[d] = new Set();
            if (event.session_id) sessions[d].add(event.session_id);
            if (event.visitor_id) visitors[d].add(event.visitor_id);
          });
          return { sessions, visitors };
        };

        const currentGrouped = groupByDate(currentEvents);
        const previousGrouped = groupByDate(previousEvents);

        const toCountMap = (setMap: Record<string, Set<string>>) => {
          const out: Record<string, number> = {};
          Object.keys(setMap).forEach(d => { out[d] = setMap[d].size; });
          return out;
        };

        const currentVisitsByDate = toCountMap(currentGrouped.sessions);
        const previousVisitsByDate = toCountMap(previousGrouped.sessions);
        const currentUniqueByDate = toCountMap(currentGrouped.visitors);
        const previousUniqueByDate = toCountMap(previousGrouped.visitors);

        allDates = [];
        const startDateLocal = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const endDateLocal = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
        const currentDate = new Date(startDateLocal);
        while (currentDate <= endDateLocal) {
          allDates.push(`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`);
          currentDate.setDate(currentDate.getDate() + 1);
        }

        chartLabels = allDates.map(date => {
          const [year, month, day] = date.split('-').map(Number);
          return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        currentVisitsData = allDates.map(date => currentVisitsByDate[date] || 0);
        currentUniqueData = allDates.map(date => currentUniqueByDate[date] || 0);
        
        previousPeriodDates = [];
        const mapToPreviousPeriod = (allDts: string[], prevByDate: Record<string, number>) => {
          return allDts.map((date) => {
            const [year, month, day] = date.split('-').map(Number);
            const cur = new Date(year, month - 1, day);
            const pm = cur.getMonth() === 0 ? 11 : cur.getMonth() - 1;
            const py = cur.getMonth() === 0 ? cur.getFullYear() - 1 : cur.getFullYear();
            const daysInPrev = new Date(py, pm + 1, 0).getDate();
            const pd = Math.min(day, daysInPrev);
            const prevKey = `${py}-${String(pm + 1).padStart(2, '0')}-${String(pd).padStart(2, '0')}`;
            previousPeriodDates.push(prevKey);
            return prevByDate[prevKey] || 0;
          });
        };

        previousVisitsData = mapToPreviousPeriod(allDates, previousVisitsByDate);
        previousPeriodDates = []; // Reset for unique
        previousUniqueData = mapToPreviousPeriod(allDates, previousUniqueByDate);
        
        datasetLabel = 'Daily Visits';
      }

      // Get totals
      const [{ data: totalVisits }, { data: totalUnique }] = await Promise.all([
        supabase.rpc('count_website_visits', { p_business_id: businessId, p_start_date: startDateUTC.toISOString(), p_end_date: endDateUTC.toISOString() }),
        supabase.rpc('count_unique_visitors', { p_business_id: businessId, p_start_date: startDateUTC.toISOString(), p_end_date: endDateUTC.toISOString() }),
      ]);

      // Build visits chart datasets
      const visitsDatasets: any[] = [
        {
          label: `Current Period (${datasetLabel})`,
          data: currentVisitsData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4, fill: true, pointRadius: 4, pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(59, 130, 246)', pointBorderColor: 'white', pointBorderWidth: 2,
          actualDates: allDates
        }
      ];
      if (dateRange !== 'this_year') {
        visitsDatasets.push({
          label: `Previous Period (${datasetLabel})`,
          data: previousVisitsData,
          borderColor: 'rgba(239, 68, 68, 0.4)',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          tension: 0.4, fill: false, pointRadius: 3, pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(239, 68, 68, 0.4)', pointBorderColor: 'white', pointBorderWidth: 2,
          borderDash: [5, 5],
          actualDates: previousPeriodDates
        });
      }

      // Build unique visitors chart datasets
      const uniqueDatasets: any[] = [
        {
          label: `Current Period (Unique ${datasetLabel.replace('Visits', 'Visitors')})`,
          data: currentUniqueData,
          borderColor: 'rgb(147, 51, 234)',
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
          tension: 0.4, fill: true, pointRadius: 4, pointHoverRadius: 6,
          pointBackgroundColor: 'rgb(147, 51, 234)', pointBorderColor: 'white', pointBorderWidth: 2,
          actualDates: allDates
        }
      ];
      if (dateRange !== 'this_year') {
        uniqueDatasets.push({
          label: `Previous Period (Unique ${datasetLabel.replace('Visits', 'Visitors')})`,
          data: previousUniqueData,
          borderColor: 'rgba(239, 68, 68, 0.4)',
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          tension: 0.4, fill: false, pointRadius: 3, pointHoverRadius: 5,
          pointBackgroundColor: 'rgba(239, 68, 68, 0.4)', pointBorderColor: 'white', pointBorderWidth: 2,
          borderDash: [5, 5],
          actualDates: previousPeriodDates
        });
      }

      // Calculate conversion rate if config is provided and enabled
      let conversionRate: string | undefined;
      if (conversionConfig?.enabled && conversionConfig.criteria.length > 0) {
        const routeCriteria = conversionConfig.criteria.filter(c => c.type === 'route' && c.value).map(c => c.value);
        const clickCriteria = conversionConfig.criteria.filter(c => c.type === 'click' && c.value).map(c => c.value);
        const urlContainsCriteria = conversionConfig.criteria.filter(c => c.type === 'url_contains' && c.value.trim()).map(c => c.value.trim());
        
        if (routeCriteria.length > 0 || clickCriteria.length > 0 || urlContainsCriteria.length > 0) {
          const { data: conversionCount } = await supabase.rpc('count_conversion_events', {
            p_business_id: businessId,
            p_start_date: startDateUTC.toISOString(),
            p_end_date: endDateUTC.toISOString(),
            p_routes: routeCriteria,
            p_clicks: clickCriteria,
            p_url_contains: urlContainsCriteria
          });
          
          const totalVisitsNum = totalVisits || 0;
          if (totalVisitsNum > 0) {
            conversionRate = (((conversionCount || 0) / totalVisitsNum) * 100).toFixed(1);
          } else {
            conversionRate = '0.0';
          }
        }
      }

      return {
        value: (totalVisits || 0).toLocaleString(),
        label: `Website Visits`,
        chartData: { labels: chartLabels, datasets: visitsDatasets },
        uniqueValue: (totalUnique || 0).toLocaleString(),
        uniqueChartData: { labels: chartLabels, datasets: uniqueDatasets },
        conversionRate,
      };
    } catch (error) {
      console.error('Error fetching website visits graph data:', error);
      return {
        value: '0',
        label: `Website Visits`,
        chartData: { labels: [], datasets: [{ label: 'Daily Visits', data: [], borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)' }] },
        uniqueValue: '0',
        uniqueChartData: { labels: [], datasets: [{ label: 'Unique Visitors', data: [], borderColor: 'rgb(147, 51, 234)', backgroundColor: 'rgba(147, 51, 234, 0.1)' }] },
      };
    }
  };

  // Function to fetch real Google reviews data from analytics_events table
  const fetchGoogleReviewsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query analytics_events for google_review_clicked events within the date range
      const { data: events, error } = await supabase
        .from('analytics_events')
        .select('id, event_type, timestamp')
        .eq('business_id', businessId)
        .eq('event_type', 'google_review_clicked')
        .gte('timestamp', startDate.toISOString())
        .lte('timestamp', endDate.toISOString());

      if (error) {
        console.error('Error fetching Google reviews data:', error);
        throw error;
      }

      // Count total Google review clicks
      const totalReviews = events?.length || 0;

      return {
        value: totalReviews.toLocaleString(),
        label: `Google Reviews`
      };
    } catch (error) {
      console.error('Error fetching Google reviews data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Google Reviews`
      };
    }
  };

  // Function to fetch real closed sales data from the database
  const fetchClosedSalesData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query only completed non-deleted bookings within the date range (same logic as ProfitLoss.tsx)
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed') // Only completed bookings for closed sales
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching closed sales data:', error);
        throw error;
      }

      // Calculate total closed sales after discounts (same logic as ProfitLoss.tsx)
      let totalClosedSales = (bookings || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // Fully-paid standalone invoices are "closed" sales too.
      const standaloneInvoices = await fetchStandaloneInvoiceSales(businessId, {
        start: startDate,
        end: endDate,
      });
      totalClosedSales += standaloneInvoices
        .filter((inv) => inv.status === 'paid')
        .reduce((sum, inv) => sum + inv.amount, 0);

      return {
        value: formatCurrency(totalClosedSales, false),
        label: `Closed Sales`
      };
    } catch (error) {
      console.error('Error fetching closed sales data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Closed Sales`
      };
    }
  };

  // Function to fetch real supplies purchased data from the database
  const fetchSuppliesPurchasedData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query stock transactions for purchases within the date range
      // Need to join with stock_items to filter by business_id since the view doesn't include it directly
      const { data: transactions, error } = await supabase
        .from('stock_transactions')
        .select(`
          quantity,
          price,
          created_at,
          type,
          stock_items!inner(business_id)
        `)
        .eq('type', 'purchase') // Only purchase transactions
        .eq('stock_items.business_id', businessId) // Filter by business
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching supplies purchased data:', error);
        throw error;
      }

      // Calculate total supplies purchased cost (same logic as SuppliesUsage.tsx)
      const totalSuppliesPurchased = (transactions || []).reduce((sum, transaction) => {
        const quantity = transaction.quantity || 0;
        const price = transaction.price || 0;
        const cost = quantity * price;
        return sum + cost;
      }, 0);

      return {
        value: formatCurrency(totalSuppliesPurchased),
        label: `Supplies Purchased`
      };
    } catch (error) {
      console.error('Error fetching supplies purchased data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Supplies Purchased`
      };
    }
  };

  // Function to fetch real expenses data from the database
  const fetchExpensesData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query expenses within the date range
      const { data: expenses, error } = await supabase
        .from('expenses')
        .select('amount')
        .eq('business_id', businessId)
        .gte('expense_date', startDate.toISOString().split('T')[0])
        .lte('expense_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching expenses data:', error);
        throw error;
      }

      // Calculate total expenses
      const totalExpenses = (expenses || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      return {
        value: formatCurrency(totalExpenses),
        label: `Total Expenses`
      };
    } catch (error) {
      console.error('Error fetching expenses data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Expenses`
      };
    }
  };

  // Function to fetch MRR (Monthly Recurring Revenue) from active subscription services
  const fetchMRRData = async (businessId: string): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { data: subscriptions, error } = await supabase
        .from('subscription_services')
        .select('amount, interval, interval_count')
        .eq('business_id', businessId)
        .eq('status', 'active');

      if (error) {
        console.error('Error fetching MRR data:', error);
        throw error;
      }

      let totalMRR = 0;
      for (const sub of subscriptions || []) {
        const amount = Number(sub.amount) || 0;
        const intervalCount = sub.interval_count || 1;

        switch (sub.interval) {
          case 'day':
            totalMRR += (amount / intervalCount) * 30;
            break;
          case 'week':
            totalMRR += (amount / intervalCount) * (52 / 12);
            break;
          case 'month':
            totalMRR += amount / intervalCount;
            break;
          case 'year':
            totalMRR += amount / (intervalCount * 12);
            break;
          default:
            totalMRR += amount;
        }
      }

      const activeCount = (subscriptions || []).length;

      return {
        value: formatCurrency(totalMRR),
        label: `MRR (${activeCount} active)`
      };
    } catch (error) {
      console.error('Error fetching MRR data:', error);
      return {
        value: '$0',
        label: 'MRR (0 active)'
      };
    }
  };

  // Function to fetch ARR (Annual Recurring Revenue) from active subscription services
  const fetchARRData = async (businessId: string): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { data: subscriptions, error } = await supabase
        .from('subscription_services')
        .select('amount, interval, interval_count')
        .eq('business_id', businessId)
        .eq('status', 'active');

      if (error) {
        console.error('Error fetching ARR data:', error);
        throw error;
      }

      let totalARR = 0;
      for (const sub of subscriptions || []) {
        const amount = Number(sub.amount) || 0;
        const intervalCount = sub.interval_count || 1;

        switch (sub.interval) {
          case 'day':
            totalARR += (amount / intervalCount) * 365;
            break;
          case 'week':
            totalARR += (amount / intervalCount) * 52;
            break;
          case 'month':
            totalARR += (amount / intervalCount) * 12;
            break;
          case 'year':
            totalARR += amount / intervalCount;
            break;
          default:
            totalARR += amount * 12;
        }
      }

      const activeCount = (subscriptions || []).length;

      return {
        value: formatCurrency(totalARR),
        label: `ARR (${activeCount} active)`
      };
    } catch (error) {
      console.error('Error fetching ARR data:', error);
      return {
        value: '$0',
        label: 'ARR (0 active)'
      };
    }
  };

  // Helper function for precise date filtering (matches ProfitLoss.tsx)
  const isDateInRange = (date: string | null | undefined, rangeOption: DateRangeOption): boolean => {
    if (!date) return false;
    
    // Convert the booking end date to UTC midnight
    const targetDate = new Date(date + 'T00:00:00Z');
    const now = new Date();
    const utcNow = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));

    switch (rangeOption) {
      case 'this_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'last_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() - 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth(), 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'next_month': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, 1));
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 2, 0, 23, 59, 59, 999));
        return targetDate >= start && targetDate <= end;
      }
      case 'this_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'last_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day - 7;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'next_week': {
        const startOfWeek = (date: Date) => {
          const d = new Date(date);
          const day = d.getUTCDay();
          const diff = d.getUTCDate() - day + 7;
          return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));
        };
        const start = startOfWeek(utcNow);
        const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000 + 23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999);
        return targetDate >= start && targetDate <= end;
      }
      case 'this_year': {
        const start = new Date(Date.UTC(utcNow.getUTCFullYear(), 0, 1)); // January 1st
        const end = new Date(Date.UTC(utcNow.getUTCFullYear(), 11, 31, 23, 59, 59, 999)); // December 31st
        return targetDate >= start && targetDate <= end;
      }
      default:
        return false;
    }
  };

  // Function to fetch real job materials data from the database (matches ProfitLoss.tsx calculation)
  const fetchJobMaterialsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Get job materials costs from booking_materials table (same as ProfitLoss.tsx)
      const { data: materialsData, error: materialsError } = await supabase
        .from('booking_materials')
        .select(`
          id,
          quantity,
          cost,
          total_cost,
          created_at,
          material:material_id (
            id,
            name,
            description,
            unit,
            category:category_id (
              id,
              name
            )
          ),
          booking:booking_id (
            id,
            status,
            end_date,
            business_id
          )
        `)
        .eq('booking.business_id', businessId)
        .eq('booking.status', 'completed')
        .gte('booking.end_date', startDate.toISOString().split('T')[0])
        .lte('booking.end_date', endDate.toISOString().split('T')[0]);

      if (materialsError) {
        console.error('Error fetching materials for job materials:', materialsError);
        throw materialsError;
      }

      // Apply additional date filtering (same as ProfitLoss.tsx)
      const filteredMaterials = (materialsData || []).filter(material => {
        return material.booking?.status === 'completed' && 
               material.booking?.end_date && 
               isDateInRange(material.booking.end_date, dateRange);
      });

      // Calculate total job materials costs (sum of all total_cost from booking_materials)
      const totalJobMaterials = filteredMaterials.reduce((sum, material) => {
        return sum + (material.total_cost || 0);
      }, 0);

      return {
        value: formatCurrency(totalJobMaterials),
        label: `Total Job Materials`
      };
    } catch (error) {
      console.error('Error fetching job materials data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Job Materials`
      };
    }
  };

  // Function to fetch real labour data from the database
  const fetchLabourData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Get total labor costs (exact same logic as ProfitLoss.tsx)
      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (flatRateError) {
        console.error('Error fetching flat rate data for labour:', flatRateError);
        throw flatRateError;
      }

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      if (timeclockError) {
        console.error('Error fetching timeclock data for labour:', timeclockError);
        throw timeclockError;
      }

      // Fetch commission entries
      const { data: commissionData, error: commissionError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'commission')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (commissionError) {
        console.error('Error fetching commission data for labour:', commissionError);
        throw commissionError;
      }

      // Fetch bonus entries
      const { data: bonusData, error: bonusError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          bonus_earnings,
          total_earnings,
          created_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'bonus')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bonusError) {
        console.error('Error fetching bonus data for labour:', bonusError);
        throw bonusError;
      }

      // Debug logging for labor report bonus data
      console.log('🔍 KPI Labor Report: Fetched bonus data:', bonusData);
      console.log('🔍 KPI Labor Report: Bonus entries count:', bonusData?.length || 0);
      if (bonusData && bonusData.length > 0) {
        const totalBonuses = bonusData.reduce((sum, entry) => sum + (entry.bonus_earnings || entry.total_earnings || 0), 0);
        console.log('💰 KPI Labor Report: Total bonus costs for period:', totalBonuses);
      }

      // Combine all four types of payroll data
      const combinedLaborData = [
        ...(flatRateData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(timeclockData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(commissionData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(bonusData?.map(entry => ({
          total_earnings: entry.bonus_earnings || entry.total_earnings
        })) || [])
      ];

      const totalLaborCosts = combinedLaborData.reduce((sum, entry) => {
        return sum + (entry.total_earnings || 0);
      }, 0);

      return {
        value: formatCurrency(totalLaborCosts),
        label: `Total Labour Costs`
      };
    } catch (error) {
      console.error('Error fetching labour data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Total Labour Costs`
      };
    }
  };

  // Function to fetch total jobs data from the database
  const fetchTotalJobsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Get all non-deleted bookings within the date range regardless of status
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('id, end_date, status')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching total jobs data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      const totalJobs = filteredBookings.length;

      return {
        value: totalJobs.toString(),
        label: `Total Jobs Booked`
      };
    } catch (error) {
      console.error('Error fetching total jobs data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Total Jobs Booked`
      };
    }
  };

  // Function to fetch completed jobs data from the database
  const fetchCompletedJobsData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Get only completed non-deleted bookings within the date range
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('id, end_date, status')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (error) {
        console.error('Error fetching completed jobs data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredCompletedBookings = (bookings || []).filter(booking => {
        return booking.status === 'completed' && 
               booking.end_date && 
               isDateInRange(booking.end_date, dateRange);
      });

      const completedJobs = filteredCompletedBookings.length;

      return {
        value: completedJobs.toString(),
        label: `Completed Jobs`
      };
    } catch (error) {
      console.error('Error fetching completed jobs data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Completed Jobs`
      };
    }
  };

  // Function to fetch real net profit data from the database (matches ProfitLoss.tsx calculation)
  const fetchNetProfitData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // 1. Get total revenue after discounts from completed bookings (same as ProfitLoss.tsx)
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (bookingsError) {
        console.error('Error fetching bookings for net profit:', bookingsError);
        throw bookingsError;
      }

      // Calculate total revenue after discounts (+ standalone invoices issued in range)
      const netProfitStandalone = await fetchStandaloneInvoiceSales(businessId, {
        start: startDate,
        end: endDate,
      });
      const totalRevenue =
        (bookings || []).reduce((sum, booking) => {
          const subtotal = booking.subtotal || 0;
          const discountAmount = booking.discount_amount || 0;
          return sum + (subtotal - discountAmount);
        }, 0) + sumStandaloneInvoiceSales(netProfitStandalone);

      // 2. Get total labor costs (exact same logic as ProfitLoss.tsx)
      // Fetch flat rate entries
      const { data: flatRateData, error: flatRateError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (flatRateError) {
        console.error('Error fetching flat rate data for net profit:', flatRateError);
        throw flatRateError;
      }

      // Fetch timeclock entries
      const { data: timeclockData, error: timeclockError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          clock_out,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      if (timeclockError) {
        console.error('Error fetching timeclock data for net profit:', timeclockError);
        throw timeclockError;
      }

      // Fetch commission entries
      const { data: commissionData, error: commissionError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          total_earnings,
          completed_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'commission')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      if (commissionError) {
        console.error('Error fetching commission data for net profit:', commissionError);
        throw commissionError;
      }

      // Fetch bonus entries
      const { data: bonusData, error: bonusError } = await supabase
        .from('payroll_page')
        .select(`
          id,
          resource_id,
          bonus_earnings,
          total_earnings,
          created_at,
          resource:resources (
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('entry_type', 'bonus')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (bonusError) {
        console.error('Error fetching bonus data for net profit:', bonusError);
        throw bonusError;
      }

      // Debug logging for bonus data
      console.log('🔍 KPI Dashboard: Fetched bonus data:', bonusData);
      console.log('🔍 KPI Dashboard: Bonus entries count:', bonusData?.length || 0);
      if (bonusData && bonusData.length > 0) {
        const totalBonuses = bonusData.reduce((sum, entry) => sum + (entry.bonus_earnings || entry.total_earnings || 0), 0);
        console.log('💰 KPI Dashboard: Total bonus costs for period:', totalBonuses);
      }

      // Combine all four types of payroll data
      const combinedLaborData = [
        ...(flatRateData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(timeclockData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(commissionData?.map(entry => ({
          total_earnings: entry.total_earnings
        })) || []),
        ...(bonusData?.map(entry => ({
          total_earnings: entry.bonus_earnings || entry.total_earnings
        })) || [])
      ];

      const totalLaborCosts = combinedLaborData.reduce((sum, entry) => {
        return sum + (entry.total_earnings || 0);
      }, 0);

      // 3. Get total supply costs from stock purchases (same logic as ProfitLoss.tsx)
      const { data: stockTransactions, error: stockError } = await supabase
        .from('stock_transactions')
        .select(`
          quantity,
          price,
          created_at,
          type,
          stock_items!inner(business_id)
        `)
        .eq('type', 'purchase')
        .eq('stock_items.business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (stockError) {
        console.error('Error fetching stock data for net profit:', stockError);
        throw stockError;
      }

      const totalSupplyCosts = (stockTransactions || []).reduce((sum, transaction) => {
        const quantity = Math.abs(Number(transaction.quantity) || 0);
        const price = Number(transaction.price) || 0;
        return sum + (quantity * price);
      }, 0);

      // 4. Get fixed expenses from operating costs (same logic as ProfitLoss.tsx)
      const { data: operatingCosts, error: operatingCostsError } = await supabase
        .from('operating_costs')
        .select('amount')
        .eq('business_id', businessId);

      if (operatingCostsError) {
        console.error('Error fetching operating costs for net profit:', operatingCostsError);
        throw operatingCostsError;
      }

      const fixedExpenses = (operatingCosts || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      // 5. Get expenses from expenses table (within date range)
      const { data: expensesData, error: expensesError } = await supabase
        .from('expenses')
        .select('amount')
        .eq('business_id', businessId)
        .gte('expense_date', startDate.toISOString().split('T')[0])
        .lte('expense_date', endDate.toISOString().split('T')[0]);

      if (expensesError) {
        console.error('Error fetching expenses for net profit:', expensesError);
        // Don't throw - just log and continue with 0 expenses
      }

      const totalExpenses = (expensesData || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      // 6. Calculate Net Profit (includes expenses from expenses table)
      const netProfit = totalRevenue - (totalLaborCosts + totalSupplyCosts + fixedExpenses + totalExpenses);

      return {
        value: formatCurrency(netProfit, true),
        label: `Bottom Line Profit`
      };
    } catch (error) {
      console.error('Error fetching net profit data:', error);
      // Return fallback data on error
      return {
        value: '+$0',
        label: `Bottom Line Profit`
      };
    }
  };

  // Function to fetch net profit breakdown data for bar graphs (sales and cost breakdown)
  const fetchNetProfitBreakdownData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ sales: number; labor: number; supplies: number; fixedExpenses: number; expenses: number; totalCosts: number }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // 1. Get total revenue after discounts from completed bookings
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('status', 'completed')
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0]);

      if (bookingsError) {
        console.error('Error fetching bookings for net profit breakdown:', bookingsError);
        throw bookingsError;
      }

      const breakdownStandalone = await fetchStandaloneInvoiceSales(businessId, {
        start: startDate,
        end: endDate,
      });
      const totalRevenue =
        (bookings || []).reduce((sum, booking) => {
          const subtotal = booking.subtotal || 0;
          const discountAmount = booking.discount_amount || 0;
          return sum + (subtotal - discountAmount);
        }, 0) + sumStandaloneInvoiceSales(breakdownStandalone);

      // 2. Get total labor costs
      const { data: flatRateData } = await supabase
        .from('payroll_page')
        .select('total_earnings')
        .eq('business_id', businessId)
        .eq('entry_type', 'flat_rate')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      const { data: timeclockData } = await supabase
        .from('payroll_page')
        .select('total_earnings')
        .eq('business_id', businessId)
        .eq('entry_type', 'timeclock')
        .gte('clock_out', startDate.toISOString())
        .lte('clock_out', endDate.toISOString());

      const { data: commissionData } = await supabase
        .from('payroll_page')
        .select('total_earnings')
        .eq('business_id', businessId)
        .eq('entry_type', 'commission')
        .gte('completed_at', startDate.toISOString())
        .lte('completed_at', endDate.toISOString());

      const { data: bonusData } = await supabase
        .from('payroll_page')
        .select('bonus_earnings, total_earnings')
        .eq('business_id', businessId)
        .eq('entry_type', 'bonus')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const totalLaborCosts = [
        ...(flatRateData?.map(e => e.total_earnings) || []),
        ...(timeclockData?.map(e => e.total_earnings) || []),
        ...(commissionData?.map(e => e.total_earnings) || []),
        ...(bonusData?.map(e => e.bonus_earnings || e.total_earnings) || [])
      ].reduce((sum, earnings) => sum + (earnings || 0), 0);

      // 3. Get total supply costs
      const { data: stockTransactions } = await supabase
        .from('stock_transactions')
        .select('quantity, price, stock_items!inner(business_id)')
        .eq('type', 'purchase')
        .eq('stock_items.business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      const totalSupplyCosts = (stockTransactions || []).reduce((sum, transaction) => {
        const quantity = Math.abs(Number(transaction.quantity) || 0);
        const price = Number(transaction.price) || 0;
        return sum + (quantity * price);
      }, 0);

      // 4. Get fixed expenses
      const { data: operatingCosts } = await supabase
        .from('operating_costs')
        .select('amount')
        .eq('business_id', businessId);

      const fixedExpenses = (operatingCosts || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      // 5. Get expenses from expenses table
      const { data: expensesData } = await supabase
        .from('expenses')
        .select('amount')
        .eq('business_id', businessId)
        .gte('expense_date', startDate.toISOString().split('T')[0])
        .lte('expense_date', endDate.toISOString().split('T')[0]);

      const totalExpenses = (expensesData || []).reduce((sum, expense) => {
        return sum + (Number(expense.amount) || 0);
      }, 0);

      const totalCosts = totalLaborCosts + totalSupplyCosts + fixedExpenses + totalExpenses;

      return {
        sales: totalRevenue,
        labor: totalLaborCosts,
        supplies: totalSupplyCosts,
        fixedExpenses: fixedExpenses,
        expenses: totalExpenses,
        totalCosts: totalCosts
      };
    } catch (error) {
      console.error('Error fetching net profit breakdown data:', error);
      return {
        sales: 0,
        labor: 0,
        supplies: 0,
        fixedExpenses: 0,
        expenses: 0,
        totalCosts: 0
      };
    }
  };

  // Function to fetch stock alert data - live current stock health
  const fetchStockAlertData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string; status: 'healthy' | 'low' }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Query all stock items for the business (excluding deleted items)
      const { data: stockItems, error } = await supabase
        .from('stock_items')
        .select('id, name, quantity, reorder_point, supplier_id, max_quantity, acquisition_cost, unit')
        .eq('business_id', businessId)
        .neq('is_deleted', true);

      if (error) {
        console.error('Error fetching stock data:', error);
        throw error;
      }

      // Update state with stock items for supplier order calculations
      setStockItems(stockItems || []);

      // Check if there are any items below reorder point
      // Note: We count the number of distinct items that are low, not the total quantity needed
      const lowStockItems = (stockItems || []).filter(item => 
        item.quantity <= item.reorder_point
      );

      const totalItems = stockItems?.length || 0;
      const lowStockCount = lowStockItems.length; // Count of distinct items needing reorder

      if (lowStockCount > 0) {
        // Low stock situation - show count of items needing reorder
        return {
          value: lowStockCount.toString(),
          label: `Item${lowStockCount > 1 ? 's' : ''} Need${lowStockCount === 1 ? 's' : ''} Reorder`,
          status: 'low'
        };
      } else {
        // Healthy stock situation - show 0 (no items need reordering)
        return {
          value: '0',
          label: 'Items Need Reorder',
          status: 'healthy'
        };
      }
    } catch (error) {
      console.error('Error fetching stock alert data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: 'Stock Status Unknown',
        status: 'healthy'
      };
    }
  };

  // Function to fetch proposals sent data from the database
  const fetchProposalsSentData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Count proposals that have been sent (including viewed, approved, declined, booked)
      // These statuses all indicate the proposal was sent at some point
      const { data: proposals, error } = await supabase
        .from('proposals')
        .select('id, status, created_at')
        .eq('business_id', businessId)
        .in('status', ['sent', 'viewed', 'approved', 'declined', 'booked'])
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching proposals sent data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredProposals = (proposals || []).filter(proposal => {
        return proposal.created_at && isDateInRange(proposal.created_at.split('T')[0], dateRange);
      });

      const totalProposalsSent = filteredProposals.length;

      return {
        value: totalProposalsSent.toString(),
        label: `Proposals Sent`
      };
    } catch (error) {
      console.error('Error fetching proposals sent data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Proposals Sent`
      };
    }
  };

  // Function to fetch proposals booked data from the database
  const fetchProposalsBookedData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query proposals with status 'booked' — filter by updated_at since that
      // reflects when the proposal was actually booked, not when it was created
      const { data: proposals, error } = await supabase
        .from('proposals')
        .select('id, status, updated_at')
        .eq('business_id', businessId)
        .eq('status', 'booked')
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching proposals booked data:', error);
        throw error;
      }

      const filteredProposals = (proposals || []).filter(proposal => {
        return proposal.updated_at && isDateInRange(proposal.updated_at.split('T')[0], dateRange);
      });

      const totalProposalsBooked = filteredProposals.length;

      return {
        value: totalProposalsBooked.toString(),
        label: `Proposals Booked`
      };
    } catch (error) {
      console.error('Error fetching proposals booked data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Proposals Booked`
      };
    }
  };

  // Function to fetch proposals approved data from the database
  const fetchProposalsApprovedData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query proposals with status 'approved' — filter by updated_at since that
      // reflects when the proposal was actually approved, not when it was created
      const { data: proposals, error } = await supabase
        .from('proposals')
        .select('id, status, updated_at')
        .eq('business_id', businessId)
        .eq('status', 'approved')
        .gte('updated_at', startDate.toISOString())
        .lte('updated_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching proposals approved data:', error);
        throw error;
      }

      const filteredProposals = (proposals || []).filter(proposal => {
        return proposal.updated_at && isDateInRange(proposal.updated_at.split('T')[0], dateRange);
      });

      const totalProposalsApproved = filteredProposals.length;

      return {
        value: totalProposalsApproved.toString(),
        label: `Proposals Approved`
      };
    } catch (error) {
      console.error('Error fetching proposals approved data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: `Proposals Approved`
      };
    }
  };

  // Function to fetch team rating data from the database
  const fetchTeamRatingData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Use the same function as TeamReports page to get team rating
      const { getDynamicBusinessFeedbackReport } = await import('../lib/feedback');
      
      const dateRangeFilter = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      const feedbackReport = await getDynamicBusinessFeedbackReport(businessId, dateRangeFilter);

      if (!feedbackReport || feedbackReport.total_feedback_count === 0) {
        return {
          value: '0.0',
          label: `Team Rating`
        };
      }

      // Format the rating to 1 decimal place
      const rating = feedbackReport.overall_business_rating.toFixed(1);

      return {
        value: rating,
        label: `Team Rating (${feedbackReport.total_feedback_count} review${feedbackReport.total_feedback_count !== 1 ? 's' : ''})`
      };
    } catch (error) {
      console.error('Error fetching team rating data:', error);
      // Return fallback data on error
      return {
        value: '0.0',
        label: `Team Rating`
      };
    }
  };

  // Function to fetch feedback percentage data from the database
  const fetchFeedbackPercentageData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Use the same function as TeamReports page to get feedback response rate
      const { getFeedbackResponseRate } = await import('../lib/feedback');
      
      const dateRangeFilter = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      const responseRateData = await getFeedbackResponseRate(businessId, dateRangeFilter);

      if (!responseRateData || responseRateData.completedBookings === 0) {
        return {
          value: '0%',
          label: `Feedback Response Rate`
        };
      }

      return {
        value: `${responseRateData.responseRate}%`,
        label: `Feedback Response Rate (${responseRateData.feedbackSubmissions}/${responseRateData.completedBookings})`
      };
    } catch (error) {
      console.error('Error fetching feedback percentage data:', error);
      // Return fallback data on error
      return {
        value: '0%',
        label: `Feedback Response Rate`
      };
    }
  };

  // Function to fetch sales graph data from the database
  const fetchSalesGraphData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string; chartData?: any }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Fetch non-deleted bookings for the date range (excluding cancelled)
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching sales graph data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      // Standalone (booking-less) invoices — treated like a booking that
      // "ended" the day it was issued so they land on the graph.
      const graphStandalone = await fetchStandaloneInvoiceSales(businessId, {
        start: startDate,
        end: endDate,
      });
      const standaloneAsPoints = graphStandalone.map((inv) => ({
        day: new Date(inv.finalizedAt).toLocaleDateString('en-CA'), // yyyy-MM-dd local
        amount: inv.amount,
      }));

      // Group sales by date or month based on date range
      let salesData, chartLabels, datasetLabel;
      
      if (dateRange === 'this_year') {
        // For yearly view: group by month and ensure all 12 months are present
        const salesByMonth = filteredBookings.reduce((acc: Record<string, number>, booking) => {
          if (!booking.end_date) return acc;
          
          // Convert to UTC date and get year-month key (YYYY-MM)
          const date = new Date(booking.end_date + 'T00:00:00Z');
          const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
          
          if (!acc[monthKey]) {
            acc[monthKey] = 0;
          }
          acc[monthKey] += (booking.subtotal || 0) - (booking.discount_amount || 0);
          return acc;
        }, {});

        for (const point of standaloneAsPoints) {
          const monthKey = point.day.slice(0, 7);
          salesByMonth[monthKey] = (salesByMonth[monthKey] || 0) + point.amount;
        }

        // Generate all 12 months for current year
        const currentYear = new Date().getFullYear();
        const allMonths = Array.from({ length: 12 }, (_, i) => {
          const month = String(i + 1).padStart(2, '0');
          return `${currentYear}-${month}`;
        });

        // Ensure all months have data (0 if no sales)
        allMonths.forEach(month => {
          if (!salesByMonth[month]) {
            salesByMonth[month] = 0;
          }
        });

        salesData = allMonths.map(month => salesByMonth[month]);
        chartLabels = allMonths.map(month => {
          const [year, monthNum] = month.split('-');
          const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
          return date.toLocaleDateString('en-US', { 
            month: 'short',
            timeZone: 'UTC'
          });
        });
        datasetLabel = 'Monthly Sales';
      } else {
        // For monthly/weekly views: group by day (existing behavior)
        const salesByDate = filteredBookings.reduce((acc: Record<string, number>, booking) => {
          if (!booking.end_date) return acc;
          
          // Convert to UTC date string (YYYY-MM-DD)
          const date = new Date(booking.end_date + 'T00:00:00Z');
          const dateKey = date.toISOString().split('T')[0];
          
          if (!acc[dateKey]) {
            acc[dateKey] = 0;
          }
          acc[dateKey] += (booking.subtotal || 0) - (booking.discount_amount || 0);
          return acc;
        }, {});

        for (const point of standaloneAsPoints) {
          salesByDate[point.day] = (salesByDate[point.day] || 0) + point.amount;
        }

        // Sort dates and prepare chart data
        const dates = Object.keys(salesByDate).sort();
        salesData = dates.map(date => salesByDate[date]);
        chartLabels = dates.map(date => {
          const d = new Date(date + 'T00:00:00Z');
          return d.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            timeZone: 'UTC'
          });
        });
        datasetLabel = 'Daily Sales';
      }

      const chartData = {
        labels: chartLabels,
        datasets: [
          {
            label: datasetLabel,
            data: salesData,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(59, 130, 246)',
            pointBorderColor: 'white',
            pointBorderWidth: 2
          }
        ]
      };

      // Calculate total sales for display
      const totalSales = salesData.reduce((sum: number, sale: number) => sum + sale, 0);

      return {
        value: formatCurrency(totalSales),
        label: `Sales Trend`,
        chartData
      };
    } catch (error) {
      console.error('Error fetching sales graph data:', error);
      // Return fallback data on error
      return {
        value: '$0.00',
        label: `Sales Trend`,
        chartData: {
          labels: [],
          datasets: [{
            label: 'Daily Sales',
            data: [],
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
          }]
        }
      };
    }
  };

  // Function to fetch average sale data from the database
  const fetchAverageSaleData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query non-deleted bookings within the date range, excluding cancelled bookings
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('subtotal, discount_amount, status, end_date')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching average sale data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      // Standalone invoices count as individual sales too.
      const avgStandalone = await fetchStandaloneInvoiceSales(businessId, {
        start: startDate,
        end: endDate,
      });

      const saleCount = filteredBookings.length + avgStandalone.length;
      if (saleCount === 0) {
        return {
          value: '$0.00',
          label: `Average Sale`
        };
      }

      // Calculate total sales after discounts
      const totalSales = filteredBookings.reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0) + sumStandaloneInvoiceSales(avgStandalone);

      // Calculate average sale
      const averageSale = totalSales / saleCount;

      return {
        value: formatCurrency(averageSale),
        label: `Average Sale`
      };
    } catch (error) {
      console.error('Error fetching average sale data:', error);
      // Return fallback data on error
      return {
        value: '$0',
        label: `Average Sale`
      };
    }
  };

  // Function to fetch booking source data for pie chart
  const fetchBookingSourceData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string; chartData?: any; isPieChart?: boolean; sourceDetails?: any[] }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query bookings within the date range, excluding cancelled bookings - now including subtotal and discount_amount for sales calculation
      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('source, end_date, status, subtotal, discount_amount')
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .gte('end_date', startDate.toISOString().split('T')[0])
        .lte('end_date', endDate.toISOString().split('T')[0])
        .neq('status', 'cancelled');

      if (error) {
        console.error('Error fetching booking source data:', error);
        throw error;
      }

      // Apply additional date filtering (same as other reports)
      const filteredBookings = (bookings || []).filter(booking => {
        return booking.end_date && isDateInRange(booking.end_date, dateRange);
      });

      if (filteredBookings.length === 0) {
        return {
          value: '0',
          label: 'Total Bookings',
          chartData: null,
          isPieChart: true,
          sourceDetails: []
        };
      }

      // Count bookings and calculate sales by source
      const sourceCounts: Record<string, number> = {};
      const sourceSales: Record<string, number> = {};
      filteredBookings.forEach(booking => {
        const source = booking.source || 'Undefined';
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
        // Calculate sales using the same formula as sales report: subtotal - discount_amount
        const sales = (booking.subtotal || 0) - (booking.discount_amount || 0);
        sourceSales[source] = (sourceSales[source] || 0) + sales;
      });

      // Sort sources by count (descending - highest to lowest)
      const sortedSources = Object.entries(sourceCounts)
        .sort(([, a], [, b]) => b - a);

      // Prepare pie chart data (now sorted from highest to lowest %)
      const labels = sortedSources.map(([source]) => source);
      const data = sortedSources.map(([, count]) => count);
      const total = filteredBookings.length;

      // Prepare source details for legend display
      const sourceDetails = sortedSources.map(([source, count]) => ({
        source,
        count,
        sales: sourceSales[source] || 0,
        percentage: ((count / total) * 100).toFixed(1)
      }));

      // Generate colors for each slice
      const colors = [
        '#3B82F6', // Blue
        '#10B981', // Green  
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#06B6D4', // Cyan
        '#F97316', // Orange
        '#84CC16', // Lime
        '#EC4899', // Pink
        '#6B7280'  // Gray
      ];

      const chartData = {
        labels: labels.map(label => {
          const count = sourceCounts[label];
          const percentage = ((count / total) * 100).toFixed(1);
          return `${label} (${percentage}%)`;
        }),
        datasets: [{
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverBorderWidth: 3
        }]
      };

      return {
        value: total.toString(),
        label: `Total Bookings`,
        chartData: chartData,
        isPieChart: true,
        sourceDetails: sourceDetails
      };
    } catch (error) {
      console.error('Error fetching booking source data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: 'Total Bookings',
        chartData: null,
        isPieChart: true,
        sourceDetails: []
      };
    }
  };

  // Function to fetch project source data for pie chart
  const fetchProjectSourceData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string; chartData?: any; isPieChart?: boolean; sourceDetails?: any[] }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Query projects within the date range, excluding cancelled projects
      const { data: projects, error } = await supabase
        .from('projects')
        .select('customer_source, created_at, project_status')
        .eq('business_id', businessId)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .neq('project_status', 'Canceled');

      if (error) {
        console.error('Error fetching project source data:', error);
        throw error;
      }

      // Apply additional date filtering
      const filteredProjects = (projects || []).filter(project => {
        if (!project.created_at) return false;
        const projectDate = project.created_at.split('T')[0];
        return isDateInRange(projectDate, dateRange);
      });

      if (filteredProjects.length === 0) {
        return {
          value: '0',
          label: 'Total Projects',
          chartData: null,
          isPieChart: true,
          sourceDetails: []
        };
      }

      // Count projects by source
      const sourceCounts: Record<string, number> = {};
      filteredProjects.forEach(project => {
        const source = project.customer_source || 'Undefined';
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      });

      // Sort sources by count (descending - highest to lowest)
      const sortedSources = Object.entries(sourceCounts)
        .sort(([, a], [, b]) => b - a);

      // Prepare pie chart data
      const labels = sortedSources.map(([source]) => source);
      const data = sortedSources.map(([, count]) => count);
      const total = filteredProjects.length;

      // Prepare source details for legend display
      const sourceDetails = sortedSources.map(([source, count]) => ({
        source,
        count,
        percentage: ((count / total) * 100).toFixed(1)
      }));

      // Generate colors for each slice
      const colors = [
        '#3B82F6', // Blue
        '#10B981', // Green  
        '#F59E0B', // Yellow
        '#EF4444', // Red
        '#8B5CF6', // Purple
        '#06B6D4', // Cyan
        '#F97316', // Orange
        '#84CC16', // Lime
        '#EC4899', // Pink
        '#6B7280'  // Gray
      ];

      const chartData = {
        labels: labels.map(label => {
          const count = sourceCounts[label];
          const percentage = ((count / total) * 100).toFixed(1);
          return `${label} (${percentage}%)`;
        }),
        datasets: [{
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverBorderWidth: 3
        }]
      };

      return {
        value: total.toString(),
        label: `Total Projects`,
        chartData: chartData,
        isPieChart: true,
        sourceDetails: sourceDetails
      };
    } catch (error) {
      console.error('Error fetching project source data:', error);
      // Return fallback data on error
      return {
        value: '0',
        label: 'Total Projects',
        chartData: null,
        isPieChart: true,
        sourceDetails: []
      };
    }
  };

  // Function to fetch project revenue data (excludes projects converted to bookings)
  const fetchProjectRevenueData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // This report ignores date range filters - it shows a live view of all potential revenue
      // Query all projects that have NOT been converted to bookings, closed, or completed
      // Projects converted to bookings have a non-null booking_id OR status = 'Converted to booking'
      // Exclude: closed, completed, canceled, or converted to booking
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select('id, proposal_id, created_at, booking_id, status, project_status')
        .eq('business_id', businessId)
        .is('booking_id', null) // Exclude projects converted to bookings (by booking_id)
        .neq('status', 'Converted to booking') // Exclude projects with status "Converted to booking"
        .neq('status', 'closed') // Exclude closed projects
        .neq('project_status', 'completed') // Exclude completed projects
        .neq('project_status', 'canceled') // Exclude canceled projects
        .eq('project_status', 'open'); // Only include open projects

      if (projectsError) {
        console.error('Error fetching projects for revenue:', projectsError);
        throw projectsError;
      }

      // No date filtering - use all matching projects
      const filteredProjects = projects || [];

      if (filteredProjects.length === 0) {
        return {
          value: formatCurrency(0),
          label: 'Project Potential Revenue'
        };
      }

      let totalRevenue = 0;

      // Get revenue from linked proposals
      const proposalIds = filteredProjects
        .map(p => p.proposal_id)
        .filter((id): id is string => !!id);

      if (proposalIds.length > 0) {
        const { data: proposals, error: proposalsError } = await supabase
          .from('proposals')
          .select('id, total_amount, valid_until')
          .in('id', proposalIds);

        if (proposalsError) {
          console.error('Error fetching proposal totals:', proposalsError);
        } else {
          // Filter out expired proposals (valid_until is not null and is in the past)
          const nonExpiredProposals = (proposals || []).filter(p => {
            if (!p.valid_until) return true; // No expiration date means not expired
            return new Date(p.valid_until) >= new Date(); // Only include if not expired
          });
          totalRevenue += nonExpiredProposals.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        }
      }

      // Get revenue from project services (for projects without proposals)
      const projectIdsWithoutProposals = filteredProjects
        .filter(p => !p.proposal_id)
        .map(p => p.id);

      if (projectIdsWithoutProposals.length > 0) {
        const { data: services, error: servicesError } = await supabase
          .from('project_services')
          .select('project_id, total_amount')
          .in('project_id', projectIdsWithoutProposals);

        if (servicesError) {
          console.error('Error fetching project services:', servicesError);
        } else {
          totalRevenue += (services || []).reduce((sum, s) => sum + (s.total_amount || 0), 0);
        }
      }

      // No comparison calculation - this is a live view that doesn't compare to previous periods
      return {
        value: formatCurrency(totalRevenue),
        label: 'Project Potential Revenue'
      };
    } catch (error) {
      console.error('Error fetching project revenue data:', error);
      return {
        value: formatCurrency(0),
        label: 'Project Potential Revenue'
      };
    }
  };

  // Function to fetch online booking funnel data
  const fetchOnlineBookingVolumeData = async (businessId: string, dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): Promise<{ value: string; label: string; funnelData?: any; bannerSubmits?: number; showBannerSubmits?: boolean }> => {
    try {
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
      
      // Check if popup is enabled on the default online booking portal
      const { data: defPortal } = await supabase
        .from('online_booking_portals')
        .select('id')
        .eq('business_id', businessId)
        .eq('is_default', true)
        .maybeSingle();
      let isPopupEnabled = false;
      if (defPortal?.id) {
        const { data: popupSettings } = await supabase
          .from('online_booking_settings')
          .select('popup_enabled')
          .eq('portal_id', defPortal.id)
          .maybeSingle();
        isPopupEnabled = popupSettings?.popup_enabled || false;
      }
      
      // Query booking step progression within the date range
      const { data: stepData, error } = await supabase
        .from('booking_step_progression')
        .select('step, session_id')
        .eq('business_id', businessId)
        .gte('reached_at', startDate.toISOString())
        .lte('reached_at', endDate.toISOString());

      if (error) {
        console.error('Error fetching booking funnel data:', error);
        throw error;
      }

      // Query banner submissions if popup is enabled
      let bannerSubmits = 0;
      if (isPopupEnabled) {
        const { data: bannerData, error: bannerError } = await supabase
          .from('banner_submissions')
          .select('id')
          .eq('business_id', businessId)
          .gte('submitted_at', startDate.toISOString())
          .lte('submitted_at', endDate.toISOString());
        
        if (!bannerError) {
          bannerSubmits = bannerData?.length || 0;
        }
      }

      // Process funnel data
      const stepCounts = {
        location: new Set(),
        category: new Set(),
        service: new Set(),
        datetime: new Set(),
        customer: new Set(),
        completed: new Set()
      };

      // Count unique sessions that reached each step
      (stepData || []).forEach(item => {
        if (stepCounts[item.step as keyof typeof stepCounts]) {
          stepCounts[item.step as keyof typeof stepCounts].add(item.session_id);
        }
      });

      const funnel = [
        { step: 'Location', count: stepCounts.location.size, icon: 'MapPin' },
        { step: 'Category', count: stepCounts.category.size, icon: 'Tag' },
        { step: 'Service', count: stepCounts.service.size, icon: 'Wrench' },
        { step: 'Date & Time', count: stepCounts.datetime.size, icon: 'Calendar' },
        { step: 'Customer Info', count: stepCounts.customer.size, icon: 'User' },
        { step: 'Completed', count: stepCounts.completed.size, icon: 'CheckCircle' }
      ];

      // Don't add banner submits to funnel - they'll be displayed separately

      const totalStarts = stepCounts.location.size;
      const totalCompletions = stepCounts.completed.size;
      const conversionRate = totalStarts > 0 ? ((totalCompletions / totalStarts) * 100).toFixed(1) : '0.0';

      return {
        value: `${conversionRate}%`,
        label: `Conversion Rate (${totalCompletions}/${totalStarts})`,
        funnelData: funnel,
        bannerSubmits: bannerSubmits,
        showBannerSubmits: isPopupEnabled
      };
    } catch (error) {
      console.error('Error fetching booking funnel data:', error);
      // Return fallback data on error
      return {
        value: '0.0%',
        label: 'Conversion Rate (0/0)',
        funnelData: [
          { step: 'Location', count: 0, icon: 'MapPin' },
          { step: 'Category', count: 0, icon: 'Tag' },
          { step: 'Service', count: 0, icon: 'Wrench' },
          { step: 'Date & Time', count: 0, icon: 'Calendar' },
          { step: 'Customer Info', count: 0, icon: 'User' },
          { step: 'Completed', count: 0, icon: 'CheckCircle' }
        ],
        bannerSubmits: 0,
        showBannerSubmits: false
      };
    }
  };

  // Load suppliers for stock alert functionality
  const loadSuppliers = useCallback(async () => {
    if (!businessId) return;
    
    try {
      const data = await getSuppliers(businessId);
      setSuppliers(data);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  }, [businessId]);

  // Load pending orders for stock alert functionality
  const loadPendingOrders = useCallback(async () => {
    if (!businessId) return;
    
    try {
      const pendingItems = await getPendingOrdersForItems(businessId);
      
      // Convert to a Map for easy lookup
      const itemMap = new Map<string, number>();
      pendingItems.forEach(item => {
        itemMap.set(item.itemId, item.quantityOrdered);
      });
      
      setPendingOrderItems(itemMap);
      
    } catch (err) {
      console.error('Error loading pending orders:', err);
    }
  }, [businessId]);

  // Calculate supplier orders for stock alert
  const calculateSupplierOrders = useCallback(() => {
    if (!stockItems.length || !suppliers.length) return;

    // Filter items that are below reorder point and have a supplier
    const itemsToOrder = stockItems.filter(item => {
      const isLowStock = item.quantity <= item.reorder_point;
      const hasSupplier = !!item.supplier_id;
      const canAddMoreStock = item.max_quantity > item.quantity;
      
      // Check if the item is already in a pending order
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const projectedQuantity = item.quantity + pendingQuantity;
      const stillNeedsOrdering = projectedQuantity <= item.reorder_point;
      
      return isLowStock && hasSupplier && canAddMoreStock && stillNeedsOrdering;
    });
    
    // Group by supplier
    const ordersBySupplier: Record<string, SupplierOrder> = {};
    
    itemsToOrder.forEach(item => {
      if (!item.supplier_id) return;
      
      const supplier = suppliers.find(s => s.id === item.supplier_id);
      if (!supplier) return;
      
      // Calculate how much quantity to order considering pending orders
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const effectiveQuantity = item.quantity + pendingQuantity;
      const quantityToOrder = item.max_quantity - effectiveQuantity;
      
      if (quantityToOrder <= 0) return;
      
      const totalCost = quantityToOrder * item.acquisition_cost;
      
      if (!ordersBySupplier[supplier.id]) {
        ordersBySupplier[supplier.id] = {
          supplier,
          items: [],
          totalCost: 0,
          itemCount: 0
        };
      }
      
      ordersBySupplier[supplier.id].items.push({
        item,
        quantityToOrder,
        totalCost
      });
      
      ordersBySupplier[supplier.id].totalCost += totalCost;
      ordersBySupplier[supplier.id].itemCount += 1;
    });
    
    // Convert to array and sort by supplier name
    const ordersArray = Object.values(ordersBySupplier)
      .sort((a, b) => {
        const aName = `${a.supplier.first_name} ${a.supplier.last_name}`.trim();
        const bName = `${b.supplier.first_name} ${b.supplier.last_name}`.trim();
        return aName.localeCompare(bName);
      });
    
    setSupplierOrders(ordersArray);
  }, [stockItems, suppliers, pendingOrderItems]);

  // Handle placing an order
  const handlePlaceOrder = async (supplierId: string) => {
    const orderGroup = supplierOrders.find(o => o.supplier.id === supplierId);
    if (!orderGroup || !businessId) {
      console.error('Order group not found for supplier', supplierId);
      return;
    }
    
    try {
      // Create order number and get current date
      const orderNumber = `PO-${Date.now().toString().slice(-6)}`;
      
      // Prepare order items for the database
      const dbOrderItems: DBOrderItem[] = orderGroup.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit
      }));
      
      // Create the order in the database
      await createOrder(
        {
          business_id: businessId,
          supplier_id: orderGroup.supplier.id,
          order_number: orderNumber,
          total_amount: orderGroup.totalCost,
          status: 'pending'
        },
        dbOrderItems
      );
      
      // Show success notification
      toast.success(`Order ${orderNumber} placed successfully!`);
      
      // Refresh data
      await loadPendingOrders();
      calculateSupplierOrders();
      
    } catch (err) {
      console.error('Error sending order:', err);
             toast.error('Failed to place order: ' + (err instanceof Error ? err.message : 'Unknown error'));
     }
   };

  // Load suppliers and pending orders when business changes
  useEffect(() => {
    if (businessId) {
      loadSuppliers();
      loadPendingOrders();
    }
  }, [businessId, loadSuppliers, loadPendingOrders]);

  // Calculate supplier orders when data changes
  useEffect(() => {
    if (stockItems.length > 0 && suppliers.length > 0) {
      calculateSupplierOrders();
    }
  }, [stockItems, suppliers, pendingOrderItems, calculateSupplierOrders]);

  // Get the previous period for comparison based on current period
  // Always returns the period immediately before the selected period
  const getPreviousPeriod = (currentPeriod: DateRangeOption): DateRangeOption => {
    switch (currentPeriod) {
      case 'this_month':
        return 'last_month';
      case 'last_month':
        // Previous period for last month is two months ago
        // We need to calculate this dynamically, but for now return a placeholder
        // The actual calculation will be done in getPreviousPeriodDates
        return 'last_month'; // Will be handled by date calculation
      case 'next_month':
        return 'this_month';
      case 'this_week':
        return 'last_week';
      case 'last_week':
        // Previous period for last week is two weeks ago
        return 'last_week'; // Will be handled by date calculation
      case 'next_week':
        return 'this_week';
      case 'this_year':
        return 'last_year';
      case 'last_year':
        // Previous period for last year is two years ago
        return 'last_year'; // Will be handled by date calculation
      case 'custom':
        return 'custom'; // Will be handled by custom date calculation
      default:
        return 'last_month'; // Default fallback
    }
  };

  // Calculate previous period dates based on current period dates
  // This handles cases where we need to go back by the same duration (e.g., last month -> month before that)
  const getPreviousPeriodDates = (dateRange: DateRangeOption, customStartDate?: Date, customEndDate?: Date): { startDate: Date; endDate: Date } => {
    const now = new Date();
    
    // For standard ranges that need special handling, calculate directly
    if (dateRange === 'last_month') {
      // Previous period for last month is 2 months ago
      const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const endOfTwoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 1, 0);
      return { startDate: twoMonthsAgo, endDate: endOfTwoMonthsAgo };
    }
    
    if (dateRange === 'last_week') {
      // Previous period for last week is 2 weeks ago
      const lastWeek = new Date(now);
      lastWeek.setDate(now.getDate() - 7);
      const startOfWeek = (date: Date) => {
        const start = new Date(date);
        const day = start.getDay();
        const diff = start.getDate() - day;
        return new Date(start.setDate(diff));
      };
      const endOfWeek = (date: Date) => {
        const end = new Date(startOfWeek(date));
        end.setDate(end.getDate() + 6);
        return end;
      };
      const twoWeeksAgo = new Date(lastWeek);
      twoWeeksAgo.setDate(lastWeek.getDate() - 7);
      return { startDate: startOfWeek(twoWeeksAgo), endDate: endOfWeek(twoWeeksAgo) };
    }
    
    if (dateRange === 'last_year') {
      // Previous period for last year is 2 years ago
      const twoYearsAgo = new Date(now.getFullYear() - 2, 0, 1);
      const endOfTwoYearsAgo = new Date(now.getFullYear() - 2, 11, 31);
      return { startDate: twoYearsAgo, endDate: endOfTwoYearsAgo };
    }
    
    // For custom ranges or other ranges, calculate based on duration
    const { startDate, endDate } = getDateRange(dateRange, customStartDate, customEndDate);
    const duration = endDate.getTime() - startDate.getTime();
    
    // Calculate previous period by subtracting duration + 1 day from start date
    // The +1 day accounts for the gap between periods
    const previousEndDate = new Date(startDate.getTime() - 1); // Day before current period starts
    const previousStartDate = new Date(previousEndDate.getTime() - duration); // Same duration before that
    
    return { startDate: previousStartDate, endDate: previousEndDate };
  };

  // Calculate percentage change between current and previous values
  const calculatePercentageChange = (current: string, previous: string): { percentage: number; direction: 'up' | 'down' | 'same' } => {
    // Extract numeric values from strings (handles currency, percentages, etc.)
    const extractNumber = (str: string): number => {
      // Remove currency symbols, commas, and other non-numeric characters except decimal points and negative signs
      const cleaned = str.replace(/[$,%+\s]/g, '').replace(/[^-\d.]/g, '');
      return parseFloat(cleaned) || 0;
    };

    const currentValue = extractNumber(current);
    const previousValue = extractNumber(previous);

    if (previousValue === 0) {
      // If previous value is 0, any current value is either 0% (if current is also 0) or 100% increase
      return currentValue === 0 
        ? { percentage: 0, direction: 'same' }
        : { percentage: 100, direction: 'up' };
    }

    const change = ((currentValue - previousValue) / Math.abs(previousValue)) * 100;
    
    return {
      percentage: Math.abs(change),
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'same'
    };
  };

  // Fetch report data with comparison to previous period
  const getFilteredReportDataWithComparison = async (reportType: string, dateRange: DateRangeOption, businessId: string, customStartDate?: Date, customEndDate?: Date, report?: any) => {
    // Handle page_route_analytics with config (needs selected route from config)
    if (reportType === 'page_route_analytics') {
      const selectedRoute = report?.data?.config?.selectedRoute;
      if (selectedRoute) {
        const currentData = await fetchPageRouteAnalyticsData(
          businessId,
          selectedRoute,
          dateRange,
          customStartDate,
          customEndDate
        );
        // Preserve the config so it's available for future date range changes
        return {
          ...currentData,
          config: {
            selectedRoute: selectedRoute
          }
        };
      }
      // No route selected yet, return placeholder
      return {
        value: '0',
        label: 'Select a route',
        routeData: {
          summary: { total_visitors: 0, total_page_views: 0, total_sessions: 0 },
          traffic_sources: [],
          route: ''
        }
      };
    }
    
    // Handle click_analytics with config (needs selected click from config)
    if (reportType === 'click_analytics') {
      const selectedClick = report?.data?.config?.selectedClick;
      if (selectedClick) {
        const currentData = await fetchClickAnalyticsData(
          businessId,
          selectedClick,
          dateRange,
          customStartDate,
          customEndDate
        );
        // Preserve the config so it's available for future date range changes
        return {
          ...currentData,
          config: {
            selectedClick: selectedClick
          }
        };
      }
      // No click selected yet, return placeholder
      return {
        value: '0',
        label: 'Select a click',
        clickData: {
          summary: { total_visitors: 0, total_clicks: 0, total_sessions: 0 },
          traffic_sources: [],
          click_identifier: ''
        }
      };
    }
    
    // Handle product_sales_by_category - always fetch categories view (items are fetched on-demand when expanded)
    if (reportType === 'product_sales_by_category') {
      const currentData = await fetchProductSalesByCategoryData(
        businessId,
        null, // Always fetch categories view, items are loaded on-demand
        dateRange,
        customStartDate,
        customEndDate
      );
      return currentData;
    }
    
    // Handle service_sales_by_category - always fetch categories view (services are fetched on-demand when expanded)
    if (reportType === 'service_sales_by_category') {
      const currentData = await fetchServiceSalesByCategoryData(
        businessId,
        null, // Always fetch categories view, services are loaded on-demand
        dateRange,
        customStartDate,
        customEndDate
      );
      return currentData;
    }
    
    // Handle website_visits_graph with conversion config
    if (reportType === 'website_visits_graph') {
      const conversionConfig = report?.data?.config?.conversionConfig;
      const data = await fetchWebsiteVisitsGraphData(businessId, dateRange, customStartDate, customEndDate, conversionConfig);
      if (report?.data?.config) {
        return { ...data, config: report.data.config };
      }
      return data;
    }

    // Get current period data
    const currentData = await getFilteredReportData(reportType, dateRange, businessId, customStartDate, customEndDate);
    
    // Skip comparison for special report types that don't need it
    // project_revenue is a live view that ignores date ranges, so skip comparison
    if (reportType === 'title' || reportType === 'stock_alert' || reportType === 'online_booking_volume' || reportType === 'team_rating' || reportType === 'sales_graph' || reportType === 'crm_leads_overview' || reportType === 'crm_new_leads_graph' || reportType === 'crm_won_lost_by_staff' || reportType === 'crm_leads_created_by_resource' || reportType === 'sales_pins' || reportType === 'wallet_overview' || reportType === 'crm_lead_lifecycle' || reportType === 'active_customers' || reportType === 'crm_pipeline_stages' || reportType === 'crm_leads_by_source' || reportType === 'web_traffic_patterns' || reportType === 'page_route_analytics' || reportType === 'click_analytics' || reportType === 'booking_source' || reportType === 'website_visits_graph' || reportType === 'payments_by_type' || reportType === 'product_sales_by_category' || reportType === 'service_sales_by_category' || reportType === 'project_pipeline_stages' || reportType === 'project_revenue' || reportType === 'mrr' || reportType === 'arr') {
      return currentData;
    }

    try {
      // Get previous period data for comparison
      // For custom ranges, calculate previous period dates based on duration
      let previousPeriod: DateRangeOption;
      let previousCustomStartDate: Date | undefined;
      let previousCustomEndDate: Date | undefined;
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        // For custom ranges, calculate previous period of same duration
        const previousDates = getPreviousPeriodDates(dateRange, customStartDate, customEndDate);
        previousPeriod = 'custom';
        previousCustomStartDate = previousDates.startDate;
        previousCustomEndDate = previousDates.endDate;
      } else {
        // For standard ranges, check if we need to calculate custom dates
        // (e.g., last_month needs to compare to month before that)
        if (dateRange === 'last_month' || dateRange === 'last_week' || dateRange === 'last_year') {
          // Calculate previous period dates dynamically
          const previousDates = getPreviousPeriodDates(dateRange);
          previousPeriod = 'custom';
          previousCustomStartDate = previousDates.startDate;
          previousCustomEndDate = previousDates.endDate;
        } else {
          // Use standard previous period
          previousPeriod = getPreviousPeriod(dateRange);
        }
      }
      
      const previousData = await getFilteredReportData(
        reportType, 
        previousPeriod, 
        businessId, 
        previousCustomStartDate, 
        previousCustomEndDate
      );
      
      // Calculate comparison if both values are available
      if (currentData && 'value' in currentData && previousData && 'value' in previousData) {
        const comparison = calculatePercentageChange(currentData.value, previousData.value);
        
        return {
          ...currentData,
          comparison: {
            percentage: comparison.percentage,
            direction: comparison.direction,
            previousValue: previousData.value
          }
        };
      }
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      // Return current data without comparison if there's an error
    }

    return currentData;
  };

  // Generate filtered report data based on date range
  const getFilteredReportData = async (reportType: string, dateRange: DateRangeOption, businessId: string, customStartDate?: Date, customEndDate?: Date) => {
    if (reportType === 'sales') {
      // Use real sales data for sales reports
      return await fetchSalesData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'bookings_sales') {
      // Use real sales data for bookings sales reports (same as sales, but will be separate report)
      return await fetchSalesData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'product_sales') {
      // Use real product sales data for product sales reports (actual sales, not internal withdrawals)
      return await fetchProductSalesData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'gift_card_sales') {
      // Use real gift card sales data for gift card sales reports
      return await fetchGiftCardSalesData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'upsells') {
      // Use real upsells data for upsells reports
      return await fetchUpsellsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'closed_sales') {
      // Use real closed sales data for closed sales reports
      return await fetchClosedSalesData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'supplies_purchased') {
      // Use real supplies purchased data for supplies purchased reports
      return await fetchSuppliesPurchasedData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'expenses') {
      // Use real expenses data for expenses reports
      return await fetchExpensesData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'net_profit') {
      // Use real net profit data for net profit reports
      return await fetchNetProfitData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'labour') {
      // Use real labour data for labour reports
      return await fetchLabourData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'job_materials') {
      // Use real job materials data for job materials reports
      return await fetchJobMaterialsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'total_jobs') {
      // Use real total jobs data for total jobs reports
      return await fetchTotalJobsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'completed_jobs') {
      // Use real completed jobs data for completed jobs reports
      return await fetchCompletedJobsData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'mrr') {
      return await fetchMRRData(businessId);
    }

    if (reportType === 'arr') {
      return await fetchARRData(businessId);
    }

    if (reportType === 'stock_alert') {
      // Use real stock alert data for stock alert reports
      return await fetchStockAlertData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'online_booking_volume') {
      // Use real online booking volume data for online booking volume reports
      return await fetchOnlineBookingVolumeData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'proposals_sent') {
      // Use real proposals sent data for proposals sent reports
      return await fetchProposalsSentData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'proposals_booked') {
      // Use real proposals booked data for proposals booked reports
      return await fetchProposalsBookedData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'proposals_approved') {
      // Use real proposals approved data for proposals approved reports
      return await fetchProposalsApprovedData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'team_rating') {
      // Use real team rating data for team rating reports
      return await fetchTeamRatingData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'feedback_percentage') {
      // Use real feedback percentage data for feedback percentage reports
      return await fetchFeedbackPercentageData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'sales_graph') {
      // Use real sales graph data for sales graph reports
      return await fetchSalesGraphData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'average_sale') {
      // Use real average sale data for average sale reports
      return await fetchAverageSaleData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'booking_source') {
      // Use real booking source data for booking source reports
      return await fetchBookingSourceData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'project_source') {
      // Use real project source data for project source reports
      return await fetchProjectSourceData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'project_revenue') {
      // Use real project revenue data (excludes projects converted to bookings)
      return await fetchProjectRevenueData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'website_visits') {
      // Use real website visits data for website visits reports
      return await fetchWebsiteVisitsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'website_visits_graph') {
      // Use real website visits graph data for website visits graph reports
      return await fetchWebsiteVisitsGraphData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'google_reviews') {
      // Use real Google reviews data for Google reviews reports
      return await fetchGoogleReviewsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'phone_calls') {
      // Use real phone calls data for phone calls reports
      return await fetchPhoneCallsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'crm_leads_overview') {
      // Use real CRM leads data
      return await fetchCRMLeadsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'crm_new_leads_graph') {
      // Stacked bar of leads per day (by creation date), split by current status
      return await fetchNewLeadsGraphData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'crm_won_lost_by_staff') {
      // Won/lost counts per assigned team member (+ win rate)
      return await fetchCRMWonLostByStaffData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'crm_leads_created_by_resource') {
      // Leads created per team member (creator's resource)
      return await fetchCRMLeadsCreatedByResourceData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'sales_pins') {
      // Sales pins dropped in range, by current knock status
      return await fetchSalesPinsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'wallet_overview') {
      // Wallet liability (current) + added/used in range from the ledger
      return await fetchWalletOverviewData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'crm_lead_lifecycle') {
      // Avg days from lead creation to close (overall / won / lost)
      return await fetchLeadLifecycleData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'active_customers') {
      // Active Customer rule snapshots at range start/end (+ became/went inactive)
      return await fetchActiveCustomersData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'crm_pipeline_stages') {
      // Use real CRM pipeline stages data (ignores date range, always shows current state)
      return await fetchCRMPipelineStagesData(businessId);
    }
    
    if (reportType === 'project_pipeline_stages') {
      // Use real project pipeline stages data (ignores date range, always shows current state)
      return await fetchProjectPipelineStagesData(businessId);
    }
    
    if (reportType === 'crm_leads_by_source') {
      // Use real CRM leads by source data
      return await fetchCRMLeadsBySourceData(businessId, dateRange, customStartDate, customEndDate);
    }

    if (reportType === 'crm_lost_reasons') {
      return await fetchCRMLostReasonsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'missed_calls') {
      // Use real missed calls data for missed calls reports
      return await fetchMissedCallsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'payments') {
      // Use real payments data for payments reports
      return await fetchPaymentsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'payments_by_type') {
      // Use real payments by type data for payments by type reports
      return await fetchPaymentsByTypeData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'web_traffic_patterns') {
      // Use real web traffic patterns data for comprehensive analytics
      return await fetchWebTrafficPatternsData(businessId, dateRange, customStartDate, customEndDate);
    }
    
    if (reportType === 'page_route_analytics') {
      // This is handled separately with config, return placeholder for now
      // The actual fetch happens when we have the selected route from config
      return {
        value: '0',
        label: 'Select a route',
        routeData: {
          summary: { total_visitors: 0, total_page_views: 0, total_sessions: 0 },
          traffic_sources: [],
          route: ''
        }
      };
    }
    
    if (reportType === 'title') {
      // For title elements, return text data instead of value/label
      return {
        text: 'Dashboard Title',
        isTitle: true
      };
    }
    
    // For other report types, use simulated data for now
    const baseValue = 24567;
    const multiplier = dateRange.includes('month') ? 1 : 0.25; // Weeks are roughly 1/4 of month
    const randomFactor = 0.8 + Math.random() * 0.4; // Random variation between 0.8 and 1.2
    
    const filteredValue = Math.round(baseValue * multiplier * randomFactor);
    
    return {
      value: `$${filteredValue.toLocaleString()}`,
      label: `Total Sales After Discounts`
    };
  };

  const handleReportToggle = (reportType: string, reportTitle: string) => {
    setSelectedReports(prev => {
      const isSelected = prev.some(r => r.type === reportType);
      if (isSelected) {
        return prev.filter(r => r.type !== reportType);
      } else {
        return [...prev, { type: reportType, name: reportTitle }];
      }
    });
  };

  const handleAddSelectedReports = async () => {
    if (!selectedDashboardId || !businessId || selectedReports.length === 0) return;

    try {
      const selectedDashboard = dashboards.find(d => d.id === selectedDashboardId);
      if (!selectedDashboard) return;

      const dateRange = selectedDashboard.dateRange || 'this_month';
      let existingReports = selectedDashboard.reports || [];
      
      // Add all selected reports
      for (const report of selectedReports) {
        const filteredData = await getFilteredReportDataWithComparison(report.type, dateRange, businessId);
        const nextPosition = existingReports.length;

        // Set different default sizes for different report types based on view type
        let defaultWidth, defaultHeight;
        
        if (currentViewType === 'mobile') {
          if (shouldUseMLXLSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('medium', 'mobile');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else if (shouldUseChartSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('large', 'mobile');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else if (shouldUseMLSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('medium', 'mobile');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else if (shouldUseSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('small', 'mobile');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else {
            defaultWidth = 20;
            defaultHeight = 28;
          }
        } else {
          if (shouldUseMLXLSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('medium', 'desktop');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else if (shouldUseChartSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('large', 'desktop');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else if (shouldUseMLSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('medium', 'desktop');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else if (shouldUseSizeSystem(report.type)) {
            const sizeDims = getSizeDimensions('small', 'desktop');
            defaultWidth = sizeDims.w;
            defaultHeight = sizeDims.h;
          } else {
            defaultWidth = 20;
            defaultHeight = 25;
          }
        }

        // Determine size for this report type
        let reportSize: ReportSize;
        if (shouldUseChartSizeSystem(report.type)) {
          reportSize = 'large';
        } else if (shouldUseMLSizeSystem(report.type)) {
          reportSize = 'medium';
        } else if (shouldUseSizeSystem(report.type)) {
          reportSize = 'small';
        } else {
          reportSize = getDefaultSize();
        }

        // For page_route_analytics, wrap data with config structure
        let finalReportData = filteredData;
        if (report.type === 'page_route_analytics') {
          finalReportData = {
            config: {
              selectedRoute: '' // Empty initially - user will select in edit mode
            },
            ...filteredData
          };
        }

        const reportData = {
          dashboard_id: selectedDashboardId,
          business_id: businessId,
          report_type: report.type,
          report_title: report.name,
          layout_x: currentViewType === 'mobile' ? (nextPosition * defaultWidth) % 20 : (nextPosition * defaultWidth) % 100,
          layout_y: currentViewType === 'mobile' ? Math.floor((nextPosition * defaultWidth) / 20) * defaultHeight : Math.floor((nextPosition * defaultWidth) / 100) * defaultHeight,
          layout_w: defaultWidth,
          layout_h: defaultHeight,
          view_type: currentViewType,
          size: reportSize,
          report_data: finalReportData
        };

        const createdReport = await createDashboardReport(reportData);

        const newReport: Report = {
          id: createdReport.id,
          type: createdReport.report_type,
          title: createdReport.report_title,
          data: createdReport.report_data,
          size: createdReport.size || reportSize,
          layout: {
            x: createdReport.layout_x,
            y: createdReport.layout_y,
            w: createdReport.layout_w,
            h: createdReport.layout_h
          }
        };

        existingReports = [...existingReports, newReport];
      }

      // Update dashboard with all new reports
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === selectedDashboardId) {
          return {
            ...dashboard,
            reports: existingReports
          };
        }
        return dashboard;
      }));

      // Update pendingLayouts to include new reports so save button highlights
      if (isEditMode) {
        setPendingLayouts(prev => {
          const newLayout = existingReports.map(report => ({
            i: report.id,
            x: report.layout.x,
            y: report.layout.y,
            w: report.layout.w,
            h: report.layout.h
          }));
          return {
            ...prev,
            [selectedDashboardId]: newLayout
          };
        });
      }

      toast.success(`Added ${selectedReports.length} report${selectedReports.length > 1 ? 's' : ''} successfully`);
      setSelectedReports([]); // Clear selection
      setIsAddReportsModalOpen(false); // Close modal
    } catch (error) {
      console.error('Error adding reports:', error);
      toast.error('Failed to add reports');
    }
  };

  const handleReportClick = async (reportType: string, reportTitle: string) => {
    if (!selectedDashboardId || !businessId) return;

    try {
      const selectedDashboard = dashboards.find(d => d.id === selectedDashboardId);
      const dateRange = selectedDashboard?.dateRange || 'this_month';
      const filteredData = await getFilteredReportDataWithComparison(reportType, dateRange, businessId);
      
      const existingReports = selectedDashboard?.reports || [];
      const nextPosition = existingReports.length;

      // Set different default sizes for different report types based on view type
      let defaultWidth, defaultHeight;
      
      if (currentViewType === 'mobile') {
        // Mobile: 20-column grid system with 8px row height - optimized for mobile usage patterns
        if (shouldUseMLXLSizeSystem(reportType)) {
          // Use medium size for M/L/XL reports on mobile
          const sizeDims = getSizeDimensions('medium', 'mobile');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else if (shouldUseChartSizeSystem(reportType)) {
          // Use large size for chart reports on mobile
          const sizeDims = getSizeDimensions('large', 'mobile');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else if (shouldUseMLSizeSystem(reportType)) {
          // Use medium size for M/L reports on mobile
          const sizeDims = getSizeDimensions('medium', 'mobile');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else if (shouldUseSizeSystem(reportType)) {
          // Use small size for size system reports
          const sizeDims = getSizeDimensions('small', 'mobile');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else {
          defaultWidth = 20; // Full width for all reports on mobile - better UX
          defaultHeight = 28; // Increased from 22 for better mobile readability
        }
      } else {
        // Desktop: 100-column grid system with 10px row height
        if (shouldUseMLXLSizeSystem(reportType)) {
          // Use medium size for M/L/XL reports on desktop
          const sizeDims = getSizeDimensions('medium', 'desktop');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else if (shouldUseMLSizeSystem(reportType)) {
          // Use medium size for M/L reports on desktop
          const sizeDims = getSizeDimensions('medium', 'desktop');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
          } else if (shouldUseChartSizeSystem(reportType)) {
            // Use large size for chart reports on desktop
            const sizeDims = getSizeDimensions('large', 'desktop');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else if (shouldUseSizeSystem(reportType)) {
          // Use small size for size system reports
          const sizeDims = getSizeDimensions('small', 'desktop');
          defaultWidth = sizeDims.w;
          defaultHeight = sizeDims.h;
        } else {
          defaultWidth = 20; // 20% width for others
          defaultHeight = 25; // Standard height
        }
      }

      // Determine size for this report type
      let reportSize: ReportSize;
      if (shouldUseMLSizeSystem(reportType)) {
        reportSize = 'medium'; // Default to medium for M/L reports
      } else if (shouldUseChartSizeSystem(reportType)) {
        reportSize = 'large'; // Default to large for chart reports
      } else if (shouldUseSizeSystem(reportType)) {
        reportSize = 'small'; // Default to small for S/M reports
      } else {
        reportSize = getDefaultSize();
      }

      // For page_route_analytics, wrap data with config structure
      let finalReportData = filteredData;
      if (reportType === 'page_route_analytics') {
        finalReportData = {
          config: {
            selectedRoute: '' // Empty initially - user will select in edit mode
          },
          ...filteredData
        };
      }

      const reportData = {
        dashboard_id: selectedDashboardId,
        business_id: businessId,
        report_type: reportType,
        report_title: reportTitle,
        layout_x: currentViewType === 'mobile' ? (nextPosition * defaultWidth) % 20 : (nextPosition * defaultWidth) % 100, // Mobile: use 20-column grid, Desktop: use 100-column grid
        layout_y: currentViewType === 'mobile' ? Math.floor((nextPosition * defaultWidth) / 20) * defaultHeight : Math.floor((nextPosition * defaultWidth) / 100) * defaultHeight, // Mobile: wrap after 20 columns, Desktop: wrap after 100
        layout_w: defaultWidth, // Variable width based on report type
        layout_h: defaultHeight, // Variable height based on report type
        view_type: currentViewType, // Add current view type
        size: reportSize, // Add size field
        report_data: finalReportData
      };

      const createdReport = await createDashboardReport(reportData);

      const newReport: Report = {
        id: createdReport.id,
        type: createdReport.report_type,
        title: createdReport.report_title,
        data: createdReport.report_data,
        size: createdReport.size || reportSize, // Include size
        layout: {
          x: createdReport.layout_x,
          y: createdReport.layout_y,
          w: createdReport.layout_w,
          h: createdReport.layout_h
        }
      };

      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === selectedDashboardId) {
          return {
            ...dashboard,
            reports: [...(dashboard.reports || []), newReport]
          };
        }
        return dashboard;
      }));

      // If it's a title element, initialize its text in our state
      if (reportType === 'title') {
        const titleText = 'text' in filteredData ? filteredData.text : 'Dashboard Title';
        setReportTitleTexts(prev => ({
          ...prev,
          [createdReport.id]: titleText
        }));
      }

      setIsAddReportsModalOpen(false);
      setSelectedDashboardId(null);
      toast.success(`${reportTitle} ${reportType === 'title' ? 'element' : 'report'} added to ${currentViewType} view`);
    } catch (error) {
      console.error('Error adding report:', error);
      toast.error('Failed to add report');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setDashboardName('');
  };

  // Generate mock data for report previews
  const generateMockPreviewData = (reportType: string): any => {
    switch (reportType) {
      case 'title':
        return { text: 'Dashboard Title', isTitle: true };
      
      case 'sales':
        return { 
          value: '$96,483',
          comparison: { percentage: 19, direction: 'up' as const }
        };
      
      case 'upsells':
        return { 
          value: '$8,423',
          comparison: { percentage: 8.3, direction: 'up' as const }
        };
      
      case 'closed_sales':
        return { 
          value: '$32,145',
          comparison: { percentage: 15.2, direction: 'up' as const }
        };
      
      case 'net_profit':
        return { 
          value: '$32,840',
          comparison: { percentage: 22, direction: 'up' as const }
        };
      
      case 'supplies_purchased':
        return { 
          value: '$3,892',
          comparison: { percentage: -8.9, direction: 'down' as const }
        };
      
      case 'expenses':
        return { 
          value: '$2,145',
          comparison: { percentage: 12.3, direction: 'up' as const }
        };
      
      case 'proposals_sent':
        return { 
          value: '23',
          comparison: { percentage: 9.5, direction: 'up' as const }
        };
      
      case 'proposals_booked':
        return { 
          value: '8',
          comparison: { percentage: 14.3, direction: 'up' as const }
        };
      
      case 'proposals_approved':
        return { 
          value: '12',
          comparison: { percentage: -3.2, direction: 'down' as const }
        };
      
      case 'total_jobs':
        return { 
          value: '247',
          comparison: { percentage: 6.2, direction: 'up' as const }
        };
      
      case 'completed_jobs':
        return { 
          value: '189',
          comparison: { percentage: 11.8, direction: 'up' as const }
        };
      
      case 'labour':
        return { 
          value: '$9,560',
          comparison: { percentage: -2.4, direction: 'down' as const }
        };
      
      case 'job_materials':
        return { 
          value: '$5,240',
          comparison: { percentage: 3.7, direction: 'up' as const }
        };
      
      case 'payments':
        return { 
          value: '$18,234',
          comparison: { percentage: 12.5, direction: 'up' as const }
        };

      case 'mrr':
        return { 
          value: '$4,850',
          comparison: { percentage: 15.2, direction: 'up' as const }
        };

      case 'arr':
        return { 
          value: '$58,200',
          comparison: { percentage: 15.2, direction: 'up' as const }
        };
      
      case 'team_rating':
        return { 
          value: '4.8',
          comparison: { percentage: 2.1, direction: 'up' as const }
        };
      
      case 'feedback_percentage':
        return { 
          value: '78%',
          comparison: { percentage: -1.3, direction: 'down' as const }
        };
      
      case 'google_reviews':
        return { 
          value: '24',
          comparison: { percentage: 4.3, direction: 'up' as const }
        };
      
      case 'phone_calls':
        return { 
          value: '47',
          comparison: { percentage: -6.4, direction: 'down' as const }
        };
      
      case 'missed_calls':
        return { 
          value: '12',
          comparison: { percentage: -20.0, direction: 'down' as const }
        };
      
      case 'website_visits':
        return { 
          value: '1,247',
          comparison: { percentage: 22.4, direction: 'up' as const }
        };
      
      case 'average_sale':
        return { 
          value: '$456.78',
          comparison: { percentage: 4.1, direction: 'up' as const }
        };
      
      case 'sales_graph':
        return {
          value: '$96,483',
          label: 'Sales Trend',
          comparison: { percentage: 19, direction: 'up' as const },
          chartData: {
            labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            datasets: [{
              label: 'Sales',
              data: [1200, 1900, 3000, 2500, 2200, 3000, 2800],
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)'
            }]
          }
        };
      
      case 'website_visits_graph':
        return {
          value: '1,247',
          label: 'Website Visits',
          comparison: { percentage: 22.4, direction: 'up' as const },
          chartData: {
            labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            datasets: [{
              label: 'Website Visits',
              data: [180, 220, 280, 240, 260, 320, 290],
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)'
            }]
          }
        };
      
      case 'booking_source':
        return {
          chartData: {
            labels: ['Website', 'Phone', 'Email', 'Walk-in'],
            datasets: [{
              data: [45, 30, 40, 27],
              backgroundColor: [
                'rgba(59, 130, 246, 0.8)',
                'rgba(147, 51, 234, 0.8)',
                'rgba(236, 72, 153, 0.8)',
                'rgba(34, 197, 94, 0.8)'
              ]
            }]
          },
          sourceDetails: [
            { source: 'Website', count: 45, sales: 18500.00 },
            { source: 'Phone', count: 30, sales: 12000.00 },
            { source: 'Email', count: 40, sales: 16000.00 },
            { source: 'Walk-in', count: 27, sales: 10800.00 }
          ],
          isPieChart: true,
          value: '142',
          label: 'Total Bookings'
        };
      
      case 'project_source':
        return {
          chartData: {
            labels: ['Referral (40%)', 'Website (30%)', 'Repeat Customer (20%)', 'Walk-in (10%)'],
            datasets: [{
              data: [40, 30, 20, 10],
              backgroundColor: [
                'rgba(59, 130, 246, 0.8)',
                'rgba(16, 185, 129, 0.8)',
                'rgba(245, 158, 11, 0.8)',
                'rgba(139, 92, 246, 0.8)'
              ]
            }]
          },
          sourceDetails: [
            { source: 'Referral', count: 40, percentage: '40.0' },
            { source: 'Website', count: 30, percentage: '30.0' },
            { source: 'Repeat Customer', count: 20, percentage: '20.0' },
            { source: 'Walk-in', count: 10, percentage: '10.0' }
          ],
          isPieChart: true,
          value: '100',
          label: 'Total Projects'
        };
      
      case 'project_revenue':
        return {
          value: '$24,500',
          label: 'Project Potential Revenue',
          comparison: { percentage: 12.5, direction: 'up' as const }
        };
      
      case 'online_booking_volume':
        return {
          value: '87.5%',
          label: 'Conversion Rate (35/40)',
          comparison: { percentage: 18.5, direction: 'up' as const },
          funnelData: [
            { step: 'Location', count: 40, icon: 'MapPin' },
            { step: 'Category', count: 38, icon: 'Tag' },
            { step: 'Service', count: 36, icon: 'Wrench' },
            { step: 'Date & Time', count: 36, icon: 'Calendar' },
            { step: 'Customer Info', count: 35, icon: 'User' },
            { step: 'Completed', count: 35, icon: 'CheckCircle' }
          ]
        };
      
      case 'crm_leads_overview':
        return { 
          total: 45, 
          won: 12, 
          lost: 8,
          comparison: { percentage: 7.1, direction: 'up' as const }
        };
      
      case 'active_customers':
        return {
          value: '86',
          label: 'Active Customers',
          enabled: true,
          active: 86,
          previousActive: 82,
          becameActive: 9,
          wentInactive: 5,
          net: 4,
          windowDays: 45
        };
      
      case 'crm_won_lost_by_staff':
        return {
          value: '12 • 8',
          label: 'Won • Lost',
          staff: [
            { id: '1', name: 'Alex Carter', open: 6, won: 7, lost: 3, winPercentage: 70 },
            { id: '2', name: 'Jamie Lee', open: 4, won: 5, lost: 5, winPercentage: 50 },
            { id: 'unassigned', name: 'Unassigned', open: 3, won: 0, lost: 0, winPercentage: 0 }
          ]
        };
      
      case 'crm_leads_created_by_resource':
        return {
          value: '25',
          label: 'Leads Created',
          staff: [
            { id: '1', name: 'Alex Carter', created: 14, percentage: 56 },
            { id: '2', name: 'Jamie Lee', created: 9, percentage: 36 },
            { id: 'unassigned', name: 'No resource', created: 2, percentage: 8 }
          ]
        };
      
      case 'crm_lead_lifecycle':
        return {
          value: '5.2 days',
          label: 'Avg Time to Close',
          newLeads: 24,
          openCount: 10,
          wonCount: 9,
          lostCount: 5,
          avgCloseDays: 5.2,
          avgWonDays: 3.8,
          avgLostDays: 7.6,
          avgCloseLabel: '5.2 days',
          avgWonLabel: '3.8 days',
          avgLostLabel: '7.6 days',
          untimedClosed: 0
        };
      
      case 'wallet_overview':
        return {
          value: '$1,284',
          label: 'Wallet Liability',
          liability: 1284,
          added: 420,
          used: 310,
          topups: 350,
          customersWithBalance: 23
        };
      
      case 'sales_pins':
        return {
          value: '64',
          label: 'Sales Pins Dropped',
          pins: [
            { type: 'not-home', label: 'Not Home', color: '#9CA3AF', count: 22, percentage: 34.4 },
            { type: 'interested', label: 'Interested', color: '#3B82F6', count: 15, percentage: 23.4 },
            { type: 'not-interested', label: 'Not Interested', color: '#DC2626', count: 12, percentage: 18.8 },
            { type: 'estimate-sent', label: 'Estimate Sent', color: '#8B5CF6', count: 8, percentage: 12.5 },
            { type: 'booked', label: 'Closed', color: '#10B981', count: 4, percentage: 6.3 },
            { type: 'comeback-later', label: 'Come Back Later', color: '#F59E0B', count: 3, percentage: 4.7 }
          ]
        };
      
      case 'crm_new_leads_graph':
        return {
          value: '18',
          label: 'New Leads',
          chartData: {
            labels: ['Sep 1', 'Sep 2', 'Sep 3', 'Sep 4', 'Sep 5'],
            datasets: [
              { label: 'Won', data: [1, 0, 2, 1, 0], backgroundColor: 'rgba(16, 185, 129, 0.85)', stack: 'leads', borderRadius: 2 },
              { label: 'Lost', data: [1, 1, 0, 0, 1], backgroundColor: 'rgba(239, 68, 68, 0.8)', stack: 'leads', borderRadius: 2 },
              { label: 'Open', data: [2, 1, 2, 3, 3], backgroundColor: 'rgba(59, 130, 246, 0.8)', stack: 'leads', borderRadius: 2 }
            ]
          }
        };
      
      case 'payments_by_type':
        return {
          total: 4918,
          byType: [
            { type: 'bookings', amount: 3281, label: 'Bookings' },
            { type: 'projects', amount: 381, label: 'Projects' },
            { type: 'products', amount: 700, label: 'Products' },
            { type: 'proposals', amount: 456, label: 'Proposals' },
            { type: 'gift_cards', amount: 100, label: 'Gift Cards' }
          ],
          byStatus: [
            { status: 'deposit', amount: 2100, label: 'Deposits' },
            { status: 'final', amount: 2800, label: 'Full Payments' },
            { status: 'partial', amount: 18, label: 'Partial Payments' }
          ],
          byMethod: [
            { method: 'credit_card', amount: 2500, label: 'Credit/Debit' },
            { method: 'bank_transfer', amount: 1500, label: 'E-Transfer' },
            { method: 'cash', amount: 918, label: 'Cash' }
          ]
        };
      
      case 'crm_pipeline_stages':
        return {
          stages: [
            { id: 1, name: 'Lead', count: 25 },
            { id: 2, name: 'Qualified', count: 15 },
            { id: 3, name: 'Proposal', count: 8 },
            { id: 4, name: 'Negotiation', count: 5 },
            { id: 5, name: 'Closed Won', count: 12 }
          ],
          pipelines: [
            { id: 1, name: 'Sales Pipeline' }
          ],
          selectedPipelineId: 1
        };
      
      case 'project_pipeline_stages':
        return {
          stages: [
            { id: 1, name: 'draft', count: 5 },
            { id: 2, name: 'Field Quote Scheduled', count: 3 },
            { id: 3, name: 'Quote - Pending Approval', count: 8 },
            { id: 4, name: 'Scheduled', count: 12 },
            { id: 5, name: 'in progress', count: 7 },
            { id: 6, name: 'Completed', count: 15 }
          ]
        };
      
      case 'product_sales_by_category':
        return {
          categoryData: {
            categories: [
              { id: 'cat1', name: 'Electronics', total_sales: 1500.00, sale_count: 12 },
              { id: 'cat2', name: 'Tools', total_sales: 850.00, sale_count: 8 },
              { id: 'cat3', name: 'Supplies', total_sales: 542.00, sale_count: 5 }
            ],
            total_sales: 2892.00,
            view: 'categories'
          }
        };
      
      case 'service_sales_by_category':
        return {
          categoryData: {
            categories: [
              { id: 'cat1', name: 'PPF Material', total_sales: 4000.00, sale_count: 8 },
              { id: 'cat2', name: 'Window Tinting', total_sales: 2500.00, sale_count: 5 },
              { id: 'cat3', name: 'Ceramic Coating', total_sales: 3200.00, sale_count: 6 }
            ],
            total_sales: 9700.00,
            view: 'categories'
          }
        };
      
      case 'crm_leads_by_source':
        return {
          sources: [
            { 
              source: 'Website', 
              open: 12, 
              won: 8, 
              lost: 3, 
              openValue: 4500.00, 
              wonValue: 12000.00, 
              lostValue: 1500.00, 
              totalValue: 18000.00, 
              winPercentage: 72.7 
            },
            { 
              source: 'Referral', 
              open: 8, 
              won: 5, 
              lost: 2, 
              openValue: 3200.00, 
              wonValue: 8500.00, 
              lostValue: 1000.00, 
              totalValue: 12700.00, 
              winPercentage: 71.4 
            },
            { 
              source: 'Social Media', 
              open: 6, 
              won: 3, 
              lost: 2, 
              openValue: 2400.00, 
              wonValue: 5200.00, 
              lostValue: 800.00, 
              totalValue: 8400.00, 
              winPercentage: 60.0 
            },
            { 
              source: 'Other', 
              open: 4, 
              won: 2, 
              lost: 1, 
              openValue: 1600.00, 
              wonValue: 3500.00, 
              lostValue: 500.00, 
              totalValue: 5600.00, 
              winPercentage: 66.7 
            }
          ]
        };
      
      case 'web_traffic_patterns':
        return {
          value: 'Comprehensive',
          label: 'Traffic Analytics',
          chartData: {
            labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            datasets: [{
              label: 'Traffic',
              data: [500, 600, 550, 700, 650, 800, 750],
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)'
            }]
          }
        };
      
      case 'page_route_analytics':
        return {
          value: '0',
          label: 'Select a route',
          routeData: {
            summary: { total_visitors: 0, total_page_views: 0, total_sessions: 0 },
            traffic_sources: [],
            route: ''
          }
        };
      
      case 'stock_alert':
        return {
          value: '3',
          label: 'Items Need Reorder'
        };
      
      default:
        return { value: '$0.00' };
    }
  };

  const handleAddReportsClick = (dashboardId: string) => {
    setSelectedDashboardId(dashboardId);
    setIsAddReportsModalOpen(true);
    
    // Generate mock preview data for all reports
    const previews: {[key: string]: any} = {};
    
    for (const category of reportCategories) {
      // Handle subcategories
      if (category.subcategories) {
        for (const subcat of category.subcategories) {
          for (const report of subcat.reports) {
            previews[report.type] = generateMockPreviewData(report.type);
          }
        }
      } else if (category.reports) {
        // Handle direct reports (backward compatibility)
        for (const report of category.reports) {
          previews[report.type] = generateMockPreviewData(report.type);
        }
      }
    }
    
    setReportPreviews(previews);
  };

  const handleAddReportsModalClose = () => {
    setIsAddReportsModalOpen(false);
    setSelectedDashboardId(null);
    setSelectedCategory('sales'); // Reset to first category when closing
    setSelectedReports([]); // Clear selected reports
  };

  // Get report usage count and dashboards for a report type
  const getReportUsage = (reportType: string) => {
    const usage: { count: number; dashboards: Array<{ id: string; name: string; viewType: 'desktop' | 'mobile' }> } = {
      count: 0,
      dashboards: []
    };

    dashboards.forEach(dashboard => {
      // Check both desktop and mobile reports for this dashboard
      // We need to check the actual reports in the dashboard
      const reports = dashboard.reports || [];
      const matchingReports = reports.filter(r => r.type === reportType);
      
      if (matchingReports.length > 0) {
        usage.count += matchingReports.length;
        // Determine view type - check if dashboard has desktop or mobile configured
        const viewType = dashboard.desktopConfigured && !dashboard.mobileConfigured ? 'desktop' :
                        dashboard.mobileConfigured && !dashboard.desktopConfigured ? 'mobile' :
                        currentViewType; // Default to current view if both configured
        
        // Check if this dashboard already added (could have multiple instances)
        const existing = usage.dashboards.find(d => d.id === dashboard.id && d.viewType === viewType);
        if (!existing) {
          usage.dashboards.push({
            id: dashboard.id,
            name: dashboard.name,
            viewType: viewType
          });
        }
      }
    });

    return usage;
  };

  // Define categories and their reports
  const reportCategories = [
    {
      id: 'sales',
      name: 'Sales & Revenue',
      subcategories: [
        {
          id: 'sales_metrics',
          name: 'Sales',
          reports: [
            { type: 'sales', name: 'Sales', value: '$24,567', description: 'Total Sales After Discounts' },
            { type: 'bookings_sales', name: 'Bookings Sales', value: '$24,567', description: 'Total Revenue from Bookings Only' },
            { type: 'product_sales', name: 'Product Sales', value: '$3,892', description: 'Total Revenue from Product Sales (excludes internal withdrawals)' },
            { type: 'gift_card_sales', name: 'Gift Card Sales', value: '$1,250', description: 'Total Revenue from Gift Card Purchases' },
            { type: 'upsells', name: 'Upsells', value: '$8,423', description: 'Additional Services Sold' },
            { type: 'closed_sales', name: 'Closed Sales', value: '$32,145', description: 'Completed Sales' },
            { type: 'net_profit', name: 'Net Profit', value: '$12,340', description: 'After All Expenses' },
            { type: 'average_sale', name: 'Average Sale', value: '$456.78', description: 'Per Booking' },
            { type: 'total_jobs', name: 'Total Jobs', value: '247', description: 'All Bookings' },
            { type: 'completed_jobs', name: 'Completed Jobs', value: '189', description: 'Finished Bookings' },
            { type: 'labour', name: 'Labour', value: '$9,560', description: 'Labour Costs' },
            { type: 'job_materials', name: 'Job Materials', value: '$5,240', description: 'Materials added to jobs, does not track from inventory consumption' },
            { type: 'payments', name: 'Payments', value: '$18,234', description: 'Total Payments Collected (bookings, projects, proposals, products, gift cards)' },
            { type: 'payments_by_type', name: 'Payments by Type', value: '$4,918', description: 'Breakdown of payments by type and status' },
            { type: 'wallet_overview', name: 'Wallet Overview', value: '$1,284', description: 'Wallet Liability • Added • Used' },
            { type: 'mrr', name: 'MRR', value: '$4,850', description: 'Monthly Recurring Revenue from Active Subscriptions' },
            { type: 'arr', name: 'ARR', value: '$58,200', description: 'Annual Recurring Revenue from Active Subscriptions' }
          ]
        },
        {
          id: 'sales_graph_section',
          name: null, // No subcategory name, just show the graph
          reports: [
            { type: 'sales_graph', name: 'Sales Graph', value: '$24,567', description: 'Sales Trend Chart' },
            { type: 'product_sales_by_category', name: 'Product Sales by Category', value: '$3,892', description: 'Sales by Category with Item Breakdown', isSpecial: true },
            { type: 'service_sales_by_category', name: 'Service Sales by Category', value: '$24,567', description: 'Booking Sales by Service Category with Service Breakdown', isSpecial: true }
          ]
        }
      ],
      reports: [] // Keep for backward compatibility but use subcategories
    },
    {
      id: 'operations',
      name: 'Customer Satisfaction',
      subcategories: [
        {
          id: 'customer_data',
          name: 'Customer Data',
          reports: [
            { type: 'booking_source', name: 'Booking Source', value: '142', description: 'Total Bookings by Source' },
            { type: 'active_customers', name: 'Active Customers', value: '86', description: 'Active Customers • Became Active • Went Inactive' }
          ]
        },
        {
          id: 'customer_reviews',
          name: 'Customer Reviews',
          reports: [
            { type: 'google_reviews', name: 'Google Reviews', value: '24', description: 'Review Clicks' },
            { type: 'team_rating', name: 'Team Rating', value: '4.8', description: 'Customer Rating' },
            { type: 'feedback_percentage', name: 'Feedback %', value: '78%', description: 'Response Rate' }
          ]
        }
      ],
      reports: [] // Keep for backward compatibility but use subcategories
    },
    {
      id: 'analytics',
      name: 'Marketing & Leads',
      subcategories: [
        {
          id: 'web_analytics_visits',
          name: 'Web Analytics',
          reports: [
            { type: 'website_visits', name: 'Website Visits', value: '1,247', description: 'Total Website Visits' }
          ]
        },
        {
          id: 'web_analytics_charts',
          name: null, // No subcategory name, just show the charts on next line
          reports: [
            { type: 'website_visits_graph', name: 'Website Visits Graph', value: '1,247', description: 'Visits Trend Chart' },
            { type: 'web_traffic_patterns', name: 'Web Traffic Patterns', value: 'Comprehensive', description: 'Comprehensive analytics report', isSpecial: true },
            { type: 'page_route_analytics', name: 'Route Analytics', value: 'Custom', description: 'Route-specific analytics', isSpecial: true },
            { type: 'click_analytics', name: 'Click Analytics', value: 'Custom', description: 'Click/element-specific analytics', isSpecial: true },
            { type: 'online_booking_volume', name: 'Online Booking Volume', value: '87', description: 'Online Bookings' }
          ]
        },
        {
          id: 'leads',
          name: 'Leads',
          reports: [
            { type: 'phone_calls', name: 'Phone Calls', value: '47', description: 'Incoming Phone Calls' },
            { type: 'missed_calls', name: 'Missed Calls', value: '12', description: 'Missed Calls' },
            { type: 'proposals_sent', name: 'Proposals Sent', value: '23', description: 'Total Proposals Sent' },
            { type: 'proposals_booked', name: 'Proposals Booked', value: '8', description: 'Proposals Converted' },
            { type: 'proposals_approved', name: 'Proposals Approved', value: '12', description: 'Customer Approved' },
            { type: 'crm_leads_overview', name: 'CRM Leads Overview', value: '45 • 12 • 8', description: 'Lead Performance' },
            { type: 'crm_new_leads_graph', name: 'New Leads Graph', value: 'Daily Trend', description: 'New Leads by Day • Open/Won/Lost' },
            { type: 'crm_pipeline_stages', name: 'CRM Pipeline Stages', value: 'Stage Distribution', description: 'Leads per Stage' },
            { type: 'crm_leads_by_source', name: 'CRM Leads by Source', value: 'Source Performance', description: 'Open • Won • Lost • Value • Win%' },
            { type: 'crm_won_lost_by_staff', name: 'Won/Lost by Team Member', value: 'Staff Performance', description: 'Open • Won • Lost • Win% per Team Member' },
            { type: 'crm_leads_created_by_resource', name: 'Leads Created by Team Member', value: 'Created Count', description: 'Leads Created per Team Member' },
            { type: 'sales_pins', name: 'Sales Pins', value: 'Pins by Status', description: 'Pins Dropped • Current Status Breakdown' },
            { type: 'crm_lead_lifecycle', name: 'Lead Lifecycle', value: '5.2 days', description: 'Avg Time to Close • Won vs Lost Speed' },
            { type: 'crm_lost_reasons', name: 'CRM Lost Reasons', value: 'Lost Breakdown', description: 'Lost Leads by Reason' }
          ]
        },
        {
          id: 'marketing_misc',
          name: 'Misc',
          reports: []
        }
      ],
      reports: [] // Keep for backward compatibility but use subcategories
    },
    {
      id: 'inventory',
      name: 'Inventory',
      reports: [
        { type: 'supplies_purchased', name: 'Supplies Purchased', value: '$3,892', description: 'Inventory Purchases' },
        { type: 'expenses', name: 'Expenses', value: '$2,145', description: 'Total Expenses' },
        { type: 'stock_alert', name: 'Stock Alert', value: '3', description: 'Items Need Reorder' }
      ]
    },
    {
      id: 'projects',
      name: 'Projects',
      reports: [
        { type: 'project_pipeline_stages', name: 'Project Pipeline Stages', value: 'Stage Distribution', description: 'Projects per Stage', isSpecial: true },
        { type: 'project_source', name: 'Project Source', value: '0', description: 'Total Projects by Source' },
        { type: 'project_revenue', name: 'Project Potential Revenue', value: '$0', description: 'Project Revenue (excl. converted to bookings)' }
      ]
    }
  ];

  const toggleEditMode = () => {
    if (!isEditMode) {
      // Entering edit mode - save current state as original
      const currentDashboardTitles: {[key: string]: string} = {};
      const currentReportTitles: {[key: string]: string} = {};
      const currentLayouts: {[key: string]: any[]} = {};
      const currentReportIds: {[key: string]: string[]} = {};
      
      dashboards.forEach(dashboard => {
        currentDashboardTitles[dashboard.id] = dashboard.name;
        currentReportIds[dashboard.id] = dashboard.reports?.map(r => r.id) || [];
        currentLayouts[dashboard.id] = dashboard.reports?.map(report => ({
          i: report.id,
          x: report.layout.x,
          y: report.layout.y,
          w: report.layout.w,
          h: report.layout.h
        })) || [];
        
        dashboard.reports?.forEach(report => {
          currentReportTitles[report.id] = report.data?.text || '';
        });
      });
      
      setOriginalState({
        dashboardTitles: currentDashboardTitles,
        reportTitles: currentReportTitles,
        layouts: currentLayouts,
        reportIds: currentReportIds
      });
      
      // Initialize pending changes with current values
      setPendingDashboardTitles(currentDashboardTitles);
      setPendingReportTitles(currentReportTitles);
      setPendingLayouts(currentLayouts);
      
      // CRITICAL: Initialize latestLayoutsRef with current layouts
      // This ensures we have a baseline when entering edit mode
      latestLayoutsRef.current.clear();
      Object.entries(currentLayouts).forEach(([dashboardId, layout]) => {
        if (layout && layout.length > 0) {
          latestLayoutsRef.current.set(dashboardId, layout);
        }
      });
    }
    
    setIsEditMode(!isEditMode);
    // Cancel any ongoing title edits when toggling edit mode
    setEditingTitleId(null);
    setEditingTitle('');
  };

  const handleStartTitleEdit = (dashboardId: string, currentTitle: string) => {
    setEditingTitleId(dashboardId);
    setEditingTitle(currentTitle);
  };

  // Auto-save dashboard title with debouncing
  const autoSaveDashboardTitle = useCallback(async (dashboardId: string, title: string) => {
    if (!title.trim()) {
      return; // Don't save empty titles
    }

    try {
      await updateKPIDashboard(dashboardId, { title: title.trim() });
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            name: title.trim()
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating dashboard title:', error);
      toast.error('Failed to update dashboard name');
    }
  }, []);

  const handleTitleChange = (dashboardId: string, newTitle: string) => {
    setEditingTitle(newTitle);
    
    if (isEditMode) {
      // In edit mode, just update pending changes
      setPendingDashboardTitles(prev => ({
        ...prev,
        [dashboardId]: newTitle
      }));
    } else {
      // Not in edit mode, use auto-save as before
      // Clear existing timeout
      if (dashboardTitleTimeoutRef.current) {
        clearTimeout(dashboardTitleTimeoutRef.current);
      }
      
      // Set new timeout for auto-save (500ms delay)
      dashboardTitleTimeoutRef.current = setTimeout(() => {
        autoSaveDashboardTitle(dashboardId, newTitle);
      }, 500);
    }
  };

  const handleCancelTitleEdit = () => {
    // Clear any pending auto-save
    if (dashboardTitleTimeoutRef.current) {
      clearTimeout(dashboardTitleTimeoutRef.current);
    }
    setEditingTitleId(null);
    setEditingTitle('');
  };

  // Click outside handler for title editing
  const titleInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingTitleId && titleInputRef.current && !titleInputRef.current.contains(event.target as Node)) {
        handleCancelTitleEdit();
      }
    };

    if (editingTitleId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingTitleId]);

  // Auto-save report title with debouncing
  const autoSaveReportTitle = useCallback(async (dashboardId: string, reportId: string, titleText: string) => {
    if (!titleText.trim()) {
      return; // Don't save empty titles
    }

    try {
      // Update the database with the new title text
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      const updatedData = {
        text: titleText.trim(),
        isTitle: true
      };

      const { error } = await supabase
        .from('kpi_dashboard_reports')
        .update({ report_data: updatedData })
        .eq('id', reportId);

      if (error) {
        console.error('Error updating report title in database:', error);
        throw error;
      }

      // Update the report data in the dashboard state
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(report => {
              if (report.id === reportId) {
                return {
                  ...report,
                  data: updatedData
                };
              }
              return report;
            })
          };
        }
        return dashboard;
      }));
    } catch (error) {
      console.error('Error updating report title:', error);
      toast.error('Failed to update title: ' + (error as Error).message);
    }
  }, []);

  const handleReportTitleChange = (dashboardId: string, reportId: string, newText: string) => {
    // Update local state immediately
    setReportTitleTexts(prev => ({
      ...prev,
      [reportId]: newText
    }));
    
    if (isEditMode) {
      // In edit mode, just update pending changes
      setPendingReportTitles(prev => ({
        ...prev,
        [reportId]: newText
      }));
    } else {
      // Not in edit mode, use auto-save as before
      // Clear existing timeout
      if (reportTitleTimeoutRef.current) {
        clearTimeout(reportTitleTimeoutRef.current);
      }
      
      // Set new timeout for auto-save (500ms delay)
      reportTitleTimeoutRef.current = setTimeout(() => {
        autoSaveReportTitle(dashboardId, reportId, newText);
      }, 500);
    }
  };

  // Real-time save function with debouncing
  const saveLayoutToDatabase = async (layout: any[], dashboardId: string) => {
    try {
      const dashboard = dashboards.find(d => d.id === dashboardId);
      if (!dashboard?.reports) {
        console.warn(`Dashboard ${dashboardId} not found or has no reports`);
        return;
      }

      const savePromises: Promise<any>[] = [];

      // FORCE SAVE: Always save all reports in the layout, regardless of change detection
      layout.forEach(layoutItem => {
        const report = dashboard.reports?.find(r => r.id === layoutItem.i);
        if (report) {
          // CRITICAL: With preset sizes, w/h MUST match the preset size dimensions
          // Get the correct dimensions for this report's size
          const reportSize = report.size || getDefaultSize();
          const presetDimensions = getSizeDimensions(reportSize, currentViewType);
          
          // Get the new layout values (rounded to integers)
          // x and y come from the grid (user can move reports)
          // w and h MUST come from preset dimensions (not from grid, which might be wrong)
          const newLayout = {
            x: Math.round(layoutItem.x),
            y: Math.round(layoutItem.y),
            w: presetDimensions.w, // ALWAYS use preset size width
            h: presetDimensions.h  // ALWAYS use preset size height
          };

          console.log(`Force saving layout for report ${report.id} (${report.title}):`, {
            gridProvided: { x: Math.round(layoutItem.x), y: Math.round(layoutItem.y), w: Math.round(layoutItem.w), h: Math.round(layoutItem.h) },
            presetSize: reportSize,
            presetDimensions: presetDimensions,
            finalSaved: newLayout
            });
            
            savePromises.push(
              updateDashboardReportLayout(report.id, {
                layout_x: newLayout.x,
                layout_y: newLayout.y,
                layout_w: newLayout.w,
                layout_h: newLayout.h
              }).then(result => {
              // Verify the save actually worked by checking the returned data
              if (result && result.layout_x === newLayout.x && result.layout_y === newLayout.y && 
                  result.layout_w === newLayout.w && result.layout_h === newLayout.h) {
                console.log(`✅ Verified save for report ${report.id} (${report.title}):`, {
                  saved: { x: result.layout_x, y: result.layout_y, w: result.layout_w, h: result.layout_h },
                  expected: newLayout
                });
              } else {
                console.warn(`⚠️ Save verification failed for report ${report.id}:`, {
                  returned: result,
                  expected: newLayout
                });
              }
                return result;
              }).catch(error => {
              console.error(`❌ Error saving layout for report ${report.id} (${report.title}):`, error);
              toast.error(`Failed to save position for ${report.title}`);
                throw error;
              })
            );
        } else {
          console.warn(`Report ${layoutItem.i} found in layout but not in dashboard reports`);
        }
      });

      if (savePromises.length > 0) {
        await Promise.all(savePromises);
        console.log(`Force saved ${savePromises.length} layout change(s) for dashboard ${dashboardId}`);
      } else {
        console.log(`No reports to save for dashboard ${dashboardId}`);
      }
    } catch (error) {
      console.error('Error saving layout changes:', error);
      toast.error('Failed to save layout changes');
    }
  };

  const handleLayoutChange = async (layout: any[], dashboardId: string) => {
    // CRITICAL: Always store the latest layout in ref (source of truth)
    latestLayoutsRef.current.set(dashboardId, layout);
    
    if (isEditMode) {
      // In edit mode, detect collisions and update pending changes
      detectCollisions(layout, dashboardId);
      
      setPendingLayouts(prev => ({
        ...prev,
        [dashboardId]: layout
      }));
      
      // Update local state for immediate visual feedback
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(report => {
              const layoutItem = layout.find(item => item.i === report.id);
              if (layoutItem) {
                // CRITICAL: Normalize w/h to preset dimensions
                const reportSize = report.size || getDefaultSize();
                const presetDimensions = getSizeDimensions(reportSize, currentViewType);
                
                return {
                  ...report,
                  layout: {
                    x: layoutItem.x, // Position from grid
                    y: layoutItem.y, // Position from grid
                    w: presetDimensions.w, // ALWAYS use preset size width
                    h: presetDimensions.h  // ALWAYS use preset size height
                  }
                };
              }
              return report;
            })
          };
        }
        return dashboard;
      }));

      // Store the latest layout for debounced save
      pendingLayoutSaves.current.set(dashboardId, { layout, dashboardId });

      // Clear existing timeout
      if (layoutSaveTimeoutRef.current) {
        clearTimeout(layoutSaveTimeoutRef.current);
      }

      // Debounce: Save to database after 500ms of no changes
      layoutSaveTimeoutRef.current = setTimeout(async () => {
        const savesToProcess = Array.from(pendingLayoutSaves.current.values());
        pendingLayoutSaves.current.clear();

        // Process all pending saves
        for (const { layout: latestLayout, dashboardId: id } of savesToProcess) {
          await saveLayoutToDatabase(latestLayout, id);
        }
      }, 500);
    } else {
      // Not in edit mode, save immediately - FORCE SAVE all reports
      try {
        const dashboard = dashboards.find(d => d.id === dashboardId);
        if (!dashboard?.reports) return;

        // FORCE SAVE: Always save all reports in the layout, regardless of change detection
        const updatePromises = dashboard.reports.map(async (report) => {
          const layoutItem = layout.find(item => item.i === report.id);
          if (layoutItem) {
            // CRITICAL: With preset sizes, w/h MUST match the preset size dimensions
            const reportSize = report.size || getDefaultSize();
            const presetDimensions = getSizeDimensions(reportSize, currentViewType);
            
            // Always save - force save regardless of comparison
            // x and y come from grid, w and h come from preset dimensions
            return updateDashboardReportLayout(report.id, {
              layout_x: Math.round(layoutItem.x),
              layout_y: Math.round(layoutItem.y),
              layout_w: presetDimensions.w, // ALWAYS use preset size width
              layout_h: presetDimensions.h  // ALWAYS use preset size height
            });
          }
        });

        await Promise.all(updatePromises.filter((p): p is Promise<any> => p !== undefined));

        // Update local state
        setDashboards(prev => prev.map(dashboard => {
          if (dashboard.id === dashboardId) {
            return {
              ...dashboard,
              reports: dashboard.reports?.map(report => {
                const layoutItem = layout.find(item => item.i === report.id);
                if (layoutItem) {
                  // CRITICAL: Normalize w/h to preset dimensions
                  const reportSize = report.size || getDefaultSize();
                  const presetDimensions = getSizeDimensions(reportSize, currentViewType);
                  
                  return {
                    ...report,
                    layout: {
                      x: layoutItem.x, // Position from grid
                      y: layoutItem.y, // Position from grid
                      w: presetDimensions.w, // ALWAYS use preset size width
                      h: presetDimensions.h  // ALWAYS use preset size height
                    }
                  };
                }
                return report;
              })
            };
          }
          return dashboard;
        }));
      } catch (error) {
        console.error('Error updating layout:', error);
        toast.error('Failed to save layout changes');
      }
    }
  };

  // Save all pending changes
  const handleSaveChanges = async () => {
    // Check for overlapping reports before saving
    if (overlappingReports.size > 0) {
      toast.error('Cannot save: Some reports are overlapping. Please resize or move them to fix the red borders.');
      return;
    }
    
    try {
      const savePromises: Promise<any>[] = [];
      
      // Save dashboard title changes
      Object.entries(pendingDashboardTitles).forEach(([dashboardId, title]) => {
        if (title !== originalState.dashboardTitles[dashboardId]) {
          savePromises.push(updateKPIDashboard(dashboardId, { title: title.trim() }));
        }
      });
      
      // Save report title changes
      Object.entries(pendingReportTitles).forEach(([reportId, text]) => {
        if (text !== originalState.reportTitles[reportId]) {
          const updatedData = {
            text: text.trim(),
            isTitle: true
          };
          savePromises.push(
            (async () => {
              const { error } = await supabase.from('kpi_dashboard_reports').update({ report_data: updatedData }).eq('id', reportId);
              if (error) throw error;
            })()
          );
        }
      });
      
      // Save page_route_analytics route selections and click_analytics click selections
      // Check all dashboards for these reports and save their selections
      // Note: product_sales_by_category doesn't need to save expanded state (it's just UI state)
      dashboards.forEach(dashboard => {
        dashboard.reports?.forEach(report => {
          if (report.type === 'page_route_analytics') {
            // Get the selected route from pageRouteSelections state (most current) or report.data.config (fallback)
            const selectedRoute = pageRouteSelections[report.id] || report.data?.config?.selectedRoute;
            
            console.log('🔍 Checking page_route_analytics report for save:', {
              reportId: report.id,
              pageRouteSelection: pageRouteSelections[report.id],
              reportDataConfig: report.data?.config?.selectedRoute,
              fullReportData: report.data,
              finalSelectedRoute: selectedRoute
            });
            
            if (selectedRoute) {
              // Build the report data to save - always include config with selectedRoute
              // Start fresh to ensure we have the right structure
              const reportDataToSave: any = {
                config: {
                  selectedRoute: selectedRoute
                }
              };
              
              // Preserve existing data if it exists (routeData, value, label)
              if (report.data) {
                if (report.data.routeData) {
                  reportDataToSave.routeData = report.data.routeData;
                }
                if (report.data.value !== undefined) {
                  reportDataToSave.value = report.data.value;
                }
                if (report.data.label) {
                  reportDataToSave.label = report.data.label;
                }
              }
              
              savePromises.push(
                (async () => {
                  console.log('💾 Saving page_route_analytics config for report:', report.id);
                  console.log('   Route:', selectedRoute);
                  console.log('   Data to save:', JSON.stringify(reportDataToSave, null, 2));
                  const { data: updatedReport, error } = await supabase
                    .from('kpi_dashboard_reports')
                    .update({ report_data: reportDataToSave })
                    .eq('id', report.id)
                    .select('id, report_data')
                    .single();
                  if (error) {
                    console.error('❌ Error saving page_route_analytics:', error);
                    throw error;
                  }
                  console.log('✅ Saved page_route_analytics config successfully');
                  console.log('   Database now has:', JSON.stringify(updatedReport?.report_data, null, 2));
                })()
              );
            } else {
              console.log('⚠️ No route selected for page_route_analytics report:', report.id, '- skipping save');
            }
          }
        });
      });
      
      // Ensure any pending layout saves are completed immediately
      // Layouts are saved in real-time as you drag, but we ensure completion here
      if (layoutSaveTimeoutRef.current) {
        clearTimeout(layoutSaveTimeoutRef.current);
        layoutSaveTimeoutRef.current = null;
      }

      // Process any pending layout saves immediately
      const savesToProcess = Array.from(pendingLayoutSaves.current.values());
      pendingLayoutSaves.current.clear();

      for (const { layout, dashboardId } of savesToProcess) {
        await saveLayoutToDatabase(layout, dashboardId);
      }

      // Also ensure any layouts in pendingLayouts are saved (fallback)
      for (const [dashboardId, layout] of Object.entries(pendingLayouts)) {
        await saveLayoutToDatabase(layout, dashboardId);
      }
      
      await Promise.all(savePromises);
      
      // Reload dashboards from database to ensure we have the latest saved positions
      // This ensures all saved layout changes are reflected correctly
      await loadDashboards();
      
      // Wait a moment for React state to update, then update originalState
      // We need to use a fresh read of dashboards state after the reload
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Re-read dashboards state to get the freshly loaded data
      // We'll update originalState in a useEffect that watches for the reload completion
      // For now, we'll update it here with a fresh state read
      setDashboards(currentDashboards => {
        const currentDashboardTitles: {[key: string]: string} = {};
        const currentReportTitles: {[key: string]: string} = {};
        const currentLayouts: {[key: string]: any[]} = {};
        const currentReportIds: {[key: string]: string[]} = {};
        
        // Get fresh data from the current dashboards state
        currentDashboards.forEach(dashboard => {
          currentDashboardTitles[dashboard.id] = dashboard.name;
          currentReportIds[dashboard.id] = dashboard.reports?.map(r => r.id) || [];
          currentLayouts[dashboard.id] = dashboard.reports?.map(report => ({
            i: report.id,
            x: report.layout.x,
            y: report.layout.y,
            w: report.layout.w,
            h: report.layout.h
          })) || [];
          
          dashboard.reports?.forEach(report => {
            currentReportTitles[report.id] = report.data?.text || '';
          });
        });
        
        setOriginalState({
          dashboardTitles: currentDashboardTitles,
          reportTitles: currentReportTitles,
          layouts: currentLayouts,
          reportIds: currentReportIds
        });
        
        return currentDashboards; // Return unchanged
      });
      
      // Clear pending changes
      setPendingDashboardTitles({});
      setPendingReportTitles({});
      setPendingLayouts({});
      setReportTitleTexts({});
      setOverlappingReports(new Set());
      
      // Exit edit mode
      setIsEditMode(false);
      setEditingTitleId(null);
      setEditingTitle('');
      
      toast.success('Changes saved successfully!');
      
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes');
    }
  };

  // Check if there are any pending changes
  const hasPendingChanges = () => {
    // Check dashboard titles
    const hasDashboardTitleChanges = Object.entries(pendingDashboardTitles).some(
      ([dashboardId, title]) => title !== originalState.dashboardTitles[dashboardId]
    );
    
    // Check report titles
    const hasReportTitleChanges = Object.entries(pendingReportTitles).some(
      ([reportId, text]) => text !== originalState.reportTitles[reportId]
    );
    
    // Check for added or removed reports
    const hasReportChanges = dashboards.some(dashboard => {
      const originalReportIds = originalState.reportIds[dashboard.id] || [];
      const currentReportIds = dashboard.reports?.map(r => r.id) || [];
      
      // Check if reports were added or removed
      if (originalReportIds.length !== currentReportIds.length) return true;
      
      // Check if any report IDs don't match
      const originalSet = new Set(originalReportIds);
      const currentSet = new Set(currentReportIds);
      if (originalSet.size !== currentSet.size) return true;
      
      for (const id of originalSet) {
        if (!currentSet.has(id)) return true;
      }
      
      return false;
    });
    
    // Check layout changes
    const hasLayoutChanges = Object.entries(pendingLayouts).some(([dashboardId, layout]) => {
      const originalLayout = originalState.layouts[dashboardId];
      const dashboard = dashboards.find(d => d.id === dashboardId);
      
      if (!dashboard?.reports || !originalLayout) {
        // If there are reports now but no original layout, or vice versa, it's a change
        if (dashboard?.reports && dashboard.reports.length > 0 && (!originalLayout || originalLayout.length === 0)) {
          return true;
        }
        return false;
      }
      
      return dashboard.reports.some(report => {
        const newLayoutItem = layout.find(item => item.i === report.id);
        const originalLayoutItem = originalLayout.find(item => item.i === report.id);
        
        // If report exists in new layout but not in original, it's a new report (layout change)
        if (newLayoutItem && !originalLayoutItem) return true;
        
        // If report exists in original but not in new, it was deleted
        if (!newLayoutItem && originalLayoutItem) return true;
        
        if (!newLayoutItem || !originalLayoutItem) return false;
        
        return (
          newLayoutItem.x !== originalLayoutItem.x ||
          newLayoutItem.y !== originalLayoutItem.y ||
          newLayoutItem.w !== originalLayoutItem.w ||
          newLayoutItem.h !== originalLayoutItem.h
        );
      });
    });
    
    return hasDashboardTitleChanges || hasReportTitleChanges || hasReportChanges || hasLayoutChanges;
  };

  // Cancel changes and revert to original state
  const handleCancelChanges = async () => {
    // CRITICAL: Save ALL pending changes before closing, including layouts
    try {
      const savePromises: Promise<any>[] = [];
      
      // Save dashboard title changes
      Object.entries(pendingDashboardTitles).forEach(([dashboardId, title]) => {
        if (title !== originalState.dashboardTitles[dashboardId]) {
          savePromises.push(updateKPIDashboard(dashboardId, { title: title.trim() }));
        }
      });
      
      // Save report title changes
      Object.entries(pendingReportTitles).forEach(([reportId, text]) => {
        if (text !== originalState.reportTitles[reportId]) {
          const updatedData = {
            text: text.trim(),
            isTitle: true
          };
          savePromises.push(
            (async () => {
              const { error } = await supabase.from('kpi_dashboard_reports').update({ report_data: updatedData }).eq('id', reportId);
              if (error) throw error;
            })()
          );
        }
      });
      
      // CRITICAL: Cancel any pending debounced saves and process immediately
    if (layoutSaveTimeoutRef.current) {
      clearTimeout(layoutSaveTimeoutRef.current);
      layoutSaveTimeoutRef.current = null;
    }

      // Save ALL layouts from pendingLayoutSaves.current (debounced queue)
    const savesToProcess = Array.from(pendingLayoutSaves.current.values());
    pendingLayoutSaves.current.clear();

    for (const { layout, dashboardId } of savesToProcess) {
      await saveLayoutToDatabase(layout, dashboardId);
      }

      // CRITICAL: Also save ALL layouts from pendingLayouts state (most current state)
      // This ensures we save even if debounced save hasn't fired yet
      for (const [dashboardId, layout] of Object.entries(pendingLayouts)) {
        if (layout && layout.length > 0) {
          console.log(`🔴 Force saving layout from pendingLayouts for dashboard ${dashboardId}:`, layout);
          await saveLayoutToDatabase(layout, dashboardId);
        }
      }

      // CRITICAL: Save from latestLayoutsRef (absolute latest from onLayoutChange)
      // This is the most reliable source - the actual layout that was last reported by GridLayout
      // Use this as the PRIMARY source since it's the actual latest layout from the grid component
      const layoutsToSave = new Map<string, any[]>();
      
      // Priority 1: Latest layout from ref (most current)
      for (const [dashboardId, layout] of latestLayoutsRef.current.entries()) {
        if (layout && layout.length > 0) {
          layoutsToSave.set(dashboardId, layout);
        }
      }
      
      // Priority 2: Fallback to pendingLayouts if ref doesn't have it
      for (const [dashboardId, layout] of Object.entries(pendingLayouts)) {
        if (layout && layout.length > 0 && !layoutsToSave.has(dashboardId)) {
          layoutsToSave.set(dashboardId, layout);
        }
      }
      
      // Priority 3: Fallback to dashboards state if neither ref nor pendingLayouts has it
      for (const dashboard of dashboards) {
        if (dashboard.reports && dashboard.reports.length > 0 && !layoutsToSave.has(dashboard.id)) {
          const currentLayout = dashboard.reports.map(report => ({
            i: report.id,
            x: report.layout.x,
            y: report.layout.y,
            w: report.layout.w,
            h: report.layout.h
          }));
          layoutsToSave.set(dashboard.id, currentLayout);
        }
      }
      
      // Save all unique layouts (deduplicated)
      for (const [dashboardId, layout] of layoutsToSave.entries()) {
        console.log(`🔴 Force saving layout for dashboard ${dashboardId}:`, layout);
        try {
          await saveLayoutToDatabase(layout, dashboardId);
          console.log(`✅ Successfully saved layout for dashboard ${dashboardId}`);
        } catch (error) {
          console.error(`❌ Failed to save layout for dashboard ${dashboardId}:`, error);
          throw error; // Re-throw to be caught by outer try-catch
        }
      }

      // Save title changes
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      console.log('✅ All changes saved before closing edit mode');
    } catch (error) {
      console.error('❌ Error saving changes before closing:', error);
      toast.error('Failed to save some changes. Please try saving again.');
      // Continue to close even if save fails
    }
    
    // Clear all pending changes
    setPendingDashboardTitles({});
    setPendingReportTitles({});
    setPendingLayouts({});
    setReportTitleTexts({});
    setOverlappingReports(new Set());
    
    // Clear the latest layouts ref
    latestLayoutsRef.current.clear();
    
    // Exit edit mode
    setIsEditMode(false);
    setEditingTitleId(null);
    setEditingTitle('');
    
    // CRITICAL: Reload dashboards to ensure UI reflects saved state from database
    // Wait a moment to ensure database has processed the updates
    await new Promise(resolve => setTimeout(resolve, 200));
    await loadDashboards();
    
    console.log('✅ Edit mode closed - all layouts saved and dashboards reloaded');
  };

  // Collision detection function
  const detectCollisions = useCallback((layout: any[], dashboardId: string) => {
    const overlapping = new Set<string>();
    
    // Check each report against all others
    for (let i = 0; i < layout.length; i++) {
      const reportA = layout[i];
      
      for (let j = i + 1; j < layout.length; j++) {
        const reportB = layout[j];
        
        // Check if reports overlap
        const aLeft = reportA.x;
        const aRight = reportA.x + reportA.w;
        const aTop = reportA.y;
        const aBottom = reportA.y + reportA.h;
        
        const bLeft = reportB.x;
        const bRight = reportB.x + reportB.w;
        const bTop = reportB.y;
        const bBottom = reportB.y + reportB.h;
        
        // Check for overlap (not just touching)
        const horizontalOverlap = aLeft < bRight && aRight > bLeft;
        const verticalOverlap = aTop < bBottom && aBottom > bTop;
        
        if (horizontalOverlap && verticalOverlap) {
          overlapping.add(reportA.i);
          overlapping.add(reportB.i);
        }
      }
    }
    
    setOverlappingReports(overlapping);
    return overlapping;
  }, []);

  // Smart responsive sizing functions
  const getResponsiveSize = (layout: { w: number; h: number }) => {
    const { w, h } = layout;
    const area = w * h;
    
    // Calculate actual pixel dimensions
    // For desktop: use actual viewport width (responsive) instead of fixed 1200px
    // This allows reports to compress as screen shrinks
    const desktopGridWidth = currentViewType === 'desktop' 
      ? Math.min(screenWidth - 32, 1200) // Responsive width with padding, max 1200px
      : 1200; // Fallback
    
    const actualWidth = currentViewType === 'mobile' 
      ? (w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (w / 100) * desktopGridWidth; // Desktop: responsive width based on viewport
    
    const actualHeight = currentViewType === 'mobile' 
      ? h * 8  // Mobile: 8px per row (consistent with desktop)
      : h * 8; // Desktop: 8px per row
    
    return {
      width: actualWidth,
      height: actualHeight,
      area: actualWidth * actualHeight
    };
  };
  
  // Get responsive font size using CSS clamp() based on actual rendered width
  const getResponsiveFontSize = (layout: { w: number; h: number }, minSize: number, maxSize: number, scaleFactor: number = 1) => {
    const { width } = getResponsiveSize(layout);
    // Scale font size based on width, with min/max bounds
    // scaleFactor allows fine-tuning (e.g., 0.8 for smaller text, 1.2 for larger)
    const baseSize = (width / 100) * scaleFactor;
    const clampedSize = Math.max(minSize, Math.min(maxSize, baseSize));
    return `${clampedSize}px`;
  };
  
  // Get responsive font size as CSS clamp() for better scaling
  const getClampedFontSize = (layout: { w: number; h: number }, minSize: number, preferredSize: number, maxSize: number) => {
    const { width } = getResponsiveSize(layout);
    // Calculate preferred size based on width (as percentage of base 1200px)
    const widthRatio = width / 1200;
    const calculatedPreferred = preferredSize * widthRatio;
    // Use clamp() for smooth scaling
    return `clamp(${minSize}px, ${calculatedPreferred}px, ${maxSize}px)`;
  };

  const getContentFontSize = (layout: { w: number; h: number }, contentLength: number = 10) => {
    const { width, height } = getResponsiveSize(layout);
    
    // Base size calculation on widget area and content length
    let baseSize = Math.sqrt(width * height) / 10; // Start with area-based sizing
    
    // Adjust for content length - longer content needs smaller font
    if (contentLength > 20) baseSize *= 0.8;
    else if (contentLength > 10) baseSize *= 0.9;
    
    // Apply view-specific constraints
    if (currentViewType === 'mobile') {
      baseSize = Math.max(8, Math.min(baseSize, 24)); // 8px to 24px range
    } else {
      baseSize = Math.max(10, Math.min(baseSize, 36)); // 10px to 36px range
    }
    
    // Convert to Tailwind classes
    if (baseSize <= 10) return { class: 'text-xs', size: 10 };
    if (baseSize <= 12) return { class: 'text-sm', size: 12 };
    if (baseSize <= 14) return { class: 'text-base', size: 14 };
    if (baseSize <= 16) return { class: 'text-lg', size: 16 };
    if (baseSize <= 18) return { class: 'text-xl', size: 18 };
    if (baseSize <= 20) return { class: 'text-2xl', size: 20 };
    if (baseSize <= 24) return { class: 'text-3xl', size: 24 };
    return { class: 'text-4xl', size: 28 };
  };

  const getTitleFontSize = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    let titleSize = Math.sqrt(width * height) / 8; // Slightly larger than content
    
    if (currentViewType === 'mobile') {
      titleSize = Math.max(8, Math.min(titleSize, 16));
    } else {
      titleSize = Math.max(10, Math.min(titleSize, 20));
    }
    
    if (titleSize <= 8) return { class: 'text-xs', size: 8 };
    if (titleSize <= 10) return { class: 'text-xs', size: 10 };
    if (titleSize <= 12) return { class: 'text-sm', size: 12 };
    if (titleSize <= 14) return { class: 'text-sm', size: 14 };
    if (titleSize <= 16) return { class: 'text-base', size: 16 };
    return { class: 'text-lg', size: 18 };
  };

  const getIconSize = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    let iconSize = Math.min(width, height) / 8; // Icon size based on smaller dimension
    
    if (currentViewType === 'mobile') {
      iconSize = Math.max(12, Math.min(iconSize, 24));
    } else {
      iconSize = Math.max(16, Math.min(iconSize, 32));
    }
    
    if (iconSize <= 12) return { class: 'w-3 h-3', size: 12 };
    if (iconSize <= 16) return { class: 'w-4 h-4', size: 16 };
    if (iconSize <= 20) return { class: 'w-5 h-5', size: 20 };
    if (iconSize <= 24) return { class: 'w-6 h-6', size: 24 };
    if (iconSize <= 28) return { class: 'w-7 h-7', size: 28 };
    return { class: 'w-8 h-8', size: 32 };
  };

  const getResponsivePadding = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    const minDimension = Math.min(width, height);
    
    if (minDimension < 80) return 'p-1';      // Very small widgets
    if (minDimension < 120) return 'p-2';     // Small widgets  
    if (minDimension < 200) return 'p-3';     // Medium widgets
    return 'p-4';                             // Large widgets
  };

  const getResponsiveSpacing = (layout: { w: number; h: number }) => {
    const { width, height } = getResponsiveSize(layout);
    const minDimension = Math.min(width, height);
    
    if (minDimension < 80) return 'space-x-1 space-y-1';
    if (minDimension < 120) return 'space-x-2 space-y-1';
    if (minDimension < 200) return 'space-x-2 space-y-2';
    return 'space-x-3 space-y-2';
  };

  const handleDeleteDashboard = async (dashboardId: string) => {
    try {
      await deleteKPIDashboard(dashboardId);
    setDashboards(dashboards.filter(d => d.id !== dashboardId));
      toast.success('Dashboard deleted successfully');
      setDashboardToDelete(null); // Close modal
      setShowUnsavedChangesWarning(false); // Close warning if open
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      toast.error('Failed to delete dashboard');
    }
  };

  const handleDeleteReport = async (dashboardId: string, reportId: string) => {
    try {
      await deleteDashboardReport(reportId);
      
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          const updatedReports = dashboard.reports?.filter(report => report.id !== reportId) || [];
          
          // Update pendingLayouts to reflect deleted report so save button highlights
          if (isEditMode) {
            setPendingLayouts(prevLayouts => {
              const newLayout = updatedReports.map(report => ({
                i: report.id,
                x: report.layout.x,
                y: report.layout.y,
                w: report.layout.w,
                h: report.layout.h
              }));
              return {
                ...prevLayouts,
                [dashboardId]: newLayout
              };
            });
          }
          
          return {
            ...dashboard,
            reports: updatedReports
          };
        }
        return dashboard;
      }));
      toast.success('Report removed from dashboard');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report');
    }
  };

  // Handle size change for a report
  const handleSizeChange = async (dashboardId: string, reportId: string, newSize: ReportSize) => {
    try {
      const report = dashboards.find(d => d.id === dashboardId)?.reports?.find(r => r.id === reportId);
      if (!report) return;

      // Prevent large/xl sizes for average_sale reports
      if (report.type === 'average_sale' && (newSize === 'large' || newSize === 'xl')) {
        toast.error('Average Sale reports only support Small and Medium sizes');
        return;
      }

      // Prevent large/xl sizes for other size system reports (only allow S/M)
      if (shouldUseSizeSystem(report.type) && (newSize === 'large' || newSize === 'xl')) {
        toast.error(`${report.title} reports only support Small and Medium sizes`);
        return;
      }

      // Check MLSizeSystem first (before ChartSizeSystem) since some reports are in both
      if (shouldUseMLSizeSystem(report.type)) {
        // M/L reports support medium, large, and xl
        if (newSize !== 'medium' && newSize !== 'large' && newSize !== 'xl') {
          toast.error(`${report.title} reports only support Medium, Large, and XL sizes`);
        return;
      }
      } else if (shouldUseMLXLSizeSystem(report.type)) {
        // M/L/XL reports support medium, large, and xl
        if (newSize !== 'medium' && newSize !== 'large' && newSize !== 'xl') {
          toast.error(`${report.title} reports only support Medium, Large, and XL sizes`);
        return;
      }
      } else if (shouldUseChartSizeSystem(report.type)) {
        // Chart reports support large and xl
        if (newSize !== 'large' && newSize !== 'xl') {
          toast.error(`${report.title} reports only support Large and XL sizes`);
          return;
        }
      }


      // Prevent small size for M/L/XL reports (only allow M/L/XL)
      if (shouldUseMLXLSizeSystem(report.type) && newSize === 'small') {
        toast.error(`${report.title} reports only support Medium, Large, and XL sizes`);
        return;
      }

      // Get new dimensions based on size
      const newDimensions = getSizeDimensions(newSize, currentViewType);
      
      // Update layout with new size and dimensions
      await updateDashboardReportLayout(reportId, {
        layout_x: report.layout.x,
        layout_y: report.layout.y,
        layout_w: newDimensions.w,
        layout_h: newDimensions.h,
        size: newSize
      });

      // Update local state
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            reports: dashboard.reports?.map(r => 
              r.id === reportId 
                ? { ...r, size: newSize, layout: { ...r.layout, w: newDimensions.w, h: newDimensions.h } }
                : r
            )
          };
        }
        return dashboard;
      }));

      toast.success(`Report size changed to ${newSize}`);
    } catch (error) {
      console.error('Error changing report size:', error);
      toast.error('Failed to change report size');
    }
  };

  const handleConfigureOppositeView = async (dashboardId: string, targetViewType: 'desktop' | 'mobile') => {
    try {
      setConfiguringView(true);
      
      const result = await configureOppositeView(dashboardId, targetViewType);
      
      // Update dashboard state with new configuration status
      setDashboards(prev => prev.map(dashboard => {
        if (dashboard.id === dashboardId) {
          return {
            ...dashboard,
            desktopConfigured: result.dashboard.desktop_configured,
            mobileConfigured: result.dashboard.mobile_configured
          };
        }
        return dashboard;
      }));
      
      // Reload the dashboard data to show the new reports
      await loadDashboards();
      
      toast.success(`${targetViewType === 'desktop' ? 'Desktop' : 'Mobile'} view configured successfully!`);
    } catch (error) {
      console.error('Error configuring opposite view:', error);
      toast.error(`Failed to configure ${targetViewType} view`);
    } finally {
      setConfiguringView(false);
    }
  };

  // Memoized mobile screen width for performance
  const mobileScreenWidth = useMemo(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 390; // Default to iPhone width
  }, [screenWidth]);

  // Dynamic responsive sizing function for maximum font size - memoized for performance
  const getDynamicFontSize = useCallback((layout: { w: number; h: number }, textContent: string = '', isTitle: boolean = false) => {
    // Calculate actual pixel dimensions - use full screen width for mobile
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding (4px each side)
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    const actualHeight = currentViewType === 'mobile' 
      ? layout.h * 8  // Mobile: 8px per row (consistent with desktop)
      : layout.h * 8; // Desktop: 8px per row
    
    // More aggressive space utilization to fill the card better
    const padding = currentViewType === 'mobile' ? 12 : 16; // Reduced padding
    const availableWidth = actualWidth - (padding * 2); // Left and right padding
    const availableHeight = actualHeight - (isTitle ? 15 : 25); // Less reserved space
    
    // More aggressive character width estimation for better space usage
    const avgCharWidth = isTitle ? 0.5 : 0.55; // Tighter estimation for more space usage
    const textLength = Math.max(textContent.length, 3); // Minimum length for calculation
    
    // Calculate font size based on available width (primary constraint)
    const widthBasedSize = (availableWidth * 0.95) / (textLength * avgCharWidth); // Use 95% of width
    
    // Calculate font size based on available height (secondary constraint)
    const heightBasedSize = availableHeight * 0.85; // Use 85% of available height
    
    // Use the smaller of the two to ensure it fits
    let optimalSize = Math.min(widthBasedSize, heightBasedSize);
    
    // Apply more generous constraints to better fill the space
    if (isTitle) {
      optimalSize = Math.max(12, Math.min(optimalSize, currentViewType === 'mobile' ? 32 : 42)); // Larger title sizes
    } else {
      optimalSize = Math.max(16, Math.min(optimalSize, currentViewType === 'mobile' ? 48 : 64)); // Much larger data sizes
    }
    
    // Add fallback for very long text - reduce size further if needed
    if (textContent.length > 15) {
      optimalSize *= 0.8; // Reduce by 20% for long text
    }
    if (textContent.length > 25) {
      optimalSize *= 0.8; // Reduce by another 20% for very long text
    }
    
    // Ensure minimum readable size
    optimalSize = Math.max(optimalSize, 10);
    
    return {
      fontSize: `${optimalSize}px`,
      lineHeight: '1.1',  // Slightly more line height for better readability
      fontWeight: isTitle ? '600' : '700',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Dynamic font size for labels (much smaller relative to data) - memoized for performance
  const getDynamicLabelSize = useCallback((layout: { w: number; h: number }) => {
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    // Label size is much smaller but still proportional - more generous calculation for mobile
    const labelSize = currentViewType === 'mobile' 
      ? Math.max(10, Math.min(actualWidth / 25, 16)) // Mobile: larger min and max, better ratio
      : Math.max(9, Math.min(actualWidth / 30, 14));  // Desktop: original calculation
    
    return {
      fontSize: `${labelSize}px`,
      lineHeight: '1.1',
      fontWeight: '400', // Even lighter for labels
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Dynamic font size for report titles (much more dynamic and prominent) - memoized for performance
  const getDynamicTitleSize = useCallback((layout: { w: number; h: number }) => {
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    const actualHeight = currentViewType === 'mobile' 
      ? layout.h * 8  // Mobile: 8px per row (consistent with desktop)
      : layout.h * 8; // Desktop: 8px per row
    
    // Title size should be proportional to both width and height but not too large
    // Base it on the smaller dimension to ensure it fits
    const baseDimension = Math.min(actualWidth, actualHeight);
    
    // Title takes roughly 15-25% of the smaller dimension
    let titleSize = baseDimension * 0.12; // Start with 12% of smaller dimension
    
    // Add width-based scaling for longer containers
    titleSize += actualWidth * 0.02; // Add 2% of width
    
    // Apply reasonable constraints based on view type - more generous for mobile
    if (currentViewType === 'mobile') {
      titleSize = Math.max(10, Math.min(titleSize, 24)); // 10px to 24px on mobile (increased max)
    } else {
      titleSize = Math.max(12, Math.min(titleSize, 28)); // 12px to 28px on desktop
    }
    
    return {
      fontSize: `${titleSize}px`,
      lineHeight: '1.2',
      fontWeight: '600',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Dynamic sizing for comparison indicators (percentage changes) - memoized for performance
  const getDynamicComparisonSize = useCallback((layout: { w: number; h: number }) => {
    const actualWidth = currentViewType === 'mobile' 
      ? (layout.w / 20) * (mobileScreenWidth - 8)  // Mobile: full screen width minus minimal padding
      : (layout.w / 100) * 1200; // Desktop: 1200px total width
    
    // Comparison size should be smaller than main data but larger than labels
    // Base the size on container width with reasonable constraints
    const fontSize = currentViewType === 'mobile' 
      ? Math.max(8, Math.min(actualWidth / 35, 12)) // Mobile: 8px to 12px
      : Math.max(10, Math.min(actualWidth / 40, 14)); // Desktop: 10px to 14px
    
    // Icon size should be proportional to font size
    const iconSize = Math.max(fontSize * 0.8, 8); // Icon slightly smaller than text, minimum 8px
    
    return {
      fontSize: `${fontSize}px`,
      iconSize: `${iconSize}px`,
      lineHeight: '1.2',
      fontWeight: '500',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [currentViewType, mobileScreenWidth]);

  // Show loading state with skeleton
  if (loading) {
    return <KPIDashboardSkeleton currentViewType={currentViewType} />;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-kpi-dashboard-page style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
      {/* Main Page Header - only show if no dashboards for current view and not on mobile */}
      {dashboards.filter(dashboard => {
        return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
      }).length === 0 && !loading && currentViewType === 'desktop' && (
        <div className="bg-white rounded-[16px] shadow-sm border border-gray-200/60 px-6 py-5 mb-6"
             style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)' }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-bold text-gray-900 text-2xl" style={{ letterSpacing: '-0.01em' }}>KPI Dashboard</h1>
            </div>
            <div className="flex items-center space-x-3">
              {!isEditMode && (
                <button
                  onClick={toggleEditMode}
                  className="p-2 rounded-[10px] flex items-center justify-center transition-all duration-150 active:scale-[0.97] text-gray-700 bg-white border border-gray-300 active:bg-gray-50"
                  style={{ 
                    minHeight: '36px',
                    minWidth: '36px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                  title="Edit"
                >
                  <Edit3 className={currentViewType === 'mobile' ? 'h-4 w-4' : 'h-5 w-5'} />
                </button>
              )}
              {isEditMode && (
                <>
                  <button 
                    onClick={handleCancelChanges}
                    className={`${currentViewType === 'mobile' ? 'p-2 min-h-[36px] min-w-[36px]' : 'px-5 py-2.5 text-[15px]'} font-semibold text-gray-700 bg-white border border-gray-300 rounded-[10px] active:bg-gray-50 flex items-center justify-center transition-all duration-150 active:scale-[0.97]`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em'
                    }}
                    title="Close"
                  >
                    {currentViewType === 'mobile' ? (
                      <X className="h-4 w-4" />
                    ) : (
                    <span>Close</span>
                    )}
                  </button>
                  <button 
                    onClick={() => toast('Please create a dashboard first, then click "Add Reports" on that dashboard')}
                    className={`${currentViewType === 'mobile' ? 'p-2 min-h-[36px] min-w-[36px]' : 'px-5 py-2.5 text-[15px]'} font-semibold text-white bg-blue-600 active:bg-blue-700 rounded-[10px] flex items-center justify-center transition-all duration-150 active:scale-[0.97] shadow-sm`}
                    title={`Add reports to ${currentViewType} view only`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    {currentViewType === 'mobile' ? (
                      <Plus className="h-4 w-4" />
                    ) : (
                  <span>Add Reports</span>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}


      <div className="space-y-6">
        {/* Render each dashboard - only show if configured for current view */}
        {dashboards.filter(dashboard => {
          // Only show dashboards that are configured for the current view type
          return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
        }).map((dashboard) => (
          <div key={dashboard.id} className="bg-white rounded-[16px] shadow-sm border border-gray-200/60"
               style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)' }}>
            {/* Dashboard Header */}
            <div className={`border-b border-gray-200/60 ${currentViewType === 'mobile' ? 'px-3 py-2.5' : 'px-5 py-3.5'}`}>
                              <div className="flex items-center justify-between">
                <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-1 min-w-0' : ''}`}>
                  <div className="flex items-center space-x-3">
                    {editingTitleId === dashboard.id ? (
                      <input
                        ref={titleInputRef}
                        type="text"
                        value={editingTitle}
                        onChange={(e) => handleTitleChange(dashboard.id, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Escape') {
                            handleCancelTitleEdit();
                          }
                        }}
                        className={`${currentViewType === 'mobile' ? 'text-lg max-w-[200px]' : 'text-xl'} font-bold text-gray-900 bg-white border border-gray-300 rounded-[10px] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        style={{ letterSpacing: '-0.01em' }}
                        autoFocus
                      />
                    ) : (
                      <div className="flex items-center space-x-2">
                        <h2 
                          className={`${currentViewType === 'mobile' ? 'text-lg truncate max-w-[200px]' : 'text-xl'} font-bold text-gray-900 ${
                            isEditMode ? 'cursor-pointer hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-[8px] transition-all' : ''
                          }`}
                          style={{ letterSpacing: '-0.01em' }}
                          onClick={() => {
                            if (isEditMode) {
                              handleStartTitleEdit(dashboard.id, pendingDashboardTitles[dashboard.id] || dashboard.name);
                            }
                          }}
                          title={isEditMode ? "Click to edit dashboard name" : ""}
                        >
                          {pendingDashboardTitles[dashboard.id] || dashboard.name}
                        </h2>
                      </div>
                    )}
                    
                  </div>
                </div>
                <div className={`flex items-center ${currentViewType === 'mobile' ? 'flex-wrap gap-1 ml-2' : 'space-x-3'}`}>
                  <DateRangeSelector
                    value={dashboard.dateRange || 'this_month'}
                    onChange={(newDateRange, customStartDate, customEndDate) => handleDateRangeChange(dashboard.id, newDateRange, customStartDate, customEndDate)}
                    defaultValue={dashboard.defaultDateRange}
                    onSetDefault={(newDefaultDateRange) => handleSetDefaultDateRange(dashboard.id, newDefaultDateRange)}
                    customStartDate={dashboard.customStartDate}
                    customEndDate={dashboard.customEndDate}
                  />
                  
                  {/* AI Analyzer Button */}
                  {!isEditMode && dashboard.reports && dashboard.reports.length > 0 && (
                    <button
                      onClick={() => {
                        setAIAnalyzerDashboardId(dashboard.id);
                        setIsAIAnalyzerOpen(true);
                      }}
                      className="p-2 rounded-[10px] flex items-center justify-center transition-all duration-150 active:scale-[0.97] text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-sm"
                      style={{ 
                        minHeight: '36px',
                        minWidth: '36px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                      title="AI Analyzer - Get insights and business advice"
                    >
                      <Sparkles className={currentViewType === 'mobile' ? 'h-4 w-4' : 'h-5 w-5'} />
                    </button>
                  )}

                  
                  {!isEditMode && (
                    <button
                      onClick={toggleEditMode}
                      className="p-2 rounded-[10px] flex items-center justify-center transition-all duration-150 active:scale-[0.97] text-gray-700 bg-white border border-gray-300 active:bg-gray-50"
                      style={{ 
                        minHeight: '36px',
                        minWidth: '36px',
                        WebkitTapHighlightColor: 'transparent',
                        letterSpacing: '-0.01em'
                      }}
                      title="Edit"
                    >
                      <Edit3 className={currentViewType === 'mobile' ? 'h-4 w-4' : 'h-5 w-5'} />
                    </button>
                  )}
                  {isEditMode && (
                    <>
                      <button
                        onClick={() => {
                          if (hasPendingChanges()) {
                            setShowUnsavedChangesWarning(true);
                            setDashboardToDelete(dashboard.id);
                          } else {
                            setDashboardToDelete(dashboard.id);
                          }
                        }}
                        className={`text-white bg-red-500 active:bg-red-600 ${currentViewType === 'mobile' ? 'p-1.5 min-h-[36px] min-w-[36px]' : 'p-2 min-h-[36px] min-w-[36px]'} rounded-[10px] flex items-center justify-center transition-all duration-150 active:scale-[0.97]`}
                        title="Delete Dashboard"
                        style={{ 
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      >
                        <Trash2 className={currentViewType === 'mobile' ? 'h-4 w-4' : 'h-4 w-4'} />
                      </button>
                      <button 
                        onClick={() => handleAddReportsClick(dashboard.id)}
                        className={`${currentViewType === 'mobile' ? 'p-2 min-h-[36px] min-w-[36px]' : 'px-4 py-2 text-[15px]'} font-semibold text-white bg-blue-600 active:bg-blue-700 rounded-[10px] flex items-center justify-center transition-all duration-150 active:scale-[0.97] shadow-sm`}
                        title={`Add reports to ${currentViewType} view only`}
                        style={{ 
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em'
                        }}
                      >
                        {currentViewType === 'mobile' ? (
                          <Plus className="h-4 w-4" />
                        ) : (
                          <span>Add Reports</span>
                        )}
                      </button>
                      <button 
                        onClick={handleCancelChanges}
                        className={`${currentViewType === 'mobile' ? 'p-2 min-h-[36px] min-w-[36px]' : 'px-4 py-2 text-[15px]'} font-semibold text-gray-700 bg-white border border-gray-300 rounded-[10px] active:bg-gray-50 flex items-center justify-center transition-all duration-150 active:scale-[0.97]`}
                        style={{ 
                          WebkitTapHighlightColor: 'transparent',
                          letterSpacing: '-0.01em'
                        }}
                        title="Close"
                      >
                        {currentViewType === 'mobile' ? (
                          <X className="h-4 w-4" />
                        ) : (
                          <span>Close</span>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Dashboard Canvas */}
            <div className={currentViewType === 'mobile' ? 'p-2' : 'p-4'}>
              <div
                className={`min-h-[500px] relative overflow-hidden rounded-[12px] bg-gray-50 ${
                  isEditMode 
                    ? 'border-2 border-dashed border-gray-300' 
                    : 'border border-gray-200/60'
                }`}
                style={isEditMode ? {
                  backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                } : {
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                }}
              >
                {/* Dashboard content rendering logic */}
                {(() => {
                  // Check if current view is configured
                  const isCurrentViewConfigured = currentViewType === 'desktop' 
                    ? dashboard.desktopConfigured 
                    : dashboard.mobileConfigured;
                  
                  // If current view is not configured, show empty state
                  if (!isCurrentViewConfigured) {
                    return (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <div className="mb-4">
                          {currentViewType === 'desktop' ? (
                            <Monitor className="h-16 w-16 mx-auto text-gray-300" />
                          ) : (
                            <Smartphone className="h-16 w-16 mx-auto text-gray-300" />
                          )}
                        </div>
                        <h3 className="text-lg font-medium mb-2">
                          {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} view not configured
                        </h3>
                        <p className="text-sm text-gray-400 mb-4">
                          This dashboard doesn't have a {currentViewType} layout configured yet.
                        </p>
                      </div>
                    );
                  }

                  return dashboard.reports && dashboard.reports.length > 0 ? (
                    /* Show Reports with Drag & Drop */
                    <div className={`${currentViewType === 'mobile' ? 'p-0' : 'p-0'}`}>
                      {/* Smart canvas with responsive grid */}
                      <div 
                        style={isEditMode ? {
                          minHeight: `${getGridHeight(dashboard)}px` // In edit mode: allow expansion with extra space
                        } : {
                          minHeight: `${getGridHeight(dashboard)}px` // Not in edit mode: minimum height to prevent cutoff
                        }}
                        className="w-full"
                      >
                        {/* Responsive font scaling for desktop mode - scales based on container width */}
                        {currentViewType === 'desktop' && (
                          <style>{`
                            /* Responsive font scaling for desktop reports */
                            .kpi-report-container {
                              --container-width: ${Math.min(screenWidth - 32, 1200)}px;
                              --scale-factor: calc(var(--container-width) / 1200);
                            }
                            
                            /* Scale font sizes based on container width */
                            .kpi-report-container .responsive-text-xs {
                              font-size: clamp(10px, calc(12px * var(--scale-factor)), 12px);
                            }
                            .kpi-report-container .responsive-text-sm {
                              font-size: clamp(11px, calc(14px * var(--scale-factor)), 14px);
                            }
                            .kpi-report-container .responsive-text-base {
                              font-size: clamp(12px, calc(16px * var(--scale-factor)), 16px);
                            }
                            .kpi-report-container .responsive-text-lg {
                              font-size: clamp(14px, calc(18px * var(--scale-factor)), 18px);
                            }
                            .kpi-report-container .responsive-text-xl {
                              font-size: clamp(16px, calc(20px * var(--scale-factor)), 20px);
                            }
                            .kpi-report-container .responsive-text-2xl {
                              font-size: clamp(18px, calc(24px * var(--scale-factor)), 24px);
                            }
                            .kpi-report-container .responsive-text-3xl {
                              font-size: clamp(20px, calc(30px * var(--scale-factor)), 30px);
                            }
                            .kpi-report-container .responsive-text-4xl {
                              font-size: clamp(24px, calc(36px * var(--scale-factor)), 36px);
                            }
                            
                            /* Scale padding based on container width */
                            .kpi-report-container .responsive-p-1 {
                              padding: clamp(2px, calc(4px * var(--scale-factor)), 4px);
                            }
                            .kpi-report-container .responsive-p-2 {
                              padding: clamp(4px, calc(8px * var(--scale-factor)), 8px);
                            }
                            .kpi-report-container .responsive-p-3 {
                              padding: clamp(6px, calc(12px * var(--scale-factor)), 12px);
                            }
                            .kpi-report-container .responsive-p-4 {
                              padding: clamp(8px, calc(16px * var(--scale-factor)), 16px);
                            }
                          `}</style>
                        )}
                        <div className={currentViewType === 'desktop' ? 'kpi-report-container' : ''}>
                        <GridLayout
                        className="layout"
                        data-edit-mode={isEditMode}
                        data-initial-load={!isInitialLoadComplete}
                        layout={dashboard.reports.map(report => {
                          // CRITICAL: Ensure w/h match preset dimensions (double-check)
                          const reportSize = report.size || getDefaultSize();
                          const presetDimensions = getSizeDimensions(reportSize, currentViewType);
                          
                          return {
                          i: report.id,
                          x: report.layout.x,
                          y: report.layout.y,
                            w: presetDimensions.w, // ALWAYS use preset size width
                            h: presetDimensions.h  // ALWAYS use preset size height
                          };
                        })}
                        cols={currentViewType === 'mobile' ? 20 : 100}  // Fixed grid: Mobile = 20 cols, Desktop = 100 cols
                        rowHeight={currentViewType === 'mobile' ? 8 : 8}  // Consistent row height: 8px for both mobile and desktop for better scaling
                        onLayoutChange={(layout: any[]) => handleLayoutChange(layout, dashboard.id)}
                        isDraggable={isEditMode}
                        isResizable={false}  // Disable resize - use size buttons instead
                        draggableCancel="button, [role='button']"
                        margin={[2, 2]}  // Smaller margins for finer positioning
                        containerPadding={isEditMode ? [4, 4] : [0, 0]}  // No padding when not in edit mode to eliminate extra space
                        resizeHandles={[]}  // No resize handles - use size buttons
                        autoSize={true} // Always auto-size to ensure all reports are visible
                        verticalCompact={false}
                        compactType={null}
                        useCSSTransforms={false}
                        preventCollision={false}  // Allow overlaps in edit mode
                        allowOverlap={true}  // Enable overlapping
                        width={currentViewType === 'mobile' ? mobileScreenWidth - 8 : Math.min(screenWidth - 32, 1200)} // Mobile: cached screen width minus minimal padding, Desktop: responsive width (max 1200px) for intelligent compression
                      >
                      {dashboard.reports.map((report) => (
                        <div key={report.id} className={`bg-white border rounded-[12px] ${
                          getResponsivePadding(report.layout)
                        } transition-all duration-200 h-full flex flex-col relative overflow-hidden ${
                          isEditMode 
                            ? overlappingReports.has(report.id)
                              ? 'border-red-500 shadow-lg cursor-move'
                              : 'border-blue-400 shadow-md hover:border-blue-500 cursor-move'
                            : 'border-gray-200/60'
                        }`}
                        style={{ 
                          boxShadow: isEditMode 
                            ? overlappingReports.has(report.id)
                              ? '0 4px 12px rgba(239, 68, 68, 0.15)'
                              : '0 2px 8px rgba(59, 130, 246, 0.12)'
                            : '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
                          transition: 'all 0.2s ease',
                          transform: 'none !important',
                          touchAction: isEditMode ? 'pan-y' : 'auto'
                        }}
                        onTouchStart={(e) => {
                          // Allow touch events on buttons to work
                          const target = e.target as HTMLElement;
                          if (target.closest('button') || target.closest('[role="button"]')) {
                            e.stopPropagation();
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isEditMode) {
                            e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.06)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isEditMode) {
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)';
                          }
                        }}>
                          {isEditMode && (
                            <>
                              {/* Size Selector Buttons - For size system reports and chart reports */}
                              {(shouldUseSizeSystem(report.type) || shouldUseChartSizeSystem(report.type) || shouldUseMLSizeSystem(report.type) || shouldUseMLXLSizeSystem(report.type)) && (
                                <div 
                                  className="absolute top-2 right-10 flex items-center gap-1 bg-white rounded-[10px] p-1 z-[9999] shadow-lg border border-gray-200/60"
                                  style={{ 
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                                    pointerEvents: 'all',
                                    touchAction: 'manipulation'
                                  }}
                                  onMouseDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                  onTouchMove={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}
                                  onPointerDown={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }}>
                                  {((shouldUseMLSizeSystem(report.type)
                                    ? ['medium', 'large', 'xl']
                                    : shouldUseChartSizeSystem(report.type)
                                    ? ['large', 'xl'] 
                                    : shouldUseMLXLSizeSystem(report.type)
                                    ? ['medium', 'large', 'xl']
                                    : shouldUseSizeSystem(report.type)
                                    ? ['small', 'medium'] 
                                    : ['small', 'medium', 'large', 'xl']) as ReportSize[]).map((size) => (
                                    <button
                                      key={size}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSizeChange(dashboard.id, report.id, size);
                                      }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      onTouchEnd={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSizeChange(dashboard.id, report.id, size);
                                      }}
                                      onTouchMove={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      className={`px-2.5 py-1 text-xs font-semibold rounded-[8px] transition-all duration-150 active:scale-[0.95] ${
                                        (report.size || getDefaultSize()) === size
                                          ? 'bg-blue-600 text-white'
                                          : 'text-gray-600 active:bg-gray-100'
                                      }`}
                                      style={{ 
                                        pointerEvents: 'all',
                                        touchAction: 'manipulation',
                                        WebkitTapHighlightColor: 'transparent',
                                        minWidth: '28px',
                                        minHeight: '24px',
                                        userSelect: 'none',
                                        WebkitUserSelect: 'none'
                                      }}
                                      onPointerDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      draggable={false}
                                      title={`${size.charAt(0).toUpperCase() + size.slice(1)} size`}
                                    >
                                      {size.charAt(0).toUpperCase()}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {/* Settings Button - only for website_visits_graph */}
                              {report.type === 'website_visits_graph' && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const config = report.data?.config?.conversionConfig;
                                    setConversionSettingsEnabled(config?.enabled || false);
                                    setConversionCriteria(config?.criteria || []);
                                    setConversionSettingsReportId(report.id);
                                    if (businessId && supabase) {
                                      const currentDashboard = dashboards.find(d => d.reports?.some(r => r.id === report.id));
                                      const dr = currentDashboard?.dateRange || 'this_month';
                                      const { startDate, endDate } = getDateRange(dr as DateRangeOption);
                                      const startUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0)).toISOString();
                                      const endUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59)).toISOString();
                                      const [{ data: routes }, { data: clicks }] = await Promise.all([
                                        supabase.rpc('get_conversion_routes', { p_business_id: businessId, p_start_date: startUTC, p_end_date: endUTC }),
                                        supabase.rpc('get_conversion_clicks', { p_business_id: businessId, p_start_date: startUTC, p_end_date: endUTC })
                                      ]);
                                      setConversionSettingsRoutes(routes || []);
                                      setConversionSettingsClicks(clicks || []);
                                    }
                                  }}
                                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                  onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                  onTouchEnd={async (e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    const config = report.data?.config?.conversionConfig;
                                    setConversionSettingsEnabled(config?.enabled || false);
                                    setConversionCriteria(config?.criteria || []);
                                    setConversionSettingsReportId(report.id);
                                    if (businessId && supabase) {
                                      const currentDashboard = dashboards.find(d => d.reports?.some(r => r.id === report.id));
                                      const dr = currentDashboard?.dateRange || 'this_month';
                                      const { startDate, endDate } = getDateRange(dr as DateRangeOption);
                                      const startUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0)).toISOString();
                                      const endUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59)).toISOString();
                                      const [{ data: routes }, { data: clicks }] = await Promise.all([
                                        supabase.rpc('get_conversion_routes', { p_business_id: businessId, p_start_date: startUTC, p_end_date: endUTC }),
                                        supabase.rpc('get_conversion_clicks', { p_business_id: businessId, p_start_date: startUTC, p_end_date: endUTC })
                                      ]);
                                      setConversionSettingsRoutes(routes || []);
                                      setConversionSettingsClicks(clicks || []);
                                    }
                                  }}
                                  onTouchMove={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                  className="absolute top-2 right-[7.5rem] text-gray-500 active:text-blue-600 p-1.5 rounded-full active:bg-blue-50 z-[9999] bg-white border border-gray-200/60 transition-all duration-150 active:scale-[0.95]"
                                  style={{
                                    pointerEvents: 'all',
                                    touchAction: 'manipulation',
                                    WebkitTapHighlightColor: 'transparent',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    minWidth: '32px',
                                    minHeight: '32px',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none'
                                  }}
                                  onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                  draggable={false}
                                  title="Conversion Rate Settings"
                                >
                                  <Settings className="h-4 w-4" />
                                </button>
                              )}
                              {/* Delete Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDeleteReport(dashboard.id, report.id);
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              onTouchEnd={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDeleteReport(dashboard.id, report.id);
                              }}
                              onTouchMove={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                                className="absolute top-2 right-2 text-gray-400 active:text-red-600 p-1.5 rounded-full active:bg-red-50 z-[9999] bg-white border border-gray-200/60 transition-all duration-150 active:scale-[0.95]"
                              style={{ 
                                pointerEvents: 'all',
                                  touchAction: 'manipulation',
                                  WebkitTapHighlightColor: 'transparent',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                  minWidth: '32px',
                                  minHeight: '32px',
                                  userSelect: 'none',
                                  WebkitUserSelect: 'none'
                              }}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              draggable={false}
                            >
                              <X className="h-4 w-4" />
                            </button>
                            </>
                          )}
                          
                          {/* Title Element Rendering */}
                          {report.type === 'title' ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-center">
                              {isEditMode ? (
                                <input
                                  type="text"
                                  value={reportTitleTexts[report.id] ?? pendingReportTitles[report.id] ?? report.data?.text ?? 'Dashboard Title'}
                                  onChange={(e) => handleReportTitleChange(dashboard.id, report.id, e.target.value)}
                                  className="font-bold text-gray-900 bg-white/80 border border-white/30 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-center w-full backdrop-blur-sm transition-all"
                                  style={{
                                    ...getDynamicFontSize(report.layout, reportTitleTexts[report.id] ?? report.data?.text ?? 'Dashboard Title', true),
                                    backdropFilter: 'blur(10px)', 
                                    WebkitBackdropFilter: 'blur(10px)'
                                  }}
                                />
                              ) : (
                                <div 
                                  className="font-bold text-gray-900 transition-all duration-200 text-center w-full px-2 overflow-hidden"
                                  style={getDynamicFontSize(report.layout, pendingReportTitles[report.id] || report.data?.text || 'Dashboard Title', true)}
                                >
                                  <div className="line-clamp-2">
                                    {pendingReportTitles[report.id] || report.data?.text || 'Dashboard Title'}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : report.type === 'stock_alert' && shouldUseMLXLSizeSystem(report.type) && ((report.size || 'medium') === 'medium' || (report.size || 'medium') === 'large' || (report.size || 'medium') === 'xl') ? (
                            /* Stock Alert - M/L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'xl' ? '16px' : (report.size || 'medium') === 'large' ? '14px' : '12px' }}>
                              <div className={`flex items-center justify-between ${(report.size || 'medium') === 'xl' ? 'mb-4' : (report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'} flex-shrink-0`}>
                                <div className="flex items-center">
                                  <span 
                                    className={`${(report.size || 'medium') === 'xl' ? 'text-xl' : (report.size || 'medium') === 'large' ? 'text-lg' : 'text-base'} font-medium text-gray-600 truncate`}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className={`flex-1 flex flex-col items-center justify-center text-center ${(report.size || 'medium') === 'xl' ? 'mb-4' : (report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>
                                <div className={`${(report.size || 'medium') === 'xl' ? 'text-4xl' : (report.size || 'medium') === 'large' ? 'text-3xl' : 'text-2xl'} font-bold text-gray-900 ${(report.size || 'medium') === 'xl' ? 'mb-2' : 'mb-1'}`}>
                                  {report.data?.value}
                                </div>
                                <div className={`${(report.size || 'medium') === 'xl' ? 'text-base' : (report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500 text-center leading-tight`}>
                                  {report.data?.label}
                                </div>
                              </div>
                              {/* Supplier Orders List */}
                              <div className="flex-1 mt-2 min-h-0 overflow-hidden" style={{ minHeight: 0 }}>
                                {supplierOrders.length === 0 ? (
                                  <div className="text-center py-4">
                                    <Package2 className={`${(report.size || 'medium') === 'xl' ? 'h-12 w-12' : (report.size || 'medium') === 'large' ? 'h-10 w-10' : 'h-8 w-8'} text-gray-300 mx-auto ${(report.size || 'medium') === 'xl' ? 'mb-3' : 'mb-2'}`} />
                                    <p className={`${(report.size || 'medium') === 'xl' ? 'text-sm' : (report.size || 'medium') === 'large' ? 'text-xs' : 'text-xs'} text-gray-500`}>No suppliers need orders</p>
                                  </div>
                                ) : (
                                  <div className={`${(report.size || 'medium') === 'xl' ? 'max-h-60' : (report.size || 'medium') === 'large' ? 'max-h-48' : 'max-h-40'} overflow-y-auto ${(report.size || 'medium') === 'xl' ? 'space-y-3' : 'space-y-2'}`}>
                                    {supplierOrders.map((order) => (
                                      <div 
                                        key={order.supplier.id} 
                                        className={`border border-gray-200 rounded-lg ${(report.size || 'medium') === 'xl' ? 'p-4' : (report.size || 'medium') === 'large' ? 'p-3.5' : 'p-3'} bg-gray-50`}
                                      >
                                        <div className={`flex items-center justify-between ${(report.size || 'medium') === 'xl' ? 'mb-3' : 'mb-2'}`}>
                                          <div className="flex-1 min-w-0">
                                            <h5 className={`${(report.size || 'medium') === 'xl' ? 'text-sm' : (report.size || 'medium') === 'large' ? 'text-xs' : 'text-xs'} font-medium text-gray-900 truncate`}>
                                              {order.supplier.first_name} {order.supplier.last_name}
                                            </h5>
                                            <p className={`${(report.size || 'medium') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500`}>
                                              {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} • ${order.totalCost.toFixed(2)}
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePlaceOrder(order.supplier.id);
                                            }}
                                            className={`ml-2 ${(report.size || 'medium') === 'xl' ? 'px-3 py-1.5' : (report.size || 'medium') === 'large' ? 'px-2.5 py-1' : 'px-2 py-1'} ${(report.size || 'medium') === 'xl' ? 'text-sm' : 'text-xs'} font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0`}
                                          >
                                            <ShoppingCart className={`${(report.size || 'medium') === 'xl' ? 'h-4 w-4' : 'h-3 w-3'} inline-block ${(report.size || 'medium') === 'xl' ? 'mr-1.5' : 'mr-1'}`} />
                                            Order
                                          </button>
                                        </div>
                                        <div className={`${(report.size || 'medium') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                          {order.items.slice(0, (report.size || 'medium') === 'xl' ? 3 : 2).map((item) => (
                                            <div key={item.item.id} className={`flex justify-between ${(report.size || 'medium') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-600`}>
                                              <span className="truncate">{item.item.name}</span>
                                              <span>{item.quantityToOrder} {item.item.unit}</span>
                                            </div>
                                          ))}
                                          {order.items.length > ((report.size || 'medium') === 'xl' ? 3 : 2) && (
                                            <div className={`${(report.size || 'medium') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500`}>
                                              +{order.items.length - ((report.size || 'medium') === 'xl' ? 3 : 2)} more item{order.items.length - ((report.size || 'medium') === 'xl' ? 3 : 2) !== 1 ? 's' : ''}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'stock_alert' ? (
                            /* Stock Alert - Fallback for non-M/L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center text-center">
                                <div 
                                  className="font-bold text-gray-900 mb-1"
                                  style={getDynamicFontSize(report.layout, String(report.data?.value || '0'))}
                                >
                                  {report.data?.value}
                                </div>
                                <div 
                                  className="text-gray-500 text-center leading-tight"
                                  style={getDynamicLabelSize(report.layout)}
                                >
                                  {report.data?.label}
                                </div>
                              </div>
                              {/* Supplier Orders List */}
                              <div className="flex-1 mt-2">
                                {supplierOrders.length === 0 ? (
                                  <div className="text-center py-4">
                                    <Package2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                                    <p className="text-xs text-gray-500">No suppliers need orders</p>
                                  </div>
                                ) : (
                                  <div className="max-h-40 overflow-y-auto space-y-2">
                                    {supplierOrders.map((order) => (
                                      <div 
                                        key={order.supplier.id} 
                                        className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex-1 min-w-0">
                                            <h5 className="text-xs font-medium text-gray-900 truncate">
                                              {order.supplier.first_name} {order.supplier.last_name}
                                            </h5>
                                            <p className="text-xs text-gray-500">
                                              {order.itemCount} item{order.itemCount !== 1 ? 's' : ''} • ${order.totalCost.toFixed(2)}
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePlaceOrder(order.supplier.id);
                                            }}
                                            className="ml-2 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                                          >
                                            <ShoppingCart className="h-3 w-3 inline-block mr-1" />
                                            Order
                                          </button>
                                        </div>
                                        <div className="space-y-1">
                                          {order.items.slice(0, 2).map((item) => (
                                            <div key={item.item.id} className="flex justify-between text-xs text-gray-600">
                                              <span className="truncate">{item.item.name}</span>
                                              <span>{item.quantityToOrder} {item.item.unit}</span>
                                            </div>
                                          ))}
                                          {order.items.length > 2 && (
                                            <div className="text-xs text-gray-500">
                                              +{order.items.length - 2} more item{order.items.length - 2 !== 1 ? 's' : ''}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'net_profit' && shouldUseMLXLSizeSystem(report.type) && ((report.size || 'medium') === 'medium' || (report.size || 'medium') === 'large' || (report.size || 'medium') === 'xl') ? (
                            /* Net Profit - M/L/XL Size Template */
                            <NetProfitReportContent 
                              report={report}
                              businessId={businessId}
                              dashboards={dashboards}
                              fetchNetProfitBreakdownData={fetchNetProfitBreakdownData}
                            />
                          ) : report.type === 'net_profit' ? (
                            /* Net Profit - Fallback for non-M/L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center text-center">
                                <div 
                                  className="font-bold text-gray-900 mb-1"
                                  style={getDynamicFontSize(report.layout, String(report.data?.value || '0'))}
                                >
                                  {report.data?.value}
                                </div>
                                <div 
                                  className="text-gray-500 text-center leading-tight"
                                  style={getDynamicLabelSize(report.layout)}
                                >
                                  {report.data?.label}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'crm_lead_lifecycle' ? (
                            /* Lead Lifecycle — avg days from creation to close, won vs lost */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'} flex-shrink-0`}>
                                <span className={`${(report.size || 'medium') === 'large' ? 'text-lg' : 'text-base'} font-semibold text-gray-600 truncate`}>
                                  {report.title}
                                </span>
                              </div>
                              <div className={`text-left ${(report.size || 'medium') === 'large' ? 'mb-2' : 'mb-1.5'} flex-shrink-0`}>
                                <div className={`${(report.size || 'medium') === 'large' ? 'text-3xl' : 'text-2xl'} font-bold text-gray-900`}>
                                  {report.data?.avgCloseLabel || '—'}
                                </div>
                                <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500 mt-0.5`}>
                                  Avg time to close ({(report.data?.wonCount ?? 0) + (report.data?.lostCount ?? 0)} closed)
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col justify-center min-h-0" style={{ minHeight: 0 }}>
                                <div className={`${(report.size || 'medium') === 'large' ? 'text-base' : 'text-sm'}`}>
                                  <div className={`flex items-center justify-between ${(report.size || 'medium') === 'large' ? 'py-2' : 'py-1.5'} border-b border-gray-100`}>
                                    <span className="font-medium text-gray-600">New leads</span>
                                    <span className="font-bold text-gray-900">
                                      {report.data?.newLeads ?? 0}
                                      <span className="font-medium text-gray-400"> · {report.data?.openCount ?? 0} still open</span>
                                    </span>
                                  </div>
                                  <div className={`flex items-center justify-between ${(report.size || 'medium') === 'large' ? 'py-2' : 'py-1.5'} border-b border-gray-100`}>
                                    <span className="font-medium text-gray-600">Created → Won</span>
                                    <span className="font-bold text-emerald-600">
                                      {report.data?.avgWonLabel || '—'}
                                      <span className="font-medium text-gray-400"> · {report.data?.wonCount ?? 0} won</span>
                                    </span>
                                  </div>
                                  <div className={`flex items-center justify-between ${(report.size || 'medium') === 'large' ? 'py-2' : 'py-1.5'}`}>
                                    <span className="font-medium text-gray-600">Created → Lost</span>
                                    <span className="font-bold text-red-600">
                                      {report.data?.avgLostLabel || '—'}
                                      <span className="font-medium text-gray-400"> · {report.data?.lostCount ?? 0} lost</span>
                                    </span>
                                  </div>
                                </div>
                                {(report.data?.untimedClosed ?? 0) > 0 && (
                                  <p className={`${(report.size || 'medium') === 'large' ? 'text-xs mt-2' : 'text-[10px] mt-1.5'} text-gray-400 text-center flex-shrink-0`}>
                                    {report.data.untimedClosed} closed lead{report.data.untimedClosed === 1 ? ' has' : 's have'} no logged close date — excluded from averages
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'wallet_overview' ? (
                            /* Wallet Overview — current liability + added/used in period */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className={`${(report.size || 'medium') === 'large' ? 'text-lg' : 'text-base'} font-semibold text-gray-600 truncate`}>
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col justify-center min-h-0" style={{ minHeight: 0 }}>
                                {/* Simple line rows — label left, amount right */}
                                <div className={`${(report.size || 'medium') === 'large' ? 'text-base' : 'text-sm'}`}>
                                  <div className={`flex items-center justify-between ${(report.size || 'medium') === 'large' ? 'py-2.5' : 'py-2'} border-b border-gray-100`}>
                                    <span className="font-medium text-gray-600">In Wallets</span>
                                    <span className={`font-bold text-indigo-600 ${(report.size || 'medium') === 'large' ? 'text-xl' : 'text-lg'}`}>
                                      {formatCurrency(report.data?.liability || 0, false)}
                                    </span>
                                  </div>
                                  <div className={`flex items-center justify-between ${(report.size || 'medium') === 'large' ? 'py-2.5' : 'py-2'} border-b border-gray-100`}>
                                    <span className="font-medium text-gray-600">Added</span>
                                    <span className={`font-bold text-emerald-600 ${(report.size || 'medium') === 'large' ? 'text-xl' : 'text-lg'}`}>
                                      +{formatCurrency(report.data?.added || 0, false)}
                                    </span>
                                  </div>
                                  <div className={`flex items-center justify-between ${(report.size || 'medium') === 'large' ? 'py-2.5' : 'py-2'}`}>
                                    <span className="font-medium text-gray-600">Used</span>
                                    <span className={`font-bold text-red-600 ${(report.size || 'medium') === 'large' ? 'text-xl' : 'text-lg'}`}>
                                      −{formatCurrency(report.data?.used || 0, false)}
                                    </span>
                                  </div>
                                </div>
                                <p className={`${(report.size || 'medium') === 'large' ? 'text-xs mt-3' : 'text-[10px] mt-2'} text-gray-400 text-center flex-shrink-0`}>
                                  {formatCurrency(report.data?.topups || 0, false)} of added was card top-ups · {report.data?.customersWithBalance ?? 0} customer{(report.data?.customersWithBalance ?? 0) === 1 ? '' : 's'} hold a balance
                                </p>
                              </div>
                            </div>
                          ) : report.type === 'active_customers' ? (
                            /* Active Customers — rule-based snapshots (active now, became active, went inactive) */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className={`${(report.size || 'medium') === 'large' ? 'text-lg' : 'text-base'} font-semibold text-gray-600 truncate`}>
                                  {report.title}
                                </span>
                              </div>
                              {report.data?.enabled === false ? (
                                <div className="flex-1 flex items-center justify-center px-3 text-center">
                                  <p className="text-xs text-gray-400">
                                    Turn on the Active Customer rule in the Customers app settings to use this report.
                                  </p>
                                </div>
                              ) : (
                                <div className="flex-1 flex flex-col items-center justify-center min-h-0" style={{ minHeight: 0 }}>
                                  <div className={`grid grid-cols-3 ${(report.size || 'medium') === 'large' ? 'gap-6' : 'gap-4'} w-full`}>
                                    {/* Active now (end of period) */}
                                    <div className={`text-center bg-emerald-50 rounded-lg ${(report.size || 'medium') === 'large' ? 'p-4' : 'p-3'} border border-emerald-200`}>
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-emerald-700 ${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>Active</div>
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-emerald-600`}>
                                        {report.data?.active ?? 0}
                                      </div>
                                    </div>
                                    {/* Became active during the period */}
                                    <div className={`text-center bg-gray-50 rounded-lg ${(report.size || 'medium') === 'large' ? 'p-4' : 'p-3'} border border-gray-200`}>
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-gray-600 ${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>Became Active</div>
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900`}>
                                        +{report.data?.becameActive ?? 0}
                                      </div>
                                    </div>
                                    {/* Went inactive during the period */}
                                    <div className={`text-center bg-red-50 rounded-lg ${(report.size || 'medium') === 'large' ? 'p-4' : 'p-3'} border border-red-200`}>
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-red-700 ${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>Went Inactive</div>
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-red-600`}>
                                        −{report.data?.wentInactive ?? 0}
                                      </div>
                                    </div>
                                  </div>
                                  <p className={`${(report.size || 'medium') === 'large' ? 'text-xs mt-3' : 'text-[10px] mt-2'} text-gray-400 text-center flex-shrink-0`}>
                                    {(() => {
                                      const net = report.data?.net ?? 0;
                                      const prev = report.data?.previousActive ?? 0;
                                      const netLabel = net > 0 ? `+${net}` : String(net);
                                      return `${netLabel} vs start of period (${prev}) · ${report.data?.windowDays ?? 45}-day rule`;
                                    })()}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : report.type === 'crm_leads_overview' && shouldUseMLSizeSystem(report.type) && ((report.size || 'medium') === 'medium' || (report.size || 'medium') === 'large') ? (
                            /* CRM Leads Overview - M/L Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on M/L */}
                              <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className={`${(report.size || 'medium') === 'large' ? 'text-lg' : 'text-base'} font-semibold text-gray-600 truncate`}>
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center min-h-0" style={{ minHeight: 0 }}>
                                <div className={`grid grid-cols-3 ${(report.size || 'medium') === 'large' ? 'gap-6' : 'gap-4'} w-full`}>
                                  {/* Total Leads */}
                                  <div className={`text-center bg-gray-50 rounded-lg ${(report.size || 'medium') === 'large' ? 'p-4' : 'p-3'} border border-gray-200`}>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-gray-600 ${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>Total</div>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900`}>
                                      {report.data?.total || 0}
                                    </div>
                                  </div>
                                  {/* Won */}
                                  <div className={`text-center bg-green-50 rounded-lg ${(report.size || 'medium') === 'large' ? 'p-4' : 'p-3'} border border-green-200`}>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-green-700 ${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>Won</div>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-green-600`}>
                                      {report.data?.won || 0}
                                    </div>
                                  </div>
                                  {/* Lost */}
                                  <div className={`text-center bg-red-50 rounded-lg ${(report.size || 'medium') === 'large' ? 'p-4' : 'p-3'} border border-red-200`}>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-red-700 ${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'}`}>Lost</div>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-red-600`}>
                                      {report.data?.lost || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'payments_by_type' && shouldUseMLSizeSystem(report.type) && ((report.size || 'medium') === 'medium' || (report.size || 'medium') === 'large') ? (
                            /* Payments by Type - M/L Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              {/* Title */}
                              <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                <span className={`${(report.size || 'medium') === 'large' ? 'text-base' : 'text-sm'} font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 truncate`}>
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-row gap-3 min-h-0" style={{ minHeight: 0 }}>
                                {/* Left Column: Total, Status, and Payment Methods */}
                                <div className="flex-1 flex flex-col min-w-0">
                                  {/* Total Payments */}
                                  <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-3xl' : 'text-2xl'} font-bold text-gray-900`}>
                                      {formatCurrency(report.data?.total || 0, false)}
                                    </div>
                                    <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500 mt-0.5`}>Total Payments</div>
                                  </div>
                                  
                                  {/* Payment Status - Deposits and Full Payments Side by Side */}
                                  {report.data?.byStatus && report.data.byStatus.length > 0 && (
                                    <div className={`${(report.size || 'medium') === 'large' ? 'mb-3' : 'mb-2'} flex-shrink-0`}>
                                      <div className="grid grid-cols-2 gap-3">
                                        {report.data.byStatus.map((item: { status: string; amount: number; label: string }) => {
                                          const isFullPayment = item.status === 'final';
                                          const isDeposit = item.status === 'deposit';
                                          const amountColor = isFullPayment ? 'text-green-600' : isDeposit ? 'text-yellow-600' : 'text-gray-900';
                                          // Change "Full Payments" to "Full"
                                          const displayLabel = item.label === 'Full Payments' ? 'Full' : item.label;
                                          
                                          return (
                                            <div key={item.status} className="flex flex-col">
                                              <span className={`${(report.size || 'medium') === 'large' ? 'text-xs' : 'text-[10px]'} text-gray-600 mb-0.5`}>{displayLabel}</span>
                                              <span className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-semibold ${amountColor}`}>
                                                {formatCurrency(item.amount, false)}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Payment Methods Breakdown */}
                                  {report.data?.byMethod && report.data.byMethod.length > 0 && (
                                    <div className="flex-shrink-0">
                                      <div className={`${(report.size || 'medium') === 'large' ? 'text-xs' : 'text-[10px]'} text-gray-500 mb-1`}>By Method</div>
                                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                        {report.data.byMethod.map((item: { method: string; amount: number; label: string }) => (
                                          <div key={item.method} className="flex items-center justify-between">
                                            <span className={`${(report.size || 'medium') === 'large' ? 'text-xs' : 'text-[10px]'} text-gray-600`}>{item.label}</span>
                                            <span className={`${(report.size || 'medium') === 'large' ? 'text-xs' : 'text-[10px]'} font-medium text-gray-900`}>{formatCurrency(item.amount, false)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Right Column: Type Breakdown */}
                                {report.data?.byType && report.data.byType.length > 0 && (
                                  <div className="flex-1 flex flex-col min-w-0">
                                    <div className="space-y-1">
                                      {report.data.byType.map((item: { type: string; amount: number; label: string }) => (
                                        <div key={item.type} className="flex items-center justify-between">
                                          <span className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-600`}>{item.label}</span>
                                          <span className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-gray-900`}>{formatCurrency(item.amount, false)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'crm_leads_overview' ? (
                            /* CRM Leads Overview - Fallback for non-M/L sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 truncate font-semibold"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center">
                                <div className="grid grid-cols-3 gap-4 w-full px-2">
                                  {/* Total Leads */}
                                  <div className="text-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                                    <div className="text-xs font-medium text-gray-600 mb-2">Total</div>
                                    <div className="text-3xl font-bold text-gray-900">
                                      {report.data?.total || 0}
                                    </div>
                                  </div>
                                  {/* Won */}
                                  <div className="text-center bg-green-50 rounded-lg p-3 border border-green-200">
                                    <div className="text-xs font-medium text-green-700 mb-2">Won</div>
                                    <div className="text-3xl font-bold text-green-600">
                                      {report.data?.won || 0}
                                    </div>
                                  </div>
                                  {/* Lost */}
                                  <div className="text-center bg-red-50 rounded-lg p-3 border border-red-200">
                                    <div className="text-xs font-medium text-red-700 mb-2">Lost</div>
                                    <div className="text-3xl font-bold text-red-600">
                                      {report.data?.lost || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'page_route_analytics' && shouldUseMLSizeSystem(report.type) && ((report.size || 'medium') === 'medium' || (report.size || 'medium') === 'large') ? (
                            /* Page Route Analytics - M/L Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              {isEditMode ? (
                                <>
                                  {/* Title in edit mode */}
                                  <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                    <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 truncate">
                                      {report.type === 'page_route_analytics' ? 'Route Analytics' : report.title}
                                    </span>
                                  </div>
                                </>
                              ) : null}
                              
                              {isEditMode ? (
                                /* Edit Mode: Route Selector */
                                <div className="flex-1 flex flex-col min-h-0">
                                  <label className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-gray-700 mb-2`}>
                                    Select Route
                                  </label>
                                  <select
                                    value={pageRouteSelections[report.id] || report.data?.config?.selectedRoute || ''}
                                    onChange={async (e) => {
                                      const selectedRoute = e.target.value;
                                      
                                      // Update local state immediately
                                      setPageRouteSelections(prev => ({
                                        ...prev,
                                        [report.id]: selectedRoute
                                      }));
                                      
                                      // Build the config to save
                                      const configToSave = {
                                        config: {
                                          selectedRoute: selectedRoute
                                        }
                                      };
                                      
                                      // If route selected, fetch analytics data first, then save everything
                                      if (selectedRoute && businessId) {
                                        const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                        const dateRange = currentDashboard?.dateRange || 'this_month';
                                        try {
                                          // Fetch the route analytics data
                                          const routeData = await fetchPageRouteAnalyticsData(
                                            businessId,
                                            selectedRoute,
                                            dateRange as DateRangeOption
                                          );
                                          
                                          // Combine config with fetched data
                                          const dataToSave = {
                                            ...routeData,
                                            ...configToSave
                                          };
                                          
                                          // Save to database immediately
                                          console.log('💾 Auto-saving page_route_analytics route selection:', selectedRoute);
                                          const { data: updatedReport, error } = await supabase
                                            .from('kpi_dashboard_reports')
                                            .update({ report_data: dataToSave })
                                            .eq('id', report.id)
                                            .select('id, report_data')
                                            .single();
                                          
                                          if (error) {
                                            console.error('❌ Error saving route selection:', error);
                                            toast.error('Failed to save route selection');
                                            return;
                                          }
                                          
                                          console.log('✅ Route selection saved successfully');
                                          
                                          // Update local state with the saved data
                                          setDashboards(prev => prev.map(d => {
                                            if (d.id === dashboard.id) {
                                              return {
                                                ...d,
                                                reports: d.reports.map(r => 
                                                  r.id === report.id 
                                                    ? { ...r, data: dataToSave }
                                                    : r
                                                )
                                              };
                                            }
                                            return d;
                                          }));
                                        } catch (error) {
                                          console.error('Error fetching/saving route data:', error);
                                          toast.error('Failed to save route selection');
                                        }
                                      } else if (!selectedRoute) {
                                        // Route was cleared, just save the config
                                        const dataToSave = {
                                          config: {
                                            selectedRoute: ''
                                          }
                                        };
                                        
                                        const { error } = await supabase
                                          .from('kpi_dashboard_reports')
                                          .update({ report_data: dataToSave })
                                          .eq('id', report.id);
                                        
                                        if (error) {
                                          console.error('❌ Error clearing route selection:', error);
                                          toast.error('Failed to clear route selection');
                                        } else {
                                          setDashboards(prev => prev.map(d => {
                                            if (d.id === dashboard.id) {
                                              return {
                                                ...d,
                                                reports: d.reports.map(r => 
                                                  r.id === report.id 
                                                    ? { ...r, data: dataToSave }
                                                    : r
                                                )
                                              };
                                            }
                                            return d;
                                          }));
                                        }
                                      }
                                    }}
                                    className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      // Load available routes when dropdown is clicked
                                      if (!loadingRoutes && businessId) {
                                        setLoadingRoutes(true);
                                        const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                        const dateRange = currentDashboard?.dateRange || 'this_month';
                                        const customStartDate = currentDashboard?.customStartDate;
                                        const customEndDate = currentDashboard?.customEndDate;
                                        try {
                                          console.log('🔍 Loading available routes for date range:', dateRange);
                                          const routes = await fetchAvailableRoutes(
                                            businessId,
                                            dateRange as DateRangeOption,
                                            customStartDate,
                                            customEndDate
                                          );
                                          console.log('✅ Loaded routes:', routes);
                                          if (routes.length === 0) {
                                            toast.error('No routes found for the selected date range. Make sure you have web tracking code installed and visitors have visited your site.');
                                          } else {
                                            setAvailableRoutes(routes);
                                          }
                                        } catch (error) {
                                          console.error('❌ Error loading routes:', error);
                                          toast.error('Failed to load routes. Please check the console for details.');
                                        } finally {
                                          setLoadingRoutes(false);
                                        }
                                      }
                                    }}
                                    onFocus={async () => {
                                      // Also load on focus as backup
                                      if (availableRoutes.length === 0 && !loadingRoutes && businessId) {
                                        setLoadingRoutes(true);
                                        const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                        const dateRange = currentDashboard?.dateRange || 'this_month';
                                        try {
                                          const routes = await fetchAvailableRoutes(
                                            businessId,
                                            dateRange as DateRangeOption
                                          );
                                          setAvailableRoutes(routes);
                                        } catch (error) {
                                          console.error('Error loading routes:', error);
                                        } finally {
                                          setLoadingRoutes(false);
                                        }
                                      }
                                    }}
                                  >
                                    <option value="">-- Select a route --</option>
                                    {(() => {
                                      const currentRoute = pageRouteSelections[report.id] || report.data?.config?.selectedRoute || '';
                                      const routesHaveSelected = availableRoutes.some(r => r.route === currentRoute);
                                      return (
                                        <>
                                          {currentRoute && !routesHaveSelected && (
                                            <option value={currentRoute}>{currentRoute} (selected)</option>
                                          )}
                                          {availableRoutes.length > 0 ? (
                                            availableRoutes.map((routeItem) => (
                                              <option key={routeItem.route} value={routeItem.route}>
                                                {routeItem.route} ({routeItem.visitors} visitors)
                                              </option>
                                            ))
                                          ) : !loadingRoutes && !currentRoute ? (
                                            <option value="" disabled>No routes available</option>
                                          ) : null}
                                        </>
                                      );
                                    })()}
                                  </select>
                                  {loadingRoutes && (
                                    <div className="text-xs text-gray-500 mt-2">Loading routes...</div>
                                  )}
                                  {!loadingRoutes && availableRoutes.length === 0 && (
                                    <div className="text-xs text-gray-500 mt-2">
                                      No routes found. Make sure web tracking is installed and you have visitors.
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* Display Mode: Show Analytics */
                                <div className="flex-1 flex flex-col min-h-0">
                                  {(() => {
                                    // Debug logging
                                    if (report.type === 'page_route_analytics') {
                                      console.log('🔍 [Display Mode] Page Route Analytics Report:', {
                                        reportId: report.id,
                                        hasConfig: !!report.data?.config,
                                        selectedRoute: report.data?.config?.selectedRoute,
                                        hasValue: !!report.data?.value,
                                        hasRouteData: !!report.data?.routeData,
                                        fullData: report.data
                                      });
                                    }
                                    
                                    const selectedRoute = report.data?.config?.selectedRoute;
                                    if (selectedRoute) {
                                      const trafficSources = report.data?.routeData?.traffic_sources || [];
                                      const formSubmissions = report.data?.routeData?.form_submissions || [];
                                      const visitorCount = report.data?.value || report.data?.routeData?.summary?.total_visitors || 0;
                                      const sessionCount = report.data?.routeData?.summary?.total_sessions || 0;
                                      
                                      return (
                                        <div className="flex flex-col h-full">
                                          {/* Top Row: Title on left, Stats in top right */}
                                          <div className="flex items-start justify-between mb-1.5 flex-shrink-0">
                                            <div className="flex-1 min-w-0">
                                              {/* Title with route path below */}
                                              <div className="mb-0.5">
                                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 inline-block">
                                                  {report.type === 'page_route_analytics' ? 'Route Analytics' : report.title}
                                                </span>
                                              </div>
                                              <div className={`${(report.size || 'medium') === 'large' ? 'text-[10px]' : 'text-[9px]'} text-gray-500 truncate mt-0.5`} title={selectedRoute}>
                                                {selectedRoute}
                                              </div>
                                            </div>
                                            <div className="text-right flex-shrink-0 flex gap-3 ml-2">
                                              {/* Visitors */}
                                              <div>
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-xl' : 'text-lg'} font-bold text-gray-900 leading-tight`}>
                                                  {visitorCount}
                                                </div>
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} text-gray-500`}>
                                                  visitors
                                                </div>
                                              </div>
                                              {/* Sessions */}
                                              <div>
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-xl' : 'text-lg'} font-bold text-gray-900 leading-tight`}>
                                                  {sessionCount}
                                                </div>
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} text-gray-500`}>
                                                  sessions
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Data Sections - Compact, scrollable if content exceeds space */}
                                          <div className="flex-1 min-h-0 flex flex-col gap-1 overflow-y-auto">
                                            {/* Form Submissions - Show all */}
                                            {formSubmissions.length > 0 && (
                                              <div className="flex-shrink-0">
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} font-medium text-gray-600 mb-0.5`}>
                                                  Form Submissions
                                                </div>
                                                <div className="space-y-0.5">
                                                  {formSubmissions.map((form: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between bg-blue-50 rounded px-1 py-0.5 border border-blue-200">
                                                      <span className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} text-gray-700 truncate flex-1`} title={form.form_name || form.form_id}>
                                                        {form.form_name || form.form_id}
                                                      </span>
                                                      <span className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} font-semibold text-blue-900 ml-1 flex-shrink-0`}>
                                                        {form.submissions}
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Traffic Sources - Show all in 2 columns */}
                                            {trafficSources.length > 0 && (
                                              <div className="flex-shrink-0">
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} font-medium text-gray-600 mb-0.5`}>
                                                  Traffic Sources
                                                </div>
                                                <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                                                  {trafficSources.map((source: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between bg-gray-50 rounded px-1 py-0.5 border border-gray-200">
                                                      <span className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} text-gray-700 truncate flex-1`} title={source.source}>
                                                        {source.source}
                                                      </span>
                                                      <span className={`${(report.size || 'medium') === 'large' ? 'text-[9px]' : 'text-[8px]'} font-semibold text-gray-900 ml-1 flex-shrink-0`}>
                                                        {source.visitors} ({source.percentage}%)
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {/* Empty state */}
                                            {trafficSources.length === 0 && formSubmissions.length === 0 && (
                                              <div className="flex items-center justify-center h-full">
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-[10px]' : 'text-[9px]'} text-gray-400 text-center`}>
                                                  No traffic sources or form submissions
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex-1 flex items-center justify-center text-center">
                                          <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500`}>
                                            Select a route in edit mode to view analytics
                                          </div>
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              )}
                            </div>
                          ) : report.type === 'page_route_analytics' ? (
                            /* Page Route Analytics - Fallback for non-M/L sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 truncate"
                                  >
                                    Route Analytics
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center text-center">
                                <div className="text-sm text-gray-500">
                                  {report.data?.config?.selectedRoute 
                                    ? `Visitors: ${report.data?.value || 0}`
                                    : 'Select a route in edit mode'}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'click_analytics' && shouldUseMLSizeSystem(report.type) && ((report.size || 'medium') === 'medium' || (report.size || 'medium') === 'large') ? (
                            /* Click Analytics - M/L Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'medium') === 'large' ? '16px' : '12px' }}>
                              {isEditMode ? (
                                <>
                                  {/* Title in edit mode */}
                                  <div className={`${(report.size || 'medium') === 'large' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                    <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 truncate">
                                      {report.type === 'click_analytics' ? 'Click Analytics' : report.title}
                                    </span>
                                  </div>
                                </>
                              ) : null}
                              
                              {isEditMode ? (
                                /* Edit Mode: Click Selector */
                                <div className="flex-1 flex flex-col min-h-0">
                                  <label className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} font-medium text-gray-700 mb-2`}>
                                    Select Click
                                  </label>
                                  <select
                                    value={clickSelections[report.id] || report.data?.config?.selectedClick || ''}
                                    onChange={async (e) => {
                                      const selectedClick = e.target.value;
                                      
                                      // Update local state immediately
                                      setClickSelections(prev => ({
                                        ...prev,
                                        [report.id]: selectedClick
                                      }));
                                      
                                      // Build the config to save
                                      const configToSave = {
                                        config: {
                                          selectedClick: selectedClick
                                        }
                                      };
                                      
                                      // If click selected, fetch analytics data first, then save everything
                                      if (selectedClick && businessId) {
                                        const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                        const dateRange = currentDashboard?.dateRange || 'this_month';
                                        try {
                                          // Fetch the click analytics data
                                          const clickData = await fetchClickAnalyticsData(
                                            businessId,
                                            selectedClick,
                                            dateRange as DateRangeOption
                                          );
                                          
                                          // Combine config with fetched data
                                          const dataToSave = {
                                            ...clickData,
                                            ...configToSave
                                          };
                                          
                                          // Save to database immediately
                                          console.log('💾 Auto-saving click_analytics click selection:', selectedClick);
                                          const { data: updatedReport, error } = await supabase
                                            .from('kpi_dashboard_reports')
                                            .update({ report_data: dataToSave })
                                            .eq('id', report.id)
                                            .select('id, report_data')
                                            .single();
                                          
                                          if (error) {
                                            console.error('❌ Error saving click selection:', error);
                                            toast.error('Failed to save click selection');
                                            return;
                                          }
                                          
                                          console.log('✅ Click selection saved successfully');
                                          
                                          // Update local state with the saved data
                                          setDashboards(prev => prev.map(d => {
                                            if (d.id === dashboard.id) {
                                              return {
                                                ...d,
                                                reports: d.reports.map(r => 
                                                  r.id === report.id 
                                                    ? { ...r, data: dataToSave }
                                                    : r
                                                )
                                              };
                                            }
                                            return d;
                                          }));
                                        } catch (error) {
                                          console.error('Error fetching/saving click data:', error);
                                          toast.error('Failed to save click selection');
                                        }
                                      } else if (!selectedClick) {
                                        // Click was cleared, just save the config
                                        const dataToSave = {
                                          config: {
                                            selectedClick: ''
                                          }
                                        };
                                        
                                        const { error } = await supabase
                                          .from('kpi_dashboard_reports')
                                          .update({ report_data: dataToSave })
                                          .eq('id', report.id);
                                        
                                        if (error) {
                                          console.error('❌ Error clearing click selection:', error);
                                          toast.error('Failed to clear click selection');
                                        } else {
                                          setDashboards(prev => prev.map(d => {
                                            if (d.id === dashboard.id) {
                                              return {
                                                ...d,
                                                reports: d.reports.map(r => 
                                                  r.id === report.id 
                                                    ? { ...r, data: dataToSave }
                                                    : r
                                                )
                                              };
                                            }
                                            return d;
                                          }));
                                        }
                                      }
                                    }}
                                    className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                    onFocus={async (e) => {
                                      e.stopPropagation();
                                      // Load available clicks when dropdown is focused
                                      if (!loadingClicks && availableClicks.length === 0 && businessId) {
                                        setLoadingClicks(true);
                                        const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                        const dateRange = currentDashboard?.dateRange || 'this_month';
                                        try {
                                          console.log('🔍 Loading available clicks...');
                                          const clicks = await fetchAvailableClicks(businessId, dateRange as DateRangeOption);
                                          console.log('✅ Loaded clicks:', clicks);
                                          setAvailableClicks(clicks);
                                        } catch (error) {
                                          console.error('❌ Error loading clicks:', error);
                                          toast.error('Failed to load clicks');
                                        } finally {
                                          setLoadingClicks(false);
                                        }
                                      }
                                    }}
                                  >
                                    <option value="">-- Select a click --</option>
                                    {(() => {
                                      const currentClick = clickSelections[report.id] || report.data?.config?.selectedClick || '';
                                      const clicksHaveSelected = availableClicks.some(c => c.identifier === currentClick);
                                      return (
                                        <>
                                          {currentClick && !clicksHaveSelected && (
                                            <option value={currentClick}>{currentClick} (selected)</option>
                                          )}
                                          {availableClicks.length > 0 ? (
                                            availableClicks.map((clickItem) => (
                                              <option key={clickItem.identifier} value={clickItem.identifier}>
                                                {clickItem.label} ({clickItem.visitors} visitors, {clickItem.clicks} clicks)
                                              </option>
                                            ))
                                          ) : !loadingClicks && !currentClick ? (
                                            <option value="" disabled>No clicks available</option>
                                          ) : null}
                                        </>
                                      );
                                    })()}
                                  </select>
                                  {loadingClicks && (
                                    <div className="text-xs text-gray-500 mt-2">Loading clicks...</div>
                                  )}
                                  {!loadingClicks && availableClicks.length === 0 && (
                                    <div className="text-xs text-gray-500 mt-2">
                                      No clicks found. Make sure web tracking is installed and you have click events.
                                    </div>
                                  )}
                                </div>
                              ) : (
                                /* Display Mode: Show Click Counts */
                                <div className="flex-1 flex flex-col min-h-0">
                                  {(() => {
                                    const selectedClick = report.data?.config?.selectedClick;
                                    if (selectedClick) {
                                      const uniqueClicks = report.data?.clickData?.summary?.total_visitors || 0;
                                      const totalClicks = report.data?.clickData?.summary?.total_clicks || 0;
                                      
                                      return (
                                        <div className="flex flex-col h-full">
                                          {/* Title and Click Identifier */}
                                          <div className="mb-2 flex-shrink-0">
                                            <div className="mb-0.5">
                                              <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 inline-block">
                                                {report.type === 'click_analytics' ? 'Click Analytics' : report.title}
                                              </span>
                                            </div>
                                            <div className={`${(report.size || 'medium') === 'large' ? 'text-[10px]' : 'text-[9px]'} text-gray-500 truncate mt-0.5`} title={selectedClick}>
                                              {selectedClick}
                                            </div>
                                          </div>
                                          
                                          {/* Click Counts - Side by Side, Centered */}
                                          <div className="flex-1 flex flex-col items-center justify-center">
                                            <div className="flex items-center gap-6">
                                              {/* Total Clicks */}
                                              <div className="text-center">
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900`}>
                                                  {totalClicks.toLocaleString()}
                                                </div>
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500 mt-1`}>
                                                  Total Clicks
                                                </div>
                                              </div>
                                              
                                              {/* Unique Clicks */}
                                              <div className="text-center">
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900`}>
                                                  {uniqueClicks.toLocaleString()}
                                                </div>
                                                <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500 mt-1`}>
                                                  Unique Clicks
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <div className="flex-1 flex items-center justify-center text-center">
                                          <div className={`${(report.size || 'medium') === 'large' ? 'text-sm' : 'text-xs'} text-gray-500`}>
                                            Select a click in edit mode to view analytics
                                          </div>
                                        </div>
                                      );
                                    }
                                  })()}
                                </div>
                              )}
                            </div>
                          ) : report.type === 'click_analytics' ? (
                            /* Click Analytics - Fallback for non-M/L sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 truncate"
                                  >
                                    Click Analytics
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col items-center justify-center text-center">
                                <div className="text-sm text-gray-500">
                                  {report.data?.config?.selectedClick 
                                    ? `Visitors: ${report.data?.value || 0}`
                                    : 'Select a click in edit mode'}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'crm_pipeline_stages' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* CRM Pipeline Stages - L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span 
                                  className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5"
                                >
                                  {report.title}
                                </span>
                                {/* Pipeline Selector Dropdown */}
                                {report.data?.pipelines && report.data.pipelines.length > 0 && (
                                  <select
                                    value={report.data?.selectedPipelineId || ''}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      const newPipelineId = parseInt(e.target.value);
                                      
                                      // Update the selection in state
                                      setCrmPipelineSelections(prev => ({
                                        ...prev,
                                        [report.id]: newPipelineId
                                      }));
                                      
                                      // Fetch new data for this pipeline
                                      const newData = await fetchCRMPipelineStagesData(businessId!, newPipelineId);
                                      
                                      // Update the report data
                                      const updatedDashboards = dashboards.map(d => {
                                        if (d.id === dashboard.id) {
                                          return {
                                            ...d,
                                            reports: d.reports?.map(r => 
                                              r.id === report.id ? { ...r, data: newData } : r
                                            )
                                          };
                                        }
                                        return d;
                                      });
                                      setDashboards(updatedDashboards);
                                      
                                      // Also save to database
                                      if (supabase) {
                                        await supabase
                                          .from('kpi_dashboard_reports')
                                          .update({ report_data: newData })
                                          .eq('id', report.id);
                                      }
                                    }}
                                    className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} border border-gray-300 rounded-lg ${(report.size || 'large') === 'xl' ? 'px-3 py-1.5' : 'px-2 py-1'} bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {report.data.pipelines.map((pipeline: any) => (
                                      <option key={pipeline.id} value={pipeline.id}>
                                        {pipeline.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
                                {report.data?.stages && report.data.stages.length > 0 ? (
                                  (() => {
                                    // Calculate the maximum count for scaling the bars
                                    const maxCount = Math.max(...report.data.stages.map((s: any) => s.count), 1);
                                    
                                    return (
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-4' : 'space-y-3'}`}>
                                        {report.data.stages.map((stage: any) => {
                                          // Calculate the bar width as a percentage (minimum 5% for visibility)
                                          const percentage = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 5) : 5;
                                          
                                          return (
                                            <div key={stage.id} className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                              {/* Stage name and count */}
                                              <div className="flex items-center justify-between">
                                                <span className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-medium text-gray-700 truncate`}>
                                                  {stage.name}
                                                </span>
                                                <span className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-blue-600 ${(report.size || 'large') === 'xl' ? 'ml-3' : 'ml-2'}`}>
                                                  {stage.count}
                                                </span>
                                              </div>
                                              {/* Progress bar */}
                                              <div className={`w-full bg-gray-200 rounded-full ${(report.size || 'large') === 'xl' ? 'h-8' : 'h-6'} overflow-hidden`}>
                                                <div 
                                                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                                                  style={{ width: `${percentage}%` }}
                                                >
                                                  {stage.count > 0 && (
                                                    <span className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-white`}>
                                                      {stage.count}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500`}>No stages in this pipeline</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'project_pipeline_stages' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Project Pipeline Stages - L/XL Size Template - Compact */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '12px' : '8px' }}>
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-2' : 'mb-1.5'} flex-shrink-0`}>
                                <span 
                                  className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5`}
                                >
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
                                {report.data?.stages && report.data.stages.length > 0 ? (
                                  (() => {
                                    // Calculate the maximum count for scaling the bars
                                    const maxCount = Math.max(...report.data.stages.map((s: any) => s.count), 1);
                                    
                                    return (
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1.5'}`}>
                                        {report.data.stages.map((stage: any) => {
                                          // Calculate the bar width as a percentage (minimum 5% for visibility)
                                          const percentage = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 5) : 5;
                                          
                                          return (
                                            <div key={stage.id} className={`${(report.size || 'large') === 'xl' ? 'space-y-1' : 'space-y-0.5'}`}>
                                              {/* Stage name and count */}
                                              <div className="flex items-center justify-between">
                                                <span className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} font-medium text-gray-700 truncate`}>
                                                  {stage.name}
                                                </span>
                                                <span className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} font-bold text-blue-600 ${(report.size || 'large') === 'xl' ? 'ml-2' : 'ml-1.5'}`}>
                                                  {stage.count}
                                                </span>
                                              </div>
                                              {/* Progress bar */}
                                              <div className={`w-full bg-gray-200 rounded-full ${(report.size || 'large') === 'xl' ? 'h-5' : 'h-4'} overflow-hidden`}>
                                                <div 
                                                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-1.5"
                                                  style={{ width: `${percentage}%` }}
                                                >
                                                  {stage.count > 0 && (
                                                    <span className={`${(report.size || 'large') === 'xl' ? 'text-[10px]' : 'text-[9px]'} font-semibold text-white`}>
                                                      {stage.count}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500`}>No project stages found</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'project_pipeline_stages' ? (
                            /* Project Pipeline Stages - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                <span 
                                  className="text-gray-600 truncate font-semibold"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex items-center justify-center text-center">
                                <div className="text-sm text-gray-500">
                                  This report requires Large or XL size. Resize in edit mode.
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'crm_pipeline_stages' ? (
                            /* CRM Pipeline Stages - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                <span 
                                  className="text-gray-600 truncate font-semibold"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                                {/* Pipeline Selector Dropdown */}
                                {report.data?.pipelines && report.data.pipelines.length > 0 && (
                                  <select
                                    value={report.data?.selectedPipelineId || ''}
                                    onChange={async (e) => {
                                      e.stopPropagation();
                                      const newPipelineId = parseInt(e.target.value);
                                      
                                      // Update the selection in state
                                      setCrmPipelineSelections(prev => ({
                                        ...prev,
                                        [report.id]: newPipelineId
                                      }));
                                      
                                      // Fetch new data for this pipeline
                                      const newData = await fetchCRMPipelineStagesData(businessId!, newPipelineId);
                                      
                                      // Update the report data
                                      const updatedDashboards = dashboards.map(d => {
                                        if (d.id === dashboard.id) {
                                          return {
                                            ...d,
                                            reports: d.reports?.map(r => 
                                              r.id === report.id ? { ...r, data: newData } : r
                                            )
                                          };
                                        }
                                        return d;
                                      });
                                      setDashboards(updatedDashboards);
                                      
                                      // Also save to database
                                      if (supabase) {
                                        await supabase
                                          .from('kpi_dashboard_reports')
                                          .update({ report_data: newData })
                                          .eq('id', report.id);
                                      }
                                    }}
                                    className="text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {report.data.pipelines.map((pipeline: any) => (
                                      <option key={pipeline.id} value={pipeline.id}>
                                        {pipeline.name}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <div className="flex-1 overflow-y-auto">
                                {report.data?.stages && report.data.stages.length > 0 ? (
                                  (() => {
                                    // Calculate the maximum count for scaling the bars
                                    const maxCount = Math.max(...report.data.stages.map((s: any) => s.count), 1);
                                    
                                    return (
                                      <div className="space-y-3">
                                        {report.data.stages.map((stage: any) => {
                                          // Calculate the bar width as a percentage (minimum 5% for visibility)
                                          const percentage = maxCount > 0 ? Math.max((stage.count / maxCount) * 100, 5) : 5;
                                          
                                          return (
                                            <div key={stage.id} className="space-y-1">
                                              {/* Stage name and count */}
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-medium text-gray-700 truncate">
                                                  {stage.name}
                                                </span>
                                                <span className="text-xs font-bold text-blue-600 ml-2">
                                                  {stage.count}
                                                </span>
                                              </div>
                                              {/* Progress bar */}
                                              <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                                <div 
                                                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                                                  style={{ width: `${percentage}%` }}
                                                >
                                                  {stage.count > 0 && (
                                                    <span className="text-xs font-semibold text-white">
                                                      {stage.count}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <p className="text-sm text-gray-500">No stages in this pipeline</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'crm_won_lost_by_staff' ? (
                            /* Won/Lost by Team Member — per-assignee open/won/lost + win% */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
                                {report.data?.staff && report.data.staff.length > 0 ? (
                                  <div className="w-full">
                                    <div className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4 pb-4 text-sm mb-3' : 'gap-3 pb-3 text-xs mb-2'} border-b-2 border-gray-300 font-bold text-gray-700 sticky top-0 bg-gray-50 z-10`}>
                                      <div>Team Member</div>
                                      <div className="text-center">Open</div>
                                      <div className="text-center">Won</div>
                                      <div className="text-center">Lost</div>
                                      <div className="text-center">Win%</div>
                                    </div>
                                    <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                      {report.data.staff.map((row: any, idx: number) => (
                                        <div
                                          key={row.id || idx}
                                          className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4 py-3 text-sm' : 'gap-3 py-2.5 text-xs'} border-b border-gray-200 hover:bg-blue-50 rounded transition-colors`}
                                        >
                                          <div className={`${(report.size || 'large') === 'xl' ? 'font-bold' : 'font-semibold'} ${row.id === 'unassigned' ? 'text-gray-500 italic' : 'text-gray-900'} truncate flex items-center`} title={row.name}>
                                            {row.name}
                                          </div>
                                          <div className={`text-center text-blue-600 font-bold ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} flex items-center justify-center`}>
                                            {row.open || 0}
                                          </div>
                                          <div className={`text-center text-green-600 font-bold ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} flex items-center justify-center`}>
                                            {row.won || 0}
                                          </div>
                                          <div className={`text-center text-red-600 font-bold ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} flex items-center justify-center`}>
                                            {row.lost || 0}
                                          </div>
                                          <div className={`text-center font-bold text-gray-900 ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} flex items-center justify-center`}>
                                            {(row.winPercentage || 0).toFixed(0)}%
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500 mb-2`}>No leads in this period</p>
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-400`}>Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'crm_leads_created_by_resource' ? (
                            /* Leads Created by Team Member — creator counts + share */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className={`text-left ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex-shrink-0`}>
                                <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                  {report.data?.value || '0'}
                                </div>
                                <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500 mt-0.5`}>
                                  {report.data?.label || 'Leads Created'}
                                </div>
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
                                {report.data?.staff && report.data.staff.length > 0 ? (
                                  <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2.5' : 'space-y-2'}`}>
                                    {report.data.staff.map((row: any, idx: number) => (
                                      <div key={row.id || idx} className="w-full">
                                        <div className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} mb-1`}>
                                          <span className={`${row.id === 'unassigned' ? 'text-gray-500 italic' : 'text-gray-900'} font-semibold truncate`} title={row.name}>
                                            {row.name}
                                          </span>
                                          <span className="text-gray-700 font-bold flex-shrink-0 ml-2">
                                            {row.created} <span className="text-gray-400 font-medium">({(row.percentage || 0).toFixed(0)}%)</span>
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                          <div
                                            className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                                            style={{ width: `${Math.max(2, Math.min(100, row.percentage || 0))}%` }}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500 mb-2`}>No leads in this period</p>
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-400`}>Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'sales_pins' ? (
                            /* Sales Pins — pins dropped in range, by current knock status */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className={`text-left ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex-shrink-0`}>
                                <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                  {report.data?.value || '0'}
                                </div>
                                <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500 mt-0.5`}>
                                  {report.data?.label || 'Sales Pins Dropped'}
                                </div>
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
                                {report.data?.pins && report.data.pins.length > 0 ? (
                                  <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2.5' : 'space-y-2'}`}>
                                    {report.data.pins.map((pin: any, idx: number) => (
                                      <div key={pin.type || idx} className="w-full">
                                        <div className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} mb-1`}>
                                          <span className="flex items-center gap-2 min-w-0">
                                            {/* Rounded-square swatch matches the map's pin badges */}
                                            <span
                                              className="h-3 w-3 rounded flex-shrink-0 border border-white shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                                              style={{ backgroundColor: pin.color }}
                                              aria-hidden
                                            />
                                            <span className="font-semibold text-gray-900 truncate">{pin.label}</span>
                                          </span>
                                          <span className="text-gray-700 font-bold flex-shrink-0 ml-2">
                                            {pin.count} <span className="text-gray-400 font-medium">({(pin.percentage || 0).toFixed(0)}%)</span>
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                          <div
                                            className="h-2 rounded-full"
                                            style={{
                                              width: `${Math.max(2, Math.min(100, pin.percentage || 0))}%`,
                                              backgroundColor: pin.color
                                            }}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500 mb-2`}>No pins dropped in this period</p>
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-400`}>Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'crm_leads_by_source' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* CRM Leads by Source - L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on L/XL */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0" style={{ minHeight: 0 }}>
                                {report.data?.sources && report.data.sources.length > 0 ? (
                                  <div className="w-full">
                                    {/* Table Header */}
                                    <div className={`grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1.5fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'pb-4' : 'pb-3'} border-b-2 border-gray-300 ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} sticky top-0 bg-gray-50 z-10`}>
                                      <div>Source</div>
                                      <div className="text-center">Open</div>
                                      <div className="text-center">Won</div>
                                      <div className="text-center">Lost</div>
                                      <div className="text-right">Total Value</div>
                                      <div className="text-center">Win%</div>
                                    </div>
                                    
                                    {/* Table Rows */}
                                    <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                      {report.data.sources.map((sourceData: any, idx: number) => (
                                        <div 
                                          key={sourceData.source || idx} 
                                          className={`grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1.5fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'py-3' : 'py-2.5'} border-b border-gray-200 ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} hover:bg-blue-50 rounded transition-colors`}
                                        >
                                          <div className={`${(report.size || 'large') === 'xl' ? 'font-bold' : 'font-semibold'} text-gray-900 truncate flex items-center`} title={sourceData.source || 'Unknown'}>
                                            {sourceData.source || 'Unknown'}
                                          </div>
                                          <div className="text-center">
                                            <div className={`text-blue-600 font-bold ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'}`}>
                                              {sourceData.open || 0}
                                            </div>
                                            <div className={`text-blue-500 font-semibold ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'mt-1' : 'mt-0.5'}`}>
                                              ${(sourceData.openValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-center">
                                            <div className={`text-green-600 font-bold ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'}`}>
                                              {sourceData.won || 0}
                                            </div>
                                            <div className={`text-green-500 font-semibold ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'mt-1' : 'mt-0.5'}`}>
                                              ${(sourceData.wonValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-center">
                                            <div className={`text-red-600 font-bold ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'}`}>
                                              {sourceData.lost || 0}
                                            </div>
                                            <div className={`text-red-500 font-semibold ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'mt-1' : 'mt-0.5'}`}>
                                              ${(sourceData.lostValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-right flex flex-col justify-center">
                                            <div className={`font-bold text-gray-900 ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'}`}>
                                              ${(sourceData.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className={`text-center font-bold text-gray-900 ${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} flex items-center justify-center`}>
                                            {(sourceData.winPercentage || 0).toFixed(1)}%
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'}`}>No leads data available</p>
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-400`}>Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'crm_leads_by_source' ? (
                            /* CRM Leads by Source - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                <span 
                                  className="text-gray-600 truncate font-semibold"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 overflow-y-auto min-h-0">
                                {report.data?.sources && report.data.sources.length > 0 ? (
                                  <div className="w-full">
                                    {/* Table Header */}
                                    <div className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1.5fr_1fr] gap-3 pb-3 border-b-2 border-gray-300 text-xs font-bold text-gray-700 mb-2 sticky top-0 bg-gray-50 z-10 px-1">
                                      <div>Source</div>
                                      <div className="text-center">Open</div>
                                      <div className="text-center">Won</div>
                                      <div className="text-center">Lost</div>
                                      <div className="text-right">Total Value</div>
                                      <div className="text-center">Win%</div>
                                    </div>
                                    
                                    {/* Table Rows */}
                                    <div className="space-y-1">
                                      {report.data.sources.map((sourceData: any, idx: number) => (
                                        <div 
                                          key={sourceData.source || idx} 
                                          className="grid grid-cols-[2fr_1.5fr_1.5fr_1.5fr_1.5fr_1fr] gap-3 py-2.5 px-1 border-b border-gray-200 text-xs hover:bg-blue-50 rounded transition-colors"
                                        >
                                          <div className="font-semibold text-gray-900 truncate flex items-center" title={sourceData.source || 'Unknown'}>
                                            {sourceData.source || 'Unknown'}
                                          </div>
                                          <div className="text-center">
                                            <div className="text-blue-600 font-bold text-sm">
                                              {sourceData.open || 0}
                                            </div>
                                            <div className="text-blue-500 font-semibold text-xs mt-0.5">
                                              ${(sourceData.openValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-green-600 font-bold text-sm">
                                              {sourceData.won || 0}
                                            </div>
                                            <div className="text-green-500 font-semibold text-xs mt-0.5">
                                              ${(sourceData.wonValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-center">
                                            <div className="text-red-600 font-bold text-sm">
                                              {sourceData.lost || 0}
                                            </div>
                                            <div className="text-red-500 font-semibold text-xs mt-0.5">
                                              ${(sourceData.lostValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-right flex flex-col justify-center">
                                            <div className="font-bold text-gray-900 text-sm">
                                              ${(sourceData.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                          <div className="text-center font-bold text-gray-900 text-sm flex items-center justify-center">
                                            {(sourceData.winPercentage || 0).toFixed(1)}%
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className="text-sm text-gray-500 mb-2">No leads data available</p>
                                      <p className="text-xs text-gray-400">Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'product_sales_by_category' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Product Sales by Category - L/XL Size Template with Expandable Rows */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 inline-block">
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Categories Table with Expandable Rows */}
                              <div className="flex-1 overflow-y-auto min-h-0">
                                {(() => {
                                  const categoryData = report.data?.categoryData;
                                  const categories = categoryData?.categories || [];
                                  const expandedCategoryId = expandedProductCategories[report.id];
                                  const cachedItems = categoryItemsCache[report.id] || {};
                                  
                                  if (categories.length > 0) {
                                    return (
                                      <div className="w-full">
                                        {/* Table Header */}
                                        <div className={`grid grid-cols-[2fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'pb-4' : 'pb-3'} border-b-2 border-gray-300 ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} sticky top-0 bg-gray-50 z-10`}>
                                          <div>Category</div>
                                          <div className="text-right">Sales Count</div>
                                          <div className="text-right">Total Sales</div>
                                        </div>
                                        
                                        {/* Table Rows with Expandable Items */}
                                        <div className={`${(report.size || 'large') === 'xl' ? 'space-y-0' : 'space-y-0'}`}>
                                          {categories.map((cat: any, idx: number) => {
                                            const isExpanded = expandedCategoryId === cat.id;
                                            const items = cachedItems[cat.id] || [];
                                            const isLoading = loadingProductCategories && expandedCategoryId === cat.id;
                                            
                                            return (
                                              <div key={cat.id || idx}>
                                                {/* Category Row - Clickable */}
                                                <div 
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!businessId) return;
                                                    
                                                    // Toggle expansion
                                                    const newExpandedId = isExpanded ? null : cat.id;
                                                    setExpandedProductCategories(prev => ({
                                                      ...prev,
                                                      [report.id]: newExpandedId
                                                    }));
                                                    
                                                    // If expanding and items not cached, fetch them
                                                    if (newExpandedId && !cachedItems[cat.id]) {
                                                      setLoadingProductCategories(true);
                                                      const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                                      const dateRange = currentDashboard?.dateRange || 'this_month';
                                                      const customStartDate = currentDashboard?.customStartDate;
                                                      const customEndDate = currentDashboard?.customEndDate;
                                                      
                                                      try {
                                                        const itemData = await fetchProductSalesByCategoryData(
                                                          businessId,
                                                          cat.id,
                                                          dateRange as DateRangeOption,
                                                          customStartDate,
                                                          customEndDate
                                                        );
                                                        
                                                        // Cache the items
                                                        setCategoryItemsCache(prev => ({
                                                          ...prev,
                                                          [report.id]: {
                                                            ...(prev[report.id] || {}),
                                                            [cat.id]: itemData.categoryData?.items || []
                                                          }
                                                        }));
                                                      } catch (error) {
                                                        console.error('Error fetching category items:', error);
                                                        toast.error('Failed to load items');
                                                      } finally {
                                                        setLoadingProductCategories(false);
                                                      }
                                                    }
                                                  }}
                                                  className={`grid grid-cols-[2fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'py-3' : 'py-2.5'} border-b border-gray-200 ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${isExpanded ? 'bg-blue-50' : 'hover:bg-blue-50'} rounded transition-colors cursor-pointer`}
                                                >
                                                  <div className={`${(report.size || 'large') === 'xl' ? 'font-bold' : 'font-semibold'} text-gray-900 truncate flex items-center gap-2`} title={cat.name}>
                                                    <span className={isExpanded ? 'transform rotate-90' : ''} style={{ transition: 'transform 0.2s' }}>▶</span>
                                                    {cat.name}
                                                  </div>
                                                  <div className="text-right font-medium text-gray-700">
                                                    {cat.sale_count || 0}
                                                  </div>
                                                  <div className="text-right font-bold text-gray-900">
                                                    {formatCurrency(cat.total_sales || 0, false)}
                                                  </div>
                                                </div>
                                                
                                                {/* Expanded Items - Show below category row */}
                                                {isExpanded && (
                                                  <div className="bg-gray-50 border-l-4 border-blue-500 pl-4 pr-2 py-2">
                                                    {isLoading ? (
                                                      <div className="text-xs text-gray-500 py-2">Loading items...</div>
                                                    ) : items.length > 0 ? (
                                                      <div className="space-y-1">
                                                        {/* Items Header */}
                                                        <div className={`grid grid-cols-[2fr_1fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'pb-2' : 'pb-1.5'} border-b border-gray-300 ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} font-semibold text-gray-600 mb-1`}>
                                                          <div>Item</div>
                                                          <div className="text-right">Quantity</div>
                                                          <div className="text-right">Unit Price</div>
                                                          <div className="text-right">Total Sales</div>
                                                        </div>
                                                        
                                                        {/* Items Rows */}
                                                        {items.map((item: any, itemIdx: number) => (
                                                          <div 
                                                            key={item.id || itemIdx} 
                                                            className={`grid grid-cols-[2fr_1fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'py-2' : 'py-1.5'} border-b border-gray-200 ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'}`}
                                                          >
                                                            <div className="font-medium text-gray-800 truncate pl-2" title={item.name}>
                                                              {item.name}
                                                            </div>
                                                            <div className="text-right text-gray-700">
                                                              {item.quantity_sold || 0}
                                                            </div>
                                                            <div className="text-right text-gray-700">
                                                              {formatCurrency(item.unit_price || 0, false)}
                                                            </div>
                                                            <div className="text-right font-semibold text-gray-900">
                                                              {formatCurrency(item.total_sales || 0, false)}
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : (
                                                      <div className="text-xs text-gray-500 py-2">No items found in this category</div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                          <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'}`}>No product sales data available</p>
                                          <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-400`}>Try adjusting your date range</p>
                                        </div>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          ) : report.type === 'product_sales_by_category' ? (
                            /* Product Sales by Category - Fallback for non-L/XL sizes */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                <span className="text-gray-600 truncate font-semibold" style={getDynamicTitleSize(report.layout)}>
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex items-center justify-center text-center">
                                <div className="text-sm text-gray-500">
                                  This report requires Large or XL size. Resize in edit mode.
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'service_sales_by_category' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Service Sales by Category - L/XL Size Template with Expandable Rows */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 inline-block">
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Total Booking Sales Summary */}
                              {(() => {
                                const categoryData = report.data?.categoryData;
                                const totalSales = categoryData?.total_sales || 0;
                                
                                return (
                                  <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} pb-3 border-b border-gray-300 flex-shrink-0`}>
                                    <div className="flex items-center justify-between">
                                      <span className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700`}>
                                        Total Booking Sales:
                                      </span>
                                      <span className={`${(report.size || 'large') === 'xl' ? 'text-lg' : 'text-base'} font-bold text-gray-900`}>
                                        {formatCurrency(totalSales, false)}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Categories Table with Expandable Rows */}
                              <div className="flex-1 overflow-y-auto min-h-0">
                                {(() => {
                                  const categoryData = report.data?.categoryData;
                                  const categories = categoryData?.categories || [];
                                  const expandedCategoryId = expandedServiceCategories[report.id];
                                  const cachedServices = serviceItemsCache[report.id] || {};
                                  
                                  if (categories.length > 0) {
                                    return (
                                      <div className="w-full">
                                        {/* Table Header */}
                                        <div className={`grid grid-cols-[2fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'pb-4' : 'pb-3'} border-b-2 border-gray-300 ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} sticky top-0 bg-gray-50 z-10`}>
                                          <div>Category</div>
                                          <div className="text-right">Sales Count</div>
                                          <div className="text-right">Total Sales</div>
                                        </div>
                                        
                                        {/* Table Rows with Expandable Services */}
                                        <div className={`${(report.size || 'large') === 'xl' ? 'space-y-0' : 'space-y-0'}`}>
                                          {categories.map((cat: any, idx: number) => {
                                            const isExpanded = expandedCategoryId === cat.id;
                                            const services = cachedServices[cat.id] || [];
                                            const isLoading = loadingServiceCategories && expandedCategoryId === cat.id;
                                            
                                            return (
                                              <div key={cat.id || idx}>
                                                {/* Category Row - Clickable */}
                                                <div 
                                                  onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!businessId) return;
                                                    
                                                    // Toggle expansion
                                                    const newExpandedId = isExpanded ? null : cat.id;
                                                    setExpandedServiceCategories(prev => ({
                                                      ...prev,
                                                      [report.id]: newExpandedId
                                                    }));
                                                    
                                                    // If expanding and services not cached, fetch them
                                                    if (newExpandedId && !cachedServices[cat.id]) {
                                                      setLoadingServiceCategories(true);
                                                      const currentDashboard = dashboards.find(d => d.id === dashboard.id);
                                                      const dateRange = currentDashboard?.dateRange || 'this_month';
                                                      const customStartDate = currentDashboard?.customStartDate;
                                                      const customEndDate = currentDashboard?.customEndDate;
                                                      
                                                      try {
                                                        const serviceData = await fetchServiceSalesByCategoryData(
                                                          businessId,
                                                          cat.id,
                                                          dateRange as DateRangeOption,
                                                          customStartDate,
                                                          customEndDate
                                                        );
                                                        
                                                        // Cache the services
                                                        setServiceItemsCache(prev => ({
                                                          ...prev,
                                                          [report.id]: {
                                                            ...(prev[report.id] || {}),
                                                            [cat.id]: serviceData.categoryData?.services || []
                                                          }
                                                        }));
                                                      } catch (error) {
                                                        console.error('Error fetching category services:', error);
                                                        toast.error('Failed to load services');
                                                      } finally {
                                                        setLoadingServiceCategories(false);
                                                      }
                                                    }
                                                  }}
                                                  className={`grid grid-cols-[2fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'py-3' : 'py-2.5'} border-b border-gray-200 ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${isExpanded ? 'bg-blue-50' : 'hover:bg-blue-50'} rounded transition-colors cursor-pointer`}
                                                >
                                                  <div className={`${(report.size || 'large') === 'xl' ? 'font-bold' : 'font-semibold'} text-gray-900 truncate flex items-center gap-2`} title={cat.name}>
                                                    <span className={isExpanded ? 'transform rotate-90' : ''} style={{ transition: 'transform 0.2s' }}>▶</span>
                                                    {cat.name}
                                                  </div>
                                                  <div className="text-right font-medium text-gray-700">
                                                    {cat.sale_count || 0}
                                                  </div>
                                                  <div className="text-right font-bold text-gray-900">
                                                    {formatCurrency(cat.total_sales || 0, false)}
                                                  </div>
                                                </div>
                                                
                                                {/* Expanded Services - Show below category row */}
                                                {isExpanded && (
                                                  <div className="bg-gray-50 border-l-4 border-blue-500 pl-4 pr-2 py-2">
                                                    {isLoading ? (
                                                      <div className="text-xs text-gray-500 py-2">Loading services...</div>
                                                    ) : services.length > 0 ? (
                                                      <div className="space-y-1">
                                                        {/* Services Header */}
                                                        <div className={`grid grid-cols-[2fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'pb-2' : 'pb-1.5'} border-b border-gray-300 ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} font-semibold text-gray-600 mb-1`}>
                                                          <div>Service</div>
                                                          <div className="text-right">Sales Count</div>
                                                          <div className="text-right">Total Sales</div>
                                                        </div>
                                                        
                                                        {/* Services Rows */}
                                                        {services.map((service: any, serviceIdx: number) => (
                                                          <div 
                                                            key={service.id || serviceIdx} 
                                                            className={`grid grid-cols-[2fr_1fr_1fr] ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'} ${(report.size || 'large') === 'xl' ? 'py-2' : 'py-1.5'} border-b border-gray-200 ${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'}`}
                                                          >
                                                            <div className="font-medium text-gray-800 truncate pl-2" title={service.name}>
                                                              {service.name}
                                                            </div>
                                                            <div className="text-right text-gray-700">
                                                              {service.sale_count || 0}
                                                            </div>
                                                            <div className="text-right font-semibold text-gray-900">
                                                              {formatCurrency(service.total_sales || 0, false)}
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : (
                                                      <div className="text-xs text-gray-500 py-2">No services found in this category</div>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div className="flex items-center justify-center h-full">
                                        <div className="text-center">
                                          <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} text-gray-500 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'}`}>No service sales data available</p>
                                          <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-400`}>Try adjusting your date range</p>
                                        </div>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            </div>
                          ) : report.type === 'service_sales_by_category' ? (
                            /* Service Sales by Category - Fallback for non-L/XL sizes */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                <span className="text-gray-600 truncate font-semibold" style={getDynamicTitleSize(report.layout)}>
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex items-center justify-center text-center">
                                <div className="text-sm text-gray-500">
                                  This report requires Large or XL size. Resize in edit mode.
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'crm_new_leads_graph' ? (
                            /* New Leads Graph - stacked bar by creation day (open/won/lost) */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-1.5'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: 0 }}>
                                <div className={`text-left ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-1.5'} flex-shrink-0`}>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                    {report.data?.value || '0'}
                                  </div>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500 ${(report.size || 'large') === 'xl' ? 'mt-1' : 'mt-0.5'}`}>
                                    {report.data?.label || 'New Leads'}
                                  </div>
                                </div>
                                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                  {report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Bar
                                        data={report.data.chartData}
                                        plugins={[stackedBarTotalsPlugin]}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: true,
                                              position: 'bottom',
                                              labels: {
                                                boxWidth: 10,
                                                font: { size: 10 },
                                                padding: 8,
                                                usePointStyle: true
                                              }
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(59, 130, 246, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const count = context.parsed.y;
                                                  return `${context.dataset.label}: ${count} lead${count !== 1 ? 's' : ''}`;
                                                },
                                                footer: (items: any[]) => {
                                                  const total = items.reduce((sum, i) => sum + (i.parsed.y || 0), 0);
                                                  return `Total: ${total} lead${total !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              stacked: true,
                                              beginAtZero: true,
                                              // Headroom so the total drawn above the bar isn't clipped
                                              grace: '10%',
                                              grid: { color: 'rgba(59, 130, 246, 0.1)' },
                                              ticks: {
                                                precision: 0,
                                                font: { size: (report.size || 'large') === 'xl' ? 12 : 10 }
                                              }
                                            },
                                            x: {
                                              stacked: true,
                                              grid: { display: false },
                                              ticks: {
                                                autoSkip: true,
                                                maxRotation: 0,
                                                font: { size: (report.size || 'large') === 'xl' ? 12 : 10 }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'sales_graph' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Sales Graph - Large/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on L/XL */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-1.5'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: 0 }}>
                                {/* Value and Label - Fixed size, more compact for L */}
                                <div className={`text-left ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-1.5'} flex-shrink-0`}>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                    {report.data?.value || '$0.00'}
                                  </div>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500 ${(report.size || 'large') === 'xl' ? 'mt-1' : 'mt-0.5'}`}>
                                    {report.data?.label || 'Sales Trend'}
                                  </div>
                                </div>
                                
                                {/* Chart Container - Takes remaining space */}
                                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                  {report.data?.chartData && report.data?.isPieChart ? (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: true,
                                              position: 'bottom',
                                              labels: {
                                                boxWidth: 12,
                                                font: {
                                                  size: 11
                                                },
                                                padding: 10,
                                                usePointStyle: true
                                              }
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} booking${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  ) : report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Line 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(59, 130, 246, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  return formatCurrency(context.parsed.y);
                                                }
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              grid: {
                                                color: 'rgba(59, 130, 246, 0.1)'
                                              },
                                              ticks: {
                                                callback: (value: any) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                                                font: {
                                                  size: (report.size || 'large') === 'xl' ? 12 : 10
                                                }
                                              }
                                            },
                                            x: {
                                              grid: {
                                                display: false
                                              },
                                              ticks: {
                                                font: {
                                                  size: (report.size || 'large') === 'xl' ? 12 : 10
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'sales_graph' ? (
                            /* Sales Graph - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 line-clamp-2 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col">
                                <div className="text-center mb-2">
                                  <div 
                                    className="font-bold text-gray-900"
                                    style={getDynamicFontSize(report.layout, report.data?.value || '$0.00')}
                                  >
                                    {report.data?.value || '$0.00'}
                                  </div>
                                  <div 
                                    className="text-gray-500"
                                    style={getDynamicLabelSize(report.layout)}
                                  >
                                    {report.data?.label || 'Sales Trend'}
                                  </div>
                                </div>
                                
                                {/* Chart Container */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  {report.data?.chartData && report.data?.isPieChart ? (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: true,
                                              position: 'bottom',
                                              labels: {
                                                boxWidth: 12,
                                                font: {
                                                  size: 11
                                                },
                                                padding: 10,
                                                usePointStyle: true
                                              }
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} booking${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  ) : report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Line 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  return formatCurrency(context.parsed.y);
                                                }
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              grid: {
                                                color: 'rgba(147, 51, 234, 0.1)'
                                              },
                                              ticks: {
                                                callback: (value: any) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                                                font: {
                                                  size: 10
                                                }
                                              }
                                            },
                                            x: {
                                              grid: {
                                                display: false
                                              },
                                              ticks: {
                                                font: {
                                                  size: 10
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'website_visits_graph' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Website Visits Graph - Large/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on L/XL */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-1.5'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: 0 }}>
                                {/* Value and Label - Fixed size, more compact for L */}
                                <div className={`text-left ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-1.5'} flex-shrink-0`}>
                                  <div className={`flex items-center ${(report.size || 'large') === 'xl' ? 'gap-4' : 'gap-3'}`}>
                                    <button
                                      type="button"
                                      onClick={() => setWebsiteVisitsMode('visits')}
                                      className={`flex items-center gap-1.5 transition-opacity ${websiteVisitsMode === 'visits' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                                    >
                                      <div className={`${(report.size || 'large') === 'xl' ? 'w-3 h-3' : 'w-2.5 h-2.5'} rounded-full flex-shrink-0`} style={{ backgroundColor: 'rgb(59, 130, 246)' }} />
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                        {report.data?.value || '0'}
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} text-gray-500 self-end mb-0.5`}>visits</div>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setWebsiteVisitsMode('unique')}
                                      className={`flex items-center gap-1.5 transition-opacity ${websiteVisitsMode === 'unique' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                                    >
                                      <div className={`${(report.size || 'large') === 'xl' ? 'w-3 h-3' : 'w-2.5 h-2.5'} rounded-full flex-shrink-0`} style={{ backgroundColor: 'rgb(147, 51, 234)' }} />
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                        {report.data?.uniqueValue || '0'}
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} text-gray-500 self-end mb-0.5`}>unique</div>
                                    </button>
                                    {report.data?.conversionRate && (
                                      <div className={`flex items-center gap-1.5`}>
                                        <div className={`${(report.size || 'large') === 'xl' ? 'w-3 h-3' : 'w-2.5 h-2.5'} rounded-full flex-shrink-0`} style={{ backgroundColor: 'rgb(234, 88, 12)' }} />
                                        <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-lg'} font-bold text-gray-900`}>
                                          {report.data.conversionRate}%
                                        </div>
                                        <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} text-gray-500 self-end mb-0.5`}>conv.</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Chart Container - Takes remaining space */}
                                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                  {(websiteVisitsMode === 'visits' ? report.data?.chartData : report.data?.uniqueChartData) && (
                                    <div className="h-full w-full">
                                      <Line 
                                        data={websiteVisitsMode === 'visits' ? report.data.chartData : report.data.uniqueChartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: true,
                                              position: 'bottom',
                                              labels: {
                                                boxWidth: 12,
                                                font: {
                                                  size: (report.size || 'large') === 'xl' ? 11 : 10
                                                },
                                                padding: (report.size || 'large') === 'xl' ? 10 : 8,
                                                usePointStyle: true
                                              }
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(59, 130, 246, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                title: (context: any) => {
                                                  // Show the actual date for the dataset being hovered
                                                  const datasetIndex = context[0].datasetIndex;
                                                  const pointIndex = context[0].dataIndex;
                                                  const dataset = context[0].chart.data.datasets[datasetIndex];
                                                  
                                                  if (dataset.actualDates && dataset.actualDates[pointIndex]) {
                                                    const actualDate = new Date(dataset.actualDates[pointIndex] + 'T00:00:00Z');
                                                    return actualDate.toLocaleDateString('en-US', { 
                                                      month: 'short', 
                                                      day: 'numeric',
                                                      timeZone: 'UTC'
                                                    });
                                                  }
                                                  return context[0].label;
                                                },
                                                label: (context: any) => {
                                                  return `${context.dataset.label}: ${context.parsed.y} visits`;
                                                }
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              grid: {
                                                color: 'rgba(59, 130, 246, 0.1)'
                                              },
                                              ticks: {
                                                callback: (value: any) => `${value}`,
                                                font: {
                                                  size: (report.size || 'large') === 'xl' ? 12 : 10
                                                }
                                              }
                                            },
                                            x: {
                                              grid: {
                                                display: false
                                              },
                                              ticks: {
                                                font: {
                                                  size: (report.size || 'large') === 'xl' ? 12 : 10
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'website_visits_graph' ? (
                            /* Website Visits Graph - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 line-clamp-2 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col">
                                <div className="text-center mb-2">
                                  <div className="flex items-center justify-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => setWebsiteVisitsMode('visits')}
                                      className={`flex items-center gap-1.5 transition-opacity ${websiteVisitsMode === 'visits' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                                    >
                                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgb(59, 130, 246)' }} />
                                      <div className="font-bold text-gray-900" style={getDynamicFontSize(report.layout, report.data?.value || '0')}>
                                        {report.data?.value || '0'}
                                      </div>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setWebsiteVisitsMode('unique')}
                                      className={`flex items-center gap-1.5 transition-opacity ${websiteVisitsMode === 'unique' ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}
                                    >
                                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgb(147, 51, 234)' }} />
                                      <div className="font-bold text-gray-900" style={getDynamicFontSize(report.layout, report.data?.uniqueValue || '0')}>
                                        {report.data?.uniqueValue || '0'}
                                      </div>
                                    </button>
                                    {report.data?.conversionRate && (
                                      <div className="flex items-center gap-1.5">
                                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgb(234, 88, 12)' }} />
                                        <div className="font-bold text-gray-900" style={getDynamicFontSize(report.layout, `${report.data.conversionRate}%`)}>
                                          {report.data.conversionRate}%
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Chart Container */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  {(websiteVisitsMode === 'visits' ? report.data?.chartData : report.data?.uniqueChartData) && (
                                    <div className="h-full w-full">
                                      <Line 
                                        data={websiteVisitsMode === 'visits' ? report.data.chartData : report.data.uniqueChartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: true,
                                              position: 'bottom',
                                              labels: {
                                                boxWidth: 12,
                                                font: {
                                                  size: 10
                                                },
                                                padding: 8,
                                                usePointStyle: true
                                              }
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(59, 130, 246, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                title: (context: any) => {
                                                  // Show the actual date for the dataset being hovered
                                                  const datasetIndex = context[0].datasetIndex;
                                                  const pointIndex = context[0].dataIndex;
                                                  const dataset = context[0].chart.data.datasets[datasetIndex];
                                                  
                                                  if (dataset.actualDates && dataset.actualDates[pointIndex]) {
                                                    const actualDate = new Date(dataset.actualDates[pointIndex] + 'T00:00:00Z');
                                                    return actualDate.toLocaleDateString('en-US', { 
                                                      month: 'short', 
                                                      day: 'numeric',
                                                      timeZone: 'UTC'
                                                    });
                                                  }
                                                  return context[0].label;
                                                },
                                                label: (context: any) => {
                                                  return `${context.dataset.label}: ${context.parsed.y} visits`;
                                                }
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              grid: {
                                                color: 'rgba(59, 130, 246, 0.1)'
                                              },
                                              ticks: {
                                                callback: (value: any) => `${value}`,
                                                font: {
                                                  size: 10
                                                }
                                              }
                                            },
                                            x: {
                                              grid: {
                                                display: false
                                              },
                                              ticks: {
                                                font: {
                                                  size: 10
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'online_booking_volume' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Online Booking Volume - L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on L/XL */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: 0 }}>
                                {/* Value and Label - Fixed size */}
                                <div className={`text-left ${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'text-3xl' : 'text-xl'} font-bold text-gray-900`}>
                                    {report.data?.value || '0.0%'}
                                  </div>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500 ${(report.size || 'large') === 'xl' ? 'mt-1' : 'mt-0.5'}`}>
                                    {report.data?.label || 'Conversion Rate'}
                                  </div>
                                </div>
                                
                                {/* Funnel Steps - Fixed size based on L/XL */}
                                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                  <div className={`${(report.size || 'large') === 'xl' ? 'space-y-3' : 'space-y-2'}`}>
                                    {(report.data?.funnelData || []).map((step: any, index: number) => {
                                      const maxCount = Math.max(...(report.data?.funnelData || []).map((s: any) => s.count));
                                      const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                                      
                                      return (
                                        <div key={step.step} className={`flex items-center ${(report.size || 'large') === 'xl' ? 'space-x-4' : 'space-x-3'}`}>
                                          <div className={`flex-1 bg-gray-100/50 backdrop-blur-sm rounded-xl ${(report.size || 'large') === 'xl' ? 'h-6' : 'h-4'} relative overflow-hidden border border-white/30`}
                                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                                            <div 
                                              className={`h-full rounded-xl transition-all duration-300 ${
                                                index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                                index === 1 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                                index === 2 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                                                index === 3 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                                                index === 4 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                                'bg-gradient-to-r from-blue-500 to-blue-600'
                                              }`}
                                              style={{ width: `${percentage}%` }}
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <span className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-medium text-gray-700`}>
                                                {step.count}
                                              </span>
                                            </div>
                                          </div>
                                          <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-600 min-w-0 flex-shrink-0 ${(report.size || 'large') === 'xl' ? 'w-24' : 'w-16'} truncate`}>
                                            {step.step}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Banner Submissions at the bottom */}
                                {report.data?.showBannerSubmits && report.data?.bannerSubmits > 0 && (
                                  <div className={`${(report.size || 'large') === 'xl' ? 'mt-4' : 'mt-3'} pt-2 border-t border-gray-200 flex-shrink-0`}>
                                    <div className={`flex items-center justify-center ${(report.size || 'large') === 'xl' ? 'space-x-3' : 'space-x-2'} bg-blue-50 ${(report.size || 'large') === 'xl' ? 'px-4 py-2.5' : 'px-3 py-2'} rounded-lg`}>
                                      <svg className={`${(report.size || 'large') === 'xl' ? 'w-5 h-5' : 'w-4 h-4'} text-blue-600`} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
                                      </svg>
                                      <span className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'} font-medium text-blue-700`}>
                                        Banner Submissions: {report.data.bannerSubmits}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'online_booking_volume' ? (
                            /* Online Booking Volume - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center mb-2 flex-shrink-0">
                                <div className="flex items-center">
                                  <span 
                                    className="text-gray-600 line-clamp-2 truncate"
                                    style={getDynamicTitleSize(report.layout)}
                                  >
                                    {report.title}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col">
                                <div className="text-center mb-2">
                                  <div 
                                    className="font-bold text-gray-900"
                                    style={getDynamicFontSize(report.layout, report.data?.value || '0.0%')}
                                  >
                                    {report.data?.value || '0.0%'}
                                  </div>
                                  <div 
                                    className="text-gray-500"
                                    style={getDynamicLabelSize(report.layout)}
                                  >
                                    {report.data?.label || 'Conversion Rate'}
                                  </div>
                                </div>
                                
                                {/* Funnel Steps - maintain existing visualization but scale container */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  <div className="space-y-2">
                                    {(report.data?.funnelData || []).map((step: any, index: number) => {
                                      const maxCount = Math.max(...(report.data?.funnelData || []).map((s: any) => s.count));
                                      const percentage = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
                                      
                                      return (
                                        <div key={step.step} className="flex items-center space-x-3">
                                          <div className="flex-1 bg-gray-100/50 backdrop-blur-sm rounded-xl h-4 relative overflow-hidden border border-white/30"
                                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                                            <div 
                                              className={`h-full rounded-xl transition-all duration-300 ${
                                                index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600' :
                                                index === 1 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                                index === 2 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                                                index === 3 ? 'bg-gradient-to-r from-orange-500 to-orange-600' :
                                                index === 4 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                                'bg-gradient-to-r from-blue-500 to-blue-600'
                                              }`}
                                              style={{ width: `${percentage}%` }}
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <span className="text-xs font-medium text-gray-700">
                                                {step.count}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="text-xs text-gray-600 min-w-0 flex-shrink-0 w-16 truncate">
                                            {step.step}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Banner Submissions at the bottom */}
                                {report.data?.showBannerSubmits && report.data?.bannerSubmits > 0 && (
                                  <div className="mt-3 pt-2 border-t border-gray-200">
                                    <div className="flex items-center justify-center space-x-2 bg-blue-50 px-3 py-2 rounded-lg">
                                      <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd" />
                                      </svg>
                                      <span className="text-sm font-medium text-blue-700">
                                        Banner Submissions: {report.data.bannerSubmits}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'web_traffic_patterns' && shouldUseChartSizeSystem(report.type) && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Web Traffic Patterns - L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-3'} flex-shrink-0`}>
                                <span 
                                  className="text-sm font-semibold text-gray-600 border-b border-dotted border-gray-400 pb-0.5"
                                >
                                  {report.title}
                                </span>
                                <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} text-gray-500 bg-blue-50 ${(report.size || 'large') === 'xl' ? 'px-3 py-1.5' : 'px-2 py-1'} rounded-lg`}>
                                  {report.data?.trafficData?.summary?.totalSessions || 0} sessions
                                </div>
                              </div>
                              
                              {/* Main Content - Scrollable Grid of Traffic Patterns */}
                              <div className="flex-1 min-h-0 overflow-y-auto" style={{ minHeight: 0 }}>
                                <div className={`${(report.size || 'large') === 'xl' ? 'space-y-4' : 'space-y-3'}`}>
                                  {/* Summary Stats Bar */}
                                  <div className={`grid grid-cols-3 ${(report.size || 'large') === 'xl' ? 'gap-3' : 'gap-2'} ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200`}>
                                    <div className="text-center">
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-blue-900`}>
                                        {report.data?.trafficData?.summary?.totalPageViews?.toLocaleString() || 0}
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-xs'} text-blue-600`}>Page Views</div>
                                    </div>
                                    <div className="text-center">
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-blue-900`}>
                                        {report.data?.trafficData?.summary?.totalClicks?.toLocaleString() || 0}
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-xs'} text-blue-600`}>Clicks</div>
                                    </div>
                                    <div className="text-center">
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-blue-900`}>
                                        {report.data?.trafficData?.summary?.totalFormSubmissions?.toLocaleString() || 0}
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-xs'} text-blue-600`}>Forms</div>
                                    </div>
                                  </div>

                                  {/* Landing Pages Section */}
                                  {report.data?.trafficData?.landingPages?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-gray-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-green-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                        </svg>
                                        Landing Pages
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.landingPages.map((page: any, index: number) => (
                                          <div key={index} className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'py-1.5' : 'py-1'}`}>
                                            <span className="truncate flex-1 text-gray-700">{page.page || 'Home'}</span>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{page.sessions}</span>
                                              <span className="text-gray-500">({page.percentage}%)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Popular Pages Section */}
                                  {report.data?.trafficData?.popularPages?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-gray-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-blue-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                        </svg>
                                        Popular Pages
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.popularPages.map((page: any, index: number) => (
                                          <div key={index} className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'py-1.5' : 'py-1'}`}>
                                            <span className="truncate flex-1 text-gray-700">{page.page || 'Home'}</span>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{page.views}</span>
                                              <span className="text-gray-500">({page.unique_visitors} visitors)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Top Clicks Section */}
                                  {report.data?.trafficData?.topClicks?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-gray-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-purple-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
                                        </svg>
                                        Top Clicks
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.topClicks.map((click: any, index: number) => (
                                          <div key={index} className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'py-1.5' : 'py-1'}`}>
                                            <span className={`truncate flex-1 text-gray-700`}>
                                              {click.element.length > ((report.size || 'large') === 'xl' ? 50 : 40) ? click.element.substring(0, ((report.size || 'large') === 'xl' ? 50 : 40)) + '...' : click.element}
                                            </span>
                                            <span className={`font-semibold text-gray-900 ${(report.size || 'large') === 'xl' ? 'ml-3' : 'ml-2'}`}>{click.clicks}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Form Submissions Section */}
                                  {report.data?.trafficData?.formSubmissions?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-gray-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-orange-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                                        </svg>
                                        Form Submissions
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.formSubmissions.map((form: any, index: number) => (
                                          <div key={index} className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'py-1.5' : 'py-1'}`}>
                                            <span className="truncate flex-1 text-gray-700">{form.form}</span>
                                            <span className="font-semibold text-gray-900">{form.submissions}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Paid vs Organic Bar */}
                                  {(report.data?.trafficData?.utmSummary?.paidSessions > 0 || report.data?.trafficData?.utmSummary?.organicSessions > 0) && (
                                    <div className={`bg-gradient-to-r from-amber-50 to-green-50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-amber-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-amber-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
                                        </svg>
                                        Paid vs Organic
                                      </div>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className={`bg-amber-100 rounded-lg ${(report.size || 'large') === 'xl' ? 'p-2.5' : 'p-1.5'} text-center border border-amber-200`}>
                                          <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-amber-900`}>
                                            {report.data.trafficData.utmSummary.paidSessions?.toLocaleString() || 0}
                                          </div>
                                          <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} text-amber-700`}>
                                            Paid ({report.data.trafficData.utmSummary.paidPercentage}%)
                                          </div>
                                        </div>
                                        <div className={`bg-green-100 rounded-lg ${(report.size || 'large') === 'xl' ? 'p-2.5' : 'p-1.5'} text-center border border-green-200`}>
                                          <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-bold text-green-900`}>
                                            {report.data.trafficData.utmSummary.organicSessions?.toLocaleString() || 0}
                                          </div>
                                          <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} text-green-700`}>
                                            Organic ({report.data.trafficData.utmSummary.organicPercentage}%)
                                          </div>
                                        </div>
                                      </div>
                                      {report.data?.trafficData?.utmSummary?.totalSessions > 0 && (
                                        <div className={`${(report.size || 'large') === 'xl' ? 'mt-2.5' : 'mt-1.5'} w-full bg-gray-200 rounded-full h-2 overflow-hidden`}>
                                          <div
                                            className="bg-amber-500 h-2 rounded-full"
                                            style={{ width: `${report.data.trafficData.utmSummary.paidPercentage}%` }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* UTM Campaigns Section */}
                                  {report.data?.trafficData?.utmCampaigns?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-amber-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-amber-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                                        </svg>
                                        Ad Campaigns (UTM)
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.utmCampaigns.map((campaign: any, index: number) => (
                                          <div key={index} className={`${(report.size || 'large') === 'xl' ? 'p-2' : 'p-1.5'} bg-gray-50 rounded-lg border border-gray-100`}>
                                            <div className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'}`}>
                                              <span className="truncate flex-1 font-medium text-gray-800">{campaign.campaign}</span>
                                              <div className="flex items-center space-x-2 flex-shrink-0">
                                                <span className="font-semibold text-gray-900">{campaign.sessions}</span>
                                                <span className="text-gray-500">({campaign.percentage}%)</span>
                                              </div>
                                            </div>
                                            <div className={`${(report.size || 'large') === 'xl' ? 'text-xs' : 'text-[10px]'} text-gray-500 mt-0.5`}>
                                              {campaign.source} / {campaign.medium} · {campaign.visitors} visitor{campaign.visitors !== 1 ? 's' : ''}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Traffic Sources Section */}
                                  {report.data?.trafficData?.trafficSources?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-gray-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-indigo-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                        </svg>
                                        Traffic Sources
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.trafficSources.map((source: any, index: number) => (
                                          <div key={index} className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'py-1.5' : 'py-1'}`}>
                                            <div className="flex items-center truncate flex-1">
                                              {source.is_paid && (
                                                <span className={`inline-block ${(report.size || 'large') === 'xl' ? 'w-2 h-2' : 'w-1.5 h-1.5'} rounded-full bg-amber-500 mr-1.5 flex-shrink-0`} />
                                              )}
                                              <span className="truncate text-gray-700">{source.source}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{source.sessions}</span>
                                              <span className="text-gray-500">({source.percentage}%)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Event Distribution Section */}
                                  {report.data?.trafficData?.eventDistribution?.length > 0 && (
                                    <div className={`bg-white/50 rounded-xl ${(report.size || 'large') === 'xl' ? 'p-3' : 'p-2'} border border-gray-200`}>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} font-semibold text-gray-700 ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} flex items-center`}>
                                        <svg className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} ${(report.size || 'large') === 'xl' ? 'mr-1.5' : 'mr-1'} text-pink-600`} fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                        </svg>
                                        Event Types
                                      </div>
                                      <div className={`${(report.size || 'large') === 'xl' ? 'space-y-2' : 'space-y-1'}`}>
                                        {report.data.trafficData.eventDistribution.map((event: any, index: number) => (
                                          <div key={index} className={`flex items-center justify-between ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'py-1.5' : 'py-1'}`}>
                                            <span className="truncate flex-1 text-gray-700 capitalize">
                                              {event.type.replace(/_/g, ' ')}
                                            </span>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{event.count.toLocaleString()}</span>
                                              <span className="text-gray-500">({event.percentage}%)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Empty State */}
                                  {(!report.data?.trafficData || 
                                    (report.data.trafficData.summary?.totalEvents === 0)) && (
                                    <div className="text-center py-8 text-gray-500">
                                      <svg className={`${(report.size || 'large') === 'xl' ? 'w-16 h-16' : 'w-12 h-12'} mx-auto ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} text-gray-300`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                      </svg>
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-base' : 'text-sm'}`}>No analytics data yet</p>
                                      <p className={`${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'} ${(report.size || 'large') === 'xl' ? 'mt-2' : 'mt-1'}`}>Install the tracking code to start collecting data</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'web_traffic_patterns' ? (
                            /* Web Traffic Patterns - Fallback for non-L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                                <span 
                                  className="text-gray-600 line-clamp-2 truncate font-semibold"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                                <div className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded-lg">
                                  {report.data?.trafficData?.summary?.totalSessions || 0} sessions
                                </div>
                              </div>
                              
                              {/* Main Content - Scrollable Grid of Traffic Patterns */}
                              <div className="flex-1 min-h-0 overflow-y-auto">
                                <div className="space-y-3">
                                  {/* Summary Stats Bar */}
                                  <div className="grid grid-cols-3 gap-2 p-2 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                                    <div className="text-center">
                                      <div className="text-xs font-bold text-blue-900">
                                        {report.data?.trafficData?.summary?.totalPageViews?.toLocaleString() || 0}
                                      </div>
                                      <div className="text-xs text-blue-600">Page Views</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-xs font-bold text-blue-900">
                                        {report.data?.trafficData?.summary?.totalClicks?.toLocaleString() || 0}
                                      </div>
                                      <div className="text-xs text-blue-600">Clicks</div>
                                    </div>
                                    <div className="text-center">
                                      <div className="text-xs font-bold text-blue-900">
                                        {report.data?.trafficData?.summary?.totalFormSubmissions?.toLocaleString() || 0}
                                      </div>
                                      <div className="text-xs text-blue-600">Forms</div>
                                    </div>
                                  </div>

                                  {/* Landing Pages Section */}
                                  {report.data?.trafficData?.landingPages?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                        </svg>
                                        Landing Pages
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.landingPages.map((page: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between text-xs py-1">
                                            <span className="truncate flex-1 text-gray-700">{page.page || 'Home'}</span>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{page.sessions}</span>
                                              <span className="text-gray-500">({page.percentage}%)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Popular Pages Section */}
                                  {report.data?.trafficData?.popularPages?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                                        </svg>
                                        Popular Pages
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.popularPages.map((page: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between text-xs py-1">
                                            <span className="truncate flex-1 text-gray-700">{page.page || 'Home'}</span>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{page.views}</span>
                                              <span className="text-gray-500">({page.unique_visitors} visitors)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Top Clicks Section */}
                                  {report.data?.trafficData?.topClicks?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
                                        </svg>
                                        Top Clicks
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.topClicks.map((click: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between text-xs py-1">
                                            <span className="truncate flex-1 text-gray-700">
                                              {click.element.length > 40 ? click.element.substring(0, 40) + '...' : click.element}
                                            </span>
                                            <span className="font-semibold text-gray-900 ml-2">{click.clicks}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Form Submissions Section */}
                                  {report.data?.trafficData?.formSubmissions?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                                        </svg>
                                        Form Submissions
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.formSubmissions.map((form: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between text-xs py-1">
                                            <span className="truncate flex-1 text-gray-700">{form.form}</span>
                                            <span className="font-semibold text-gray-900">{form.submissions}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Paid vs Organic Bar */}
                                  {(report.data?.trafficData?.utmSummary?.paidSessions > 0 || report.data?.trafficData?.utmSummary?.organicSessions > 0) && (
                                    <div className="bg-gradient-to-r from-amber-50 to-green-50 rounded-xl p-2 border border-amber-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
                                        </svg>
                                        Paid vs Organic
                                      </div>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-amber-100 rounded-lg p-1.5 text-center border border-amber-200">
                                          <div className="text-xs font-bold text-amber-900">
                                            {report.data.trafficData.utmSummary.paidSessions?.toLocaleString() || 0}
                                          </div>
                                          <div className="text-[10px] text-amber-700">
                                            Paid ({report.data.trafficData.utmSummary.paidPercentage}%)
                                          </div>
                                        </div>
                                        <div className="bg-green-100 rounded-lg p-1.5 text-center border border-green-200">
                                          <div className="text-xs font-bold text-green-900">
                                            {report.data.trafficData.utmSummary.organicSessions?.toLocaleString() || 0}
                                          </div>
                                          <div className="text-[10px] text-green-700">
                                            Organic ({report.data.trafficData.utmSummary.organicPercentage}%)
                                          </div>
                                        </div>
                                      </div>
                                      {report.data?.trafficData?.utmSummary?.totalSessions > 0 && (
                                        <div className="mt-1.5 w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                          <div
                                            className="bg-amber-500 h-2 rounded-full"
                                            style={{ width: `${report.data.trafficData.utmSummary.paidPercentage}%` }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* UTM Campaigns Section */}
                                  {report.data?.trafficData?.utmCampaigns?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-amber-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                                        </svg>
                                        Ad Campaigns (UTM)
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.utmCampaigns.map((campaign: any, index: number) => (
                                          <div key={index} className="p-1.5 bg-gray-50 rounded-lg border border-gray-100">
                                            <div className="flex items-center justify-between text-xs">
                                              <span className="truncate flex-1 font-medium text-gray-800">{campaign.campaign}</span>
                                              <div className="flex items-center space-x-2 flex-shrink-0">
                                                <span className="font-semibold text-gray-900">{campaign.sessions}</span>
                                                <span className="text-gray-500">({campaign.percentage}%)</span>
                                              </div>
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-0.5">
                                              {campaign.source} / {campaign.medium} · {campaign.visitors} visitor{campaign.visitors !== 1 ? 's' : ''}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Traffic Sources Section */}
                                  {report.data?.trafficData?.trafficSources?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                                        </svg>
                                        Traffic Sources
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.trafficSources.map((source: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between text-xs py-1">
                                            <div className="flex items-center truncate flex-1">
                                              {source.is_paid && (
                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5 flex-shrink-0" />
                                              )}
                                              <span className="truncate text-gray-700">{source.source}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{source.sessions}</span>
                                              <span className="text-gray-500">({source.percentage}%)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Event Distribution Section */}
                                  {report.data?.trafficData?.eventDistribution?.length > 0 && (
                                    <div className="bg-white/50 rounded-xl p-2 border border-gray-200">
                                      <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                                        <svg className="w-3 h-3 mr-1 text-pink-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                        </svg>
                                        Event Types
                                      </div>
                                      <div className="space-y-1">
                                        {report.data.trafficData.eventDistribution.map((event: any, index: number) => (
                                          <div key={index} className="flex items-center justify-between text-xs py-1">
                                            <span className="truncate flex-1 text-gray-700 capitalize">
                                              {event.type.replace(/_/g, ' ')}
                                            </span>
                                            <div className="flex items-center space-x-2">
                                              <span className="font-semibold text-gray-900">{event.count.toLocaleString()}</span>
                                              <span className="text-gray-500">({event.percentage}%)</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Empty State */}
                                  {(!report.data?.trafficData || 
                                    (report.data.trafficData.summary?.totalEvents === 0)) && (
                                    <div className="text-center py-8 text-gray-500">
                                      <svg className="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                      </svg>
                                      <p className="text-sm">No analytics data yet</p>
                                      <p className="text-xs mt-1">Install the tracking code to start collecting data</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'booking_source' && shouldUseMLSizeSystem(report.type) && (report.size || 'medium') === 'medium' ? (
                            /* Booking Source - Medium Size Template (Table Only, No Pie Chart) - Compact */
                            <div className="flex flex-col h-full" style={{ padding: '8px' }}>
                              {/* Title */}
                              <div className="mb-1.5 flex-shrink-0">
                                <span className="text-xs font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Table Only - No Pie Chart */}
                              <div className="flex-1 overflow-y-auto min-h-0">
                                {report.data?.sourceDetails && report.data.sourceDetails.length > 0 ? (
                                  <div className="w-full">
                                    {/* Calculate total bookings for percentage */}
                                    {(() => {
                                      const totalBookings = report.data.sourceDetails.reduce((sum: number, detail: any) => sum + (detail.count || 0), 0);
                                      
                                      return (
                                        <div className="space-y-0">
                                          {report.data.sourceDetails.map((detail: any, index: number) => {
                                            const backgroundColor = report.data.chartData?.datasets?.[0]?.backgroundColor?.[index] || '#3b82f6';
                                            const percentage = totalBookings > 0 ? ((detail.count || 0) / totalBookings * 100).toFixed(1) : '0.0';
                                            
                                            return (
                                              <div 
                                                key={index} 
                                                className="grid grid-cols-[2fr_0.8fr_1fr_0.8fr] gap-1.5 py-1 border-b border-gray-200 text-[10px] hover:bg-blue-50 rounded transition-colors"
                                              >
                                                <div className="font-semibold text-gray-900 truncate flex items-center gap-1.5" title={detail.source}>
                                                  <div 
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor }}
                                                  ></div>
                                                  {detail.source}
                                                </div>
                                                <div className="text-right font-medium text-gray-700">
                                                  {detail.count}
                                                </div>
                                                <div className="text-right font-bold text-gray-900">
                                                  {formatCurrency(detail.sales, false)}
                                                </div>
                                                <div className="text-right font-medium text-gray-600">
                                                  {percentage}%
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className="text-xs text-gray-500 mb-1">No booking source data available</p>
                                      <p className="text-[10px] text-gray-400">Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'booking_source' && shouldUseChartSizeSystem(report.type) && report.data?.isPieChart && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Booking Source - L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on L/XL */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Horizontal Layout: Left sidebar + Right chart */}
                              <div className="flex-1 flex flex-row min-h-0" style={{ minHeight: 0 }}>
                                {/* Left Sidebar: Legend Only */}
                                <div className={`flex flex-col ${(report.size || 'large') === 'xl' ? 'w-2/5' : 'w-1/3'} ${(report.size || 'large') === 'xl' ? 'pr-3' : 'pr-2'} flex-shrink-0`}>
                                  {/* Custom Legend on Left */}
                                  <div className="flex-1 overflow-y-auto">
                                    {report.data?.sourceDetails?.map((detail: any, index: number) => {
                                      const backgroundColor = report.data.chartData.datasets[0].backgroundColor[index];
                                      return (
                                        <div key={index} className={`flex items-center ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'}`}>
                                          <div 
                                            className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} rounded-full ${(report.size || 'large') === 'xl' ? 'mr-3' : 'mr-2'} flex-shrink-0`}
                                            style={{ backgroundColor }}
                                          ></div>
                                          <div className="flex-1 min-w-0">
                                            <div className={`truncate text-gray-700 ${(report.size || 'large') === 'xl' ? 'font-semibold' : 'font-medium'}`}>
                                              {detail.source}
                                            </div>
                                            <div className="text-gray-500">
                                              {detail.count} booking{detail.count !== 1 ? 's' : ''}
                                            </div>
                                            <div className={`text-gray-900 ${(report.size || 'large') === 'xl' ? 'font-bold text-sm' : 'font-semibold'}`}>
                                              {formatCurrency(detail.sales)}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Right Side: Pie Chart */}
                                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                  {report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false // Hide default legend since we have custom one
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} booking${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'booking_source' && report.data?.isPieChart ? (
                            /* Booking Source - Fallback for non-M/L/XL sizes (shouldn't happen but keeping for safety) */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center mb-2 flex-shrink-0">
                                <span 
                                  className="text-gray-600 line-clamp-2 truncate"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Horizontal Layout: Left sidebar + Right chart */}
                              <div className="flex-1 flex flex-row min-h-0">
                                {/* Left Sidebar: Legend Only */}
                                <div className="flex flex-col w-1/3 pr-2 flex-shrink-0">
                                  {/* Custom Legend on Left */}
                                  <div className="flex-1 overflow-y-auto">
                                    {report.data?.sourceDetails?.map((detail: any, index: number) => {
                                      const backgroundColor = report.data.chartData.datasets[0].backgroundColor[index];
                                      return (
                                        <div key={index} className="flex items-center mb-2 text-xs">
                                          <div 
                                            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                                            style={{ backgroundColor }}
                                          ></div>
                                          <div className="flex-1 min-w-0">
                                            <div className="truncate text-gray-700 font-medium">
                                              {detail.source}
                                            </div>
                                            <div className="text-gray-500">
                                              {detail.count} booking{detail.count !== 1 ? 's' : ''}
                                            </div>
                                            <div className="text-gray-900 font-semibold">
                                              {formatCurrency(detail.sales)}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Right Side: Pie Chart */}
                                <div className="flex-1 min-h-0 overflow-hidden">
                                  {report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false // Hide default legend since we have custom one
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} booking${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : report.type === 'crm_lost_reasons' && report.data?.isPieChart && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* CRM Lost Reasons - L/XL Pie Chart */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              
                              {report.data?.sourceDetails && report.data.sourceDetails.length > 0 ? (
                                <div className="flex-1 flex flex-row min-h-0" style={{ minHeight: 0 }}>
                                  {/* Left Sidebar: Legend */}
                                  <div className={`flex flex-col ${(report.size || 'large') === 'xl' ? 'w-2/5' : 'w-1/3'} ${(report.size || 'large') === 'xl' ? 'pr-3' : 'pr-2'} flex-shrink-0`}>
                                    <div className="flex-1 overflow-y-auto">
                                      {report.data.sourceDetails.map((detail: any, index: number) => {
                                        const backgroundColor = report.data.chartData?.datasets?.[0]?.backgroundColor?.[index] || '#6B7280';
                                        return (
                                          <div key={index} className={`flex items-center ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'}`}>
                                            <div 
                                              className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} rounded-full ${(report.size || 'large') === 'xl' ? 'mr-3' : 'mr-2'} flex-shrink-0`}
                                              style={{ backgroundColor }}
                                            ></div>
                                            <div className="flex-1 min-w-0">
                                              <div className={`truncate text-gray-700 ${(report.size || 'large') === 'xl' ? 'font-semibold' : 'font-medium'}`}>
                                                {detail.source}
                                              </div>
                                              <div className="text-gray-500">
                                                {detail.count} lead{detail.count !== 1 ? 's' : ''} · {detail.percentage}%
                                              </div>
                                              <div className={`text-gray-900 ${(report.size || 'large') === 'xl' ? 'font-bold text-sm' : 'font-semibold'}`}>
                                                {formatCurrency(detail.sales)}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  
                                  {/* Right Side: Pie Chart */}
                                  <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                    {report.data?.chartData && (
                                      <div className="h-full w-full">
                                        <Pie 
                                          data={report.data.chartData}
                                          options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                              legend: { display: false },
                                              tooltip: {
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                titleColor: 'rgb(17, 24, 39)',
                                                bodyColor: 'rgb(17, 24, 39)',
                                                borderColor: 'rgba(239, 68, 68, 0.2)',
                                                borderWidth: 1,
                                                cornerRadius: 12,
                                                callbacks: {
                                                  label: (context: any) => {
                                                    const label = context.label || '';
                                                    const value = context.parsed;
                                                    return `${label}: ${value} lead${value !== 1 ? 's' : ''}`;
                                                  }
                                                }
                                              }
                                            }
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex-1 flex items-center justify-center">
                                  <p className="text-sm text-gray-400">No lost leads in this period</p>
                                </div>
                              )}
                            </div>
                          ) : report.type === 'project_source' && shouldUseMLSizeSystem(report.type) && (report.size || 'medium') === 'medium' ? (
                            /* Project Source - Medium Size Template (Table Only, No Pie Chart) */
                            <div className="flex flex-col h-full" style={{ padding: '8px' }}>
                              {/* Title */}
                              <div className="mb-1.5 flex-shrink-0">
                                <span className="text-xs font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Table Only - No Pie Chart */}
                              <div className="flex-1 overflow-y-auto min-h-0">
                                {report.data?.sourceDetails && report.data.sourceDetails.length > 0 ? (
                                  <div className="w-full">
                                    {(() => {
                                      const totalProjects = report.data.sourceDetails.reduce((sum: number, detail: any) => sum + (detail.count || 0), 0);
                                      
                                      return (
                                        <div className="space-y-0">
                                          {report.data.sourceDetails.map((detail: any, index: number) => {
                                            const backgroundColor = report.data.chartData?.datasets?.[0]?.backgroundColor?.[index] || '#3b82f6';
                                            const percentage = totalProjects > 0 ? ((detail.count || 0) / totalProjects * 100).toFixed(1) : '0.0';
                                            
                                            return (
                                              <div 
                                                key={index} 
                                                className="grid grid-cols-[2fr_1fr_1fr] gap-1.5 py-1 border-b border-gray-200 text-[10px] hover:bg-blue-50 rounded transition-colors"
                                              >
                                                <div className="font-semibold text-gray-900 truncate flex items-center gap-1.5" title={detail.source}>
                                                  <div 
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor }}
                                                  ></div>
                                                  {detail.source}
                                                </div>
                                                <div className="text-right font-medium text-gray-700">
                                                  {detail.count}
                                                </div>
                                                <div className="text-right font-medium text-gray-600">
                                                  {percentage}%
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                      <p className="text-xs text-gray-500 mb-1">No project source data available</p>
                                      <p className="text-[10px] text-gray-400">Try adjusting your date range</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : report.type === 'project_source' && shouldUseChartSizeSystem(report.type) && report.data?.isPieChart && ((report.size || 'large') === 'large' || (report.size || 'large') === 'xl') ? (
                            /* Project Source - L/XL Size Template */
                            <div className="flex flex-col h-full" style={{ padding: (report.size || 'large') === 'xl' ? '16px' : '12px' }}>
                              {/* Title - Fixed size based on L/XL */}
                              <div className={`${(report.size || 'large') === 'xl' ? 'mb-4' : 'mb-2'} flex-shrink-0`}>
                                <span className="text-sm font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5">
                                  {report.title}
                                </span>
                              </div>
                              
                              {/* Horizontal Layout: Left sidebar + Right chart */}
                              <div className="flex-1 flex flex-row min-h-0" style={{ minHeight: 0 }}>
                                {/* Left Sidebar: Legend Only */}
                                <div className={`flex flex-col ${(report.size || 'large') === 'xl' ? 'w-2/5' : 'w-1/3'} ${(report.size || 'large') === 'xl' ? 'pr-3' : 'pr-2'} flex-shrink-0`}>
                                  {/* Custom Legend on Left */}
                                  <div className="flex-1 overflow-y-auto">
                                    {report.data?.sourceDetails?.map((detail: any, index: number) => {
                                      const backgroundColor = report.data.chartData.datasets[0].backgroundColor[index];
                                      return (
                                        <div key={index} className={`flex items-center ${(report.size || 'large') === 'xl' ? 'mb-3' : 'mb-2'} ${(report.size || 'large') === 'xl' ? 'text-sm' : 'text-xs'}`}>
                                          <div 
                                            className={`${(report.size || 'large') === 'xl' ? 'w-4 h-4' : 'w-3 h-3'} rounded-full ${(report.size || 'large') === 'xl' ? 'mr-3' : 'mr-2'} flex-shrink-0`}
                                            style={{ backgroundColor }}
                                          ></div>
                                          <div className="flex-1 min-w-0">
                                            <div className={`truncate text-gray-700 ${(report.size || 'large') === 'xl' ? 'font-semibold' : 'font-medium'}`}>
                                              {detail.source}
                                            </div>
                                            <div className="text-gray-500">
                                              {detail.count} project{detail.count !== 1 ? 's' : ''} ({detail.percentage}%)
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                
                                {/* Right Side: Pie Chart */}
                                <div className="flex-1 min-h-0 overflow-hidden" style={{ minHeight: 0, height: '100%' }}>
                                  {report.data?.chartData && (
                                    <div className="h-full w-full">
                                      <Pie 
                                        data={report.data.chartData}
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            legend: {
                                              display: false // Hide default legend since we have custom one
                                            },
                                            tooltip: {
                                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                              titleColor: 'rgb(17, 24, 39)',
                                              bodyColor: 'rgb(17, 24, 39)',
                                              borderColor: 'rgba(147, 51, 234, 0.2)',
                                              borderWidth: 1,
                                              cornerRadius: 12,
                                              callbacks: {
                                                label: (context: any) => {
                                                  const label = context.label || '';
                                                  const value = context.parsed;
                                                  return `${label}: ${value} project${value !== 1 ? 's' : ''}`;
                                                }
                                              }
                                            }
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Regular Data Report Rendering */
                            <>
                              {/* Small Size Template for Size System Reports */}
                              {shouldUseSizeSystem(report.type) && (report.size || getDefaultSize()) === 'small' ? (
                                <div 
                                  className="flex flex-col h-full"
                                  style={{
                                    padding: getResponsiveFontSize(report.layout, 8, 12, 0.1)
                                  }}
                                >
                                  {/* Title with dotted underline */}
                                  <div className="mb-2 flex-shrink-0 w-full overflow-hidden">
                                    <span 
                                      className="font-medium text-gray-600 border-b border-dotted border-gray-400 pb-0.5 inline-block truncate max-w-full"
                                      style={{ fontSize: getClampedFontSize(report.layout, 11, 14, 14) }}
                                    >
                                      {report.title}
                                    </span>
                                  </div>
                                  {/* Value - Responsive size */}
                                  <div className="flex-1 flex flex-col items-start justify-center">
                                    <div className="flex items-center space-x-2 w-full">
                                      {/* Special layout for Team Rating - stars first, then number */}
                                      {report.type === 'team_rating' && report.data?.value && report.data.value !== '0.0' ? (
                                        <div className="flex items-center gap-1 w-full">
                                          <div className="flex-shrink-0">
                                            <StarRating 
                                              rating={parseFloat(report.data.value)} 
                                              size="small"
                                            />
                                          </div>
                                          <div 
                                            className="font-bold text-gray-900 flex-shrink-0"
                                            style={{ fontSize: getClampedFontSize(report.layout, 16, 20, 20) }}
                                          >
                                            {report.data.value}
                                          </div>
                                        </div>
                                      ) : (
                                        <div 
                                          className="font-bold text-gray-900"
                                          style={{ fontSize: getClampedFontSize(report.layout, 16, 20, 20) }}
                                        >
                                          {report.data?.value || '$0.00'}
                                        </div>
                                      )}
                                      {/* Comparison indicator */}
                                      {report.data?.comparison && (
                                        <div 
                                          className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-full font-medium ${
                                            report.data.comparison.direction === 'up' 
                                              ? 'bg-green-100 text-green-700' 
                                              : report.data.comparison.direction === 'down' 
                                              ? 'bg-red-100 text-red-700' 
                                              : 'bg-gray-100 text-gray-700'
                                          }`}
                                          style={{ fontSize: getClampedFontSize(report.layout, 9, 12, 12) }}
                                        >
                                          {report.data.comparison.direction === 'up' && '↑'}
                                          {report.data.comparison.direction === 'down' && '↓'}
                                          {report.data.comparison.direction === 'same' && '='}
                                          <span>{Math.round(report.data.comparison.percentage)}%</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : shouldUseSizeSystem(report.type) && (report.size || getDefaultSize()) === 'medium' ? (
                                /* Medium Size Template for Sales Reports */
                                <div 
                                  className="flex flex-col h-full"
                                  style={{
                                    padding: getResponsiveFontSize(report.layout, 10, 16, 0.13)
                                  }}
                                >
                                  {/* Title with dotted underline */}
                                  <div className="mb-3 flex-shrink-0 w-full overflow-hidden">
                                    <span 
                                      className="font-medium text-gray-600 border-b border-dotted border-gray-400 pb-1 inline-block truncate max-w-full"
                                      style={{ fontSize: getClampedFontSize(report.layout, 11, 14, 14) }}
                                    >
                                      {report.title}
                                    </span>
                                  </div>
                                  {/* Value - Responsive size */}
                                  <div className="flex-1 flex flex-col items-start justify-center">
                                    <div className="flex items-center space-x-3 w-full">
                                      {/* Special layout for Team Rating - stars first, then number */}
                                      {report.type === 'team_rating' && report.data?.value && report.data.value !== '0.0' ? (
                                        <div className="flex items-center gap-2 w-full">
                                          <div className="flex-shrink-0">
                                            <StarRating 
                                              rating={parseFloat(report.data.value)} 
                                              size="medium"
                                            />
                                          </div>
                                          <div 
                                            className="font-bold text-gray-900 flex-shrink-0"
                                            style={{ fontSize: currentViewType === 'mobile' 
                                              ? getClampedFontSize(report.layout, 20, 24, 24)
                                              : getClampedFontSize(report.layout, 24, 30, 30)
                                            }}
                                          >
                                            {report.data.value}
                                          </div>
                                        </div>
                                      ) : (
                                        <div 
                                          className="font-bold text-gray-900"
                                          style={{ fontSize: currentViewType === 'mobile'
                                            ? getClampedFontSize(report.layout, 20, 24, 24)
                                            : getClampedFontSize(report.layout, 24, 30, 30)
                                          }}
                                        >
                                          {report.data?.value || '$0.00'}
                                        </div>
                                      )}
                                      {/* Comparison indicator - Updated styling */}
                                      {report.data?.comparison && (
                                        <div 
                                          className={`flex items-center space-x-1 px-2 py-1 rounded-full font-medium ${
                                            (() => {
                                              // For expenses, invert colors: up (increase) is bad (red), down (decrease) is good (green)
                                              if (report.type === 'expenses') {
                                                return report.data.comparison.direction === 'up' 
                                                  ? 'bg-red-100 text-red-700' 
                                                  : report.data.comparison.direction === 'down' 
                                                  ? 'bg-green-100 text-green-700' 
                                                  : 'bg-gray-100 text-gray-700';
                                              }
                                              // For other reports, normal logic: up is good (green), down is bad (red)
                                              return report.data.comparison.direction === 'up' 
                                                ? 'bg-green-100 text-green-700' 
                                                : report.data.comparison.direction === 'down' 
                                                ? 'bg-red-100 text-red-700' 
                                                : 'bg-gray-100 text-gray-700';
                                            })()
                                          }`}
                                          style={{ fontSize: getClampedFontSize(report.layout, 11, 14, 14) }}
                                        >
                                          {report.data.comparison.direction === 'up' && '↑'}
                                          {report.data.comparison.direction === 'down' && '↓'}
                                          {report.data.comparison.direction === 'same' && '='}
                                          <span>{Math.round(report.data.comparison.percentage)}%</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* Default Dynamic Rendering for other sizes/types */
                            <div className="flex flex-col h-full">
                              <div className="flex items-center mb-1 flex-shrink-0">
                                <span 
                                  className="text-gray-600 line-clamp-2 truncate"
                                  style={getDynamicTitleSize(report.layout)}
                                >
                                  {report.title}
                                </span>
                              </div>
                              <div className="flex-1 flex flex-col items-start justify-center text-left">
                                <div className="flex items-center space-x-2 mb-0.5 w-full">
                                  {/* Special layout for Team Rating - stars first, then number */}
                                  {report.type === 'team_rating' && report.data?.value && report.data.value !== '0.0' ? (
                                    <div className="flex items-center gap-0.5 w-full overflow-hidden">
                                      <div className="flex-shrink-0" style={{ minWidth: 0 }}>
                                        <StarRating 
                                          rating={parseFloat(report.data.value)} 
                                          dynamicSize={getDynamicFontSize(report.layout, String(report.data?.value || '')).fontSize ? 
                                            parseInt(getDynamicFontSize(report.layout, String(report.data?.value || '')).fontSize.replace('px', '')) * 0.6 : 
                                            12
                                          }
                                        />
                                      </div>
                                      <div 
                                        className="font-bold text-gray-900 flex-shrink-0 ml-1"
                                        style={{
                                          ...getDynamicFontSize(report.layout, String(report.data?.value || '')),
                                          wordBreak: 'keep-all',
                                          overflowWrap: 'normal'
                                        }}
                                      >
                                        {report.data?.value}
                                      </div>
                                    </div>
                                  ) : (
                                    <div 
                                      className={`font-bold text-left ${
                                        report.type === 'missed_calls' ? 'text-red-600' : 'text-gray-900'
                                      }`}
                                      style={{
                                        ...getDynamicFontSize(report.layout, String(report.data?.value || '')),
                                        wordBreak: 'keep-all',
                                        overflowWrap: 'normal'
                                      }}
                                    >
                                      {report.data?.value}
                                    </div>
                                  )}
                                  
                                  {/* Comparison indicator on same line */}
                                  {report.data?.comparison && (() => {
                                    const comparisonStyles = getDynamicComparisonSize(report.layout);
                                    return (
                                      <div className="flex items-center">
                                        <div className="flex items-center space-x-1 px-2 py-1 rounded-full font-medium"
                                             style={{
                                               backgroundColor: (() => {
                                                 if (report.type === 'missed_calls') {
                                                   // For missed calls: up is bad (red), down is good (green)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? 'rgba(239, 68, 68, 0.1)' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? 'rgba(34, 197, 94, 0.1)' 
                                                     : 'rgba(107, 114, 128, 0.1)';
                                                 } else {
                                                   // For normal metrics: up is good (green), down is bad (red)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? 'rgba(34, 197, 94, 0.1)' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? 'rgba(239, 68, 68, 0.1)' 
                                                     : 'rgba(107, 114, 128, 0.1)';
                                                 }
                                               })(),
                                               color: (() => {
                                                 if (report.type === 'missed_calls') {
                                                   // For missed calls: up is bad (red), down is good (green)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? '#dc2626' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? '#16a34a' 
                                                     : '#6b7280';
                                                 } else {
                                                   // For normal metrics: up is good (green), down is bad (red)
                                                   return report.data.comparison.direction === 'up' 
                                                     ? '#16a34a' 
                                                     : report.data.comparison.direction === 'down' 
                                                     ? '#dc2626' 
                                                     : '#6b7280';
                                                 }
                                               })(),
                                               fontSize: comparisonStyles.fontSize,
                                               fontWeight: comparisonStyles.fontWeight,
                                               fontFamily: comparisonStyles.fontFamily,
                                               lineHeight: comparisonStyles.lineHeight
                                             }}>
                                          {report.data.comparison.direction === 'up' && (
                                            <svg 
                                              style={{ width: comparisonStyles.iconSize, height: comparisonStyles.iconSize }} 
                                              fill="currentColor" 
                                              viewBox="0 0 20 20"
                                            >
                                              <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L10 4.414 6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                          {report.data.comparison.direction === 'down' && (
                                            <svg 
                                              style={{ width: comparisonStyles.iconSize, height: comparisonStyles.iconSize }} 
                                              fill="currentColor" 
                                              viewBox="0 0 20 20"
                                            >
                                              <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L10 15.586l3.293-3.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                          {report.data.comparison.direction === 'same' && (
                                            <svg 
                                              style={{ width: comparisonStyles.iconSize, height: comparisonStyles.iconSize }} 
                                              fill="currentColor" 
                                              viewBox="0 0 20 20"
                                            >
                                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                          <span>
                                            {Math.round(report.data.comparison.percentage)}%
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                                
                              </div>
                            </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                        </GridLayout>
                        </div>
                      </div>
                    </div>
                    ) : (
                      // Empty state for individual dashboard with smart canvas height
                      <div 
                        className="flex flex-col items-center justify-center" 
                          style={isEditMode ? {
                            minHeight: `${getGridHeight(dashboard)}px`
                          } : {
                            height: `${getGridHeight(dashboard)}px`
                          }}
                      >
                        <Activity className="h-12 w-12 mb-4 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {isEditMode ? 'Start Building Your Dashboard' : 'No Reports Added Yet'}
                        </h3>
                        <p className="text-center max-w-md text-gray-500 mb-6">
                          {isEditMode 
                            ? 'Click "Add Reports" to add widgets and reports to your dashboard.'
                            : 'Switch to Edit mode to add reports and customize this dashboard.'
                          }
                        </p>
                      </div>
                    );
                })()}
              </div>
            </div>
          </div>
        ))}

        {/* Create Dashboard Button */}
        <div className="flex flex-col items-center">
          {dashboards.filter(dashboard => {
            return currentViewType === 'desktop' ? dashboard.desktopConfigured : dashboard.mobileConfigured;
          }).length === 0 ? (
            // Initial state for current view type
            <>
              <div className="flex flex-col items-center justify-center min-h-[400px]">
                {/* Check if there are dashboards in the opposite view that can be configured */}
                {dashboards.some(dashboard => 
                  currentViewType === 'desktop' ? dashboard.mobileConfigured && !dashboard.desktopConfigured 
                  : dashboard.desktopConfigured && !dashboard.mobileConfigured
                ) ? (
                  <div className="text-center max-w-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      No {currentViewType} dashboards configured
                    </h3>
                    <p className="text-gray-600 text-sm mb-6">
                      You have dashboards configured for {currentViewType === 'desktop' ? 'mobile' : 'desktop'} view. 
                      Configure them for {currentViewType} view or create a new independent dashboard.
                    </p>
                    <div className="space-y-3">
                      <button 
                        onClick={() => setIsModalOpen(true)}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-xl hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 inline mr-2" />
                        Create New {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard
                      </button>
                      <p className="text-xs text-gray-500">
                        Create a new dashboard optimized for {currentViewType} view
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-600 text-sm mb-4">Create Your First {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} Dashboard</p>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="bg-white rounded-full shadow-lg border border-gray-200 p-4 hover:shadow-xl transition-shadow"
                    >
                      <Plus className="h-8 w-8 text-gray-400" />
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            // After dashboards exist - smaller button below
            <button 
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-xl hover:bg-blue-700 flex items-center space-x-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span>Add Another Dashboard</span>
            </button>
          )}
        </div>
      </div>

      {/* Conversion Rate Settings Side Panel */}
      {conversionSettingsReportId && createPortal(
        <div 
          className="fixed inset-0 z-[9999]"
          style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => saveConversionSettings()} />
          <div 
            className="absolute top-0 right-0 h-full w-full max-w-[380px] bg-white shadow-2xl flex flex-col"
            style={{ 
              animation: 'slideInRight 0.3s ease-out',
              borderTopLeftRadius: '20px',
              borderBottomLeftRadius: '20px'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60">
              <h3 className="text-[17px] font-semibold text-gray-900">Settings</h3>
              <button
                onClick={() => saveConversionSettings()}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition-colors"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {/* Conversion Rate Toggle */}
              <div className="bg-gray-50 rounded-[14px] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[15px] font-medium text-gray-900">Conversion Rate</div>
                    <div className="text-[12px] text-gray-500 mt-0.5">Track page views and clicks as conversions</div>
                  </div>
                  <button
                    onClick={() => setConversionSettingsEnabled(!conversionSettingsEnabled)}
                    className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${conversionSettingsEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <div className={`absolute top-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-md transition-transform duration-200 ${conversionSettingsEnabled ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                  </button>
                </div>
              </div>

              {/* Conversion Criteria */}
              {conversionSettingsEnabled && (
                <div className="mt-4 space-y-3">
                  <div className="text-[13px] font-medium text-gray-500 uppercase tracking-wide">Conversion Events</div>
                  
                  {conversionCriteria.map((criterion, index) => (
                    <div key={index} className="bg-gray-50 rounded-[14px] p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            criterion.type === 'route' ? 'bg-blue-500' :
                            criterion.type === 'click' ? 'bg-purple-500' : 'bg-orange-500'
                          }`} />
                          <span className="text-[13px] font-medium text-gray-700">
                            {criterion.type === 'route' ? 'Page Route' :
                             criterion.type === 'click' ? 'Element Click' : 'URL Contains'}
                          </span>
                        </div>
                        <button
                          onClick={() => setConversionCriteria(prev => prev.filter((_, i) => i !== index))}
                          className="w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 active:bg-red-100 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={criterion.type}
                          onChange={(e) => {
                            setConversionCriteria(prev => prev.map((c, i) => i === index ? { ...c, type: e.target.value as 'route' | 'click' | 'url_contains', value: '' } : c));
                          }}
                          className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-[120px] shrink-0"
                        >
                          <option value="route">Route</option>
                          <option value="click">Click</option>
                          <option value="url_contains">URL contains</option>
                        </select>
                        {criterion.type === 'url_contains' ? (
                          <input
                            type="text"
                            value={criterion.value}
                            onChange={(e) => {
                              setConversionCriteria(prev => prev.map((c, i) => i === index ? { ...c, value: e.target.value } : c));
                            }}
                            placeholder="/thank-you"
                            className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-0"
                          />
                        ) : (
                          <select
                            value={criterion.value}
                            onChange={(e) => {
                              setConversionCriteria(prev => prev.map((c, i) => i === index ? { ...c, value: e.target.value } : c));
                            }}
                            onClick={async () => {
                              if (!businessId || !supabase || conversionSettingsLoading || criterion.type === 'url_contains') return;
                              setConversionSettingsLoading(true);
                              try {
                                const currentDashboard = dashboards.find(d => d.reports?.some(r => r.id === conversionSettingsReportId));
                                const dateRange = (currentDashboard?.dateRange || 'this_month') as DateRangeOption;
                                const { startDate, endDate } = getDateRange(dateRange);
                                const startUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0)).toISOString();
                                const endUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59)).toISOString();
                                if (criterion.type === 'route') {
                                  const { data } = await supabase.rpc('get_conversion_routes', { p_business_id: businessId, p_start_date: startUTC, p_end_date: endUTC });
                                  setConversionSettingsRoutes(data || []);
                                } else {
                                  const { data } = await supabase.rpc('get_conversion_clicks', { p_business_id: businessId, p_start_date: startUTC, p_end_date: endUTC });
                                  setConversionSettingsClicks(data || []);
                                }
                              } catch (err) {
                                console.error('Error loading options:', err);
                              } finally {
                                setConversionSettingsLoading(false);
                              }
                            }}
                            className="text-[13px] border border-gray-200 rounded-[10px] px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 min-w-0"
                          >
                            <option value="">-- Select --</option>
                            {criterion.type === 'route' ? (
                              conversionSettingsRoutes.map(r => (
                                <option key={r.route} value={r.route}>{r.route} ({r.sessions} sessions)</option>
                              ))
                            ) : (
                              conversionSettingsClicks.map(c => (
                                <option key={c.identifier} value={c.identifier}>{c.label || c.identifier} ({c.sessions} sessions)</option>
                              ))
                            )}
                          </select>
                        )}
                      </div>
                      {criterion.type === 'url_contains' ? (
                        <p className="text-[11px] text-gray-500 mt-2">
                          Counts a visit when the page path or URL contains this text (e.g. /thank-you).
                        </p>
                      ) : null}
                    </div>
                  ))}

                  {/* Add button */}
                  <button
                    onClick={() => setConversionCriteria(prev => [...prev, { type: 'route', value: '' }])}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-[14px] border-2 border-dashed border-gray-300 text-gray-500 text-[13px] font-medium active:bg-gray-50 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                    Add Conversion Event
                  </button>
                </div>
              )}
            </div>
          </div>
          <style>{`
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
          `}</style>
        </div>,
        document.body
      )}

      {/* Create Dashboard Modal */}
      {isModalOpen && (
        <div className={`fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4`} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif' }}>
          <div className="bg-white rounded-[20px] shadow-xl p-6 border border-gray-200/60 w-full max-w-md mx-4"
               style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)' }}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                Create New Dashboard
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 active:text-gray-600 p-1.5 rounded-full active:bg-gray-100 transition-all duration-150"
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  minWidth: '36px',
                  minHeight: '36px'
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label htmlFor="dashboard-name" className="block text-sm font-medium text-gray-700 mb-2">
                Dashboard Name
              </label>
              <input
                type="text"
                id="dashboard-name"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                placeholder="Enter dashboard name..."
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                style={{ letterSpacing: '-0.01em' }}
                autoFocus
              />
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2.5 text-[15px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-[10px] active:bg-gray-50 transition-all duration-150 active:scale-[0.97]"
                style={{ 
                  minHeight: '36px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDashboard}
                disabled={!dashboardName.trim()}
                className="px-5 py-2.5 text-[15px] font-semibold text-white bg-blue-600 active:bg-blue-700 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.97] shadow-sm"
                style={{ 
                  minHeight: '36px',
                  WebkitTapHighlightColor: 'transparent',
                  letterSpacing: '-0.01em'
                }}
              >
                Create Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Reports Modal - portaled so it appears above app nav (z-[80]) on iOS */}
      {isAddReportsModalOpen && createPortal(
        <div
          className="fixed inset-0 bg-white"
          style={{
            zIndex: 100000,
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            top: 'env(safe-area-inset-top, 0px)',
            left: 'env(safe-area-inset-left, 0px)',
            right: 'env(safe-area-inset-right, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
          }}
        >
          <div className="h-full w-full overflow-y-auto"
               style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)' }}>
            <div className="sticky top-0 bg-white border-b border-gray-200/60 z-10 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate pr-2" style={{ letterSpacing: '-0.01em' }}>
                Add Reports to {currentViewType === 'desktop' ? 'Desktop' : 'Mobile'} View
              </h2>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                {selectedReports.length > 0 && (
                  <button
                    onClick={handleAddSelectedReports}
                    className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-[10px] active:bg-blue-700 transition-all duration-150 active:scale-[0.97] font-medium text-xs sm:text-[15px] whitespace-nowrap"
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      minHeight: '36px',
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Add {selectedReports.length} to {dashboards.find(d => d.id === selectedDashboardId)?.name || 'Dashboard'}
                  </button>
                )}
              <button
                onClick={handleAddReportsModalClose}
                  className="text-gray-400 active:text-gray-600 p-1.5 rounded-full active:bg-gray-100 transition-all duration-150 flex-shrink-0"
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    minWidth: '36px',
                    minHeight: '36px'
                  }}
                >
                  <X className="h-5 w-5" />
              </button>
                </div>
              </div>

            <div className="flex flex-col h-[calc(100vh-73px)]">
              {/* Category Dropdown */}
              <div className="px-4 sm:px-6 py-3 border-b border-gray-200/60 bg-white flex-shrink-0">
                <div className="relative">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 bg-white border border-gray-300 rounded-[10px] text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer"
                    style={{ 
                      letterSpacing: '-0.01em',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    {reportCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-[calc(50%+12px)] -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>

              {/* Main Content Area - Reports */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-4 sm:p-6">
                  {(() => {
                    const currentCategory = reportCategories.find((cat: { id: string; name: string; reports?: any[]; subcategories?: any[] }) => cat.id === selectedCategory);
                    if (!currentCategory) return null;

                    // Get reports to display - either from subcategories or direct reports
                    const reportsToDisplay = currentCategory.subcategories 
                      ? currentCategory.subcategories.flatMap((subcat: { reports: any[] }) => subcat.reports)
                      : (currentCategory.reports || []);

                    return (
                      <>
              <div className="mb-4 sm:mb-6">
                          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2" style={{ letterSpacing: '-0.01em' }}>
                            {currentCategory.name}
                          </h2>
                          <p className="text-xs sm:text-sm text-gray-500">
                            Select a report to add it to your {currentViewType} dashboard
                          </p>
                  </div>

                {currentCategory.subcategories ? (
                  <div className="space-y-0">
                    {currentCategory.subcategories.map((subcat: { id: string; name: string | null; reports: any[] }, subcatIdx: number) => (
                      <div key={subcat.id} className={subcatIdx < currentCategory.subcategories.length - 1 ? 'pb-4 mb-4 border-b border-gray-200/40' : ''}>
                        {subcat.name && (
                          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3" style={{ letterSpacing: '-0.01em' }}>
                            {subcat.name}
                          </h3>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3">
                          {subcat.reports.map((report: { type: string; name: string; description?: string; isSpecial?: boolean }) => {

                            const previewSize = getSmallestAvailableSize(report.type);
                            const previewData = reportPreviews[report.type];
                            const availableSizes = getAvailableSizes(report.type);
                            const isChartReport = shouldUseChartSizeSystem(report.type);
                            
                            const isSelected = selectedReports.some(r => r.type === report.type);
                            
                            return (
                              <div
                                key={report.type}
                                onClick={() => handleReportToggle(report.type, report.name)}
                                className={`active:scale-[0.98] transition-all duration-150 cursor-pointer group ${isChartReport ? 'md:col-span-2' : ''} ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 rounded-[12px]' : ''}`}
                                style={{ 
                                  WebkitTapHighlightColor: 'transparent'
                                }}
                              >
                                <div className="flex flex-col gap-2 relative">
                                  <ReportPreview
                                    reportType={report.type}
                                    reportTitle={report.name}
                                    size={previewSize}
                                    viewType={currentViewType}
                                    data={previewData}
                                  />
                                  {(() => {
                                    const usage = getReportUsage(report.type);
                                    if (usage.count > 0) {
                                      return (
                                        <div className="absolute top-2 right-2 group/usage">
                                          <div className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                            {usage.count}
                      </div>
                                          <div
                                            className="hidden absolute z-50 bottom-full right-0 mb-2 w-56 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none group-hover/usage:block"
                                          >
                                            <div className="font-semibold mb-2">Used in {usage.count} {usage.count === 1 ? 'dashboard' : 'dashboards'}:</div>
                                            <div className="space-y-1">
                                              {usage.dashboards.map(d => (
                                                <div key={d.id} className="text-gray-300">
                                                  • {d.name} ({d.viewType})
                      </div>
                                              ))}
                    </div>
                                            <div className="absolute top-full right-4 -mt-1">
                                              <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                    </div>
                  </div>
                      </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                  <div className="px-1">
                                    {report.description && (
                                      <div className="mb-1.5 group/info relative">
                                        <div className="flex items-center gap-1.5 mb-1">
                                          <p className="text-xs text-gray-600 font-medium">
                                            {report.name}
                                          </p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                            }}
                                            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors relative"
                                            onMouseEnter={(e) => {
                                              const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                              if (tooltip) tooltip.style.display = 'block';
                                            }}
                                            onMouseLeave={(e) => {
                                              const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                              if (tooltip) tooltip.style.display = 'none';
                                            }}
                                          >
                                            <Info className="h-3.5 w-3.5" />
                                          </button>
                                          <div
                                            className="hidden absolute z-50 bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none"
                                            style={{ display: 'none' }}
                                          >
                                            <p className="leading-relaxed">{getReportSummary(report.type)}</p>
                                            <div className="absolute top-full left-4 -mt-1">
                                              <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                      </div>
                    </div>
                    </div>
                                        <p className="text-xs text-gray-600 line-clamp-2">
                                          {report.description}
                                        </p>
                  </div>
                                    )}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-[10px] text-gray-500 font-medium">Sizes:</span>
                                      {availableSizes.map((size) => (
                                        <span
                                          key={size}
                                          className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded font-medium"
                                        >
                                          {formatSizeLabel(size)}
                                        </span>
                                      ))}
                      </div>
                    </div>
                    </div>
                  </div>
                            );
                          })}
                      </div>
                    </div>
                    ))}
                    </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
                    {reportsToDisplay.map((report: { type: string; name: string; description?: string; isSpecial?: boolean }) => {
                      const previewSize = getSmallestAvailableSize(report.type);
                      const previewData = reportPreviews[report.type];
                      const availableSizes = getAvailableSizes(report.type);
                      const isChartReport = shouldUseChartSizeSystem(report.type);
                      
                      const isSelected = selectedReports.some(r => r.type === report.type);
                      
                      return (
                        <div
                          key={report.type}
                          onClick={() => handleReportToggle(report.type, report.name)}
                          className={`active:scale-[0.98] transition-all duration-150 cursor-pointer group ${isChartReport ? 'md:col-span-2' : ''} ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2 rounded-[12px]' : ''}`}
                          style={{ 
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        >
                          <div className="flex flex-col gap-2 relative">
                            <ReportPreview
                              reportType={report.type}
                              reportTitle={report.name}
                              size={previewSize}
                              viewType={currentViewType}
                              data={previewData}
                            />
                            {(() => {
                              const usage = getReportUsage(report.type);
                              if (usage.count > 0) {
                                return (
                                  <div className="absolute top-2 right-2 group/usage">
                                    <div className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                      {usage.count}
                      </div>
                                    <div
                                      className="hidden absolute z-50 bottom-full right-0 mb-2 w-56 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none group-hover/usage:block"
                                    >
                                      <div className="font-semibold mb-2">Used in {usage.count} {usage.count === 1 ? 'dashboard' : 'dashboards'}:</div>
                                      <div className="space-y-1">
                                        {usage.dashboards.map(d => (
                                          <div key={d.id} className="text-gray-300">
                                            • {d.name} ({d.viewType})
                      </div>
                                        ))}
                    </div>
                                      <div className="absolute top-full right-4 -mt-1">
                                        <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                    </div>
                  </div>
                </div>
                                );
                              }
                              return null;
                            })()}
                            <div className="px-1">
                              {report.description && (
                                <div className="mb-1.5 group/info relative">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <p className="text-xs text-gray-600 font-medium">
                                      {report.name}
                                    </p>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                      }}
                                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors relative"
                                      onMouseEnter={(e) => {
                                        const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (tooltip) tooltip.style.display = 'block';
                                      }}
                                      onMouseLeave={(e) => {
                                        const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                                        if (tooltip) tooltip.style.display = 'none';
                                      }}
                                    >
                                      <Info className="h-3.5 w-3.5" />
                                    </button>
                                    <div
                                      className="hidden absolute z-50 bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl pointer-events-none"
                                      style={{ display: 'none' }}
                                    >
                                      <p className="leading-relaxed">{getReportSummary(report.type)}</p>
                                      <div className="absolute top-full left-4 -mt-1">
                                        <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
                      </div>
                    </div>
                    </div>
                                  <p className="text-xs text-gray-600 line-clamp-2">
                                    {report.description}
                                  </p>
                  </div>
                              )}
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] text-gray-500 font-medium">Sizes:</span>
                                {availableSizes.map((size) => (
                                  <span
                                    key={size}
                                    className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded font-medium"
                                  >
                                    {formatSizeLabel(size)}
                                  </span>
                                ))}
                      </div>
                    </div>
                    </div>
                  </div>
                      );
                    })}
                      </div>
                )}
                      </>
                    );
                  })()}
                    </div>
                    </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Mobile-specific optimizations */}
      {currentViewType === 'mobile' && (
        <style jsx global>{`
          /* Mobile touch optimizations */
          * {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            touch-action: manipulation;
          }

          /* Improve scroll performance on mobile */
          .layout {
            -webkit-overflow-scrolling: touch;
            -webkit-transform: translateZ(0);
            backface-visibility: hidden;
          }

          /* Better touch targets for mobile */
          @media (max-width: 768px) {
            /* Ensure all buttons meet minimum touch target size */
            button {
              min-height: 44px;
              min-width: 44px;
            }

            /* Optimize grid interactions for touch */
            .react-grid-item {
              touch-action: manipulation;
            }

            .react-resizable-handle {
              width: 20px !important;
              height: 20px !important;
            }

            /* Prevent text selection during drag operations */
            .react-grid-layout {
              -webkit-user-select: none;
              user-select: none;
            }

            /* Optimize report card interactions */
            .report-card {
              touch-action: manipulation;
              -webkit-tap-highlight-color: rgba(147, 51, 234, 0.1);
            }

            /* Better spacing for mobile interactions */
            .mobile-button-group {
              gap: 12px;
            }

            /* Optimize scrolling in dashboard canvas */
            .dashboard-canvas {
              -webkit-overflow-scrolling: touch;
              overscroll-behavior: contain;
            }
          }

          /* iOS-specific fixes */
          @supports (-webkit-touch-callout: none) {
            /* Prevent zoom on double tap */
            .dashboard-content {
              touch-action: manipulation;
            }

            /* Fix sticky hover states on iOS */
            @media (hover: none) {
              button:hover {
                background-color: initial;
              }
            }
          }

          /* Performance optimizations for mobile */
          @media (max-width: 768px) {
            .react-grid-layout {
              will-change: transform;
            }

            .react-grid-item {
              will-change: transform;
            }

            /* Reduce motion for better performance on lower-end devices */
            @media (prefers-reduced-motion: reduce) {
              * {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
              }
            }
          }

          /* Completely disable all initial animations for KPI reports */
          .react-grid-item {
            transition: none !important;
            transform: none !important;
            animation: none !important;
          }

          .react-grid-item.react-grid-placeholder {
            transition: none !important;
            animation: none !important;
          }

          /* Disable all grid layout animations initially */
          .react-grid-layout {
            transition: none !important;
            animation: none !important;
          }

          /* Ensure the grid container itself doesn't animate */
          .layout[data-initial-load="true"] {
            transition: none !important;
            animation: none !important;
          }

          /* Only enable transitions when in edit mode and after initial load */
          .layout[data-edit-mode="true"]:not([data-initial-load="true"]) .react-grid-item {
            transition: all 0.3s ease !important;
          }

          /* Prevent any transform animations on initial load */
          .react-grid-item > * {
            transition: none !important;
            animation: none !important;
          }

          /* Ensure grid items are positioned correctly from the start */
          .layout[data-initial-load="true"] .react-grid-item {
            transform: translate3d(0, 0, 0) !important;
            transition: none !important;
            animation: none !important;
          }
        `}</style>
      )}

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedChangesWarning && dashboardToDelete && createPortal(
        <div 
          className="fixed inset-0 z-[10002] bg-black/40 backdrop-blur-sm flex items-center justify-center"
          onClick={() => {
            setShowUnsavedChangesWarning(false);
            setDashboardToDelete(null);
          }}
          style={{ 
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-4"
            style={{
              minWidth: '280px',
              maxWidth: '400px',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Unsaved Changes</h3>
              <p className="text-sm text-gray-600 mb-6">
                You have unsaved changes. If you delete this dashboard, all unsaved changes will be lost.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowUnsavedChangesWarning(false);
                    setDashboardToDelete(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-[10px] active:bg-gray-200 transition-colors touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowUnsavedChangesWarning(false);
                    // Proceed to delete confirmation - keep dashboardToDelete set
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-[10px] active:bg-red-600 transition-colors touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Delete Anyway
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Dashboard Confirmation Modal */}
      {dashboardToDelete && !showUnsavedChangesWarning && createPortal(
        <div 
          className="fixed inset-0 z-[10001] bg-black/40 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setDashboardToDelete(null)}
          style={{ 
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl overflow-hidden mx-4"
            style={{
              minWidth: '280px',
              maxWidth: '400px',
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Dashboard</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete this dashboard? This action cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDashboardToDelete(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-[10px] active:bg-gray-200 transition-colors touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    if (dashboardToDelete) {
                      handleDeleteDashboard(dashboardToDelete);
                    }
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-[10px] active:bg-red-600 transition-colors touch-manipulation"
                  style={{ 
                    minHeight: '44px',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* AI Analyzer Modal */}
      {isAIAnalyzerOpen && aiAnalyzerDashboardId && (() => {
        const dashboard = dashboards.find(d => d.id === aiAnalyzerDashboardId);
        if (!dashboard || !businessId) return null;
        
        // Function to fetch data for different date ranges
        const fetchDataForDateRange = async (dateRange: string, customStart?: Date, customEnd?: Date) => {
          console.log(`🔄 Fetching data for ${dateRange}...`, customStart, customEnd);
          
          // Fetch all reports with the specified date range
          const reportsWithData = await Promise.all(
            (dashboard.reports || []).map(async (report) => {
              try {
                const reportData = await getFilteredReportData(
                  report.type,
                  dateRange as any,
                  businessId,
                  customStart,
                  customEnd
                );
                
                return {
                  ...report,
                  data: reportData
                };
              } catch (error) {
                console.error(`Error fetching ${report.type}:`, error);
                return {
                  ...report,
                  data: null
                };
              }
            })
          );
          
          console.log(`✅ Fetched ${reportsWithData.length} reports for ${dateRange}`);
          
          return {
            reports: reportsWithData,
            dateRange: dateRange,
            customStart,
            customEnd
          };
        };
        
        return (
          <AIAnalyzerModal
            isOpen={isAIAnalyzerOpen}
            onClose={() => {
              setIsAIAnalyzerOpen(false);
              setAIAnalyzerDashboardId(null);
            }}
            dashboardId={dashboard.id}
            dashboardName={dashboard.name}
            dashboardData={{
              reports: dashboard.reports || [],
              dateRange: dashboard.dateRange || 'this_month',
              customStartDate: dashboard.customStartDate,
              customEndDate: dashboard.customEndDate
            }}
            businessId={businessId}
            onFetchDataForDateRange={fetchDataForDateRange}
            availableReports={reportCategories}
          />
        );
      })()}
    </div>
  );
}

// Wrap with PermissionGuard
function KPIDashboardWithPermission() {
  return (
    <PermissionGuard permission="kpiDashboard.pageAccess">
      <KPIDashboard />
    </PermissionGuard>
  );
}

export default KPIDashboardWithPermission; 



